import {enterpriseWorkforceFr} from './enterpriseWorkforceTranslations'
import { frDocumentIntelligence } from './documentIntelligenceTranslations'
import { personalTableViewTranslations } from './personalTableViewTranslations'
import { smartTableTranslations } from './smartTableTranslations'
import { tableColumnTranslations } from './tableColumnTranslations'
import { frEmployment } from './employmentTranslations'
import { interfaceDensityTranslations } from './interfaceDensityTranslations'
import { commandPaletteTranslations } from './commandPaletteTranslations'
import { productivityNavigationTranslations } from './productivityNavigationTranslations'
import {frSupervisorOperations,arSupervisorOperations,trSupervisorOperations} from './supervisorOperationsTranslations'
import {frUnionAllocation} from './unionAllocationTranslations'
import { frWorkerCredentialSummary, arWorkerCredentialSummary } from './workerCredentialSummaryTranslations'
import { frWorkerDocumentConsent, arWorkerDocumentConsent } from './workerDocumentConsentTranslations'
import { frWorkerAssignmentReadiness, arWorkerAssignmentReadiness } from './workerAssignmentReadinessTranslations'
import { frWorkerCareer, arWorkerCareer } from './workerCareerTranslations'
import { frWorkerOnboardingPolicy, arWorkerOnboardingPolicy } from './workerOnboardingPolicyTranslations'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { classifyPublicPath, resolvePublicPageLocale } from '../components/landing/marketingSeoUrls'
import type { PublicMarketingLocale } from '../components/landing/marketingPages'
import frWorkspaceLocalization from './frWorkspaceLocalization'
import frWorkerMobileLocalization from './frWorkerMobileLocalization'

export type Locale = 'en' | 'ar' | 'tr' | 'fr'

type Dictionary = Record<string, string>

const STORAGE_KEY = 'punch.locale'
export const supportedLocales: Locale[] = ['en', 'ar', 'tr', 'fr']
const localeNames: Record<Locale, string> = {
  en: 'English',
  ar: 'العربية',
  tr: 'Türkçe',
  fr: 'Français',
}
export const getLocaleName = (locale: Locale) => localeNames[locale]

const ar: Dictionary = {
  'Location privacy': 'خصوصية الموقع',
  'Location tracking': 'تتبّع الموقع',
  'Authorized active-shift tracking': 'تتبّع مصرّح به أثناء الوردية النشطة',
  'No authorized active-work context': 'لا يوجد سياق عمل نشط مصرّح به',
  'Purpose': 'الغرض',
  'Assignment / location': 'المهمة / الموقع',
  'Tracking stops': 'يتوقف التتبّع',
  'When the active work context ends': 'عند انتهاء سياق العمل النشط',
  'Last accepted location': 'آخر موقع مقبول',
  'None yet': 'لا يوجد بعد',
  'Permission / provider': 'الإذن / المزوّد',
  'Browser geolocation': 'تحديد الموقع في المتصفح',
  'Location privacy information': 'معلومات خصوصية الموقع',
  'Precise-coordinate retention': 'مدة الاحتفاظ بالإحداثيات الدقيقة',
  'Punch collects precise location only for an authorized punch, on-the-way window, active shift, permitted break, or configured safety purpose. The server suppresses off-shift submissions, and retention removes precise coordinates unless evidence is under an active hold.': 'يجمع Punch الموقع الدقيق فقط لتسجيل حضور مصرّح به أو نافذة في الطريق أو وردية نشطة أو استراحة مسموحة أو غرض سلامة مهيأ. يحجب الخادم الإرسالات خارج الوردية وتزيل سياسة الاحتفاظ الإحداثيات الدقيقة ما لم يكن الدليل خاضعاً لتعليق نشط.',
  'Decisions & Appeals': 'القرارات والاعتراضات',
  'Request human review': 'طلب مراجعة بشرية',
  'Appeal status': 'حالة الاعتراض',
  'No recorded decisions': 'لا توجد قرارات مسجلة',
  'Work access decision': 'قرار الوصول إلى العمل',
  'This decision does not have a human review path.': 'لا يتوفر مسار مراجعة بشرية لهذا القرار.',
  'Your review request was submitted.': 'تم إرسال طلب المراجعة.',
  'This action is not currently available.': 'هذا الإجراء غير متاح حالياً.',
  'You can request a human review.': 'يمكنك طلب مراجعة بشرية.',
  'Schedule timezone': 'المنطقة الزمنية للجدول',
  'Timezone provenance unavailable': 'مصدر المنطقة الزمنية غير متاح',
  'Legacy time — source timezone unknown': 'وقت قديم — المنطقة الزمنية المصدر غير معروفة',
  'Device timezone fallback': 'استخدام المنطقة الزمنية للجهاز احتياطياً',
  'Worker details': 'تفاصيل العامل',
  'Shift details': 'تفاصيل الوردية',
  'Message worker': 'مراسلة العامل',
  'Scheduled start': 'بداية الوردية المجدولة',
  'Scheduled end': 'نهاية الوردية المجدولة',
  'Conversation ready.': 'المحادثة جاهزة.',
  'Open conversation': 'فتح المحادثة',
  'This worker is no longer active on this map.': 'لم يعد هذا العامل نشطًا على هذه الخريطة.',
  'This location is unavailable or you no longer have access.': 'هذا الموقع غير متاح أو لم يعد لديك إذن الوصول إليه.',
  'Live tracking is disabled by your platform administrator or plan.': 'تم تعطيل التتبع المباشر بواسطة مسؤول المنصة أو الخطة.',
  'Worker details are no longer available.': 'تفاصيل العامل لم تعد متاحة.',
  'Shift details are no longer available.': 'تفاصيل الوردية لم تعد متاحة.',
  'Unable to open a conversation with this worker.': 'تعذر فتح محادثة مع هذا العامل.',
  'Admin Workspace': 'مساحة الإدارة',
  'Shared Agency SaaS controls for organizations, access, workers, jobs, and operations.': 'لوحة تحكم SaaS مشتركة للوكالات لإدارة المؤسسات والصلاحيات والعاملين والوظائف والعمليات.',
  'Unknown user': 'مستخدم غير معروف',
  'role unavailable': 'الدور غير متاح',
  'Organization context unavailable': 'سياق المؤسسة غير متاح',
  'Platform scope — all tenants': 'نطاق المنصة — جميع المستأجرين',
  'Tenant context': 'سياق المستأجر',
  'Platform scope — select a tenant…': 'نطاق المنصة — اختر مستأجرًا…',
  'Clear tenant context': 'مسح سياق المستأجر',
  'Select a tenant to manage tenant-scoped resources.': 'اختر مستأجرًا لإدارة الموارد الخاصة بالمستأجر.',
  'Create the first tenant': 'إنشاء أول مستأجر',
  'No tenants exist yet. Create the first tenant workspace to start onboarding organizations.': 'لا يوجد مستأجرون بعد. أنشئ أول مساحة عمل للمستأجر لبدء إعداد المؤسسات.',
  'Tenant name': 'اسم المستأجر',
  'Tenant slug (optional)': 'معرّف المستأجر (اختياري)',
  'Creating tenant…': 'جارٍ إنشاء المستأجر…',
  'Create tenant': 'إنشاء مستأجر',
  'Unable to create tenant.': 'تعذر إنشاء المستأجر.',
  'Industry Type': 'نوع القطاع',
  'Industry updated.': 'تم تحديث القطاع.',
  'Unable to update tenant industry.': 'تعذر تحديث قطاع المستأجر.',
  'General Workforce Operations': 'عمليات القوى العاملة العامة',
  'Staffing Agency': 'وكالة توظيف',
  'Security Company': 'شركة أمن',
  'Healthcare Provider': 'مزود رعاية صحية',
  'Home Care Agency': 'وكالة رعاية منزلية',
  'Hospitality / Events': 'الضيافة / الفعاليات',
  'Construction / Field Services': 'البناء / الخدمات الميدانية',
  'Property Services': 'خدمات العقارات',
  'Generic workforce coordination for people, sites, shifts, compliance, and payroll.': 'تنسيق عام للقوى العاملة يشمل الأشخاص والمواقع والمناوبات والامتثال والرواتب.',
  'Client orders, worker availability, assignment coverage, approvals, and payroll/billing readiness.': 'طلبات العملاء وتوفر العمال وتغطية التعيينات والموافقات وجاهزية الرواتب والفوترة.',
  'Guard scheduling, site coverage, attendance verification, and incident readiness.': 'جدولة الحراس وتغطية المواقع والتحقق من الحضور وجاهزية الحوادث.',
  'Open staffing requests, coverage gaps, credentials, attendance, and approval readiness.': 'طلبات التوظيف المفتوحة وفجوات التغطية والاعتمادات والحضور وجاهزية الموافقات.',
  'Caregiver visits, client-home coverage, missed visits, availability, and credentials.': 'زيارات مقدمي الرعاية وتغطية منازل العملاء والزيارات الفائتة والتوفر والاعتمادات.',
  'Event staffing, venue coverage, seasonal labor, approvals, and payroll readiness.': 'توظيف الفعاليات وتغطية الأماكن والعمالة الموسمية والموافقات وجاهزية الرواتب.',
  'Jobsite labor, subcontractor workforce, safety documents, timesheets, and payroll.': 'عمالة مواقع العمل وقوى المقاولين الفرعيين ووثائق السلامة وسجلات الدوام والرواتب.',
  'Recurring service orders, field teams, proof of work, site attendance, and billing/payroll readiness.': 'أوامر الخدمة المتكررة والفرق الميدانية وإثبات العمل وحضور الموقع وجاهزية الفوترة والرواتب.',
  'Workforce demand': 'طلب القوى العاملة',
  'Workforce supply': 'عرض القوى العاملة',
  'Assignment coverage': 'تغطية التعيينات',
  'Open client orders': 'طلبات العملاء المفتوحة',
  'Fill rate': 'معدل التغطية',
  'Available workers': 'العمال المتاحون',
  'Client approvals': 'موافقات العملاء',
  'Payroll/billing readiness': 'جاهزية الرواتب والفوترة',
  'Active guards': 'الحراس النشطون',
  'Site coverage': 'تغطية المواقع',
  'Late/missed check-ins': 'تسجيلات دخول متأخرة أو فائتة',
  'Open posts': 'المواقع المفتوحة',
  'Incident/report readiness': 'جاهزية الحوادث والتقارير',
  'Open staffing requests': 'طلبات التوظيف المفتوحة',
  'Coverage gaps': 'فجوات التغطية',
  'Credential expirations': 'انتهاء الاعتمادات',
  'Staff attendance': 'حضور الموظفين',
  'Approval readiness': 'جاهزية الموافقة',
  'Scheduled visits': 'الزيارات المجدولة',
  'Missed/late visits': 'زيارات فائتة أو متأخرة',
  'Caregiver availability': 'توفر مقدمي الرعاية',
  'Client coverage': 'تغطية العملاء',
  'Payroll readiness': 'جاهزية الرواتب',
  'Event coverage': 'تغطية الفعاليات',
  'Open shifts': 'المناوبات المفتوحة',
  'Staff availability': 'توفر الموظفين',
  'Venue activity': 'نشاط المكان',
  'Job site coverage': 'تغطية مواقع العمل',
  'Trade/skill matching': 'مطابقة الحرف والمهارات',
  'Safety/compliance docs': 'وثائق السلامة والامتثال',
  'Timesheet approvals': 'موافقات سجلات الدوام',
  'Payroll export readiness': 'جاهزية تصدير الرواتب',
  'Service order coverage': 'تغطية أوامر الخدمة',
  'Site attendance': 'حضور الموقع',
  'Proof of service': 'إثبات الخدمة',
  'Missed visits': 'الزيارات الفائتة',
  'Billing/payroll readiness': 'جاهزية الفوترة والرواتب',
  'Toggle dark mode': 'تبديل الوضع الداكن',
  'Light mode': 'الوضع الفاتح',
  'Dark mode': 'الوضع الداكن',
  'Switch to dark': 'التبديل إلى الوضع الداكن',
  'Switch to light': 'التبديل إلى الوضع الفاتح',
  'Sign out': 'تسجيل الخروج',
  'Language': 'اللغة',
  'English': 'English',
  'Arabic': 'العربية',
  'Administration Tools': 'أدوات الإدارة',
  'Operations': 'العمليات',
  'Administration': 'الإدارة',
  'Overview': 'نظرة عامة',
  'Organizations': 'المؤسسات',
  'Organization': 'المؤسسة',
  'Org Tree': 'هيكل المؤسسة',
  'Roles & Access': 'الأدوار والصلاحيات',
  'Workers': 'العاملون',
  'Worker': 'عامل',
  'Sharing': 'المشاركة',
  'Documents': 'المستندات',
  'Jobs / Orders': 'الوظائف / الطلبات',
  'Timesheets': 'الجداول الزمنية',
  'Payroll Export': 'تصدير الرواتب',
  'Branding': 'الهوية',
  'Settings': 'الإعدادات',
  'Dashboard': 'لوحة المعلومات',
  'Companies': 'الشركات',
  'Agencies': 'الوكالات',
  'Relationships': 'العلاقات',
  'Locations': 'المواقع',
  'Messages': 'الرسائل',
  'Reports': 'التقارير',
  'Supervisor': 'المشرف',
  'Mapper': 'الخريطة',
  'Name': 'الاسم',
  'Type': 'النوع',
  'Status': 'الحالة',
  'Created': 'تاريخ الإنشاء',
  'Updated': 'آخر تحديث',
  'Actions': 'الإجراءات',
  'Email': 'البريد الإلكتروني',
  'Phone': 'الهاتف',
  'Role': 'الدور',
  'Location': 'الموقع',
  'Company': 'الشركة',
  'Agency': 'الوكالة',
  'Job': 'الوظيفة',
  'Shift': 'المناوبة',
  'Start': 'البداية',
  'End': 'النهاية',
  'Date': 'التاريخ',
  'Time': 'الوقت',
  'Hours': 'الساعات',
  'Total': 'الإجمالي',
  'Notes': 'ملاحظات',
  'Description': 'الوصف',
  'Address': 'العنوان',
  'Search': 'بحث',
  'Filter': 'تصفية',
  'Refresh': 'تحديث',
  'Retry': 'إعادة المحاولة',
  'Cancel': 'إلغاء',
  'Save': 'حفظ',
  'Create': 'إنشاء',
  'Update': 'تحديث',
  'Edit': 'تعديل',
  'Delete': 'حذف',
  'Archive': 'أرشفة',
  'Suspend': 'تعليق',
  'Invite': 'دعوة',
  'Approve': 'اعتماد',
  'Reject': 'رفض',
  'Export': 'تصدير',
  'Import': 'استيراد',
  'Upload': 'رفع',
  'Download': 'تنزيل',
  'Submit': 'إرسال',
  'Reset': 'إعادة ضبط',
  'Close': 'إغلاق',
  'Open': 'فتح',
  'View': 'عرض',
  'Details': 'التفاصيل',
  'Loading...': 'جارٍ التحميل...',
  'No data available.': 'لا توجد بيانات متاحة.',
  'Unavailable': 'غير متاح',
  'Active': 'نشط',
  'Inactive': 'غير نشط',
  'Pending': 'قيد الانتظار',
  'Confirmed': 'مؤكد',
  'Completed': 'مكتمل',
  'Cancelled': 'ملغى',
  'Draft': 'مسودة',
  'Posted': 'منشور',
  'Assigned': 'معين',
  'Applied': 'تم التقديم',
  'Approved': 'معتمد',
  'Rejected': 'مرفوض',
  'Sign in': 'تسجيل الدخول',
  'Create account': 'إنشاء حساب',
  'Password': 'كلمة المرور',
  'Confirm password': 'تأكيد كلمة المرور',
  'Continue with Google': 'المتابعة باستخدام Google',
  'Current context': 'السياق الحالي',
  'Accessible locations': 'المواقع المتاحة',
  'Effective permissions': 'الصلاحيات الفعلية',
  'Tenant': 'المستأجر',
  'Tenant admin': 'مدير المستأجر',
  'Platform admin': 'مدير المنصة',
  'Agency admin': 'مدير الوكالة',
  'Company admin': 'مدير الشركة',
  'Supervisor workspace': 'مساحة عمل المشرف',
  'Worker roster': 'قائمة العاملين',
  'Upcoming': 'القادمة',
  'Punched in': 'مسجل دخول',
  'On break': 'في استراحة',
  'Late': 'متأخر',
  'Punched out': 'مسجل خروج',
  'Timesheet': 'جدول الوقت',
  'CSV export': 'تصدير CSV',
  'PDF export': 'تصدير PDF',
  'Excel export': 'تصدير Excel',
  'Create organization': 'إنشاء مؤسسة',
  'Create tenant organization': 'إنشاء مؤسسة داخل المستأجر',
  'Create relationship': 'إنشاء علاقة',
  'Create OU': 'إنشاء وحدة تنظيمية',
  'Invite worker': 'دعوة عامل',
  'Add worker': 'إضافة عامل',
  'Share worker': 'مشاركة عامل',
  'Create job': 'إنشاء وظيفة',
  'Create shift': 'إنشاء مناوبة',
  'Post shift': 'نشر المناوبة',
  'Duplicate shift': 'نسخ المناوبة',
  'Cancel shift': 'إلغاء المناوبة',
  'Approve timesheet': 'اعتماد جدول الوقت',
  'Generate timesheet': 'إنشاء جدول الوقت',
  'Geofence': 'النطاق الجغرافي',
  'Geofences': 'النطاقات الجغرافية',
  'Latitude': 'خط العرض',
  'Longitude': 'خط الطول',
  'Radius': 'نصف القطر',
  'Draw fence': 'رسم النطاق',
  'Save fence': 'حفظ النطاق',
  'Worker locations': 'مواقع العاملين',
  'Breadcrumbs': 'مسارات الحركة',
  'Map': 'الخريطة',
  'First name': 'الاسم الأول',
  'Last name': 'اسم العائلة',
  'Profile': 'الملف الشخصي',
  'Skills': 'المهارات',
  'Availability': 'التوفر',
  'Emergency contact': 'جهة اتصال الطوارئ',
  'Emergency contact name': 'اسم جهة اتصال الطوارئ',
  'Emergency contact phone': 'هاتف جهة اتصال الطوارئ',
  'Documents consent': 'موافقة المستندات',
  'Subscription': 'الاشتراك',
  'Plan': 'الخطة',
  'Billing': 'الفوترة',
  'Invoices': 'الفواتير',
  'Payment': 'الدفع'
}

Object.assign(ar, {
  'Org Relationships': 'علاقات المؤسسات',
  'Users & Access': 'المستخدمون والصلاحيات',
  'Locations & Geofences': 'المواقع والنطاقات الجغرافية',
  'Operating context': 'سياق التشغيل',
  'Current user unavailable': 'المستخدم الحالي غير متاح',
  'No organization selected': 'لم يتم اختيار مؤسسة',
  'No active organization context': 'لا يوجد سياق مؤسسة نشط',
  'Switch organization context': 'تبديل سياق المؤسسة',
  'Switch organization unit context': 'تبديل سياق الوحدة التنظيمية',
  'Switch location context': 'تبديل سياق الموقع',
  'All visible OUs': 'كل الوحدات المرئية',
  'All visible locations': 'كل المواقع المرئية',
  'Selected location': 'الموقع المحدد',
  'Available location': 'الموقع المتاح',
  'Organization brand colors': 'ألوان هوية المؤسسة',
  'Setup checklist': 'قائمة التهيئة',
  'setup steps are complete.': 'خطوات تهيئة مكتملة.',
  'Setup status is not available from the access context yet.': 'حالة التهيئة غير متاحة من سياق الصلاحيات بعد.',
  'Done': 'تم',
  'Todo': 'بانتظار',
  'Refresh the workspace. If this keeps happening, ask an owner to verify the access context response.': 'حدّث مساحة العمل. إذا استمر ذلك، اطلب من المالك التحقق من استجابة سياق الصلاحيات.',
  'Create an organization to finish setup': 'أنشئ مؤسسة لإكمال التهيئة',
  'Your account is signed in, but there is no organization in the current tenant context yet.': 'تم تسجيل الدخول، لكن لا توجد مؤسسة في سياق المستأجر الحالي بعد.',
  'Open branding': 'فتح الهوية',
  'Admin workspace': 'مساحة الإدارة',
  'Loading your Admin access, organizations, and locations...': 'جار تحميل صلاحيات الإدارة والمؤسسات والمواقع...',
  'We could not load your workspace permissions yet. You can retry, and backend permissions still protect every action.': 'لم نتمكن من تحميل صلاحيات مساحة العمل بعد. يمكنك إعادة المحاولة، وستبقى صلاحيات الخادم حامية لكل إجراء.',
  'You are signed in, but this account does not have Admin Workspace access.': 'تم تسجيل الدخول، لكن هذا الحساب لا يملك صلاحية مساحة الإدارة.',
  'Improved by {value}': 'تحسّن بمقدار {value}',
  'Organization created': 'تم إنشاء مؤسسة',
  'Location added': 'تمت إضافة موقع',
  'Worker invited/added': 'تمت دعوة أو إضافة عامل',
  'Job/order created': 'تم إنشاء وظيفة أو طلب',
  'Worker assigned': 'تم تعيين عامل',
  'Test punch completed or punch troubleshooting available': 'تم اختبار التسجيل أو تتوفر أدوات التشخيص',
  'Rate not shown': 'الأجر غير ظاهر',
  'Recommended': 'موصى به',
  'Worker Marketplace': 'سوق المناوبات للعاملين',
  'Manage availability and apply to eligible open shifts.': 'إدارة التوفر والتقديم على المناوبات المفتوحة المؤهلة.',
  'Availability windows': 'نوافذ التوفر',
  'Eligible open shifts': 'المناوبات المفتوحة المؤهلة',
  'Pending applications': 'الطلبات المعلقة',
  'Upcoming applied shifts': 'المناوبات القادمة التي تم التقديم لها',
  'Day': 'اليوم',
  'Shift type': 'نوع المناوبة',
  'Travel radius km': 'نطاق التنقل كم',
  'Optional': 'اختياري',
  'Add availability': 'إضافة توفر',
  'One-off': 'مرة واحدة',
  'Remove': 'إزالة',
  'Blackout dates': 'تواريخ عدم التوفر',
  'Starts': 'تبدأ',
  'Ends': 'تنتهي',
  'Reason': 'السبب',
  'Add blackout': 'إضافة عدم توفر',
  'Recommended shifts': 'المناوبات الموصى بها',
  'Open shifts': 'المناوبات المفتوحة',
  'No eligible open shifts': 'لا توجد مناوبات مفتوحة مؤهلة',
  'Published, visible, future shifts will appear here.': 'ستظهر هنا المناوبات المنشورة والمرئية والمستقبلية.',
  'Applications': 'الطلبات',
  'When': 'الموعد',
  'Action': 'الإجراء',
  'Withdraw': 'سحب الطلب',
  'No active applications.': 'لا توجد طلبات نشطة.',
  'Loading worker marketplace...': 'جار تحميل سوق المناوبات...',
  'Unable to load marketplace.': 'تعذر تحميل السوق.',
  'Action failed.': 'فشل الإجراء.',
  'Availability added.': 'تمت إضافة التوفر.',
  'Availability removed.': 'تمت إزالة التوفر.',
  'Blackout end time must be after the start time.': 'يجب أن يكون وقت نهاية عدم التوفر بعد وقت البداية.',
  'Blackout added.': 'تمت إضافة عدم التوفر.',
  'Blackout removed.': 'تمت إزالة عدم التوفر.',
  'Application submitted.': 'تم إرسال الطلب.',
  'Application withdrawn.': 'تم سحب الطلب.',
  'Administrator Access': 'دخول المسؤول',
  'Accept Invitation': 'قبول الدعوة',
  'Join the organization scope selected by your administrator.': 'انضم إلى نطاق المؤسسة الذي حدده المسؤول.',
  'Create your admin account and first agency or company workspace.': 'أنشئ حساب المسؤول وأول مساحة وكالة أو شركة.',
  'Sign in with your administrator account to manage shifts and enterprise tools.': 'سجل الدخول بحساب المسؤول لإدارة المناوبات وأدوات المؤسسة.',
  'Already have an account? ': 'لديك حساب بالفعل؟ ',
  "Don't have an account? ": 'ليس لديك حساب؟ ',
  'New admins can create an agency or company workspace from the signup flow.': 'يمكن للمسؤولين الجدد إنشاء مساحة وكالة أو شركة من مسار التسجيل.',
  'Signing in...': 'جار تسجيل الدخول...',
  'Login failed': 'فشل تسجيل الدخول',
  'Use the email provisioned for your admin identity.': 'استخدم البريد الإلكتروني المخصص لهوية المسؤول.',
  '1 visible location': 'موقع مرئي واحد',
  'visible locations': 'مواقع مرئية',
  'Unread notifications': 'إشعارات غير مقروءة',
  'Admin workspace sections': 'أقسام مساحة الإدارة',
  'Time not set': 'الوقت غير محدد',
  'Sun': 'الأحد',
  'Mon': 'الاثنين',
  'Tue': 'الثلاثاء',
  'Wed': 'الأربعاء',
  'Thu': 'الخميس',
  'Fri': 'الجمعة',
  'Sat': 'السبت',
  'Platform Owner': 'مالك المنصة'
})

Object.assign(ar, {
  'Book demo': 'احجز عرضًا توضيحيًا',
  'Request Demo': 'طلب عرض توضيحي',
  'Sign up': 'اشترك',
  'Get started': 'ابدأ',
  'Learn more': 'تعرف على المزيد',
  'Next': 'التالي',
  'Previous': 'السابق',
  'Back': 'العودة',
  'Continue': 'متابعة',
  'Submit': 'إرسال',
  'Reset': 'إعادة تعيين',
  'Close': 'إغلاق',
  'Open': 'فتح',
  'View': 'عرض',
  'Details': 'التفاصيل',
  'Name': 'الاسم',
  'Email': 'البريد الإلكتروني',
  'Password': 'كلمة المرور',
  'Phone': 'الهاتف',
  'Address': 'العنوان',
  'Type': 'النوع',
  'Status': 'الحالة',
  'Created': 'تم الإنشاء',
  'Updated': 'تم التحديث',
  'Actions': 'الإجراءات',
  'Company': 'الشركة',
  'Agency': 'الوكالة',
  'Job': 'الوظيفة',
  'Date': 'التاريخ',
  'Time': 'الوقت',
  'Start': 'البداية',
  'End': 'النهاية',
  'Duration': 'المدة',
  'Hours': 'الساعات',
  'Minutes': 'الدقائق',
  'Total': 'الإجمالي',
  'Amount': 'المبلغ',
  'Currency': 'العملة',
  'Description': 'الوصف',
  'Notes': 'ملاحظات',
  'Required': 'مطلوب',
  'Optional': 'اختياري',
  'Already have an account? ': 'هل لديك حساب بالفعل؟ ',
  "Don't have an account? ": 'ليس لديك حساب؟ ',
  'Create account': 'إنشاء حساب',
  'Signing in...': 'جار تسجيل الدخول...',
  'Login failed': 'فشل تسجيل الدخول',
  'Email address': 'عنوان البريد الإلكتروني',
  'Enter your email': 'أدخل بريدك الإلكتروني',
  'Enter your password': 'أدخل كلمة مرورك',
  'Enter a valid email address': 'أدخل عنوان بريد إلكتروني صحيح',
  'Use the email provisioned for your admin identity.': 'استخدم البريد الإلكتروني المخصص لهوية المسؤول.',
  'Password must be at least 8 characters': 'يجب أن تكون كلمة المرور 8 أحرف على الأقل',
  'First name': 'الاسم الأول',
  'Last name': 'اسم العائلة',
  'Confirm password': 'تأكيد كلمة المرور',
  'Passwords do not match': 'كلمات المرور غير متطابقة',
  'Accept invitation': 'قبول الدعوة',
  'Create organization': 'إنشاء مؤسسة',
  'Create tenant organization': 'إنشاء مؤسسة داخل المستأجر',
  'Organization name': 'اسم المؤسسة',
  'Organization type': 'نوع المؤسسة',
  'Select organization type': 'اختر نوع المؤسسة',
  'Add first location': 'أضف أول موقع',
  'Location name': 'اسم الموقع',
  'City': 'المدينة',
  'State': 'الدولة',
  'ZIP code': 'الرمز البريدي',
  'Country': 'الدولة',
  'Next step': 'الخطوة التالية',
  'Error': 'خطأ',
  'Success': 'نجح',
  'Warning': 'تحذير',
  'Info': 'معلومات',
  'Week': 'الأسبوع',
  'Calendar': 'التقويم',
  'Open Shifts': 'المناوبات المفتوحة',
  'Conflicts': 'التعارضات',
  'Templates': 'القوالب',
  'Scheduling board': 'لوحة الجدولة',
  'Create, edit, fill, and manage shifts directly from the calendar.': 'إنشاء المناوبات وتعديلها وتعبئتها وإدارتها مباشرة من التقويم.',
  'Shift IDs stay internal; cards show job, organization, location, worker, skill, and status labels.': 'تبقى معرفات المناوبات داخلية؛ وتعرض البطاقات مسميات الوظيفة والمؤسسة والموقع والعامل والمهارة والحالة.',
  'Job, worker, location, skill': 'الوظيفة، العامل، الموقع، المهارة',
  'All locations': 'كل المواقع',
  'All companies': 'كل الشركات',
  'All agencies': 'كل الوكالات',
  'All jobs': 'كل الوظائف',
  'All statuses': 'كل الحالات',
  'All assigned workers': 'كل العمال المعينين',
  'Choose a job': 'اختر وظيفة',
  'Use job location': 'استخدام موقع الوظيفة',
  'Loading shifts...': 'جار تحميل المناوبات...',
  'No shifts scheduled for this range.': 'لا توجد مناوبات مجدولة ضمن هذا النطاق.',
  'Untitled job': 'وظيفة بلا عنوان',
  'No location': 'لا يوجد موقع',
  'No company': 'لا توجد شركة',
  'No agency': 'لا توجد وكالة',
  'No required skills': 'لا توجد مهارات مطلوبة',
  'assigned': 'معين',
  'open': 'مفتوح',
  'applications': 'طلبات',
  'Open job': 'فتح الوظيفة',
  'Duplicate': 'نسخ',
  'Shift detail': 'تفاصيل المناوبة',
  'Coverage': 'التغطية',
  'Assigned worker': 'العامل المعين',
  'Open profile': 'فتح الملف',
  'No assigned worker yet.': 'لا يوجد عامل معين بعد.',
  'Refresh details': 'تحديث التفاصيل',
  'Open job detail': 'فتح تفاصيل الوظيفة',
  'Cancel reason': 'سبب الإلغاء',
  'Matched workers': 'العمال المطابقون',
  'Application': 'الطلب',
  'No contact shown': 'لا توجد بيانات اتصال ظاهرة',
  'Not applied': 'لم يتقدم',
  'Not assigned': 'غير معين',
  'Assign': 'تعيين',
  'No matched workers yet.': 'لا يوجد عمال مطابقون بعد.',
  'Applicants': 'المتقدمون',
  'No applicants yet.': 'لا يوجد متقدمون بعد.',
  'Assignments': 'التعيينات',
  'No assignments yet.': 'لا توجد تعيينات بعد.',
  'Unable to load schedule.': 'تعذر تحميل الجدول.',
  'Unable to load shift details.': 'تعذر تحميل تفاصيل المناوبة.',
  'Schedule action failed.': 'فشل إجراء الجدولة.',
  'Shift created.': 'تم إنشاء المناوبة.',
  'Shift duplicated.': 'تم نسخ المناوبة.',
  'Shift cancelled.': 'تم إلغاء المناوبة.',
  'Worker assignment created.': 'تم إنشاء تعيين العامل.',
  'this shift': 'هذه المناوبة',
  'on': 'في',
  'Schedule access requires job, shift, application, or assignment view permission.': 'يتطلب الوصول إلى الجدولة صلاحية عرض الوظائف أو المناوبات أو الطلبات أو التعيينات.',
  'Open shift status': 'مفتوح',
  'Partially Filled': 'معبأة جزئيا',
  'Filled': 'معبأة',
  'In Progress': 'قيد التنفيذ',
  'Live Map / Breadcrumbs': 'الخريطة المباشرة / مسارات الحركة',
  'Location trails are available only while tracking is enabled and workers are on assigned shifts.': 'تتوفر مسارات الموقع فقط عند تفعيل التتبع ووجود العمال على مناوبات معينة.',
  "Access is limited to authorized supervisors/admins for the worker's assigned location or organization.": 'يقتصر الوصول على المشرفين أو الإداريين المصرح لهم ضمن موقع العامل المعين أو مؤسسته.',
  'Tracking is disabled for this location. Enable live worker tracking for assigned shifts in Location Edit before expecting live worker movement.': 'التتبع معطل لهذا الموقع. فعّل تتبع العمال المباشر للمناوبات المعينة من تعديل الموقع قبل توقع ظهور الحركة.',
  'Unable to load live worker locations.': 'تعذر تحميل مواقع العمال المباشرة.',
  'Unable to load worker breadcrumb trail.': 'تعذر تحميل مسار حركة العامل.',
  'Select worker': 'اختر عاملا',
  'Time range': 'نطاق الوقت',
  'Last 30 min': 'آخر 30 دقيقة',
  'Last 2 hours': 'آخر ساعتين',
  'Last 12 hours': 'آخر 12 ساعة',
  'Custom': 'مخصص',
  'From': 'من',
  'To': 'إلى',
  'Loading map data...': 'جار تحميل بيانات الخريطة...',
  'Worker breadcrumb map': 'خريطة مسار العامل',
  'live workers with GPS points': 'عمال مباشرون لديهم نقاط GPS',
  'stale latest points': 'آخر نقاط قديمة',
  'selected trail points': 'نقاط المسار المحدد',
  'last refresh': 'آخر تحديث',
  'No active worker location events exist for this location yet.': 'لا توجد أحداث موقع عامل نشطة لهذا الموقع بعد.',
  'Select a worker to view breadcrumb trail.': 'اختر عاملا لعرض مسار الحركة.',
  'Live GPS unavailable; showing assigned location.': 'GPS المباشر غير متاح؛ يتم عرض الموقع المعين.',
  'Breadcrumbs returned, but none had valid coordinates.': 'تم إرجاع مسارات، لكن لا توجد إحداثيات صالحة.',
  'No breadcrumb trail for this worker in selected range.': 'لا يوجد مسار حركة لهذا العامل في النطاق المحدد.',
  'punch status': 'حالة التسجيل',
  'location source': 'مصدر الموقع',
  'last GPS update': 'آخر تحديث GPS',
  'accuracy': 'الدقة',
  'No contact or assignment summary available.': 'لا يتوفر ملخص اتصال أو تعيين.',
  'Location fallback': 'موقع بديل',
  'Stale GPS': 'GPS قديم',
  'Live GPS': 'GPS مباشر',
  'Missing GPS': 'GPS مفقود',
  'Role/assignment': 'الدور / التعيين',
  'Shift start': 'بداية المناوبة',
  'Last punch': 'آخر تسجيل',
  'Last GPS': 'آخر GPS',
  'Source': 'المصدر',
  'Point': 'نقطة',
  'of': 'من',
  'Event': 'الحدث',
  'Sequence': 'التسلسل',
  'Trail start': 'بداية المسار',
  'Trail end': 'نهاية المسار',
  'geofence': 'النطاق الجغرافي',
  'Timesheet / payroll export': 'تصدير الجداول الزمنية / الرواتب',
  'Filters show readable worker, location, organization, job, and assignment labels. CSV reference IDs remain internal to selectors and appear only as export columns.': 'تعرض الفلاتر أسماء واضحة للعامل والموقع والمؤسسة والوظيفة والتعيين. تبقى معرفات CSV المرجعية داخلية في المحددات ولا تظهر إلا كأعمدة تصدير.',
  'Clear filters': 'مسح الفلاتر',
  'Preview rows': 'معاينة الصفوف',
  'Loading preview...': 'جار تحميل المعاينة...',
  'Downloading...': 'جار التنزيل...',
  'Loading export filters...': 'جار تحميل فلاتر التصدير...',
  'Timesheet start date': 'تاريخ بداية الجدول الزمني',
  'Timesheet end date': 'تاريخ نهاية الجدول الزمني',
  'Work location': 'موقع العمل',
  'Worker profile': 'ملف العامل',
  'Job / order': 'وظيفة / طلب',
  'All jobs / orders': 'كل الوظائف / الطلبات',
  'Worker assignment': 'تعيين العامل',
  'All assignments': 'كل التعيينات',
  'Approval status': 'حالة الاعتماد',
  'Pending approval': 'بانتظار الاعتماد',
  'Supervisor approved': 'معتمد من المشرف',
  'Approved/export-ready': 'معتمد / جاهز للتصدير',
  'Disputed': 'متنازع عليه',
  'Payroll locked': 'مقفل للرواتب',
  'Export state': 'حالة التصدير',
  'All export states': 'كل حالات التصدير',
  'Not exported': 'لم يصدر',
  'Format': 'الصيغة',
  'JSON integration payload': 'حمولة تكامل JSON',
  'XLSX workbook': 'مصنف XLSX',
  'Template label': 'تسمية القالب',
  'Punch standard payroll': 'رواتب Punch القياسية',
  'Mark approved rows exported after download': 'وضع علامة مصدّر على الصفوف المعتمدة بعد التنزيل',
  'Approval and export filters apply to generated files. Mark exported only changes approved company or payroll-locked rows, keeps audit history, and does not create irreversible payroll locking.': 'تنطبق فلاتر الاعتماد والتصدير على الملفات المولدة. وضع علامة مصدّر يغير فقط صفوف الشركة المعتمدة أو المقفلة للرواتب، ويحافظ على سجل التدقيق، ولا ينشئ قفلا نهائيا للرواتب.',
  'Timesheet approval queue': 'قائمة اعتماد الجداول الزمنية',
  'Completed shifts are grouped by approval status and scoped by tenant, organization, and location permissions on the backend.': 'تجمع المناوبات المكتملة حسب حالة الاعتماد وتخضع في الخادم لصلاحيات المستأجر والمؤسسة والموقع.',
  'Loading completed shifts...': 'جار تحميل المناوبات المكتملة...',
  'No completed shifts matched': 'لا توجد مناوبات مكتملة مطابقة',
  'Completed worker shifts that need review, approval, rejection, or export tracking will appear here.': 'ستظهر هنا مناوبات العمال المكتملة التي تحتاج إلى مراجعة أو اعتماد أو رفض أو تتبع تصدير.',
  'Company / agency': 'الشركة / الوكالة',
  'Location / job': 'الموقع / الوظيفة',
  'Actual time': 'الوقت الفعلي',
  'Net payable': 'الصافي المستحق',
  'Breaks': 'الاستراحات',
  'Review': 'مراجعة'
})

const tr: Dictionary = {
  'Language': 'Dil',
  'English': 'English',
  'Arabic': 'العربية',
  'Turkish': 'Türkçe',
  'French': 'Français',
  'Worker': 'Çalışan',
  'Workers': 'Çalışanlar',
  'Supervisor': 'Süpervizör',
  'Location': 'Konum',
  'Locations': 'Konumlar',
  'Shift': 'Vardiya',
  'Timesheet': 'Puantaj',
  'Timesheets': 'Puantajlar',
  'Approval': 'Onay',
  'Payroll Export': 'Bordro dışa aktarımı',
  'Assignment': 'Atama',
  'Punch In': 'Giriş kaydı',
  'Punch Out': 'Çıkış kaydı',
  'Break': 'Mola',
  'Geofence': 'Konum sınırı',
  'Documents': 'Belgeler',
  'Compliance': 'Uyumluluk',
  'Sign in': 'Oturum aç',
  'Sign out': 'Oturumu kapat',
  'Switch to dark': 'Koyu moda geç',
  'Switch to light': 'Açık moda geç',
  'Search': 'Ara',
  'Filter': 'Filtrele',
  'Refresh': 'Yenile',
  'Retry': 'Tekrar dene',
  'Cancel': 'İptal',
  'Save': 'Kaydet',
  'Create': 'Oluştur',
  'Update': 'Güncelle',
  'Edit': 'Düzenle',
  'Delete': 'Sil',
  'Archive': 'Arşivle',
  'Suspend': 'Askıya al',
  'Approve': 'Onayla',
  'Reject': 'Reddet',
  'Export': 'Dışa aktar',
  'Loading...': 'Yükleniyor...',
  'No data available.': 'Kullanılabilir veri yok.',
  'Unavailable': 'Kullanılamıyor',
  'Active': 'Aktif',
  'Inactive': 'Pasif',
  'Pending': 'Beklemede',
  'Confirmed': 'Onaylandı',
  'Completed': 'Tamamlandı',
  'Cancelled': 'İptal edildi',
  'Approved': 'Onaylandı',
  'Rejected': 'Reddedildi',
  'Rate not shown': 'Ücret gösterilmiyor',
  'Book demo': 'Demo kitabı',
  'Request Demo': 'Demo talep et',
  'Sign up': 'Kaydol',
  'Get started': 'Başla',
  'Learn more': 'Daha fazla bilgi',
  'Next': 'İleri',
  'Previous': 'Geri',
  'Back': 'Geri dön',
  'Continue': 'Devam et',
  'Submit': 'Gönder',
  'Reset': 'Sıfırla',
  'Close': 'Kapat',
  'Open': 'Aç',
  'View': 'Görüntüle',
  'Details': 'Ayrıntılar',
  'Name': 'Ad',
  'Email': 'E-posta',
  'Password': 'Şifre',
  'Phone': 'Telefon',
  'Address': 'Adres',
  'Type': 'Tür',
  'Status': 'Durum',
  'Created': 'Oluşturulan',
  'Updated': 'Güncellenen',
  'Actions': 'İşlemler',
  'Company': 'Şirket',
  'Agency': 'Ajans',
  'Job': 'İş',
  'Date': 'Tarih',
  'Time': 'Saat',
  'Start': 'Başla',
  'End': 'Sonlandır',
  'Duration': 'Süre',
  'Hours': 'Saatler',
  'Minutes': 'Dakikalar',
  'Total': 'Toplam',
  'Amount': 'Tutar',
  'Currency': 'Para birimi',
  'Description': 'Açıklama',
  'Notes': 'Notlar',
  'Required': 'Gerekli',
  'Optional': 'İsteğe bağlı',
  'Already have an account? ': 'Zaten bir hesabınız var mı? ',
  "Don't have an account? ": 'Henüz bir hesabınız yok mu? ',
  'Create account': 'Hesap oluştur',
  'Signing in...': 'Giriş yapılıyor...',
  'Login failed': 'Giriş başarısız',
  'Email address': 'E-posta adresi',
  'Enter your email': 'E-postanızı girin',
  'Enter your password': 'Şifrenizi girin',
  'Enter a valid email address': 'Geçerli bir e-posta adresi girin',
  'Use the email provisioned for your admin identity.': 'Yönetici kimliğiniz için sağlanan e-posta adresini kullanın.',
  'Password must be at least 8 characters': 'Şifre en az 8 karakter olmalıdır',
  'First name': 'Ad',
  'Last name': 'Soyadı',
  'Confirm password': 'Şifreyi Onayla',
  'Passwords do not match': 'Şifreler eşleşmiyor',
  'Accept invitation': 'Daveti kabul et',
  'Create organization': 'Kuruluş oluştur',
  'Create tenant organization': 'Kiracı kuruluşu oluştur',
  'Organization name': 'Kuruluş adı',
  'Organization type': 'Kuruluş türü',
  'Select organization type': 'Kuruluş türünü seçin',
  'Add first location': 'İlk konumu ekle',
  'Location name': 'Konum adı',
  'City': 'Şehir',
  'State': 'Devlet',
  'ZIP code': 'Posta kodu',
  'Country': 'Ülke',
  'Next step': 'Sonraki adım',
  'Error': 'Hata',
  'Success': 'Başarı',
  'Warning': 'Uyarı',
  'Info': 'Bilgi',
}

const fr: Dictionary = {
  'Global profile and visibility settings are managed by the account owner or system security administration.': 'Le profil global et les paramètres de visibilité sont gérés par le titulaire du compte ou l’administration de la sécurité du système.',
  'Location privacy': 'Confidentialité de la localisation',
  'Location tracking': 'Suivi de la localisation',
  'Authorized active-shift tracking': 'Suivi autorisé pendant le quart actif',
  'No authorized active-work context': 'Aucun contexte de travail actif autorisé',
  'Purpose': 'Finalité',
  'Assignment / location': 'Affectation / lieu',
  'Tracking stops': 'Fin du suivi',
  'When the active work context ends': 'À la fin du contexte de travail actif',
  'Last accepted location': 'Dernière localisation acceptée',
  'None yet': 'Aucune pour le moment',
  'Permission / provider': 'Autorisation / fournisseur',
  'Browser geolocation': 'Géolocalisation du navigateur',
  'Location privacy information': 'Renseignements sur la confidentialité de la localisation',
  'Precise-coordinate retention': 'Conservation des coordonnées précises',
  'Punch collects precise location only for an authorized punch, on-the-way window, active shift, permitted break, or configured safety purpose. The server suppresses off-shift submissions, and retention removes precise coordinates unless evidence is under an active hold.': 'Punch recueille la localisation précise uniquement pour un pointage autorisé, une fenêtre En route, un quart actif, une pause permise ou une finalité de sécurité configurée. Le serveur supprime les envois hors quart et la conservation retire les coordonnées précises sauf si la preuve fait l’objet d’une retenue active.',
  'Decisions & Appeals': 'Décisions et appels',
  'Request human review': 'Demander une révision humaine',
  'Appeal status': 'État de l’appel',
  'No recorded decisions': 'Aucune décision enregistrée',
  'Work access decision': 'Décision d’accès au travail',
  'This decision does not have a human review path.': 'Cette décision ne prévoit aucun processus de révision humaine.',
  'Your review request was submitted.': 'Votre demande de révision a été soumise.',
  'This action is not currently available.': 'Cette action n’est pas disponible actuellement.',
  'You can request a human review.': 'Vous pouvez demander une révision humaine.',
  'Language': 'Langue',
  'English': 'English',
  'Arabic': 'العربية',
  'Turkish': 'Türkçe',
  'French': 'Français',
  'Worker': 'Employé',
  'Workers': 'Employés',
  'Supervisor': 'Superviseur',
  'Location': 'Site',
  'Locations': 'Sites',
  'Shift': 'Quart',
  'Timesheet': 'Feuille de temps',
  'Timesheets': 'Feuilles de temps',
  'Approval': 'Approbation',
  'Payroll Export': 'Export de paie',
  'Assignment': 'Affectation',
  'Punch In': "Pointer l'arrivée",
  'Punch Out': 'Pointer le départ',
  'Break': 'Pause',
  'Geofence': 'Zone géographique',
  'Documents': 'Documents',
  'Compliance': 'Conformité',
  'Sign in': 'Se connecter',
  'Sign out': 'Se déconnecter',
  'Switch to dark': 'Passer au mode sombre',
  'Switch to light': 'Passer au mode clair',
  'Search': 'Rechercher',
  'Filter': 'Filtrer',
  'Refresh': 'Actualiser',
  'Retry': 'Réessayer',
  'Cancel': 'Annuler',
  'Save': 'Enregistrer',
  'Create': 'Créer',
  'Update': 'Mettre à jour',
  'Edit': 'Modifier',
  'Delete': 'Supprimer',
  'Archive': 'Archiver',
  'Suspend': 'Suspendre',
  'Approve': 'Approuver',
  'Reject': 'Rejeter',
  'Export': 'Exporter',
  'Loading...': 'Chargement...',
  'No data available.': 'Aucune donnée disponible.',
  'Unavailable': 'Indisponible',
  'Active': 'Actif',
  'Inactive': 'Inactif',
  'Pending': 'En attente',
  'Confirmed': 'Confirmé',
  'Completed': 'Terminé',
  'Cancelled': 'Annulé',
  'Approved': 'Approuvé',
  'Rejected': 'Rejeté',
  'Rate not shown': 'Taux non affiché',
  'Book demo': 'Réserver une démo',
  'Request Demo': 'Demander une démo',
  'Sign up': "S'inscrire",
  'Get started': 'Commencer',
  'Learn more': 'En savoir plus',
  'Next': 'Suivant',
  'Previous': 'Précédent',
  'Back': 'Retour',
  'Continue': 'Continuer',
  'Submit': 'Soumettre',
  'Reset': 'Réinitialiser',
  'Close': 'Fermer',
  'Open': 'Ouvrir',
  'View': 'Afficher',
  'Details': 'Détails',
  'Name': 'Nom',
  'Email': 'E-mail',
  'Password': 'Mot de passe',
  'Phone': 'Téléphone',
  'Address': 'Adresse',
  'Type': 'Type',
  'Status': 'Statut',
  'Created': 'Créé',
  'Updated': 'Mis à jour',
  'Actions': 'Actions',
  'Company': 'Entreprise',
  'Agency': 'Agence',
  'Job': 'Emploi',
  'Date': 'Date',
  'Time': 'Heure',
  'Start': 'Début',
  'End': 'Fin',
  'Duration': 'Durée',
  'Hours': 'Heures',
  'Minutes': 'Minutes',
  'Total': 'Total',
  'Amount': 'Montant',
  'Currency': 'Devise',
  'Description': 'Description',
  'Notes': 'Remarques',
  'Required': 'Requis',
  'Optional': 'Optionnel',
  'Already have an account? ': 'Vous avez déjà un compte ? ',
  "Don't have an account? ": "Pas encore de compte ? ",
  'Create account': 'Créer un compte',
  'Signing in...': 'Connexion en cours...',
  'Login failed': 'La connexion a échoué',
  'Email address': 'Adresse e-mail',
  'Enter your email': 'Entrez votre e-mail',
  'Enter your password': 'Entrez votre mot de passe',
  'Enter a valid email address': 'Entrez une adresse e-mail valide',
  'Use the email provisioned for your admin identity.': 'Utilisez l\'adresse e-mail fournie pour votre identité administrateur.',
  'Password must be at least 8 characters': 'Le mot de passe doit avoir au moins 8 caractères',
  'First name': 'Prénom',
  'Last name': 'Nom',
  'Confirm password': 'Confirmer le mot de passe',
  'Passwords do not match': 'Les mots de passe ne correspondent pas',
  'Accept invitation': 'Accepter l\'invitation',
  'Create organization': 'Créer une organisation',
  'Create tenant organization': 'Créer une organisation de locataire',
  'Industry Type': 'Type de secteur',
  'Industry updated.': 'Secteur mis à jour.',
  'Unable to update tenant industry.': 'Impossible de mettre à jour le secteur du locataire.',
  'General Workforce Operations': 'Opérations générales de main-d’œuvre',
  'Staffing Agency': 'Agence de personnel',
  'Security Company': 'Société de sécurité',
  'Healthcare Provider': 'Prestataire de santé',
  'Home Care Agency': 'Agence de soins à domicile',
  'Hospitality / Events': 'Hôtellerie / Événements',
  'Construction / Field Services': 'Construction / Services terrain',
  'Property Services': 'Services immobiliers',
  'Generic workforce coordination for people, sites, shifts, compliance, and payroll.': 'Coordination générale de la main-d’œuvre pour les personnes, les sites, les quarts, la conformité et la paie.',
  'Client orders, worker availability, assignment coverage, approvals, and payroll/billing readiness.': 'Commandes clients, disponibilité des travailleurs, couverture des affectations, approbations et préparation paie/facturation.',
  'Guard scheduling, site coverage, attendance verification, and incident readiness.': 'Planification des gardes, couverture des sites, vérification de présence et préparation des incidents.',
  'Open staffing requests, coverage gaps, credentials, attendance, and approval readiness.': 'Demandes de personnel ouvertes, écarts de couverture, titres, présence et préparation des approbations.',
  'Caregiver visits, client-home coverage, missed visits, availability, and credentials.': 'Visites des aidants, couverture des domiciles clients, visites manquées, disponibilité et titres.',
  'Event staffing, venue coverage, seasonal labor, approvals, and payroll readiness.': 'Personnel événementiel, couverture des lieux, main-d’œuvre saisonnière, approbations et préparation de la paie.',
  'Jobsite labor, subcontractor workforce, safety documents, timesheets, and payroll.': 'Main-d’œuvre de chantier, sous-traitants, documents de sécurité, feuilles de temps et paie.',
  'Recurring service orders, field teams, proof of work, site attendance, and billing/payroll readiness.': 'Ordres de service récurrents, équipes terrain, preuve de travail, présence sur site et préparation facturation/paie.',
  'Workforce demand': 'Demande de main-d’œuvre',
  'Workforce supply': 'Offre de main-d’œuvre',
  'Assignment coverage': 'Couverture des affectations',
  'Open client orders': 'Commandes clients ouvertes',
  'Fill rate': 'Taux de couverture',
  'Available workers': 'Travailleurs disponibles',
  'Client approvals': 'Approbations client',
  'Payroll/billing readiness': 'Préparation paie/facturation',
  'Active guards': 'Gardes actifs',
  'Site coverage': 'Couverture des sites',
  'Late/missed check-ins': 'Pointages tardifs ou manqués',
  'Open posts': 'Postes ouverts',
  'Incident/report readiness': 'Préparation incidents/rapports',
  'Open staffing requests': 'Demandes de personnel ouvertes',
  'Coverage gaps': 'Écarts de couverture',
  'Credential expirations': 'Expirations des titres',
  'Staff attendance': 'Présence du personnel',
  'Approval readiness': 'Préparation des approbations',
  'Scheduled visits': 'Visites planifiées',
  'Missed/late visits': 'Visites manquées ou tardives',
  'Caregiver availability': 'Disponibilité des aidants',
  'Client coverage': 'Couverture client',
  'Payroll readiness': 'Préparation de la paie',
  'Event coverage': 'Couverture des événements',
  'Open shifts': 'Quarts ouverts',
  'Staff availability': 'Disponibilité du personnel',
  'Venue activity': 'Activité du lieu',
  'Job site coverage': 'Couverture du chantier',
  'Trade/skill matching': 'Correspondance métiers/compétences',
  'Safety/compliance docs': 'Documents sécurité/conformité',
  'Timesheet approvals': 'Approbations des feuilles de temps',
  'Payroll export readiness': 'Préparation export paie',
  'Service order coverage': 'Couverture des ordres de service',
  'Site attendance': 'Présence sur site',
  'Proof of service': 'Preuve de service',
  'Missed visits': 'Visites manquées',
  'Billing/payroll readiness': 'Préparation facturation/paie',
  'Organization name': 'Nom de l\'organisation',
  'Organization type': 'Type d\'organisation',
  'Select organization type': 'Sélectionner le type d\'organisation',
  'Add first location': 'Ajouter le premier site',
  'Location name': 'Nom du site',
  'City': 'Ville',
  'State': 'État',
  'ZIP code': 'Code postal',
  'Country': 'Pays',
  'Next step': 'Prochaine étape',
  'Error': 'Erreur',
  'Success': 'Succès',
  'Warning': 'Avertissement',
  'Info': 'Information',
}

Object.assign(ar, {
  'Skip to workspace content': 'تخطَّ إلى محتوى مساحة العمل',
  'unread notifications': 'إشعارات غير مقروءة',
  'Workspaces': 'مساحات العمل',
  'Jump to the area you need.': 'انتقل إلى المساحة التي تحتاجها.',
  'Current workspace': 'مساحة العمل الحالية',
  'Switch': 'تبديل',
  'Platform': 'المنصة',
  'Learning': 'التعلم',
  'Quick access': 'وصول سريع',
  'More tools': 'أدوات إضافية',
  'Platform Console': 'وحدة تحكم المنصة',
  'Admin workspace': 'مساحة الإدارة',
  'Mentor Admin': 'إدارة المرشدين',
  'Worker marketplace': 'سوق العاملين',
  'Worker mentorship': 'إرشاد العاملين',
  'Shift imports': 'استيراد المناوبات',
  'Location supervisor': 'إشراف المواقع',
  'Fence mapper': 'مخطط النطاقات',
  'Super admin workspace': 'مساحة الإدارة العليا',
  'Billing & plans': 'الفوترة والخطط',
  'Sales / Revenue': 'المبيعات / الإيرادات',
})

Object.assign(tr, {
  'Skip to workspace content': 'Çalışma alanı içeriğine geç',
  'unread notifications': 'okunmamış bildirim',
  'Workspaces': 'Çalışma alanları',
  'Jump to the area you need.': 'İhtiyacınız olan alana geçin.',
  'Current workspace': 'Mevcut çalışma alanı',
  'Switch': 'Değiştir',
  'Platform': 'Platform',
  'Operations': 'Operasyonlar',
  'Learning': 'Öğrenme',
  'Quick access': 'Hızlı erişim',
  'More tools': 'Daha fazla araç',
  'Platform Console': 'Platform Konsolu',
  'Admin workspace': 'Yönetim alanı',
  'Mentor Admin': 'Mentor Yönetimi',
  'Worker marketplace': 'Çalışan pazarı',
  'Worker mentorship': 'Çalışan mentorluğu',
  'Shift imports': 'Vardiya içe aktarımları',
  'Location supervisor': 'Konum denetimi',
  'Fence mapper': 'Sınır haritası',
  'Super admin workspace': 'Süper yönetici alanı',
  'Roles & Access': 'Roller ve Erişim',
  'Billing & plans': 'Faturalandırma ve planlar',
  'Sales / Revenue': 'Satış / Gelir',
})

Object.assign(fr, {
  'Skip to workspace content': 'Accéder au contenu de l’espace de travail',
  'unread notifications': 'notifications non lues',
  'Workspaces': 'Espaces de travail',
  'Jump to the area you need.': 'Accédez à l’espace dont vous avez besoin.',
  'Current workspace': 'Espace actuel',
  'Switch': 'Changer',
  'Platform': 'Plateforme',
  'Operations': 'Opérations',
  'Learning': 'Apprentissage',
  'Quick access': 'Accès rapide',
  'More tools': 'Plus d’outils',
  'Platform Console': 'Console de la plateforme',
  'Admin workspace': 'Espace d’administration',
  'Mentor Admin': 'Administration des mentors',
  'Worker marketplace': 'Marché des travailleurs',
  'Worker mentorship': 'Mentorat des travailleurs',
  'Shift imports': 'Importation des quarts',
  'Location supervisor': 'Supervision des sites',
  'Fence mapper': 'Cartographie des zones',
  'Super admin workspace': 'Espace de super administration',
  'Roles & Access': 'Rôles et accès',
  'Billing & plans': 'Facturation et forfaits',
  'Sales / Revenue': 'Ventes / Revenus',
})


Object.assign(fr, {
  'Global permission already matches this request.': 'L’autorisation globale correspond déjà à cette demande.',
  'Global permission expiry must be in the future.': 'L’expiration de l’autorisation globale doit être ultérieure à maintenant.',
  'Global platform permissions': 'Autorisations globales de la plateforme',
  'These permissions apply across tenants and require MFA. Tenant roles do not grant them.': 'Ces autorisations s’appliquent à tous les espaces et exigent l’authentification multifacteur. Les rôles locaux ne les accordent pas.',
  'System administrators already have global platform authority.': 'Les administrateurs système disposent déjà de l’autorité globale sur la plateforme.',
  'Reason for global permission change': 'Motif de modification d’une autorisation globale',
  'Optional global permission expiry': 'Expiration facultative de l’autorisation globale',
  'Grant global permission': 'Accorder l’autorisation globale',
  'Revoke global permission': 'Révoquer l’autorisation globale',
  'Not granted': 'Non accordée',
  'Global permission updated. The account owner must sign in again.': 'Autorisation globale mise à jour. Le titulaire du compte doit se reconnecter.',
  'Unable to load global platform permissions.': 'Impossible de charger les autorisations globales de la plateforme.',
  'Unable to update global platform permissions.': 'Impossible de modifier les autorisations globales de la plateforme.',
  'Authentication changed. Sign in again.': 'L’authentification a changé. Reconnectez-vous.',
  'The code or enrollment has expired. Start enrollment again.': 'Le code ou l’inscription a expiré. Recommencez l’inscription.',
  'Verify the current MFA factor before changing security settings.': 'Vérifiez votre facteur multifacteur actuel avant de modifier les paramètres de sécurité.',
  'Current MFA verification is required.': 'Une vérification multifacteur actuelle est requise.',
  'Too many security-setting attempts. Try again later.': 'Trop de tentatives de modification des paramètres de sécurité. Réessayez plus tard.',
  'MFA cannot be disabled while a security policy requires it.': 'L’authentification multifacteur ne peut pas être désactivée tant qu’une politique de sécurité l’exige.',
  'MFA disabled. Sign in again.': 'Authentification multifacteur désactivée. Reconnectez-vous.',
  'Your session or tenant access changed. Sign in again to complete the required authentication.': 'Votre session ou votre accès à cet espace a changé. Reconnectez-vous pour effectuer l’authentification requise.',
  'Sign in again': 'Se reconnecter',
  'Recover your account': 'Récupérer votre compte',
  'Choose a new password': 'Choisir un nouveau mot de passe',
  'Use matching passwords with at least 8 characters and at most 72 bytes.': 'Utilisez des mots de passe identiques comportant au moins 8 caractères et au plus 72 octets.',
  'This recovery link is invalid or expired. Request a new link.': 'Ce lien de récupération est invalide ou a expiré. Demandez un nouveau lien.',
  'Too many recovery attempts. Try again later.': 'Trop de tentatives de récupération. Réessayez plus tard.',
  'Recovery could not be completed. Try again later.': 'La récupération n’a pas pu aboutir. Réessayez plus tard.',
  'Password reset completed. Sign in again. Your MFA factors remain required.': 'Mot de passe réinitialisé. Reconnectez-vous. Vos facteurs d’authentification multifacteur restent obligatoires.',
  'If recovery is available for this account, you will receive an email with instructions.': 'Si la récupération est disponible pour ce compte, vous recevrez un courriel contenant les instructions.',
  'Send recovery instructions': 'Envoyer les instructions de récupération',
  'Return to sign in': 'Revenir à la connexion',
  'Request a new recovery link': 'Demander un nouveau lien de récupération',
  'Forgot password?': 'Mot de passe oublié ?',
})

export const dictionaries: Record<Locale, Dictionary> = { en: {}, ar, tr, fr }

const fallbackToEnglishForMissingKeys = (dictionary: Dictionary) => {
  Object.keys(ar).forEach((key) => {
    if (!(key in dictionary)) dictionary[key] = key
  })
}

fallbackToEnglishForMissingKeys(tr)
fallbackToEnglishForMissingKeys(fr)

Object.assign(ar, {
  'Invite sent. Waiting for partner to accept.': 'تم إرسال الدعوة. في انتظار قبول الشريك.',
  'Connected, but no visibility policy has been configured.': 'متصل، ولكن لم يتم تكوين سياسة رؤية بعد.',
  'Permissions configured, but no locations granted.': 'تم تكوين الأذونات، ولكن لم يتم منح أي مواقع.',
  'Connection active.': 'الاتصال نشط.',
  'Delegated workforce access active.': 'الوصول المفوض للقوى العاملة نشط.',
  'Connection suspended. Future access is paused.': 'الاتصال معلق. الوصول المستقبلي متوقف مؤقتًا.',
  'Connection revoked. Historical records remain available where permitted.': 'تم إلغاء الاتصال. تظل السجلات التاريخية متاحة حيثما يُسمح بذلك.',
  'Connection references a missing organization and needs platform repair.': 'يشير الاتصال إلى مؤسسة مفقودة ويحتاج إلى إصلاح من المنصة.',
  'Connection status unknown.': 'حالة الاتصال غير معروفة.',
  'Pending invite': 'دعوة معلقة',
  'Linked, unconfigured': 'مرتبط، غير مُهيأ',
  'Policy configured, no locations': 'تم تكوين السياسة، بدون مواقع',
  'Connection built': 'تم إنشاء الاتصال',
  'Delegated connection': 'اتصال مفوض',
  'Suspended': 'معلق',
  'Revoked': 'ملغى',
  'Missing organization': 'مؤسسة مفقودة',
  'No link': 'لا يوجد ارتباط',
  'Access token secret name': 'اسم سر رمز الوصول',
  'active': 'نشط',
  'Add partner': 'إضافة شريك',
  'Agency-managed company profiles. These represent clients that have not yet joined the platform independently.': 'ملفات تعريف الشركات التي تديرها الوكالة. تمثل هذه عملاء لم ينضموا بعد إلى المنصة بشكل مستقل.',
  'Agency partner': 'شريك الوكالة',
  'Allocation': 'التخصيص',
  'Approvals': 'الموافقات',
  'Assigned location scope': 'نطاق الموقع المخصص',
  'assignments': 'المهام',
  'Assign role': 'تعيين دور',
  'Assign scoped role': 'تعيين دور محدد النطاق',
  'Assign workers': 'تعيين العمال',
  'Automatically punch workers in or out based on geofence dwell time. Only fires for confirmed scheduled assignments.': 'تسجيل دخول وخروج العمال تلقائيًا بناءً على وقت التواجد داخل السياج الجغرافي. يتم التنفيذ فقط للمهام المجدولة المؤكدة.',
  'Automatic — punch out immediately': 'تلقائي — تسجيل الخروج فورًا',
  'Auto punch-in / punch-out': 'تسجيل الدخول/الخروج التلقائي',
  'Auto-punch settings saved.': 'تم حفظ إعدادات التسجيل التلقائي.',
  'Auto punch unavailable': 'التسجيل التلقائي غير متاح',
  'built company-agency connections': 'اتصالات شركة-وكالة تم إنشاؤها',
  'Business account id': 'معرف الحساب التجاري',
  'Claim status': 'حالة المطالبة',
  'Client Companies': 'الشركات العميلة',
  'Client company': 'شركة عميلة',
  'Client profile': 'ملف تعريف العميل',
  'Companies visible to this agency through granted relationship scope.': 'الشركات المرئية لهذه الوكالة من خلال نطاق العلاقة الممنوح.',
  'Configure': 'تهيئة',
  'Configure tenant-level provider readiness, opt-in auditing, and template status without storing provider secrets directly.': 'تهيئة جاهزية المزود على مستوى المستأجر، وتدقيق الموافقة، وحالة القوالب دون تخزين أسرار المزود مباشرة.',
  'Connect client': 'ربط عميل',
  'Connected': 'متصل',
  'Connections': 'الاتصالات',
  'Connect metric later': 'ربط المقياس لاحقًا',
  'Context:': 'السياق:',
  'Create jobs': 'إنشاء وظائف',
  'Currently punched in': 'مسجل الدخول حاليًا',
  'Custom provider': 'مزود مخصص',
  'Dashboard date': 'تاريخ لوحة التحكم',
  'Dashboard location': 'موقع لوحة التحكم',
  'Dashboard status': 'حالة لوحة التحكم',
  'Default locale': 'اللغة الافتراضية',
  'Demand': 'الطلب',
  'Direct only': 'مباشر فقط',
  'directory_only': 'الدليل فقط',
  'Disable': 'تعطيل',
  'Disabled': 'معطل',
  'Enable automatic geofence punch-in / punch-out': 'تفعيل تسجيل الدخول/الخروج التلقائي بالسياج الجغرافي',
  'Enabled': 'مفعّل',
  'Enable WhatsApp channel after provider setup': 'تفعيل قناة واتساب بعد إعداد المزود',
  'Execution': 'التنفيذ',
  'External agency': 'وكالة خارجية',
  'External company': 'شركة خارجية',
  'Federated partner': 'شريك فدرالي',
  'Free Agent Pool': 'مجمع العمال المستقلين',
  'GPS accuracy and freshness gates apply — stale or low-accuracy points will not trigger auto punch. Manual punch behavior is unaffected.': 'تنطبق ضوابط دقة وحداثة GPS — النقاط القديمة أو منخفضة الدقة لن تؤدي إلى تسجيل تلقائي. سلوك التسجيل اليدوي لا يتأثر.',
  'Granted sites': 'المواقع الممنوحة',
  'Headcount': 'عدد العمال',
  'Inherits down': 'يرث للأسفل',
  'Internal, shared, and free-agent visible workforce signals.': 'مؤشرات القوى العاملة الداخلية والمشتركة والمرئية للعمال المستقلين.',
  'Internal Workers': 'العمال الداخليون',
  'jobs': 'وظائف',
  'Jobs, orders, and open work requirements.': 'الوظائف والطلبات ومتطلبات العمل المفتوحة.',
  'Late / no-show': 'تأخير / عدم حضور',
  'Linked agencies and the sharing scope granted by this company.': 'الوكالات المرتبطة ونطاق المشاركة الممنوح من هذه الشركة.',
  'Linked agency': 'وكالة مرتبطة',
  'Linked company': 'شركة مرتبطة',
  'linked company-agency relationships': 'علاقات شركة-وكالة مرتبطة',
  'Loading auto-punch settings...': 'جارٍ تحميل إعدادات التسجيل التلقائي...',
  'Loading WhatsApp settings...': 'جارٍ تحميل إعدادات واتساب...',
  'Location messages': 'رسائل الموقع',
  'Location operations': 'عمليات الموقع',
  'Managed Client Profile': 'ملف تعريف عميل مُدار',
  'Managed Client Profiles': 'ملفات تعريف العملاء المُدارة',
  'Meta Cloud API': 'واجهة Meta Cloud API',
  'Network Health': 'سلامة الشبكة',
  'No': 'لا',
  'No agencies are linked to this company yet.': 'لا توجد وكالات مرتبطة بهذه الشركة حتى الآن.',
  'No agencies linked yet.': 'لا توجد وكالات مرتبطة حتى الآن.',
  'No client companies': 'لا توجد شركات عميلة',
  'No client companies are linked to this agency yet.': 'لا توجد شركات عميلة مرتبطة بهذه الوكالة حتى الآن.',
  'No effective permissions visible.': 'لا توجد أذونات فعلية مرئية.',
  'No linked agencies': 'لا توجد وكالات مرتبطة',
  'No scoped role assignments visible.': 'لا توجد تعيينات أدوار محددة النطاق مرئية.',
  'Not connected yet': 'غير متصل بعد',
  'Not delivery-ready': 'غير جاهز للتسليم',
  'Only auto-punch workers with a confirmed scheduled assignment': 'تسجيل تلقائي فقط للعمال ذوي المهمة المجدولة المؤكدة',
  'Open Jobs': 'الوظائف المفتوحة',
  'Operational detail lists are scoped by backend location access. Use Schedule, Timesheets, Messages, and Jobs / Orders for the filtered records behind these counts.': 'قوائم التفاصيل التشغيلية محددة النطاق بحسب وصول الموقع في الخلفية. استخدم الجدول والسجلات الزمنية والرسائل والوظائف/الطلبات للسجلات المفصلة وراء هذه الأعداد.',
  'Opted in': 'مشترك',
  'Organizations, workforce supply, and sharing relationships visible in this scope.': 'المؤسسات وعرض القوى العاملة وعلاقات المشاركة المرئية في هذا النطاق.',
  'Organization unit': 'الوحدة التنظيمية',
  'OU assignments marked Inherits down grant access through descendant OUs and linked locations in v1.': 'تعيينات الوحدة التنظيمية المُعلَّمة "يرث للأسفل" تمنح الوصول من خلال الوحدات التنظيمية الفرعية والمواقع المرتبطة في الإصدار 1.',
  'Pay/Bill': 'الدفع/الفوترة',
  'Pending approvals': 'الموافقات المعلقة',
  'Pending opt-in': 'اشتراك معلق',
  'Phone number id': 'معرف رقم الهاتف',
  'Post jobs': 'نشر الوظائف',
  'Preset': 'إعداد مسبق',
  'Provider': 'المزود',
  'Provider failures will be logged as WhatsApp delivery attempts when a delivery worker is added; core Punch writes stay independent.': 'سيتم تسجيل أعطال المزود كمحاولات تسليم واتساب عند إضافة عامل تسليم؛ تظل عمليات كتابة Punch الأساسية مستقلة.',
  'Punch in after (minutes on site)': 'تسجيل الدخول بعد (دقائق في الموقع)',
  'Punch out after (minutes off site)': 'تسجيل الخروج بعد (دقائق خارج الموقع)',
  'Punch-out mode': 'نمط تسجيل الخروج',
  'Read-only': 'قراءة فقط',
  'Ready for delivery worker': 'جاهز لعامل التسليم',
  'Receive jobs': 'استقبال الوظائف',
  'Role assignment changes require tenant RBAC management permission.': 'تتطلب تغييرات تعيين الأدوار إذن إدارة RBAC على مستوى المستأجر.',
  'Role id': 'معرف الدور',
  'Save auto-punch settings': 'حفظ إعدادات التسجيل التلقائي',
  'Save WhatsApp settings': 'حفظ إعدادات واتساب',
  'Saving...': 'جارٍ الحفظ...',
  'Scope': 'النطاق',
  'Scoped role assignment created.': 'تم إنشاء تعيين الدور محدد النطاق.',
  'Scoped role assignment disabled.': 'تم تعطيل تعيين الدور محدد النطاق.',
  'Scoped role assignments': 'تعيينات الأدوار محددة النطاق',
  'Scope id is required outside tenant scope.': 'معرف النطاق مطلوب خارج نطاق المستأجر.',
  'Scope type': 'نوع النطاق',
  'Select scope': 'اختر النطاق',
  'Sender phone': 'هاتف المرسل',
  'Settlement': 'التسوية',
  'sharing agreements': 'اتفاقيات المشاركة',
  'Sharing Agreements': 'اتفاقيات المشاركة',
  'Shifts': 'المناوبات',
  'Sites': 'المواقع',
  'Supervisor dashboard': 'لوحة تحكم المشرف',
  'Supervisor review — create pending action': 'مراجعة المشرف — إنشاء إجراء معلق',
  'Supply': 'العرض',
  'Suspend / Revoke': 'تعليق / إلغاء',
  'tenant scope': 'نطاق المستأجر',
  'Tenant-wide': 'على مستوى المستأجر',
  'This first assignment form uses UUIDs from the active tenant context; user and role directory pickers can be layered on later.': 'يستخدم نموذج التعيين الأول هذا معرفات UUID من سياق المستأجر النشط؛ يمكن إضافة أدوات اختيار المستخدمين والأدوار من الدليل لاحقًا.',
  'This profile was created by your agency. Once the company joins the platform, they can claim and split this into a full federated relationship.': 'تم إنشاء هذا الملف الشخصي من قبل وكالتك. بمجرد انضمام الشركة إلى المنصة، يمكنها المطالبة به وتقسيمه إلى علاقة فدرالية كاملة.',
  'timesheets': 'السجلات الزمنية',
  'Twilio': 'Twilio',
  'Unable to create scoped role assignment.': 'تعذر إنشاء تعيين الدور محدد النطاق.',
  'Unable to disable scoped role assignment.': 'تعذر تعطيل تعيين الدور محدد النطاق.',
  'Unable to load auto-punch settings.': 'تعذر تحميل إعدادات التسجيل التلقائي.',
  'Unable to load WhatsApp notification status.': 'تعذر تحميل حالة إشعارات واتساب.',
  'Unable to save auto-punch settings.': 'تعذر حفظ إعدادات التسجيل التلقائي.',
  'Unable to save WhatsApp settings.': 'تعذر حفظ إعدادات واتساب.',
  'unclaimed': 'غير مُطالب به',
  'Unfilled': 'غير مشغول',
  'Ungrouped': 'غير مجمع',
  'Upcoming shifts': 'المناوبات القادمة',
  'User id': 'معرف المستخدم',
  'User id and role id are required.': 'معرف المستخدم ومعرف الدور مطلوبان.',
  'View jobs': 'عرض الوظائف',
  'View Jobs': 'عرض الوظائف',
  'View locations': 'عرض المواقع',
  'View Scheduling': 'عرض الجدولة',
  'View Sharing': 'عرض المشاركة',
  'View Workforce': 'عرض القوى العاملة',
  'Webhook verify token configured outside the app': 'تم تكوين رمز التحقق من Webhook خارج التطبيق',
  'WhatsApp notifications': 'إشعارات واتساب',
  'WhatsApp settings saved. Delivery remains inactive until provider credentials, opt-ins, and approved templates are ready.': 'تم حفظ إعدادات واتساب. يظل التسليم غير نشط حتى تجهيز بيانات اعتماد المزود والاشتراكات والقوالب المعتمدة.',
  'WhatsApp unavailable': 'واتساب غير متاح',
  'worker org relationships loaded': 'تم تحميل علاقات المؤسسة للعمال',
  'worker relationships': 'علاقات العمال',
  'Workforce Network Overview': 'نظرة عامة على شبكة القوى العاملة',
  'Workforce Partners': 'شركاء القوى العاملة',
  'Yes': 'نعم',
  'Your current role does not include messaging or tenant settings access.': 'دورك الحالي لا يشمل الوصول إلى المراسلة أو إعدادات المستأجر.',
  'Your role does not include tenant settings access.': 'دورك لا يشمل الوصول إلى إعدادات المستأجر.',
})

Object.assign(fr, {
  'Invite sent. Waiting for partner to accept.': 'Invitation envoyée. En attente d\'acceptation du partenaire.',
  'Connected, but no visibility policy has been configured.': 'Connecté, mais aucune politique de visibilité n\'a été configurée.',
  'Permissions configured, but no locations granted.': 'Autorisations configurées, mais aucun site accordé.',
  'Connection active.': 'Connexion active.',
  'Delegated workforce access active.': 'Accès délégué à la main-d\'œuvre actif.',
  'Connection suspended. Future access is paused.': 'Connexion suspendue. L\'accès futur est en pause.',
  'Connection revoked. Historical records remain available where permitted.': 'Connexion révoquée. Les enregistrements historiques restent disponibles si autorisé.',
  'Connection references a missing organization and needs platform repair.': 'La connexion référence une organisation manquante et nécessite une réparation de la plateforme.',
  'Connection status unknown.': 'Statut de connexion inconnu.',
  'Pending invite': 'Invitation en attente',
  'Linked, unconfigured': 'Lié, non configuré',
  'Policy configured, no locations': 'Politique configurée, aucun site',
  'Connection built': 'Connexion établie',
  'Delegated connection': 'Connexion déléguée',
  'Suspended': 'Suspendu',
  'Revoked': 'Révoqué',
  'Missing organization': 'Organisation manquante',
  'No link': 'Aucun lien',
  'Access token secret name': 'Nom du secret du jeton d\'accès',
  'active': 'actif',
  'Add partner': 'Ajouter un partenaire',
  'Agency-managed company profiles. These represent clients that have not yet joined the platform independently.': 'Profils d\'entreprises gérés par l\'agence. Ils représentent des clients qui n\'ont pas encore rejoint la plateforme de manière indépendante.',
  'Agency partner': 'Partenaire agence',
  'Allocation': 'Allocation',
  'Approvals': 'Approbations',
  'Assigned location scope': 'Périmètre de site assigné',
  'assignments': 'affectations',
  'Assign role': 'Attribuer un rôle',
  'Assign scoped role': 'Attribuer un rôle à portée définie',
  'Assign workers': 'Affecter des travailleurs',
  'Automatically punch workers in or out based on geofence dwell time. Only fires for confirmed scheduled assignments.': 'Pointer automatiquement l\'entrée ou la sortie des travailleurs en fonction du temps de présence dans la géoclôture. Ne s\'applique qu\'aux affectations planifiées confirmées.',
  'Automatic — punch out immediately': 'Automatique — pointage de sortie immédiat',
  'Auto punch-in / punch-out': 'Pointage automatique entrée / sortie',
  'Auto-punch settings saved.': 'Paramètres de pointage automatique enregistrés.',
  'Auto punch unavailable': 'Pointage automatique indisponible',
  'built company-agency connections': 'connexions entreprise-agence établies',
  'Business account id': 'ID du compte professionnel',
  'Claim status': 'Statut de revendication',
  'Client Companies': 'Entreprises clientes',
  'Client company': 'Entreprise cliente',
  'Client profile': 'Profil client',
  'Companies visible to this agency through granted relationship scope.': 'Entreprises visibles par cette agence grâce au périmètre de relation accordé.',
  'Configure': 'Configurer',
  'Configure tenant-level provider readiness, opt-in auditing, and template status without storing provider secrets directly.': 'Configurer la disponibilité du fournisseur au niveau du tenant, l\'audit des consentements et le statut des modèles sans stocker directement les secrets du fournisseur.',
  'Connect client': 'Connecter un client',
  'Connected': 'Connecté',
  'Connections': 'Connexions',
  'Connect metric later': 'Connecter la métrique plus tard',
  'Context:': 'Contexte :',
  'Create jobs': 'Créer des emplois',
  'Currently punched in': 'Actuellement pointé',
  'Custom provider': 'Fournisseur personnalisé',
  'Dashboard date': 'Date du tableau de bord',
  'Dashboard location': 'Site du tableau de bord',
  'Dashboard status': 'Statut du tableau de bord',
  'Default locale': 'Langue par défaut',
  'Demand': 'Demande',
  'Direct only': 'Direct uniquement',
  'directory_only': 'Annuaire uniquement',
  'Disable': 'Désactiver',
  'Disabled': 'Désactivé',
  'Enable automatic geofence punch-in / punch-out': 'Activer le pointage automatique par géoclôture',
  'Enabled': 'Activé',
  'Enable WhatsApp channel after provider setup': 'Activer le canal WhatsApp après configuration du fournisseur',
  'Execution': 'Exécution',
  'External agency': 'Agence externe',
  'External company': 'Entreprise externe',
  'Federated partner': 'Partenaire fédéré',
  'Free Agent Pool': 'Pool d\'agents libres',
  'GPS accuracy and freshness gates apply — stale or low-accuracy points will not trigger auto punch. Manual punch behavior is unaffected.': 'Des seuils de précision et de fraîcheur GPS s\'appliquent — les points anciens ou peu précis ne déclencheront pas de pointage automatique. Le pointage manuel n\'est pas affecté.',
  'Granted sites': 'Sites accordés',
  'Headcount': 'Effectif',
  'Inherits down': 'Hérite vers le bas',
  'Internal, shared, and free-agent visible workforce signals.': 'Signaux de main-d\'œuvre internes, partagés et visibles pour les agents libres.',
  'Internal Workers': 'Travailleurs internes',
  'jobs': 'emplois',
  'Jobs, orders, and open work requirements.': 'Emplois, commandes et besoins de travail ouverts.',
  'Late / no-show': 'Retard / absence',
  'Linked agencies and the sharing scope granted by this company.': 'Agences liées et périmètre de partage accordé par cette entreprise.',
  'Linked agency': 'Agence liée',
  'Linked company': 'Entreprise liée',
  'linked company-agency relationships': 'relations entreprise-agence liées',
  'Loading auto-punch settings...': 'Chargement des paramètres de pointage automatique...',
  'Loading WhatsApp settings...': 'Chargement des paramètres WhatsApp...',
  'Location messages': 'Messages du site',
  'Location operations': 'Opérations du site',
  'Managed Client Profile': 'Profil client géré',
  'Managed Client Profiles': 'Profils clients gérés',
  'Meta Cloud API': 'API Meta Cloud',
  'Network Health': 'Santé du réseau',
  'No': 'Non',
  'No agencies are linked to this company yet.': 'Aucune agence n\'est encore liée à cette entreprise.',
  'No agencies linked yet.': 'Aucune agence liée pour le moment.',
  'No client companies': 'Aucune entreprise cliente',
  'No client companies are linked to this agency yet.': 'Aucune entreprise cliente n\'est encore liée à cette agence.',
  'No effective permissions visible.': 'Aucune autorisation effective visible.',
  'No linked agencies': 'Aucune agence liée',
  'No scoped role assignments visible.': 'Aucune attribution de rôle à portée définie visible.',
  'Not connected yet': 'Pas encore connecté',
  'Not delivery-ready': 'Non prêt pour la livraison',
  'Only auto-punch workers with a confirmed scheduled assignment': 'Pointer automatiquement uniquement les travailleurs ayant une affectation planifiée confirmée',
  'Open Jobs': 'Emplois ouverts',
  'Operational detail lists are scoped by backend location access. Use Schedule, Timesheets, Messages, and Jobs / Orders for the filtered records behind these counts.': 'Les listes de détails opérationnels sont limitées par l\'accès aux sites côté serveur. Utilisez Planning, Feuilles de temps, Messages et Emplois / Commandes pour les enregistrements filtrés derrière ces chiffres.',
  'Opted in': 'Inscrit',
  'Organizations, workforce supply, and sharing relationships visible in this scope.': 'Organisations, offre de main-d\'œuvre et relations de partage visibles dans ce périmètre.',
  'Organization unit': 'Unité organisationnelle',
  'OU assignments marked Inherits down grant access through descendant OUs and linked locations in v1.': 'Les attributions d\'unité organisationnelle marquées "Hérite vers le bas" accordent l\'accès via les unités descendantes et les sites liés en v1.',
  'Pay/Bill': 'Paie/Facturation',
  'Pending approvals': 'Approbations en attente',
  'Pending opt-in': 'Consentement en attente',
  'Phone number id': 'ID du numéro de téléphone',
  'Post jobs': 'Publier des emplois',
  'Preset': 'Préréglage',
  'Provider': 'Fournisseur',
  'Provider failures will be logged as WhatsApp delivery attempts when a delivery worker is added; core Punch writes stay independent.': 'Les échecs du fournisseur seront enregistrés comme tentatives de livraison WhatsApp lorsqu\'un worker de livraison sera ajouté ; les écritures Punch principales restent indépendantes.',
  'Punch in after (minutes on site)': 'Pointage d\'entrée après (minutes sur site)',
  'Punch out after (minutes off site)': 'Pointage de sortie après (minutes hors site)',
  'Punch-out mode': 'Mode de pointage de sortie',
  'Read-only': 'Lecture seule',
  'Ready for delivery worker': 'Prêt pour le worker de livraison',
  'Receive jobs': 'Recevoir des emplois',
  'Role assignment changes require tenant RBAC management permission.': 'Les modifications d\'attribution de rôle nécessitent l\'autorisation de gestion RBAC du tenant.',
  'Role id': 'ID du rôle',
  'Save auto-punch settings': 'Enregistrer les paramètres de pointage automatique',
  'Save WhatsApp settings': 'Enregistrer les paramètres WhatsApp',
  'Saving...': 'Enregistrement...',
  'Scope': 'Portée',
  'Scoped role assignment created.': 'Attribution de rôle à portée définie créée.',
  'Scoped role assignment disabled.': 'Attribution de rôle à portée définie désactivée.',
  'Scoped role assignments': 'Attributions de rôle à portée définie',
  'Scope id is required outside tenant scope.': 'L\'ID de portée est requis hors du périmètre tenant.',
  'Scope type': 'Type de portée',
  'Select scope': 'Sélectionner la portée',
  'Sender phone': 'Téléphone de l\'expéditeur',
  'Settlement': 'Règlement',
  'sharing agreements': 'accords de partage',
  'Sharing Agreements': 'Accords de partage',
  'Shifts': 'Quarts',
  'Sites': 'Sites',
  'Supervisor dashboard': 'Tableau de bord superviseur',
  'Supervisor review — create pending action': 'Révision du superviseur — créer une action en attente',
  'Supply': 'Offre',
  'Suspend / Revoke': 'Suspendre / Révoquer',
  'tenant scope': 'périmètre du tenant',
  'Tenant-wide': 'À l\'échelle du tenant',
  'This first assignment form uses UUIDs from the active tenant context; user and role directory pickers can be layered on later.': 'Ce premier formulaire d\'attribution utilise des UUID du contexte tenant actif ; des sélecteurs d\'annuaire pour les utilisateurs et les rôles pourront être ajoutés plus tard.',
  'This profile was created by your agency. Once the company joins the platform, they can claim and split this into a full federated relationship.': 'Ce profil a été créé par votre agence. Une fois que l\'entreprise rejoint la plateforme, elle peut le revendiquer et le scinder en une relation fédérée complète.',
  'timesheets': 'feuilles de temps',
  'Twilio': 'Twilio',
  'Unable to create scoped role assignment.': 'Impossible de créer l\'attribution de rôle à portée définie.',
  'Unable to disable scoped role assignment.': 'Impossible de désactiver l\'attribution de rôle à portée définie.',
  'Unable to load auto-punch settings.': 'Impossible de charger les paramètres de pointage automatique.',
  'Unable to load WhatsApp notification status.': 'Impossible de charger le statut des notifications WhatsApp.',
  'Unable to save auto-punch settings.': 'Impossible d\'enregistrer les paramètres de pointage automatique.',
  'Unable to save WhatsApp settings.': 'Impossible d\'enregistrer les paramètres WhatsApp.',
  'unclaimed': 'non revendiqué',
  'Unfilled': 'Non pourvu',
  'Ungrouped': 'Non groupé',
  'Upcoming shifts': 'Quarts à venir',
  'User id': 'ID utilisateur',
  'User id and role id are required.': 'L\'ID utilisateur et l\'ID de rôle sont requis.',
  'View jobs': 'Voir les emplois',
  'View Jobs': 'Voir les emplois',
  'View locations': 'Voir les sites',
  'View Scheduling': 'Voir la planification',
  'View Sharing': 'Voir le partage',
  'View Workforce': 'Voir la main-d\'œuvre',
  'Webhook verify token configured outside the app': 'Jeton de vérification du webhook configuré en dehors de l\'application',
  'WhatsApp notifications': 'Notifications WhatsApp',
  'WhatsApp settings saved. Delivery remains inactive until provider credentials, opt-ins, and approved templates are ready.': 'Paramètres WhatsApp enregistrés. La livraison reste inactive jusqu\'à ce que les identifiants du fournisseur, les consentements et les modèles approuvés soient prêts.',
  'WhatsApp unavailable': 'WhatsApp indisponible',
  'worker org relationships loaded': 'relations organisation-travailleur chargées',
  'worker relationships': 'relations des travailleurs',
  'Workforce Network Overview': 'Vue d\'ensemble du réseau de main-d\'œuvre',
  'Workforce Partners': 'Partenaires de main-d\'œuvre',
  'Yes': 'Oui',
  'Your current role does not include messaging or tenant settings access.': 'Votre rôle actuel n\'inclut pas l\'accès à la messagerie ou aux paramètres du tenant.',
  'Your role does not include tenant settings access.': 'Votre rôle n\'inclut pas l\'accès aux paramètres du tenant.',
})

// LocationsPanel localization batch
Object.assign(ar, {
  'Access is limited to authorized supervisors/admins for the worker\'s assigned location or organization.': 'الوصول مقصور على المشرفين/المسؤولين المخوّلين لموقع العامل أو مؤسسته المخصصة.',
  'Add location': 'إضافة موقع',
  'Add your first workplace, job site, venue, or service location and configure its punch geofence.': 'أضف أول مكان عمل أو موقع عمل أو قاعة أو موقع خدمة، ثم قم بإعداد النطاق الجغرافي للبصمة.',
  'No locations yet': 'لا توجد مواقع بعد',
  'Location created. Next, configure its punch geofence.': 'تم إنشاء الموقع. بعد ذلك، قم بإعداد النطاق الجغرافي للبصمة.',
  'All location workers': 'جميع عاملي الموقع',
  'All owners': 'جميع الملاك',
  'Any geofence': 'أي سياج جغرافي',
  'Approve timesheets': 'اعتماد سجلات الدوام',
  'Archive = hide this location from normal operations but keep history.': 'الأرشفة = إخفاء هذا الموقع من العمليات العادية مع الاحتفاظ بالسجل.',
  'Audience': 'الجهة المستهدفة',
  'Breadcrumb/location tracking': 'تتبع المسار/الموقع',
  'break': 'استراحة',
  'Circle fence, {radius}m radius': 'سياج دائري، نطاق {radius} م',
  'Clear geofence': 'إزالة السياج الجغرافي',
  'Clear geofence = remove geofence config only if no active usage depends on it.': 'إزالة السياج = حذف إعدادات السياج فقط إذا لم يعتمد عليها أي استخدام نشط.',
  'Client Company Locations': 'مواقع الشركات العميلة',
  'Closed': 'مغلق',
  '{completed} completed / {cancelled} cancelled': '{completed} مكتملة / {cancelled} ملغاة',
  'Configure a geofence and enable live worker tracking before using breadcrumbs/live map.': 'قم بإعداد سياج جغرافي وتفعيل تتبع العاملين المباشر قبل استخدام المسارات/الخريطة المباشرة.',
  'Configured': 'مُهيَّأ',
  'Configure geofence': 'إعداد السياج الجغرافي',
  '{count} messages sent.': 'تم إرسال {count} رسالة.',
  '{count} upcoming': '{count} قادمة',
  '{count} upcoming workers needed': '{count} عامل مطلوب قريبًا',
  'Create job here': 'إنشاء وظيفة هنا',
  'Create Jobs': 'إنشاء وظائف',
  'Create location': 'إنشاء موقع',
  'Create shifts': 'إنشاء ورديات',
  'Create Shifts': 'إنشاء ورديات',
  'Currently active workers': 'العاملون النشطون حاليًا',
  'Delete blocked. Dependencies: {dependencies}. Recommended action: {action}.': 'تم حظر الحذف. الارتباطات: {dependencies}. الإجراء الموصى به: {action}.',
  'Delete = remove only if this location has no historical dependencies.': 'الحذف = إزالة الموقع فقط إذا لم تكن له ارتباطات تاريخية.',
  'Delivery': 'التسليم',
  'Demo location data purged.': 'تم محو بيانات موقع التجربة التوضيحية.',
  'Disabled for this location.': 'معطّل لهذا الموقع.',
  'Disable geofence': 'تعطيل السياج الجغرافي',
  'Disable geofence = stop geofence validation for future use.': 'تعطيل السياج = إيقاف التحقق من السياج للاستخدام المستقبلي.',
  'Disable = stop future use of this location while keeping history.': 'التعطيل = إيقاف الاستخدام المستقبلي لهذا الموقع مع الاحتفاظ بالسجل.',
  'Edit location': 'تعديل الموقع',
  'Edit mode': 'وضع التعديل',
  'Enabled for assigned shifts at this location.': 'مفعّل للورديات المخصصة في هذا الموقع.',
  'Enable live worker tracking for assigned shifts': 'تفعيل التتبع المباشر للعاملين في الورديات المخصصة',
  'Expired': 'منتهي الصلاحية',
  'Federated': 'موحّد (فيدرالي)',
  'Geofence and live worker tracking are ready for assigned shifts.': 'السياج الجغرافي والتتبع المباشر للعاملين جاهزان للورديات المخصصة.',
  'Geofence clear blocked. Dependencies: {dependencies}. Recommended action: {action}.': 'تم حظر إزالة السياج. الارتباطات: {dependencies}. الإجراء الموصى به: {action}.',
  'Geofence cleared.': 'تمت إزالة السياج الجغرافي.',
  'Geofence clear failed.': 'فشلت إزالة السياج الجغرافي.',
  'Geofence disabled.': 'تم تعطيل السياج الجغرافي.',
  'Geofence is configured. Enable live worker tracking in Location Edit to show breadcrumbs/live map.': 'تم إعداد السياج الجغرافي. فعّل التتبع المباشر للعاملين من تعديل الموقع لإظهار المسارات/الخريطة المباشرة.',
  'Geofence: {name}': 'السياج الجغرافي: {name}',
  'Geofence restored.': 'تمت استعادة السياج الجغرافي.',
  'Geofence summary': 'ملخص السياج الجغرافي',
  'Granted access': 'الصلاحيات الممنوحة',
  'Granted locations from federated client relationships. You can create jobs at these sites on behalf of the client.': 'مواقع ممنوحة من علاقات العملاء الموحّدة. يمكنك إنشاء وظائف في هذه المواقع نيابة عن العميل.',
  'Has geofence': 'يحتوي على سياج جغرافي',
  'In progress': 'قيد التنفيذ',
  'Jobs': 'الوظائف',
  'Key': 'المعرّف',
  'Linked OU': 'الوحدة التنظيمية المرتبطة',
  'Live map: {name}': 'الخريطة المباشرة: {name}',
  'Live tracking': 'التتبع المباشر',
  'Live tracking is enabled, but this location still needs a geofence.': 'التتبع المباشر مفعّل، لكن هذا الموقع لا يزال يحتاج إلى سياج جغرافي.',
  'Live worker status': 'حالة العاملين المباشرة',
  'Loading location operations...': 'جارٍ تحميل عمليات الموقع...',
  'Location and geofence edits require location management permission. Visible locations are still scoped by access context.': 'تتطلب تعديلات الموقع والسياج الجغرافي صلاحية إدارة المواقع. تبقى المواقع المعروضة محددة بسياق الوصول.',
  'Location archived.': 'تمت أرشفة الموقع.',
  'Location created. Configure the geofence from the location actions.': 'تم إنشاء الموقع. قم بإعداد السياج الجغرافي من إجراءات الموقع.',
  'Location deleted.': 'تم حذف الموقع.',
  'Location delete failed.': 'فشل حذف الموقع.',
  'Location disabled.': 'تم تعطيل الموقع.',
  'Location geofence': 'السياج الجغرافي للموقع',
  'Location messaging': 'رسائل الموقع',
  'Location name is required.': 'اسم الموقع مطلوب.',
  'Location, owner, address': 'الموقع، المالك، العنوان',
  'Location restored.': 'تمت استعادة الموقع.',
  'Location timesheets': 'سجلات دوام الموقع',
  'Location updated.': 'تم تحديث الموقع.',
  'Message': 'الرسالة',
  'Missing': 'غير متوفر',
  'Needed': 'المطلوب',
  'Needs fence': 'يحتاج إلى سياج',
  'Needs geofence': 'يحتاج إلى سياج جغرافي',
  'Next shift: {value}': 'الوردية التالية: {value}',
  'No address provided': 'لم يتم تقديم عنوان',
  'No dependencies were found. Delete this empty location?': 'لم يتم العثور على ارتباطات. هل تريد حذف هذا الموقع الفارغ؟',
  'No geofence dependencies were found. Clear this geofence?': 'لم يتم العثور على ارتباطات للسياج الجغرافي. هل تريد إزالة هذا السياج؟',
  'No jobs are tied to this location yet.': 'لا توجد وظائف مرتبطة بهذا الموقع حتى الآن.',
  'No linked OU is visible for this location. OU-scoped access may not inherit to this site.': 'لا توجد وحدة تنظيمية مرتبطة مرئية لهذا الموقع. قد لا تنتقل الصلاحيات المحددة بالوحدة التنظيمية إلى هذا الموقع.',
  'No linked OU is visible for this location. OU-scoped inherited access may not reach this site until the link exists and is visible.': 'لا توجد وحدة تنظيمية مرتبطة مرئية لهذا الموقع. قد لا تصل الصلاحيات الموروثة المحددة بالوحدة التنظيمية إلى هذا الموقع حتى يتم إنشاء الارتباط وإظهاره.',
  'No linked OU visible': 'لا توجد وحدة تنظيمية مرتبطة مرئية',
  'No live worker status is available for this location.': 'لا تتوفر حالة عاملين مباشرة لهذا الموقع.',
  'No location messages found.': 'لم يتم العثور على رسائل للموقع.',
  'No locations found': 'لم يتم العثور على مواقع',
  'No locations have been granted for this relationship.': 'لم يتم منح أي مواقع لهذه العلاقة.',
  'none': 'لا يوجد',
  'None': 'لا يوجد',
  'No owner org': 'لا توجد مؤسسة مالكة',
  'No shifts are tied to this location yet.': 'لا توجد ورديات مرتبطة بهذا الموقع حتى الآن.',
  'No timesheets found for this location/date range.': 'لم يتم العثور على سجلات دوام لهذا الموقع/الفترة الزمنية.',
  'Not marked': 'غير محدد',
  'No workers are currently active at this location.': 'لا يوجد عاملون نشطون حاليًا في هذا الموقع.',
  'Only locations returned by the access context are shown here.': 'تُعرض هنا فقط المواقع التي يعيدها سياق الوصول.',
  'On the way': 'في الطريق',
  'Open live map / tracker': 'فتح الخريطة المباشرة / المتعقب',
  '{open} open / {active} active': '{open} مفتوحة / {active} نشطة',
  'Open slots': 'الشواغر المفتوحة',
  'Operational inventory': 'المخزون التشغيلي',
  'Owner': 'المالك',
  'Owner organization': 'المؤسسة المالكة',
  'Owner organization is missing. Linked OU creation cannot happen without an owner organization.': 'المؤسسة المالكة غير محددة. لا يمكن إنشاء وحدة تنظيمية مرتبطة بدون مؤسسة مالكة.',
  'Owner org branding': 'هوية المؤسسة المالكة',
  'Paid hours': 'ساعات مدفوعة',
  'Partner Access': 'صلاحيات الشريك',
  'Polygon fence': 'سياج مضلّع',
  'Polygon fence, {count} points': 'سياج مضلّع، {count} نقطة',
  'Published': 'منشور',
  'punched in': 'تسجيل حضور',
  'punched out': 'تسجيل خروج',
  'Punch in': 'تسجيل الحضور',
  'Punch out': 'تسجيل الخروج',
  'Punch out {name} at {location}?': 'تسجيل خروج {name} في {location}؟',
  'Punch out worker': 'تسجيل خروج العامل',
  'Purge demo data': 'محو بيانات التجربة التوضيحية',
  'Purge demo data = irreversible cleanup for demo/test location records only.': 'محو بيانات التجربة = تنظيف لا يمكن التراجع عنه لسجلات المواقع التجريبية/الاختبارية فقط.',
  'Read-only mode': 'وضع القراءة فقط',
  'Reason is required.': 'السبب مطلوب.',
  'Refresh messages': 'تحديث الرسائل',
  'Relationship: {id} · Granted sites: {count}': 'العلاقة: {id} · المواقع الممنوحة: {count}',
  'Require scheduled shift upload': 'طلب رفع جدول الورديات',
  'Restore': 'استعادة',
  'Restore geofence': 'استعادة السياج الجغرافي',
  'Restore geofence validation for this location.': 'استعادة التحقق من السياج الجغرافي لهذا الموقع.',
  'Restore this location to operational use.': 'استعادة هذا الموقع للاستخدام التشغيلي.',
  'Saved geofence configured': 'تم إعداد سياج جغرافي محفوظ',
  'Save location': 'حفظ الموقع',
  'Schedule': 'الجدولة',
  'Select a worker before sending a direct location message.': 'اختر عاملاً قبل إرسال رسالة مباشرة للموقع.',
  'Selected worker': 'العامل المحدد',
  'Send message': 'إرسال الرسالة',
  'Sent': 'تم الإرسال',
  'Setup status': 'حالة الإعداد',
  'Since {time}': 'منذ {time}',
  'Staff': 'التزويد بالعاملين',
  'System lifecycle action failed.': 'فشل إجراء دورة حياة النظام.',
  'Tenant-scoped jobs and shifts tied directly to this location.': 'الوظائف والورديات الخاصة بالمستأجر المرتبطة مباشرة بهذا الموقع.',
  'this location': 'هذا الموقع',
  'This location has no owner organization visible in your current access context.': 'لا توجد مؤسسة مالكة مرئية لهذا الموقع في سياق الوصول الحالي.',
  'this worker': 'هذا العامل',
  'Timesheet from': 'سجل الدوام من',
  'Timesheet to': 'سجل الدوام إلى',
  'Tracking is currently disabled for this location. Enable it in Location Edit; the geofence mapper only manages boundaries.': 'التتبع معطّل حاليًا لهذا الموقع. فعّله من تعديل الموقع؛ يدير محرر السياج الحدود فقط.',
  'Unable to load location operations.': 'تعذر تحميل عمليات الموقع.',
  'Unable to save location.': 'تعذر حفظ الموقع.',
  'Unable to send location message.': 'تعذر إرسال رسالة الموقع.',
  'Upcoming / recent shift': 'الوردية القادمة / الأخيرة',
  'View breadcrumbs': 'عرض المسارات',
  'View details': 'عرض التفاصيل',
  'View geofence': 'عرض السياج الجغرافي',
  'View latest worker positions and selected worker breadcrumb trails.': 'عرض أحدث مواقع العاملين ومسارات العامل المحدد.',
  'View live map': 'عرض الخريطة المباشرة',
  'View your agency workers\' positions and breadcrumb trails at this client location.': 'عرض مواقع ومسارات عاملي وكالتك في موقع هذا العميل.',
  'When an owner organization is set, the backend creates or preserves a linked location OU under that owner organization.': 'عند تحديد مؤسسة مالكة، يقوم النظام الخلفي بإنشاء أو الحفاظ على وحدة تنظيمية مرتبطة بالموقع تحت تلك المؤسسة.',
  'Worker punched out.': 'تم تسجيل خروج العامل.',
  'Worker punch status is already current.': 'حالة تسجيل العامل محدثة بالفعل.',
  'You can view this location but cannot change tracking settings.': 'يمكنك عرض هذا الموقع ولكن لا يمكنك تغيير إعدادات التتبع.',
  'You do not have permission to punch out this worker for this location or agency.': 'لا تملك صلاحية تسجيل خروج هذا العامل لهذا الموقع أو الوكالة.',
})

Object.assign(fr, {
  'Access is limited to authorized supervisors/admins for the worker\'s assigned location or organization.': 'L\'accès est limité aux superviseurs/administrateurs autorisés pour le site ou l\'organisation assignés au travailleur.',
  'All location workers': 'Tous les travailleurs du site',
  'All owners': 'Tous les propriétaires',
  'Any geofence': 'Toute géoclôture',
  'Approve timesheets': 'Approuver les feuilles de temps',
  'Archive = hide this location from normal operations but keep history.': 'Archiver = masquer ce site des opérations normales tout en conservant l\'historique.',
  'Audience': 'Audience',
  'Breadcrumb/location tracking': 'Suivi des traces / de la localisation',
  'break': 'pause',
  'Circle fence, {radius}m radius': 'Clôture circulaire, rayon de {radius} m',
  'Clear geofence': 'Effacer la géoclôture',
  'Clear geofence = remove geofence config only if no active usage depends on it.': 'Effacer la géoclôture = supprimer la configuration de la géoclôture uniquement si aucune utilisation active n\'en dépend.',
  'Client Company Locations': 'Sites des entreprises clientes',
  'Closed': 'Fermé',
  '{completed} completed / {cancelled} cancelled': '{completed} terminées / {cancelled} annulées',
  'Configure a geofence and enable live worker tracking before using breadcrumbs/live map.': 'Configurez une géoclôture et activez le suivi en direct des travailleurs avant d\'utiliser les traces / la carte en direct.',
  'Configured': 'Configuré',
  'Configure geofence': 'Configurer la géoclôture',
  '{count} messages sent.': '{count} messages envoyés.',
  '{count} upcoming': '{count} à venir',
  '{count} upcoming workers needed': '{count} travailleurs requis à venir',
  'Add location': 'Ajouter un site',
  'Add your first workplace, job site, venue, or service location and configure its punch geofence.': 'Ajoutez votre premier lieu de travail, chantier, salle ou site de service, puis configurez sa géoclôture de pointage.',
  'No locations yet': 'Aucun site pour le moment',
  'Location created. Next, configure its punch geofence.': 'Site créé. Configurez maintenant sa géoclôture de pointage.',
  'Create job here': 'Créer une offre ici',
  'Create Jobs': 'Créer des offres',
  'Create location': 'Créer un site',
  'Create shifts': 'Créer des quarts',
  'Create Shifts': 'Créer des quarts',
  'Currently active workers': 'Travailleurs actuellement actifs',
  'Delete blocked. Dependencies: {dependencies}. Recommended action: {action}.': 'Suppression bloquée. Dépendances : {dependencies}. Action recommandée : {action}.',
  'Delete = remove only if this location has no historical dependencies.': 'Supprimer = retirer ce site uniquement s\'il n\'a aucune dépendance historique.',
  'Delivery': 'Livraison',
  'Demo location data purged.': 'Les données de démonstration du site ont été purgées.',
  'Disabled for this location.': 'Désactivé pour ce site.',
  'Disable geofence': 'Désactiver la géoclôture',
  'Disable geofence = stop geofence validation for future use.': 'Désactiver la géoclôture = arrêter la validation de la géoclôture pour les usages futurs.',
  'Disable = stop future use of this location while keeping history.': 'Désactiver = arrêter l\'utilisation future de ce site tout en conservant l\'historique.',
  'Edit location': 'Modifier le site',
  'Edit mode': 'Mode édition',
  'Enabled for assigned shifts at this location.': 'Activé pour les quarts assignés à ce site.',
  'Enable live worker tracking for assigned shifts': 'Activer le suivi en direct des travailleurs pour les quarts assignés',
  'Expired': 'Expiré',
  'Federated': 'Fédéré',
  'Geofence and live worker tracking are ready for assigned shifts.': 'La géoclôture et le suivi en direct des travailleurs sont prêts pour les quarts assignés.',
  'Geofence clear blocked. Dependencies: {dependencies}. Recommended action: {action}.': 'Suppression de la géoclôture bloquée. Dépendances : {dependencies}. Action recommandée : {action}.',
  'Geofence cleared.': 'Géoclôture effacée.',
  'Geofence clear failed.': 'Échec de la suppression de la géoclôture.',
  'Geofence disabled.': 'Géoclôture désactivée.',
  'Geofence is configured. Enable live worker tracking in Location Edit to show breadcrumbs/live map.': 'La géoclôture est configurée. Activez le suivi en direct des travailleurs dans l\'édition du site pour afficher les traces / la carte en direct.',
  'Geofence: {name}': 'Géoclôture : {name}',
  'Geofence restored.': 'Géoclôture restaurée.',
  'Geofence summary': 'Résumé de la géoclôture',
  'Granted access': 'Accès accordés',
  'Granted locations from federated client relationships. You can create jobs at these sites on behalf of the client.': 'Sites accordés via les relations fédérées avec les clients. Vous pouvez créer des offres sur ces sites pour le compte du client.',
  'Has geofence': 'Possède une géoclôture',
  'In progress': 'En cours',
  'Jobs': 'Offres',
  'Key': 'Clé',
  'Linked OU': 'Unité organisationnelle liée',
  'Live map: {name}': 'Carte en direct : {name}',
  'Live tracking': 'Suivi en direct',
  'Live tracking is enabled, but this location still needs a geofence.': 'Le suivi en direct est activé, mais ce site a encore besoin d\'une géoclôture.',
  'Live worker status': 'Statut en direct des travailleurs',
  'Loading location operations...': 'Chargement des opérations du site...',
  'Location and geofence edits require location management permission. Visible locations are still scoped by access context.': 'Les modifications du site et de la géoclôture nécessitent l\'autorisation de gestion des sites. Les sites visibles restent limités par le contexte d\'accès.',
  'Location archived.': 'Site archivé.',
  'Location created. Configure the geofence from the location actions.': 'Site créé. Configurez la géoclôture depuis les actions du site.',
  'Location deleted.': 'Site supprimé.',
  'Location delete failed.': 'Échec de la suppression du site.',
  'Location disabled.': 'Site désactivé.',
  'Location geofence': 'Géoclôture du site',
  'Location messaging': 'Messagerie du site',
  'Location name is required.': 'Le nom du site est requis.',
  'Location, owner, address': 'Site, propriétaire, adresse',
  'Location restored.': 'Site restauré.',
  'Location timesheets': 'Feuilles de temps du site',
  'Location updated.': 'Site mis à jour.',
  'Message': 'Message',
  'Missing': 'Manquant',
  'Needed': 'Requis',
  'Needs fence': 'Géoclôture requise',
  'Needs geofence': 'Géoclôture requise',
  'Next shift: {value}': 'Prochain quart : {value}',
  'No address provided': 'Aucune adresse fournie',
  'No dependencies were found. Delete this empty location?': 'Aucune dépendance trouvée. Supprimer ce site vide ?',
  'No geofence dependencies were found. Clear this geofence?': 'Aucune dépendance de géoclôture trouvée. Effacer cette géoclôture ?',
  'No jobs are tied to this location yet.': 'Aucune offre n\'est encore liée à ce site.',
  'No linked OU is visible for this location. OU-scoped access may not inherit to this site.': 'Aucune unité organisationnelle liée n\'est visible pour ce site. L\'accès limité à l\'unité organisationnelle pourrait ne pas s\'appliquer à ce site.',
  'No linked OU is visible for this location. OU-scoped inherited access may not reach this site until the link exists and is visible.': 'Aucune unité organisationnelle liée n\'est visible pour ce site. L\'accès hérité limité à l\'unité organisationnelle pourrait ne pas atteindre ce site jusqu\'à ce que le lien existe et soit visible.',
  'No linked OU visible': 'Aucune unité organisationnelle liée visible',
  'No live worker status is available for this location.': 'Aucun statut en direct des travailleurs n\'est disponible pour ce site.',
  'No location messages found.': 'Aucun message de site trouvé.',
  'No locations found': 'Aucun site trouvé',
  'No locations have been granted for this relationship.': 'Aucun site n\'a été accordé pour cette relation.',
  'none': 'aucune',
  'None': 'Aucun',
  'No owner org': 'Aucune organisation propriétaire',
  'No shifts are tied to this location yet.': 'Aucun quart n\'est encore lié à ce site.',
  'No timesheets found for this location/date range.': 'Aucune feuille de temps trouvée pour ce site / cette période.',
  'Not marked': 'Non indiqué',
  'No workers are currently active at this location.': 'Aucun travailleur n\'est actuellement actif sur ce site.',
  'Only locations returned by the access context are shown here.': 'Seuls les sites renvoyés par le contexte d\'accès sont affichés ici.',
  'On the way': 'En route',
  'Open live map / tracker': 'Ouvrir la carte en direct / le traceur',
  '{open} open / {active} active': '{open} ouvertes / {active} actives',
  'Open slots': 'Postes ouverts',
  'Operational inventory': 'Inventaire opérationnel',
  'Owner': 'Propriétaire',
  'Owner organization': 'Organisation propriétaire',
  'Owner organization is missing. Linked OU creation cannot happen without an owner organization.': 'L\'organisation propriétaire est manquante. La création d\'une unité organisationnelle liée est impossible sans organisation propriétaire.',
  'Owner org branding': 'Image de marque de l\'organisation propriétaire',
  'Paid hours': 'Heures payées',
  'Partner Access': 'Accès partenaire',
  'Polygon fence': 'Clôture polygonale',
  'Polygon fence, {count} points': 'Clôture polygonale, {count} points',
  'Published': 'Publié',
  'punched in': 'pointé entrée',
  'punched out': 'pointé sortie',
  'Punch in': 'Pointage entrée',
  'Punch out': 'Pointage sortie',
  'Punch out {name} at {location}?': 'Enregistrer la sortie de {name} à {location} ?',
  'Punch out worker': 'Pointer la sortie du travailleur',
  'Purge demo data': 'Purger les données de démonstration',
  'Purge demo data = irreversible cleanup for demo/test location records only.': 'Purger les données de démonstration = nettoyage irréversible réservé aux enregistrements de sites de démonstration/test.',
  'Read-only mode': 'Mode lecture seule',
  'Reason is required.': 'Le motif est requis.',
  'Refresh messages': 'Actualiser les messages',
  'Relationship: {id} · Granted sites: {count}': 'Relation : {id} · Sites accordés : {count}',
  'Require scheduled shift upload': 'Exiger le téléversement du planning des quarts',
  'Restore': 'Restaurer',
  'Restore geofence': 'Restaurer la géoclôture',
  'Restore geofence validation for this location.': 'Restaurer la validation de la géoclôture pour ce site.',
  'Restore this location to operational use.': 'Restaurer ce site pour une utilisation opérationnelle.',
  'Saved geofence configured': 'Géoclôture enregistrée configurée',
  'Save location': 'Enregistrer le site',
  'Schedule': 'Planification',
  'Select a worker before sending a direct location message.': 'Sélectionnez un travailleur avant d\'envoyer un message direct au site.',
  'Selected worker': 'Travailleur sélectionné',
  'Send message': 'Envoyer le message',
  'Sent': 'Envoyé',
  'Setup status': 'État de la configuration',
  'Since {time}': 'Depuis {time}',
  'Staff': 'Personnel',
  'System lifecycle action failed.': 'L\'action de cycle de vie système a échoué.',
  'Tenant-scoped jobs and shifts tied directly to this location.': 'Offres et quarts limités au locataire liés directement à ce site.',
  'this location': 'ce site',
  'This location has no owner organization visible in your current access context.': 'Ce site n\'a aucune organisation propriétaire visible dans votre contexte d\'accès actuel.',
  'this worker': 'ce travailleur',
  'Timesheet from': 'Feuille de temps à partir de',
  'Timesheet to': 'Feuille de temps jusqu\'à',
  'Tracking is currently disabled for this location. Enable it in Location Edit; the geofence mapper only manages boundaries.': 'Le suivi est actuellement désactivé pour ce site. Activez-le dans l\'édition du site ; l\'éditeur de géoclôture ne gère que les limites.',
  'Unable to load location operations.': 'Impossible de charger les opérations du site.',
  'Unable to save location.': 'Impossible d\'enregistrer le site.',
  'Unable to send location message.': 'Impossible d\'envoyer le message du site.',
  'Upcoming / recent shift': 'Quart à venir / récent',
  'View breadcrumbs': 'Voir les traces',
  'View details': 'Voir les détails',
  'View geofence': 'Voir la géoclôture',
  'View latest worker positions and selected worker breadcrumb trails.': 'Afficher les dernières positions des travailleurs et les traces du travailleur sélectionné.',
  'View live map': 'Voir la carte en direct',
  'View your agency workers\' positions and breadcrumb trails at this client location.': 'Afficher les positions et les traces des travailleurs de votre agence sur ce site client.',
  'When an owner organization is set, the backend creates or preserves a linked location OU under that owner organization.': 'Lorsqu\'une organisation propriétaire est définie, le backend crée ou conserve une unité organisationnelle de site liée sous cette organisation propriétaire.',
  'Worker punched out.': 'Le travailleur a pointé sa sortie.',
  'Worker punch status is already current.': 'Le statut de pointage du travailleur est déjà à jour.',
  'You can view this location but cannot change tracking settings.': 'Vous pouvez consulter ce site mais vous ne pouvez pas modifier les paramètres de suivi.',
  'You do not have permission to punch out this worker for this location or agency.': 'Vous n\'avez pas la permission de pointer la sortie de ce travailleur pour ce site ou cette agence.',
})

// OrgRelationshipsPanel localization batch
Object.assign(ar, {
  'Accept': 'قبول',
  'Agency - Company': 'وكالة - شركة',
  'Agency connections configured as workforce partners with company-controlled visibility, site grants, and delegated actions.': 'اتصالات الوكالة المهيأة كشركاء قوى عاملة مع رؤية تتحكم بها الشركة، ومنح المواقع، والإجراءات المفوّضة.',
  'Agency search': 'بحث عن الوكالة',
  'Agency - Subcontractor': 'وكالة - متعاقد من الباطن',
  'Agency tenant': 'مستأجر الوكالة',
  'Agency users cannot self-grant permissions. Only the company side can modify these flags.': 'لا يمكن لمستخدمي الوكالة منح الأذونات لأنفسهم. يمكن لجانب الشركة فقط تعديل هذه الإعدادات.',
  'Approval rules': 'قواعد الموافقة',
  'Archived': 'مؤرشف',
  'Assign workers: {assign} · Timesheets: {timesheets} · Pay/Bill: {pay}/{bill}': 'تعيين العمال: {assign} · سجلات الدوام: {timesheets} · الأجر/الفاتورة: {pay}/{bill}',
  'Claim status: {status}': 'حالة المطالبة: {status}',
  'Company - Agency': 'شركة - وكالة',
  'Company connections visible to this agency. Company-side grants are read-only here unless your access grants policy management.': 'اتصالات الشركة المرئية لهذه الوكالة. صلاحيات جانب الشركة هنا للقراءة فقط إلا إذا كان وصولك يمنح إدارة السياسات.',
  'Company search': 'بحث عن الشركة',
  'Company tenant': 'مستأجر الشركة',
  'Configure Workforce Partner': 'تهيئة شريك القوى العاملة',
  'Confirm revoke': 'تأكيد الإلغاء',
  'Confirm suspend': 'تأكيد التعليق',
  'Connection': 'الاتصال',
  'Connection state': 'حالة الاتصال',
  'Connection suspended.': 'تم تعليق الاتصال.',
  'Connection type': 'نوع الاتصال',
  'Create active relationship': 'إنشاء علاقة نشطة',
  'Delegated Staffing Partner': 'شريك توظيف مفوّض',
  'Deleted': 'محذوف',
  'Directory Only': 'الدليل فقط',
  'Directory only builds the link without site access. Delegated and managed presets enable company-controlled job, shift, assignment, and timesheet actions.': 'يقوم "الدليل فقط" بإنشاء الرابط دون الوصول إلى المواقع. تُمكّن الإعدادات المسبقة المفوّضة والمُدارة من إجراءات الوظائف والمناوبات والتعيينات وسجلات الدوام التي تتحكم بها الشركة.',
  'Ends at': 'ينتهي في',
  'Enter an invite code before redeeming.': 'أدخل رمز الدعوة قبل الاستبدال.',
  'ext': 'خارجي',
  'external': 'خارجي',
  'Federated: policy saves to relationship ID only. Tenant ownership is resolved by the backend.': 'اتحادي: تُحفظ السياسة على معرف العلاقة فقط. تُحدد الواجهة الخلفية ملكية المستأجر.',
  'Find {target}': 'البحث عن {target}',
  'Generate invite code': 'إنشاء رمز دعوة',
  'Global organization relationship created.': 'تم إنشاء علاقة المؤسسة العالمية.',
  'Incoming requests': 'الطلبات الواردة',
  'Invalid: missing endpoint': 'غير صالح: نقطة النهاية مفقودة',
  'Invite code': 'رمز الدعوة',
  'Invite code generated.': 'تم إنشاء رمز الدعوة.',
  'Invite code redeemed. The relationship is pending unless the invite allowed auto-accept.': 'تم استبدال رمز الدعوة. العلاقة قيد الانتظار إلا إذا سمحت الدعوة بالقبول التلقائي.',
  'Job Receiving Agency': 'وكالة استقبال الوظائف',
  'Jobs: receive {receive}, create {create} · Shifts: {shifts} · Assign workers: {assign}': 'الوظائف: استقبال {receive}، إنشاء {create} · المناوبات: {shifts} · تعيين العمال: {assign}',
  'Loading relationships...': 'جارٍ تحميل العلاقات...',
  'Location Scoped Supplier': 'مورّد محدد بالموقع',
  'Onboard Associated Client Company': 'إلحاق شركة عميلة مرتبطة',
  'Creates a dedicated company tenant, company organization, default site-supervisor role, and workforce-network relationship. This replaces legacy agency-managed client profiles.': 'ينشئ مستأجر شركة مخصصاً ومؤسسة شركة ودور مشرف موقع افتراضياً وعلاقة شبكة قوى عاملة. يحل هذا محل ملفات العملاء القديمة التي تديرها الوكالة.',
  'Associated client company onboarding requires agency admin access.': 'يتطلب إلحاق الشركة العميلة المرتبطة وصول مسؤول الوكالة.',
  'Client company name': 'اسم الشركة العميلة',
  'Client company name is required.': 'اسم الشركة العميلة مطلوب.',
  'Company slug': 'معرّف الشركة',
  'Tenant slug': 'معرّف المستأجر',
  'Business email': 'البريد الإلكتروني التجاري',
  'Client supervisor name': 'اسم مشرف العميل',
  'Client supervisor email': 'بريد مشرف العميل الإلكتروني',
  'Client supervisor email is required.': 'بريد مشرف العميل الإلكتروني مطلوب.',
  'Client supervisor phone': 'هاتف مشرف العميل',
  'Initial supervisor password': 'كلمة مرور المشرف الأولية',
  'Create Associated Client Company': 'إنشاء شركة عميلة مرتبطة',
  'Associated client company created.': 'تم إنشاء الشركة العميلة المرتبطة.',
  'Associated client company created. Temporary supervisor password: {password}': 'تم إنشاء الشركة العميلة المرتبطة. كلمة مرور المشرف المؤقتة: {password}',
  'Unable to create associated client company.': 'تعذر إنشاء الشركة العميلة المرتبطة.',
  'Legacy agency-managed profile': 'ملف قديم تديره الوكالة',
  'Legacy profile': 'ملف قديم',
  'Legacy agency-managed profile. New client companies should be onboarded as Associated Client Companies with their own tenant.': 'ملف قديم تديره الوكالة. يجب إلحاق الشركات العميلة الجديدة كشركات عميلة مرتبطة لها مستأجر خاص بها.',
  'Managed client profile': 'ملف عميل مُدار',
  'Managed Workforce Partner': 'شريك قوى عاملة مُدار',
  'No incoming requests.': 'لا توجد طلبات واردة.',
  'No invite codes yet.': 'لا توجد رموز دعوة بعد.',
  'No locations are available in this scope. The agency can view policy, but no sites have been granted.': 'لا توجد مواقع متاحة في هذا النطاق. يمكن للوكالة عرض السياسة، ولكن لم يتم منح أي مواقع.',
  'No matching organizations found in this tenant. Cross-tenant directory search is not supported yet.': 'لم يتم العثور على مؤسسات مطابقة في هذا المستأجر. البحث في الدليل عبر المستأجرين غير مدعوم بعد.',
  'No outgoing requests.': 'لا توجد طلبات صادرة.',
  'No policy': 'لا توجد سياسة',
  'Outgoing requests': 'الطلبات الصادرة',
  'Paste invite code': 'لصق رمز الدعوة',
  'Pending Deletion': 'قيد الحذف',
  'Permissions': 'الأذونات',
  '{preset} · {count} locations': '{preset} · {count} موقع',
  'Preset: {preset} · Granted sites: {count}': 'الإعداد المسبق: {preset} · المواقع الممنوحة: {count}',
  'Processing...': 'جارٍ المعالجة...',
  'Read-only view. You can view this connection, but only the company or platform admin can modify access grants.': 'عرض للقراءة فقط. يمكنك عرض هذا الاتصال، ولكن يمكن فقط لمسؤول الشركة أو النظام تعديل صلاحيات الوصول.',
  'Receive jobs: {receive} · Create jobs: {create} · Shifts: {shifts}': 'استقبال الوظائف: {receive} · إنشاء الوظائف: {create} · المناوبات: {shifts}',
  'Redeem code': 'استبدال الرمز',
  'Redeem invite': 'استبدال الدعوة',
  'Relationship created.': 'تم إنشاء العلاقة.',
  'Relationship creation requires tenant RBAC management permission in the current backend routes.': 'يتطلب إنشاء العلاقة إذن إدارة RBAC للمستأجر في مسارات الواجهة الخلفية الحالية.',
  'Relationship ID': 'معرف العلاقة',
  'Relationship ID: {id}': 'معرف العلاقة: {id}',
  'Relationship policy updated.': 'تم تحديث سياسة العلاقة.',
  'Relationship request accepted.': 'تم قبول طلب العلاقة.',
  'Relationship request rejected.': 'تم رفض طلب العلاقة.',
  'Relationship request sent.': 'تم إرسال طلب العلاقة.',
  'Relationship requests require organization admin access.': 'تتطلب طلبات العلاقة وصول مسؤول المؤسسة.',
  'Relationships are loaded from the selected organization in either source or target direction.': 'يتم تحميل العلاقات من المؤسسة المحددة في اتجاه المصدر أو الهدف.',
  'Relationship: {status} · Preset: {preset} · Sites: {count}': 'العلاقة: {status} · الإعداد المسبق: {preset} · المواقع: {count}',
  'Relationship status updated.': 'تم تحديث حالة العلاقة.',
  'Request link': 'طلب ربط',
  'Request relationship': 'طلب علاقة',
  'Review and activate': 'المراجعة والتفعيل',
  'Review or configure the company-side sharing policy, granted sites, delegated actions, and approval rules.': 'مراجعة أو تهيئة سياسة المشاركة من جانب الشركة، والمواقع الممنوحة، والإجراءات المفوّضة، وقواعد الموافقة.',
  'Revoke': 'إلغاء',
  'Revoke connection': 'إلغاء الاتصال',
  'Revoking this connection will permanently block future access for the agency. Historical records (timesheets, assignments, punches) remain available where permitted.': 'سيؤدي إلغاء هذا الاتصال إلى حظر الوصول المستقبلي للوكالة بشكل دائم. تظل السجلات التاريخية (سجلات الدوام، التعيينات، التسجيلات) متاحة حيثما يُسمح بذلك.',
  'Save policy': 'حفظ السياسة',
  'Scoped: {value} · View/staff follows policy flags below': 'النطاق: {value} · يتبع العرض/التعيين إعدادات السياسة أدناه',
  'Search global directory': 'البحث في الدليل العالمي',
  'Searching...': 'جارٍ البحث...',
  'Search {target} name or slug': 'البحث عن اسم أو معرف {target}',
  'Search tenant directory': 'البحث في دليل المستأجر',
  'Select agency': 'اختر الوكالة',
  'Select an organization before generating an invite code.': 'اختر مؤسسة قبل إنشاء رمز دعوة.',
  'Select an organization before redeeming an invite code.': 'اختر مؤسسة قبل استبدال رمز الدعوة.',
  'Select company': 'اختر الشركة',
  'Selected agency is missing tenant context.': 'الوكالة المحددة تفتقد سياق المستأجر.',
  'Selected company is missing tenant context.': 'الشركة المحددة تفتقد سياق المستأجر.',
  'Selected locations are granted to the agency as scope for the enabled permissions below.': 'المواقع المحددة مُمنوحة للوكالة كنطاق للأذونات الممكّنة أدناه.',
  'Selected organization': 'المؤسسة المحددة',
  'Select one agency and one company.': 'اختر وكالة واحدة وشركة واحدة.',
  'Select target': 'اختر الهدف',
  'Sites / locations': 'المواقع',
  '{source} requested {target}': '{source} طلب {target}',
  'Starts at': 'يبدأ في',
  'Super Admin global linker': 'رابط المسؤول العام العالمي',
  'Suspend connection': 'تعليق الاتصال',
  'Suspending this connection will pause future access for the agency. Historical records remain available where permitted by your data retention policy.': 'سيؤدي تعليق هذا الاتصال إلى إيقاف الوصول المستقبلي للوكالة مؤقتًا. تظل السجلات التاريخية متاحة وفقًا لسياسة الاحتفاظ بالبيانات الخاصة بك.',
  'Target': 'الهدف',
  'Target organization is required.': 'المؤسسة الهدف مطلوبة.',
  'Termination/revocation blocks future access while preserving historical records where permitted.': 'يحظر الإنهاء/الإلغاء الوصول المستقبلي مع الحفاظ على السجلات التاريخية حيثما يُسمح بذلك.',
  'The agency will lose access to all granted sites and delegated abilities. You can reactivate the connection later.': 'ستفقد الوكالة الوصول إلى جميع المواقع الممنوحة والصلاحيات المفوّضة. يمكنك إعادة تفعيل الاتصال لاحقًا.',
  'This action cannot be undone automatically. Historical workforce records are preserved per your data retention policy.': 'لا يمكن التراجع عن هذا الإجراء تلقائيًا. يتم الحفاظ على سجلات القوى العاملة التاريخية وفقًا لسياسة الاحتفاظ بالبيانات الخاصة بك.',
  'This is a federated relationship. The company and agency exist in separate tenants. Policy is controlled by the company side.': 'هذه علاقة اتحادية. توجد الشركة والوكالة في مستأجرين منفصلين. تتحكم الشركة في السياسة.',
  'This is an agency-managed client profile. The company has not yet independently registered on the platform.': 'هذا ملف عميل تُديره الوكالة. لم تسجل الشركة بشكل مستقل على المنصة بعد.',
  'Timesheets: submit {submit}, view {view} · Rates: pay {pay}, bill {bill}': 'سجلات الدوام: إرسال {submit}، عرض {view} · المعدلات: الأجر {pay}، الفاتورة {bill}',
  '{type} invite ending {code}': 'دعوة {type} تنتهي بـ {code}',
  'Unable to accept relationship request.': 'تعذر قبول طلب العلاقة.',
  'Unable to create global relationship.': 'تعذر إنشاء علاقة عالمية.',
  'Unable to create organization relationship.': 'تعذر إنشاء علاقة المؤسسة.',
  'Unable to generate invite code.': 'تعذر إنشاء رمز الدعوة.',
  'Unable to load invite codes.': 'تعذر تحميل رموز الدعوة.',
  'Unable to load organization relationships.': 'تعذر تحميل علاقات المؤسسة.',
  'Unable to load relationship requests.': 'تعذر تحميل طلبات العلاقة.',
  'Unable to redeem invite code.': 'تعذر استبدال رمز الدعوة.',
  'Unable to reject relationship request.': 'تعذر رفض طلب العلاقة.',
  'Unable to revoke connection.': 'تعذر إلغاء الاتصال.',
  'Unable to search global organizations.': 'تعذر البحث في المؤسسات العالمية.',
  'Unable to search organizations.': 'تعذر البحث عن المؤسسات.',
  'Unable to send relationship request.': 'تعذر إرسال طلب العلاقة.',
  'Unable to suspend connection.': 'تعذر تعليق الاتصال.',
  'Unable to update relationship policy.': 'تعذر تحديث سياسة العلاقة.',
  'Unable to update relationship status.': 'تعذر تحديث حالة العلاقة.',
  'Unclaimed': 'غير مُطالب به',
  'Used': 'مستخدم',
  'View scope': 'عرض النطاق',
  'Workforce partner': 'شريك القوى العاملة',
  'Your organization': 'مؤسستك',
})

Object.assign(fr, {
  'Accept': 'Accepter',
  'Agency - Company': 'Agence - Entreprise',
  'Agency connections configured as workforce partners with company-controlled visibility, site grants, and delegated actions.': 'Connexions d\'agence configurées en tant que partenaires de main-d\'œuvre avec une visibilité contrôlée par l\'entreprise, des accès aux sites et des actions déléguées.',
  'Agency search': 'Recherche d\'agence',
  'Agency - Subcontractor': 'Agence - Sous-traitant',
  'Agency tenant': 'Locataire de l\'agence',
  'Agency users cannot self-grant permissions. Only the company side can modify these flags.': 'Les utilisateurs de l\'agence ne peuvent pas s\'attribuer eux-mêmes des autorisations. Seul le côté entreprise peut modifier ces indicateurs.',
  'Approval rules': 'Règles d\'approbation',
  'Archived': 'Archivé',
  'Assign workers: {assign} · Timesheets: {timesheets} · Pay/Bill: {pay}/{bill}': 'Affecter des travailleurs : {assign} · Feuilles de temps : {timesheets} · Paie/Facturation : {pay}/{bill}',
  'Claim status: {status}': 'Statut de revendication : {status}',
  'Company - Agency': 'Entreprise - Agence',
  'Company connections visible to this agency. Company-side grants are read-only here unless your access grants policy management.': 'Connexions d\'entreprise visibles par cette agence. Les accès côté entreprise sont en lecture seule ici, sauf si votre accès accorde la gestion des politiques.',
  'Company search': 'Recherche d\'entreprise',
  'Company tenant': 'Locataire de l\'entreprise',
  'Configure Workforce Partner': 'Configurer le partenaire de main-d\'œuvre',
  'Confirm revoke': 'Confirmer la révocation',
  'Confirm suspend': 'Confirmer la suspension',
  'Connection': 'Connexion',
  'Connection state': 'État de la connexion',
  'Connection suspended.': 'Connexion suspendue.',
  'Connection type': 'Type de connexion',
  'Create active relationship': 'Créer une relation active',
  'Delegated Staffing Partner': 'Partenaire de personnel délégué',
  'Deleted': 'Supprimé',
  'Directory Only': 'Annuaire uniquement',
  'Directory only builds the link without site access. Delegated and managed presets enable company-controlled job, shift, assignment, and timesheet actions.': '« Annuaire uniquement » crée le lien sans accès aux sites. Les préréglages délégués et gérés permettent des actions sur les emplois, les quarts, les affectations et les feuilles de temps contrôlées par l\'entreprise.',
  'Ends at': 'Se termine le',
  'Enter an invite code before redeeming.': 'Saisissez un code d\'invitation avant de le valider.',
  'ext': 'ext',
  'external': 'externe',
  'Federated: policy saves to relationship ID only. Tenant ownership is resolved by the backend.': 'Fédéré : la politique est enregistrée uniquement avec l\'ID de la relation. La propriété du locataire est résolue par le backend.',
  'Find {target}': 'Rechercher {target}',
  'Generate invite code': 'Générer un code d\'invitation',
  'Global organization relationship created.': 'Relation d\'organisation globale créée.',
  'Incoming requests': 'Demandes entrantes',
  'Invalid: missing endpoint': 'Invalide : point de terminaison manquant',
  'Invite code': 'Code d\'invitation',
  'Invite code generated.': 'Code d\'invitation généré.',
  'Invite code redeemed. The relationship is pending unless the invite allowed auto-accept.': 'Code d\'invitation utilisé. La relation est en attente, sauf si l\'invitation autorisait l\'acceptation automatique.',
  'Job Receiving Agency': 'Agence de réception des emplois',
  'Jobs: receive {receive}, create {create} · Shifts: {shifts} · Assign workers: {assign}': 'Emplois : recevoir {receive}, créer {create} · Quarts : {shifts} · Affecter des travailleurs : {assign}',
  'Loading relationships...': 'Chargement des relations...',
  'Location Scoped Supplier': 'Fournisseur limité au site',
  'Onboard Associated Client Company': 'Intégrer une entreprise cliente associée',
  'Creates a dedicated company tenant, company organization, default site-supervisor role, and workforce-network relationship. This replaces legacy agency-managed client profiles.': 'Crée un locataire d’entreprise dédié, une organisation d’entreprise, un rôle de superviseur de site par défaut et une relation de réseau de main-d’œuvre. Cela remplace les anciens profils clients gérés par l’agence.',
  'Associated client company onboarding requires agency admin access.': 'L’intégration d’une entreprise cliente associée nécessite un accès administrateur de l’agence.',
  'Client company name': 'Nom de l’entreprise cliente',
  'Client company name is required.': 'Le nom de l’entreprise cliente est requis.',
  'Company slug': 'Slug de l’entreprise',
  'Tenant slug': 'Slug du locataire',
  'Business email': 'E-mail professionnel',
  'Client supervisor name': 'Nom du superviseur client',
  'Client supervisor email': 'E-mail du superviseur client',
  'Client supervisor email is required.': 'L’e-mail du superviseur client est requis.',
  'Client supervisor phone': 'Téléphone du superviseur client',
  'Initial supervisor password': 'Mot de passe initial du superviseur',
  'Create Associated Client Company': 'Créer une entreprise cliente associée',
  'Associated client company created.': 'Entreprise cliente associée créée.',
  'Associated client company created. Temporary supervisor password: {password}': 'Entreprise cliente associée créée. Mot de passe temporaire du superviseur : {password}',
  'Unable to create associated client company.': 'Impossible de créer l’entreprise cliente associée.',
  'Legacy agency-managed profile': 'Profil hérité géré par l’agence',
  'Legacy profile': 'Profil hérité',
  'Legacy agency-managed profile. New client companies should be onboarded as Associated Client Companies with their own tenant.': 'Profil hérité géré par l’agence. Les nouvelles entreprises clientes doivent être intégrées comme entreprises clientes associées avec leur propre locataire.',
  'Managed client profile': 'Profil client géré',
  'Managed Workforce Partner': 'Partenaire de main-d\'œuvre géré',
  'No incoming requests.': 'Aucune demande entrante.',
  'No invite codes yet.': 'Aucun code d\'invitation pour le moment.',
  'No locations are available in this scope. The agency can view policy, but no sites have been granted.': 'Aucun site n\'est disponible dans ce périmètre. L\'agence peut consulter la politique, mais aucun site n\'a été accordé.',
  'No matching organizations found in this tenant. Cross-tenant directory search is not supported yet.': 'Aucune organisation correspondante trouvée dans ce locataire. La recherche dans l\'annuaire entre locataires n\'est pas encore prise en charge.',
  'No outgoing requests.': 'Aucune demande sortante.',
  'No policy': 'Aucune politique',
  'Outgoing requests': 'Demandes sortantes',
  'Paste invite code': 'Coller le code d\'invitation',
  'Pending Deletion': 'Suppression en attente',
  'Permissions': 'Autorisations',
  '{preset} · {count} locations': '{preset} · {count} sites',
  'Preset: {preset} · Granted sites: {count}': 'Préréglage : {preset} · Sites accordés : {count}',
  'Processing...': 'Traitement en cours...',
  'Read-only view. You can view this connection, but only the company or platform admin can modify access grants.': 'Vue en lecture seule. Vous pouvez consulter cette connexion, mais seul l\'administrateur de l\'entreprise ou de la plateforme peut modifier les accès accordés.',
  'Receive jobs: {receive} · Create jobs: {create} · Shifts: {shifts}': 'Recevoir des emplois : {receive} · Créer des emplois : {create} · Quarts : {shifts}',
  'Redeem code': 'Valider le code',
  'Redeem invite': 'Valider l\'invitation',
  'Relationship created.': 'Relation créée.',
  'Relationship creation requires tenant RBAC management permission in the current backend routes.': 'La création d\'une relation nécessite une autorisation de gestion RBAC du locataire dans les routes backend actuelles.',
  'Relationship ID': 'ID de la relation',
  'Relationship ID: {id}': 'ID de la relation : {id}',
  'Relationship policy updated.': 'Politique de relation mise à jour.',
  'Relationship request accepted.': 'Demande de relation acceptée.',
  'Relationship request rejected.': 'Demande de relation rejetée.',
  'Relationship request sent.': 'Demande de relation envoyée.',
  'Relationship requests require organization admin access.': 'Les demandes de relation nécessitent un accès administrateur de l\'organisation.',
  'Relationships are loaded from the selected organization in either source or target direction.': 'Les relations sont chargées à partir de l\'organisation sélectionnée, dans le sens source ou cible.',
  'Relationship: {status} · Preset: {preset} · Sites: {count}': 'Relation : {status} · Préréglage : {preset} · Sites : {count}',
  'Relationship status updated.': 'Statut de la relation mis à jour.',
  'Request link': 'Demander la liaison',
  'Request relationship': 'Demander une relation',
  'Review and activate': 'Vérifier et activer',
  'Review or configure the company-side sharing policy, granted sites, delegated actions, and approval rules.': 'Vérifiez ou configurez la politique de partage côté entreprise, les sites accordés, les actions déléguées et les règles d\'approbation.',
  'Revoke': 'Révoquer',
  'Revoke connection': 'Révoquer la connexion',
  'Revoking this connection will permanently block future access for the agency. Historical records (timesheets, assignments, punches) remain available where permitted.': 'La révocation de cette connexion bloquera définitivement les accès futurs de l\'agence. Les enregistrements historiques (feuilles de temps, affectations, pointages) restent disponibles lorsque cela est autorisé.',
  'Save policy': 'Enregistrer la politique',
  'Scoped: {value} · View/staff follows policy flags below': 'Périmètre : {value} · La visualisation/affectation suit les indicateurs de politique ci-dessous',
  'Search global directory': 'Rechercher dans l\'annuaire global',
  'Searching...': 'Recherche en cours...',
  'Search {target} name or slug': 'Rechercher le nom ou le slug de {target}',
  'Search tenant directory': 'Rechercher dans l\'annuaire du locataire',
  'Select agency': 'Sélectionner une agence',
  'Select an organization before generating an invite code.': 'Sélectionnez une organisation avant de générer un code d\'invitation.',
  'Select an organization before redeeming an invite code.': 'Sélectionnez une organisation avant de valider un code d\'invitation.',
  'Select company': 'Sélectionner une entreprise',
  'Selected agency is missing tenant context.': 'L\'agence sélectionnée n\'a pas de contexte de locataire.',
  'Selected company is missing tenant context.': 'L\'entreprise sélectionnée n\'a pas de contexte de locataire.',
  'Selected locations are granted to the agency as scope for the enabled permissions below.': 'Les sites sélectionnés sont accordés à l\'agence comme périmètre pour les autorisations activées ci-dessous.',
  'Selected organization': 'Organisation sélectionnée',
  'Select one agency and one company.': 'Sélectionnez une agence et une entreprise.',
  'Select target': 'Sélectionner une cible',
  'Sites / locations': 'Sites / emplacements',
  '{source} requested {target}': '{source} a demandé {target}',
  'Starts at': 'Commence le',
  'Super Admin global linker': 'Lien global du super administrateur',
  'Suspend connection': 'Suspendre la connexion',
  'Suspending this connection will pause future access for the agency. Historical records remain available where permitted by your data retention policy.': 'La suspension de cette connexion mettra en pause les accès futurs de l\'agence. Les enregistrements historiques restent disponibles selon votre politique de conservation des données.',
  'Target': 'Cible',
  'Target organization is required.': 'L\'organisation cible est requise.',
  'Termination/revocation blocks future access while preserving historical records where permitted.': 'La résiliation/révocation bloque les accès futurs tout en conservant les enregistrements historiques lorsque cela est autorisé.',
  'The agency will lose access to all granted sites and delegated abilities. You can reactivate the connection later.': 'L\'agence perdra l\'accès à tous les sites accordés et aux capacités déléguées. Vous pourrez réactiver la connexion ultérieurement.',
  'This action cannot be undone automatically. Historical workforce records are preserved per your data retention policy.': 'Cette action ne peut pas être annulée automatiquement. Les enregistrements historiques de la main-d\'œuvre sont conservés conformément à votre politique de conservation des données.',
  'This is a federated relationship. The company and agency exist in separate tenants. Policy is controlled by the company side.': 'Il s\'agit d\'une relation fédérée. L\'entreprise et l\'agence existent dans des locataires distincts. La politique est contrôlée par le côté entreprise.',
  'This is an agency-managed client profile. The company has not yet independently registered on the platform.': 'Il s\'agit d\'un profil client géré par l\'agence. L\'entreprise ne s\'est pas encore inscrite indépendamment sur la plateforme.',
  'Timesheets: submit {submit}, view {view} · Rates: pay {pay}, bill {bill}': 'Feuilles de temps : soumettre {submit}, consulter {view} · Tarifs : paie {pay}, facturation {bill}',
  '{type} invite ending {code}': 'Invitation {type} se terminant par {code}',
  'Unable to accept relationship request.': 'Impossible d\'accepter la demande de relation.',
  'Unable to create global relationship.': 'Impossible de créer la relation globale.',
  'Unable to create organization relationship.': 'Impossible de créer la relation d\'organisation.',
  'Unable to generate invite code.': 'Impossible de générer le code d\'invitation.',
  'Unable to load invite codes.': 'Impossible de charger les codes d\'invitation.',
  'Unable to load organization relationships.': 'Impossible de charger les relations de l\'organisation.',
  'Unable to load relationship requests.': 'Impossible de charger les demandes de relation.',
  'Unable to redeem invite code.': 'Impossible de valider le code d\'invitation.',
  'Unable to reject relationship request.': 'Impossible de rejeter la demande de relation.',
  'Unable to revoke connection.': 'Impossible de révoquer la connexion.',
  'Unable to search global organizations.': 'Impossible de rechercher les organisations globales.',
  'Unable to search organizations.': 'Impossible de rechercher des organisations.',
  'Unable to send relationship request.': 'Impossible d\'envoyer la demande de relation.',
  'Unable to suspend connection.': 'Impossible de suspendre la connexion.',
  'Unable to update relationship policy.': 'Impossible de mettre à jour la politique de relation.',
  'Unable to update relationship status.': 'Impossible de mettre à jour le statut de la relation.',
  'Unclaimed': 'Non revendiqué',
  'Used': 'Utilisé',
  'View scope': 'Afficher le périmètre',
  'Workforce partner': 'Partenaire de main-d\'œuvre',
  'Your organization': 'Votre organisation',
})

// UsersAccessPanel localization batch
Object.assign(ar, {
  'Accepted': 'مقبولة',
  'Access for {userName} is limited to the selected tenant organization context.': 'وصول {userName} مقيد بسياق المؤسسة المحددة ضمن المستأجر.',
  'Adding...': 'جارٍ الإضافة...',
  'Adding users requires user management permission.': 'إضافة المستخدمين تتطلب صلاحية إدارة المستخدمين.',
  'Add user': 'إضافة مستخدم',
  'Admin': 'المسؤول',
  'Also accepted: {keys}': 'يُقبل أيضًا: {keys}',
  'Audit / Recent Changes': 'التدقيق / التغييرات الأخيرة',
  'Broad assignment: this can inherit through the selected organization, OUs, and linked locations.': 'تعيين واسع: يمكن أن يُورَّث عبر المؤسسة المحددة والوحدات التنظيمية والمواقع المرتبطة.',
  'Choose an initial role before adding an active user.': 'اختر دورًا أوليًا قبل إضافة مستخدم نشط.',
  'Choose a role before assigning access.': 'اختر دورًا قبل تعيين الوصول.',
  'Choose a {scope} scope before assigning access.': 'اختر نطاق {scope} قبل تعيين الوصول.',
  'Choose a {scope} scope before sending the invitation.': 'اختر نطاق {scope} قبل إرسال الدعوة.',
  'Corporate User': 'مستخدم الشركة',
  '{count} permissions': '{count} صلاحية',
  'Create access inside {orgName}.': 'إنشاء وصول داخل {orgName}.',
  'Create active access inside {orgName}.': 'إنشاء وصول نشط داخل {orgName}.',
  'Create a tenant role and assign feature permissions.': 'أنشئ دورًا للمستأجر وحدد صلاحيات الميزات.',
  'Create role': 'إنشاء دور',
  'Dedicated audit events are not part of the current PostgreSQL schema. The backend still enforces every user and role mutation by tenant and organization scope.': 'أحداث التدقيق المخصصة ليست جزءًا من مخطط PostgreSQL الحالي. تستمر الواجهة الخلفية في تطبيق كل تعديل على المستخدمين والأدوار وفق نطاق المستأجر والمؤسسة.',
  'Delete if safe': 'حذف إن كان آمنًا',
  'Delete role if safe?': 'حذف الدور إن كان آمنًا؟',
  'Delete user if safe?': 'حذف المستخدم إن كان آمنًا؟',
  'Dependencies: {dependencies}.': 'الارتباطات: {dependencies}.',
  'Direct organization access only.': 'وصول مباشر للمؤسسة فقط.',
  'Direct OU access only.': 'وصول مباشر للوحدة التنظيمية فقط.',
  'Edit role': 'تعديل الدور',
  'Edit user': 'تعديل المستخدم',
  'Expires': 'تنتهي الصلاحية',
  'Feature': 'الميزة',
  'Feature permission catalog': 'دليل صلاحيات الميزات',
  'Generated invite links for this organization will appear here.': 'ستظهر روابط الدعوات المُنشأة لهذه المؤسسة هنا.',
  'Hidden': 'مخفي',
  'Inherited through child OUs and linked locations.': 'موروث عبر الوحدات التنظيمية الفرعية والمواقع المرتبطة.',
  'Inherited through this organization, its OUs, and linked locations where backend rules allow.': 'موروث عبر هذه المؤسسة ووحداتها التنظيمية والمواقع المرتبطة وفق قواعد الواجهة الخلفية.',
  'Invitation created.': 'تم إنشاء الدعوة.',
  'Invitation created. Share the invite link with the member.': 'تم إنشاء الدعوة. شارك رابط الدعوة مع العضو.',
  'Invited': 'مدعو',
  'Invite link:': 'رابط الدعوة:',
  'Invite revoked.': 'تم إلغاء الدعوة.',
  'Invite user': 'دعوة مستخدم',
  'Inviting...': 'جارٍ الدعوة...',
  'Inviting users requires invite or user management permission.': 'دعوة المستخدمين تتطلب صلاحية الدعوة أو إدارة المستخدمين.',
  'Jobs, schedule board, assignments, and top-workers visibility inherit through tenant, organization, OU, and location resource containment.': 'تُورَّث رؤية الوظائف ولوحة الجدولة والتكليفات وأفضل العمال عبر احتواء موارد المستأجر والمؤسسة والوحدة التنظيمية والموقع.',
  'Jobs / Scheduling': 'الوظائف / الجدولة',
  'Joined': 'تاريخ الانضمام',
  '{label} ({count} permissions)': '{label} ({count} صلاحية)',
  'Last active': 'آخر نشاط',
  'Loading users...': 'جارٍ تحميل المستخدمين...',
  'Location-scoped access for this work site.': 'وصول مقيد بالموقع لهذا الموقع الميداني.',
  'Membership': 'العضوية',
  'New password': 'كلمة المرور الجديدة',
  'No pending invites': 'لا توجد دعوات معلقة',
  'No roles found': 'لا توجد أدوار',
  'No role yet': 'لا يوجد دور حتى الآن',
  'No scoped role assignments visible for this user.': 'لا توجد تعيينات أدوار محددة النطاق ظاهرة لهذا المستخدم.',
  'No scoped roles': 'لا توجد أدوار محددة النطاق',
  'No users found': 'لا يوجد مستخدمون',
  'No users match the current organization, status, and search filters.': 'لا يوجد مستخدمون يطابقون المؤسسة والحالة ومرشحات البحث الحالية.',
  'No valid {scope} scopes are visible for this organization.': 'لا توجد نطاقات {scope} صالحة ظاهرة لهذه المؤسسة.',
  'No visible grant in this context': 'لا يوجد منح ظاهر في هذا السياق',
  'No visible role assignments': 'لا توجد تعيينات أدوار ظاهرة',
  'Only system administrators can assign platform roles.': 'يمكن لمسؤولي النظام فقط تعيين أدوار المنصة.',
  'Operating in {orgName}. Role choices below are limited to this tenant and organization context; backend permission checks still enforce every mutation.': 'العمل ضمن {orgName}. خيارات الأدوار أدناه مقيدة بسياق هذا المستأجر والمؤسسة؛ ولا تزال فحوصات الصلاحيات في الواجهة الخلفية تُطبَّق على كل تعديل.',
  'Organization directory and context views.': 'دليل المؤسسات وعروض السياق.',
  'Organization Unit': 'الوحدة التنظيمية',
  'Organization visibility': 'رؤية المؤسسة',
  'Org Unit': 'الوحدة التنظيمية',
  'Password reset.': 'تمت إعادة تعيين كلمة المرور.',
  'Permission visibility hints': 'تلميحات رؤية الصلاحيات',
  'Reactivate': 'إعادة التفعيل',
  'Reactivate user?': 'إعادة تفعيل المستخدم؟',
  'Reliability and attendance events are visible to anyone who can read the worker profile; reviews still use the centralized worker review scope.': 'أحداث الموثوقية والحضور ظاهرة لأي شخص يمكنه قراءة ملف العامل؛ ولا تزال التقييمات تستخدم نطاق تقييم العمال المركزي.',
  'Remove role': 'إزالة الدور',
  'Required permission key': 'مفتاح الصلاحية المطلوب',
  'Reset password': 'إعادة تعيين كلمة المرور',
  'Resetting...': 'جارٍ إعادة التعيين...',
  'Revoke invite': 'إلغاء الدعوة',
  'Revoke invite?': 'إلغاء الدعوة؟',
  'Revoke role': 'إلغاء الدور',
  'Revoke scoped role?': 'إلغاء الدور المحدد النطاق؟',
  'Role created.': 'تم إنشاء الدور.',
  'Role deleted.': 'تم حذف الدور.',
  'Role key': 'مفتاح الدور',
  'Role name is required.': 'اسم الدور مطلوب.',
  'Roles': 'الأدوار',
  'Role summary': 'ملخص الأدوار',
  'Role updated.': 'تم تحديث الدور.',
  '{role} will be deleted only if it has no active assignments or pending invites.': 'سيتم حذف {role} فقط إذا لم تكن له تعيينات نشطة أو دعوات معلقة.',
  '{role} will be removed from {userName} for {scope}.': 'سيُزال {role} من {userName} لنطاق {scope}.',
  'Save role': 'حفظ الدور',
  'Save user': 'حفظ المستخدم',
  'Scoped access in the selected organization context.': 'وصول محدد النطاق في سياق المؤسسة المحددة.',
  'Scoped location, geofence, and location context visibility can be direct or inherited from tenant, organization, or parent OU.': 'يمكن أن تكون رؤية الموقع المحدد النطاق والسياج الجغرافي وسياق الموقع مباشرة أو موروثة من المستأجر أو المؤسسة أو الوحدة التنظيمية الأصل.',
  'Scoped permissions': 'الصلاحيات المحددة النطاق',
  'Scoped role assigned.': 'تم تعيين الدور المحدد النطاق.',
  'Scoped role revoked.': 'تم إلغاء الدور المحدد النطاق.',
  'Scope summary': 'ملخص النطاق',
  'Search name or email': 'البحث بالاسم أو البريد الإلكتروني',
  'Select an organization before adding a user.': 'اختر مؤسسة قبل إضافة مستخدم.',
  'Select an organization before changing users or scoped access.': 'اختر مؤسسة قبل تغيير المستخدمين أو الوصول المحدد النطاق.',
  'Select an organization before inviting a user.': 'اختر مؤسسة قبل دعوة مستخدم.',
  'Select a user': 'اختر مستخدمًا',
  'Selected user': 'المستخدم المحدد',
  'Select feature permissions for this tenant role.': 'حدد صلاحيات الميزات لهذا دور المستأجر.',
  'Select role': 'اختر دورًا',
  'Send invitation': 'إرسال الدعوة',
  'Set a new password for {userName}.': 'عيّن كلمة مرور جديدة لـ {userName}.',
  'Source role / scope': 'الدور المصدر / النطاق',
  'Suspend user?': 'تعليق المستخدم؟',
  'Tenant-wide access across every organization in this tenant.': 'وصول على مستوى المستأجر عبر جميع المؤسسات في هذا المستأجر.',
  'The invite for {email} will no longer be accepted.': 'لن يتم قبول الدعوة الخاصة بـ {email} بعد الآن.',
  'the selected user': 'المستخدم المحدد',
  'This role': 'هذا الدور',
  'This user has no scoped role assignments visible in the selected organization.': 'لا توجد لهذا المستخدم تعيينات أدوار محددة النطاق ظاهرة في المؤسسة المحددة.',
  'Timesheet queues and payroll/export surfaces.': 'قوائم انتظار جداول الدوام وواجهات الرواتب/التصدير.',
  'Timesheets / Payroll': 'جداول الدوام / الرواتب',
  'Unable to add user.': 'تعذّرت إضافة المستخدم.',
  'Unable to assign role.': 'تعذّر تعيين الدور.',
  'Unable to delete role.': 'تعذّر حذف الدور.',
  'Unable to delete user.': 'تعذّر حذف المستخدم.',
  'Unable to disable user.': 'تعذّر تعطيل المستخدم.',
  'Unable to invite user.': 'تعذّرت دعوة المستخدم.',
  'Unable to reset password.': 'تعذّرت إعادة تعيين كلمة المرور.',
  'Unable to restore user.': 'تعذّرت استعادة المستخدم.',
  'Unable to revoke invite.': 'تعذّر إلغاء الدعوة.',
  'Unable to revoke role.': 'تعذّر إلغاء الدور.',
  'Unable to save role.': 'تعذّر حفظ الدور.',
  'Unable to update user.': 'تعذّر تحديث المستخدم.',
  'Unable to update user status.': 'تعذّر تحديث حالة المستخدم.',
  'User added.': 'تمت إضافة المستخدم.',
  'User details': 'تفاصيل المستخدم',
  'User disable complete.': 'تم تعطيل المستخدم.',
  'User lists, role catalog, and scoped assignments.': 'قوائم المستخدمين، ودليل الأدوار، والتعيينات المحددة النطاق.',
  '{userName} will be restored to active access.': 'سيُستعاد {userName} إلى الوصول النشط.',
  '{userName} will be suspended in this organization scope.': 'سيُعلَّق {userName} في نطاق هذه المؤسسة.',
  '{userName} will only be deleted if no dependent business history exists.': 'سيُحذف {userName} فقط إذا لم يوجد سجل أعمال مرتبط به.',
  'User reactivated.': 'تم إعادة تفعيل المستخدم.',
  'User restore complete.': 'تمت استعادة المستخدم.',
  'Users & Access requires user or role view permission.': 'يتطلب المستخدمون والوصول صلاحية عرض المستخدمين أو الأدوار.',
  'User safely deleted.': 'تم حذف المستخدم بأمان.',
  'Users and access sections': 'أقسام المستخدمين والوصول',
  'User suspended.': 'تم تعليق المستخدم.',
  'User updated.': 'تم تحديث المستخدم.',
  'Visible': 'ظاهر',
  'Visible to anyone who can read the worker profile through the backend worker resource resolver; blocked only for missing worker visibility or outside scope.': 'ظاهر لأي شخص يمكنه قراءة ملف العامل عبر محلل موارد العامل في الواجهة الخلفية؛ يُحظر فقط عند غياب رؤية العامل أو الخروج عن النطاق.',
  'What can this user see?': 'ماذا يمكن لهذا المستخدم رؤيته؟',
  'Worker Availability': 'توفر العامل',
  'Worker availability, reliability, and attendance events are visible to anyone who can read the worker profile through {workersPermission} or {assignmentsPermission}. Access can be direct, inherited from tenant, organization, parent OU, or location, and denials are missing worker visibility or outside scope.': 'تتوفر رؤية توفر العامل وموثوقيته وأحداث الحضور لأي شخص يمكنه قراءة ملف العامل من خلال {workersPermission} أو {assignmentsPermission}. يمكن أن يكون الوصول مباشرًا أو موروثًا من المستأجر أو المؤسسة أو الوحدة التنظيمية الأصل أو الموقع، وحالات الرفض هي غياب رؤية العامل أو الخروج عن النطاق.',
  'Worker availability, reliability, and attendance events follow worker profile visibility through {workersPermission} or {assignmentsPermission} plus backend inherited worker scope. The backend reports direct, inherited from tenant, organization, parent OU, location, missing worker visibility, or outside scope.': 'تتبع رؤية توفر العامل وموثوقيته وأحداث الحضور رؤية ملف العامل من خلال {workersPermission} أو {assignmentsPermission} بالإضافة إلى نطاق العامل الموروث في الواجهة الخلفية. يُبيّن الواجهة الخلفية ما إذا كان الوصول مباشرًا أو موروثًا من المستأجر أو المؤسسة أو الوحدة التنظيمية الأصل أو الموقع، أو غياب رؤية العامل، أو الخروج عن النطاق.',
  'Worker directory and detail visibility: direct, inherited from tenant, organization, parent OU, or location; blocked when worker visibility is missing or the worker is outside scope.': 'دليل العمال ورؤية التفاصيل: مباشرة أو موروثة من المستأجر أو المؤسسة أو الوحدة التنظيمية الأصل أو الموقع؛ تُحظر عند غياب رؤية العامل أو خروجه عن النطاق.',
  'Worker Reliability / Rating': 'موثوقية العامل / التقييم',
  'Your current access': 'وصولك الحالي',
})

Object.assign(fr, {
  'Accepted': 'Acceptée',
  'Access for {userName} is limited to the selected tenant organization context.': 'L\'accès de {userName} est limité au contexte de l\'organisation sélectionnée dans ce locataire.',
  'Adding...': 'Ajout...',
  'Adding users requires user management permission.': 'L\'ajout d\'utilisateurs nécessite la permission de gestion des utilisateurs.',
  'Add user': 'Ajouter un utilisateur',
  'Admin': 'Administration',
  'Also accepted: {keys}': 'Également acceptés : {keys}',
  'Audit / Recent Changes': 'Audit / Modifications récentes',
  'Broad assignment: this can inherit through the selected organization, OUs, and linked locations.': 'Attribution large : peut être hérité via l\'organisation sélectionnée, ses unités organisationnelles et les sites liés.',
  'Choose an initial role before adding an active user.': 'Choisissez un rôle initial avant d\'ajouter un utilisateur actif.',
  'Choose a role before assigning access.': 'Choisissez un rôle avant d\'attribuer l\'accès.',
  'Choose a {scope} scope before assigning access.': 'Choisissez une portée {scope} avant d\'attribuer l\'accès.',
  'Choose a {scope} scope before sending the invitation.': 'Choisissez une portée {scope} avant d\'envoyer l\'invitation.',
  'Corporate User': 'Utilisateur d\'entreprise',
  '{count} permissions': '{count} permissions',
  'Create access inside {orgName}.': 'Créer un accès au sein de {orgName}.',
  'Create active access inside {orgName}.': 'Créer un accès actif au sein de {orgName}.',
  'Create a tenant role and assign feature permissions.': 'Créez un rôle de locataire et attribuez les permissions des fonctionnalités.',
  'Create role': 'Créer un rôle',
  'Dedicated audit events are not part of the current PostgreSQL schema. The backend still enforces every user and role mutation by tenant and organization scope.': 'Les événements d\'audit dédiés ne font pas partie du schéma PostgreSQL actuel. Le backend continue d\'appliquer chaque modification d\'utilisateur et de rôle selon la portée du locataire et de l\'organisation.',
  'Delete if safe': 'Supprimer si possible',
  'Delete role if safe?': 'Supprimer le rôle si possible ?',
  'Delete user if safe?': 'Supprimer l\'utilisateur si possible ?',
  'Dependencies: {dependencies}.': 'Dépendances : {dependencies}.',
  'Direct organization access only.': 'Accès direct à l\'organisation uniquement.',
  'Direct OU access only.': 'Accès direct à l\'unité organisationnelle uniquement.',
  'Edit role': 'Modifier le rôle',
  'Edit user': 'Modifier l\'utilisateur',
  'Expires': 'Expire',
  'Feature': 'Fonctionnalité',
  'Feature permission catalog': 'Catalogue des permissions de fonctionnalités',
  'Generated invite links for this organization will appear here.': 'Les liens d\'invitation générés pour cette organisation apparaîtront ici.',
  'Hidden': 'Masqué',
  'Inherited through child OUs and linked locations.': 'Hérité via les unités organisationnelles enfants et les sites liés.',
  'Inherited through this organization, its OUs, and linked locations where backend rules allow.': 'Hérité via cette organisation, ses unités organisationnelles et les sites liés, selon les règles du backend.',
  'Invitation created.': 'Invitation créée.',
  'Invitation created. Share the invite link with the member.': 'Invitation créée. Partagez le lien d\'invitation avec le membre.',
  'Invited': 'Invité',
  'Invite link:': 'Lien d\'invitation :',
  'Invite revoked.': 'Invitation révoquée.',
  'Invite user': 'Inviter un utilisateur',
  'Inviting...': 'Invitation en cours...',
  'Inviting users requires invite or user management permission.': 'L\'invitation d\'utilisateurs nécessite la permission d\'invitation ou de gestion des utilisateurs.',
  'Jobs, schedule board, assignments, and top-workers visibility inherit through tenant, organization, OU, and location resource containment.': 'La visibilité des emplois, du tableau de planification, des affectations et des meilleurs travailleurs est héritée via le confinement des ressources du locataire, de l\'organisation, de l\'OU et du site.',
  'Jobs / Scheduling': 'Emplois / Planification',
  'Joined': 'Inscrit le',
  '{label} ({count} permissions)': '{label} ({count} permissions)',
  'Last active': 'Dernière activité',
  'Loading users...': 'Chargement des utilisateurs...',
  'Location-scoped access for this work site.': 'Accès limité au site pour ce lieu de travail.',
  'Membership': 'Adhésion',
  'New password': 'Nouveau mot de passe',
  'No pending invites': 'Aucune invitation en attente',
  'No roles found': 'Aucun rôle trouvé',
  'No role yet': 'Aucun rôle pour le moment',
  'No scoped role assignments visible for this user.': 'Aucune attribution de rôle limité visible pour cet utilisateur.',
  'No scoped roles': 'Aucun rôle limité',
  'No users found': 'Aucun utilisateur trouvé',
  'No users match the current organization, status, and search filters.': 'Aucun utilisateur ne correspond à l\'organisation, au statut et aux filtres de recherche actuels.',
  'No valid {scope} scopes are visible for this organization.': 'Aucune portée {scope} valide n\'est visible pour cette organisation.',
  'No visible grant in this context': 'Aucun accès visible dans ce contexte',
  'No visible role assignments': 'Aucune attribution de rôle visible',
  'Only system administrators can assign platform roles.': 'Seuls les administrateurs système peuvent attribuer des rôles de plateforme.',
  'Operating in {orgName}. Role choices below are limited to this tenant and organization context; backend permission checks still enforce every mutation.': 'Travail dans {orgName}. Les choix de rôles ci-dessous sont limités au contexte de ce locataire et de cette organisation ; les vérifications de permissions du backend s\'appliquent toujours à chaque modification.',
  'Organization directory and context views.': 'Annuaire des organisations et vues contextuelles.',
  'Organization Unit': 'Unité organisationnelle',
  'Organization visibility': 'Visibilité de l\'organisation',
  'Org Unit': 'Unité organisationnelle',
  'Password reset.': 'Mot de passe réinitialisé.',
  'Permission visibility hints': 'Indices de visibilité des permissions',
  'Reactivate': 'Réactiver',
  'Reactivate user?': 'Réactiver l\'utilisateur ?',
  'Reliability and attendance events are visible to anyone who can read the worker profile; reviews still use the centralized worker review scope.': 'Les événements de fiabilité et de présence sont visibles par toute personne pouvant consulter le profil du travailleur ; les évaluations utilisent toujours la portée centralisée d\'évaluation des travailleurs.',
  'Remove role': 'Retirer le rôle',
  'Required permission key': 'Clé de permission requise',
  'Reset password': 'Réinitialiser le mot de passe',
  'Resetting...': 'Réinitialisation...',
  'Revoke invite': 'Révoquer l\'invitation',
  'Revoke invite?': 'Révoquer l\'invitation ?',
  'Revoke role': 'Révoquer le rôle',
  'Revoke scoped role?': 'Révoquer le rôle limité ?',
  'Role created.': 'Rôle créé.',
  'Role deleted.': 'Rôle supprimé.',
  'Role key': 'Clé du rôle',
  'Role name is required.': 'Le nom du rôle est requis.',
  'Roles': 'Rôles',
  'Role summary': 'Résumé des rôles',
  'Role updated.': 'Rôle mis à jour.',
  '{role} will be deleted only if it has no active assignments or pending invites.': '{role} ne sera supprimé que s\'il n\'a aucune attribution active ni invitation en attente.',
  '{role} will be removed from {userName} for {scope}.': '{role} sera retiré de {userName} pour {scope}.',
  'Save role': 'Enregistrer le rôle',
  'Save user': 'Enregistrer l\'utilisateur',
  'Scoped access in the selected organization context.': 'Accès limité dans le contexte de l\'organisation sélectionnée.',
  'Scoped location, geofence, and location context visibility can be direct or inherited from tenant, organization, or parent OU.': 'La visibilité limitée du site, de la géoclôture et du contexte de localisation peut être directe ou héritée du locataire, de l\'organisation ou de l\'unité organisationnelle parente.',
  'Scoped permissions': 'Permissions limitées',
  'Scoped role assigned.': 'Rôle limité attribué.',
  'Scoped role revoked.': 'Rôle limité révoqué.',
  'Scope summary': 'Résumé de la portée',
  'Search name or email': 'Rechercher par nom ou e-mail',
  'Select an organization before adding a user.': 'Sélectionnez une organisation avant d\'ajouter un utilisateur.',
  'Select an organization before changing users or scoped access.': 'Sélectionnez une organisation avant de modifier les utilisateurs ou l\'accès limité.',
  'Select an organization before inviting a user.': 'Sélectionnez une organisation avant d\'inviter un utilisateur.',
  'Select a user': 'Sélectionner un utilisateur',
  'Selected user': 'Utilisateur sélectionné',
  'Select feature permissions for this tenant role.': 'Sélectionnez les permissions de fonctionnalités pour ce rôle de locataire.',
  'Select role': 'Sélectionner un rôle',
  'Send invitation': 'Envoyer l\'invitation',
  'Set a new password for {userName}.': 'Définissez un nouveau mot de passe pour {userName}.',
  'Source role / scope': 'Rôle source / portée',
  'Suspend user?': 'Suspendre l\'utilisateur ?',
  'Tenant-wide access across every organization in this tenant.': 'Accès à l\'échelle du locataire pour toutes les organisations de ce locataire.',
  'The invite for {email} will no longer be accepted.': 'L\'invitation pour {email} ne sera plus acceptée.',
  'the selected user': 'l\'utilisateur sélectionné',
  'This role': 'Ce rôle',
  'This user has no scoped role assignments visible in the selected organization.': 'Cet utilisateur n\'a aucune attribution de rôle limité visible dans l\'organisation sélectionnée.',
  'Timesheet queues and payroll/export surfaces.': 'Files d\'attente des feuilles de temps et interfaces de paie/export.',
  'Timesheets / Payroll': 'Feuilles de temps / Paie',
  'Unable to add user.': 'Impossible d\'ajouter l\'utilisateur.',
  'Unable to assign role.': 'Impossible d\'attribuer le rôle.',
  'Unable to delete role.': 'Impossible de supprimer le rôle.',
  'Unable to delete user.': 'Impossible de supprimer l\'utilisateur.',
  'Unable to disable user.': 'Impossible de désactiver l\'utilisateur.',
  'Unable to invite user.': 'Impossible d\'inviter l\'utilisateur.',
  'Unable to reset password.': 'Impossible de réinitialiser le mot de passe.',
  'Unable to restore user.': 'Impossible de restaurer l\'utilisateur.',
  'Unable to revoke invite.': 'Impossible de révoquer l\'invitation.',
  'Unable to revoke role.': 'Impossible de révoquer le rôle.',
  'Unable to save role.': 'Impossible d\'enregistrer le rôle.',
  'Unable to update user.': 'Impossible de mettre à jour l\'utilisateur.',
  'Unable to update user status.': 'Impossible de mettre à jour le statut de l\'utilisateur.',
  'User added.': 'Utilisateur ajouté.',
  'User details': 'Détails de l\'utilisateur',
  'User disable complete.': 'Désactivation de l\'utilisateur terminée.',
  'User lists, role catalog, and scoped assignments.': 'Listes d\'utilisateurs, catalogue des rôles et attributions limitées.',
  '{userName} will be restored to active access.': '{userName} sera rétabli en accès actif.',
  '{userName} will be suspended in this organization scope.': '{userName} sera suspendu dans ce périmètre d\'organisation.',
  '{userName} will only be deleted if no dependent business history exists.': '{userName} ne sera supprimé que s\'il n\'existe aucun historique métier dépendant.',
  'User reactivated.': 'Utilisateur réactivé.',
  'User restore complete.': 'Restauration de l\'utilisateur terminée.',
  'Users & Access requires user or role view permission.': 'Utilisateurs et accès nécessite la permission de consultation des utilisateurs ou des rôles.',
  'User safely deleted.': 'Utilisateur supprimé en toute sécurité.',
  'Users and access sections': 'Sections utilisateurs et accès',
  'User suspended.': 'Utilisateur suspendu.',
  'User updated.': 'Utilisateur mis à jour.',
  'Visible': 'Visible',
  'Visible to anyone who can read the worker profile through the backend worker resource resolver; blocked only for missing worker visibility or outside scope.': 'Visible par toute personne pouvant consulter le profil du travailleur via le résolveur de ressources du backend ; bloqué uniquement en cas d\'absence de visibilité du travailleur ou en dehors du périmètre.',
  'What can this user see?': 'Que peut voir cet utilisateur ?',
  'Worker Availability': 'Disponibilité du travailleur',
  'Worker availability, reliability, and attendance events are visible to anyone who can read the worker profile through {workersPermission} or {assignmentsPermission}. Access can be direct, inherited from tenant, organization, parent OU, or location, and denials are missing worker visibility or outside scope.': 'La disponibilité, la fiabilité et les événements de présence du travailleur sont visibles par toute personne pouvant consulter le profil du travailleur via {workersPermission} ou {assignmentsPermission}. L\'accès peut être direct ou hérité du locataire, de l\'organisation, de l\'OU parente ou du site, et les refus sont dus à l\'absence de visibilité du travailleur ou à un périmètre hors de portée.',
  'Worker availability, reliability, and attendance events follow worker profile visibility through {workersPermission} or {assignmentsPermission} plus backend inherited worker scope. The backend reports direct, inherited from tenant, organization, parent OU, location, missing worker visibility, or outside scope.': 'La visibilité de la disponibilité, de la fiabilité et des événements de présence du travailleur suit la visibilité du profil du travailleur via {workersPermission} ou {assignmentsPermission}, ainsi que la portée héritée du travailleur côté backend. Le backend indique si l\'accès est direct, hérité du locataire, de l\'organisation, de l\'OU parente, du site, ou s\'il y a absence de visibilité du travailleur, ou un périmètre hors de portée.',
  'Worker directory and detail visibility: direct, inherited from tenant, organization, parent OU, or location; blocked when worker visibility is missing or the worker is outside scope.': 'Annuaire des travailleurs et visibilité des détails : direct, hérité du locataire, de l\'organisation, de l\'OU parente ou du site ; bloqué en cas d\'absence de visibilité du travailleur ou si le travailleur est hors périmètre.',
  'Worker Reliability / Rating': 'Fiabilité / Évaluation du travailleur',
  'Your current access': 'Votre accès actuel',
})

Object.assign(ar, {
  '{document} will be marked verified for {worker}.': 'سيتم وضع علامة تم التحقق على {document} للعامل {worker}.',
  '{events} attendance events / {reviews} supervisor reviews visible in scope.': '{events} حدث حضور / {reviews} مراجعة مشرف ظاهرة في النطاق.',
  '{hours} hrs / {assignments} assignments / {level}': '{hours} ساعة / {assignments} مهمة / {level}',
  '{km} km radius / {consent}': 'نطاق {km} كم / {consent}',
  'Add document': 'إضافة مستند',
  'Add or remove skills from the selected worker profile.': 'إضافة أو إزالة المهارات من ملف العامل المحدد.',
  'Add skill': 'إضافة مهارة',
  'Add Skill': 'إضافة مهارة',
  'Add worker document': 'إضافة مستند العامل',
  'Address line 1': 'العنوان - السطر 1',
  'Address line 2': 'العنوان - السطر 2',
  'Agency / company': 'الوكالة / الشركة',
  'Agency Worker': 'عامل وكالة',
  'Anonymize': 'إخفاء الهوية',
  'Any document status': 'أي حالة مستند',
  'Any skill': 'أي مهارة',
  'Any status': 'أي حالة',
  'Assign a visible worker to the selected organization through a scoped relationship.': 'تعيين عامل ظاهر للمؤسسة المحددة من خلال علاقة محددة النطاق.',
  'Attendance {attendance}% / Punctuality {punctuality}% / Completion {completion}%': 'الحضور {attendance}% / الالتزام بالمواعيد {punctuality}% / الإنجاز {completion}%',
  'Avatar': 'الصورة الرمزية',
  'Background Check': 'فحص الخلفية',
  'Bio': 'نبذة شخصية',
  'Break compliance {compliance}% / Review score {review}/5': 'الالتزام بالاستراحات {compliance}% / تقييم المراجعة {review}/5',
  'Certificate': 'شهادة',
  'Choose a worker': 'اختر عاملاً',
  'Choose a worker before updating visibility.': 'اختر عاملاً قبل تحديث الظهور.',
  'Choose a worker from the directory before creating a relationship.': 'اختر عاملاً من الدليل قبل إنشاء علاقة.',
  'Company Contractor': 'متعاقد الشركة',
  'Company Employee': 'موظف الشركة',
  'Compliance documents': 'مستندات الامتثال',
  'Contact info': 'معلومات الاتصال',
  'Control marketplace visibility for the selected worker profile.': 'التحكم في ظهور السوق لملف العامل المحدد.',
  'Copied': 'تم النسخ',
  'Copy code': 'نسخ الرمز',
  'Copy failed — select and copy manually.': 'فشل النسخ — حدد وانسخ يدويًا.',
  'Copy instructions': 'نسخ التعليمات',
  'Create a worker invite code': 'إنشاء رمز دعوة عامل',
  'Create an invite code to let workers join this organization during Android registration.': 'أنشئ رمز دعوة للسماح للعمال بالانضمام إلى هذه المؤسسة عند التسجيل عبر أندرويد.',
  'Create invite code': 'إنشاء رمز دعوة',
  'Create worker invite code': 'إنشاء رمز دعوة عامل',
  'Create worker relationship': 'إنشاء علاقة عامل',
  'Created by': 'أنشأه',
  'Creating…': 'جارٍ الإنشاء…',
  'Dashboard document risks: {expired} expired, {expiringSoon} expiring soon, {pending} pending or rejected.': 'مخاطر مستندات اللوحة: {expired} منتهية، {expiringSoon} قريبة الانتهاء، {pending} قيد الانتظار أو مرفوضة.',
  'Dates': 'التواريخ',
  'Direct Deposit Form': 'نموذج الإيداع المباشر',
  'Document opened in a protected blob URL. Allow popups if a new tab did not appear.': 'تم فتح المستند في رابط محمي. اسمح بالنوافذ المنبثقة إذا لم يظهر علامة تبويب جديدة.',
  'Document verification/rejection is a review action; it does not transfer ownership. Sensitive documents remain private and require permission/consent to view.': 'التحقق من المستند أو رفضه هو إجراء مراجعة؛ ولا ينقل الملكية. تظل المستندات الحساسة خاصة وتتطلب إذنًا/موافقة للعرض.',
  'Driver License Id': 'رخصة القيادة',
  'Edit profile': 'تعديل الملف الشخصي',
  'Edit worker document': 'تعديل مستند العامل',
  'Edit worker profile': 'تعديل ملف العامل',
  'Ended': 'منتهية',
  'Exclusive until': 'حصري حتى',
  'Exclusive Worker': 'عامل حصري',
  'Expire': 'إنهاء الصلاحية',
  'Expires {date} · {type} · {org}': 'تنتهي في {date} · {type} · {org}',
  'Expires after (days)': 'تنتهي بعد (أيام)',
  'Expiring in 30 days': 'تنتهي في غضون 30 يومًا',
  'Expiry date': 'تاريخ الانتهاء',
  'File': 'الملف',
  'Free agent limited profile': 'ملف عامل مستقل محدود',
  'Free-agent profile': 'ملف عامل مستقل',
  'Free-agent profiles expose limited public fields only until a relationship and any required document consent exists.': 'تعرض ملفات العمال المستقلين حقولاً عامة محدودة فقط حتى تتوفر علاقة وأي موافقة مستندية مطلوبة.',
  'Generate a short code that a worker enters during Android registration or from the link-org screen to join this organization automatically.': 'أنشئ رمزًا قصيرًا يدخله العامل عند التسجيل عبر أندرويد أو من شاشة ربط المؤسسة للانضمام إلى هذه المؤسسة تلقائيًا.',
  'Government Id': 'الهوية الحكومية',
  'Headline': 'العنوان التعريفي',
  'Immigration Document': 'وثيقة الهجرة',
  'Instructions copied!': 'تم نسخ التعليمات!',
  'Invite a worker to join {org}': 'دعوة عامل للانضمام إلى {org}',
  'Invite code copied!': 'تم نسخ رمز الدعوة!',
  'Invite code revoked.': 'تم إلغاء رمز الدعوة.',
  'Iqama': 'الإقامة',
  'Issue date': 'تاريخ الإصدار',
  'Issuer': 'الجهة المصدرة',
  'Languages': 'اللغات',
  'Learned Through Mentorship': 'تم تعلمها من خلال الإرشاد',
  'License': 'رخصة',
  'Limited marketplace profile': 'ملف سوق محدود',
  'Loading free agents...': 'جارٍ تحميل العمال المستقلين...',
  'Loading worker relationships...': 'جارٍ تحميل علاقات العمال...',
  'location matching consent enabled': 'موافقة مطابقة الموقع مفعّلة',
  'Location matching consent enabled': 'موافقة مطابقة الموقع مفعّلة',
  'location matching consent off': 'موافقة مطابقة الموقع متوقفة',
  'Manage skills': 'إدارة المهارات',
  'Manage visibility': 'إدارة الظهور',
  'Manage worker skills': 'إدارة مهارات العامل',
  'Marketplace visibility': 'ظهور السوق',
  'Medical Clearance': 'التصريح الطبي',
  'Missing / pending': 'مفقود / قيد الانتظار',
  'Missing worker management permission. If the logged-in user owns the worker profile, use worker-owned profile UI for direct free-agent changes.': 'صلاحية إدارة العمال مفقودة. إذا كان المستخدم الحالي يملك ملف العامل، فاستخدم واجهة الملف الشخصي الخاصة بالعامل للتغييرات المباشرة للعامل المستقل.',
  'Name, email, phone, reference': 'الاسم، البريد الإلكتروني، الهاتف، المرجع',
  'National Id': 'الهوية الوطنية',
  'No blackout periods declared.': 'لم يتم الإعلان عن فترات حظر.',
  'No certification or document metadata provided yet.': 'لم يتم تقديم بيانات شهادة أو مستند حتى الآن.',
  'No completed-work experience recorded yet.': 'لم يتم تسجيل أي خبرة عمل مكتملة حتى الآن.',
  'No contact info provided.': 'لم تُقدَّم معلومات اتصال.',
  'No invite codes yet': 'لا توجد رموز دعوة حتى الآن',
  'No private contact shown': 'لا يتم عرض جهة اتصال خاصة',
  'No recommendations yet.': 'لا توجد توصيات حتى الآن.',
  'No recurring availability declared.': 'لم يتم الإعلان عن توفر متكرر.',
  'No reliability data yet': 'لا توجد بيانات موثوقية حتى الآن',
  'No reliability data yet.': 'لا توجد بيانات موثوقية حتى الآن.',
  'No skills recorded yet.': 'لم يتم تسجيل أي مهارات حتى الآن.',
  'No skills yet': 'لا توجد مهارات حتى الآن',
  'No upcoming availability conflicts.': 'لا توجد تعارضات توفر قادمة.',
  'No visible free-agent workers found.': 'لم يتم العثور على عمال مستقلين ظاهرين.',
  'No worker relationships match this filter.': 'لا توجد علاقات عمال تطابق هذا الفلتر.',
  'No workers match these filters': 'لا يوجد عمال يطابقون هذه الفلاتر',
  'Not provided': 'غير متوفر',
  'optional': 'اختياري',
  'Other': 'أخرى',
  'Passport': 'جواز السفر',
  'Pending Verification': 'قيد التحقق',
  'Portfolio': 'ملف الأعمال',
  'Postal code': 'الرمز البريدي',
  'Preferred locations': 'المواقع المفضلة',
  'Private': 'خاص',
  'Profile availability summary': 'ملخص توفر الملف الشخصي',
  'Profile media': 'وسائط الملف الشخصي',
  'Profile Photo': 'صورة الملف الشخصي',
  'Profile summary unavailable': 'ملخص الملف الشخصي غير متوفر',
  'Province/State/Region': 'المقاطعة/الولاية/المنطقة',
  'Public bio': 'نبذة عامة',
  'Public headline': 'العنوان التعريفي العام',
  'Public info': 'معلومات عامة',
  'Rating': 'التقييم',
  'Recommended skill added.': 'تمت إضافة المهارة الموصى بها.',
  'Recommended Skills': 'المهارات الموصى بها',
  'Recurring windows': 'النوافذ المتكررة',
  'Register with the Punch app and enter invite code: {code}\nOrganization: {org}\nExpires: {date}': 'سجّل في تطبيق Punch وأدخل رمز الدعوة: {code}\nالمؤسسة: {org}\nتنتهي في: {date}',
  'Reject document': 'رفض المستند',
  'Reject worker document': 'رفض مستند العامل',
  'Rejecting...': 'جارٍ الرفض...',
  'Rejection reason is required.': 'سبب الرفض مطلوب.',
  'Relationship': 'العلاقة',
  'Relationship edits are scoped to {org}.': 'تعديلات العلاقة مقتصرة على {org}.',
  'Relationship type': 'نوع العلاقة',
  'Reliability': 'الموثوقية',
  'Reliability {score}/100': 'الموثوقية {score}/100',
  'Resume': 'السيرة الذاتية',
  'Reviewer notes': 'ملاحظات المراجع',
  'Safety Card': 'بطاقة السلامة',
  'Safety Induction': 'تأهيل السلامة',
  'Save document': 'حفظ المستند',
  'Save profile': 'حفظ الملف الشخصي',
  'Search and filters use profile, relationship, status, and skill metadata while keeping record IDs internal.': 'تستخدم البحث والفلاتر بيانات الملف الشخصي والعلاقة والحالة والمهارة مع الحفاظ على معرفات السجلات داخلية.',
  'Search canonical skills': 'البحث في المهارات المعيارية',
  'Search workers': 'البحث عن العمال',
  'Select an organization to inspect workers.': 'حدد مؤسسة لفحص العمال.',
  'Self Reported': 'مُعلن ذاتيًا',
  'Sensitive': 'حساس',
  'Share this code with the worker. It will only be shown once.': 'شارك هذا الرمز مع العامل. سيتم عرضه مرة واحدة فقط.',
  'Shared Worker': 'عامل مشترك',
  'Site Access Card': 'بطاقة الوصول للموقع',
  'Skill': 'المهارة',
  'Skill Experience': 'خبرة المهارة',
  'Submitted': 'مُقدَّم',
  'Tax Document': 'وثيقة ضريبية',
  'the selected organization': 'المؤسسة المحددة',
  'The worker uploads and owns these files. Your organization may review, verify, reject, expire, or revoke document records only within your permitted relationship and consent scope.': 'يقوم العامل بتحميل هذه الملفات وامتلاكها. يجوز لمؤسستك مراجعة سجلات المستندات أو التحقق منها أو رفضها أو إنهاء صلاحيتها أو إلغاؤها فقط في نطاق العلاقة والموافقة المسموح بها.',
  'to': 'إلى',
  'Trade Certificate': 'شهادة مهنية',
  'Training Card': 'بطاقة تدريب',
  'Try a different search, status, skill, document, or organization filter.': 'حاول استخدام بحث أو حالة أو مهارة أو مستند أو فلتر مؤسسة مختلف.',
  'Unable to {action} worker.': 'تعذر {action} العامل.',
  'Unable to add recommended skill.': 'تعذرت إضافة المهارة الموصى بها.',
  'Unable to create invite code.': 'تعذر إنشاء رمز الدعوة.',
  'Unable to create worker relationship.': 'تعذر إنشاء علاقة العامل.',
  'Unable to end worker relationship.': 'تعذر إنهاء علاقة العامل.',
  'Unable to expire worker document.': 'تعذر إنهاء صلاحية مستند العامل.',
  'Unable to load worker availability.': 'تعذر تحميل توفر العامل.',
  'Unable to load worker profile.': 'تعذر تحميل ملف العامل.',
  'Unable to load worker reliability.': 'تعذر تحميل موثوقية العامل.',
  'Unable to load worker visibility details.': 'تعذر تحميل تفاصيل ظهور العامل.',
  'Unable to load workers.': 'تعذر تحميل العمال.',
  'Unable to open worker document file.': 'تعذر فتح ملف مستند العامل.',
  'Unable to reject worker document.': 'تعذر رفض مستند العامل.',
  'Unable to remove worker skill.': 'تعذرت إزالة مهارة العامل.',
  'Unable to revoke invite code.': 'تعذر إلغاء رمز الدعوة.',
  'Unable to revoke worker document.': 'تعذر إلغاء مستند العامل.',
  'Unable to save worker document.': 'تعذر حفظ مستند العامل.',
  'Unable to update worker profile visibility.': 'تعذر تحديث ظهور ملف العامل.',
  'Unable to update worker profile.': 'تعذر تحديث ملف العامل.',
  'Unable to update worker relationship.': 'تعذر تحديث علاقة العامل.',
  'Unable to update worker skill.': 'تعذر تحديث مهارة العامل.',
  'Unable to verify worker document.': 'تعذر التحقق من مستند العامل.',
  'Unavailable / blackout': 'غير متاح / فترة حظر',
  'Upcoming conflicts': 'التعارضات القادمة',
  'Update basic profile fields without expanding the worker list.': 'تحديث الحقول الأساسية للملف الشخصي دون توسيع قائمة العمال.',
  'Update visibility': 'تحديث الظهور',
  'Uses': 'الاستخدامات',
  'Verified': 'موثّق',
  'Verify': 'تحقق',
  'Verify worker document?': 'تحقق من مستند العامل؟',
  'View details for {name}': 'عرض تفاصيل {name}',
  'Visibility': 'الظهور',
  'Visibility: Free-agent visibility never exposes private documents or sensitive identity files.': 'الظهور: لا يكشف ظهور العامل المستقل أبدًا عن المستندات الخاصة أو ملفات الهوية الحساسة.',
  'Visibility/free-agent settings are managed in a modal and enforced again by backend permissions.': 'تُدار إعدادات الظهور/العامل المستقل في نافذة منبثقة ويتم تطبيقها مرة أخرى بواسطة صلاحيات الخلفية.',
  'Visible relationship exists': 'توجد علاقة ظاهرة',
  'Visible To All': 'ظاهر للجميع',
  'Visible To Selected': 'ظاهر للمحددين',
  'Work area': 'منطقة العمل',
  'Work eligibility': 'أهلية العمل',
  'Work Permit': 'تصريح عمل',
  'Work radius km': 'نطاق العمل (كم)',
  'Worker {action} complete.': 'اكتمل {action} العامل.',
  'Worker contact not available': 'جهة اتصال العامل غير متوفرة',
  'Worker directory': 'دليل العمال',
  'Worker document added.': 'تمت إضافة مستند العامل.',
  'Worker document expired.': 'انتهت صلاحية مستند العامل.',
  'Worker document file was not found.': 'لم يتم العثور على ملف مستند العامل.',
  'Worker document rejected.': 'تم رفض مستند العامل.',
  'Worker document revoked.': 'تم إلغاء مستند العامل.',
  'Worker document updated.': 'تم تحديث مستند العامل.',
  'Worker document verified.': 'تم التحقق من مستند العامل.',
  'Worker email': 'البريد الإلكتروني للعامل',
  'Worker invite codes': 'رموز دعوة العمال',
  'Worker lists are scoped to the selected organization and current effective permissions.': 'تكون قوائم العمال مقتصرة على المؤسسة المحددة والصلاحيات الفعلية الحالية.',
  'Worker profile updated.': 'تم تحديث ملف العامل.',
  'Worker profile visibility updated.': 'تم تحديث ظهور ملف العامل.',
  'Worker relationship created.': 'تم إنشاء علاقة العامل.',
  'Worker relationship creation requires worker management, worker sharing, or sharing management permission depending on relationship type.': 'يتطلب إنشاء علاقة العامل صلاحية إدارة العمال أو مشاركة العمال أو إدارة المشاركة حسب نوع العلاقة.',
  'Worker relationship ended.': 'تم إنهاء علاقة العامل.',
  'Worker relationship updated.': 'تم تحديث علاقة العامل.',
  'Worker relationships': 'علاقات العمال',
  'Worker roster access requires worker view or worker management permission.': 'يتطلب الوصول إلى قائمة العمال صلاحية عرض العمال أو إدارة العمال.',
  'Worker safely deleted.': 'تم حذف العامل بأمان.',
  'Worker skill added.': 'تمت إضافة مهارة العامل.',
  'Worker skill removed.': 'تمت إزالة مهارة العامل.',
  'Worker visibility': 'ظهور العامل',
  'Worker-owned documents': 'مستندات مملوكة للعامل',
  'worker@example.com': 'worker@example.com',
  'Workers own their profile and uploaded documents. Organizations can manage relationships, assignments, compliance review, and eligibility.': 'يملك العمال ملفاتهم الشخصية والمستندات المرفوعة. يمكن للمؤسسات إدارة العلاقات والمهام ومراجعة الامتثال والأهلية.',
  'You are not authorized to open this worker document file.': 'غير مخول لك بفتح ملف مستند العامل هذا.',
})

Object.assign(fr, {
  '{document} will be marked verified for {worker}.': '{document} sera marqué comme vérifié pour {worker}.',
  '{events} attendance events / {reviews} supervisor reviews visible in scope.': '{events} événements de présence / {reviews} avis de superviseur visibles dans le périmètre.',
  '{hours} hrs / {assignments} assignments / {level}': '{hours} h / {assignments} affectations / {level}',
  '{km} km radius / {consent}': 'Rayon de {km} km / {consent}',
  'Add document': 'Ajouter un document',
  'Add or remove skills from the selected worker profile.': 'Ajouter ou supprimer des compétences du profil du travailleur sélectionné.',
  'Add skill': 'Ajouter une compétence',
  'Add Skill': 'Ajouter une compétence',
  'Add worker document': 'Ajouter un document du travailleur',
  'Address line 1': 'Adresse ligne 1',
  'Address line 2': 'Adresse ligne 2',
  'Agency / company': 'Agence / entreprise',
  'Agency Worker': 'Travailleur d\'agence',
  'Anonymize': 'Anonymiser',
  'Any document status': 'Tout statut de document',
  'Any skill': 'Toute compétence',
  'Any status': 'Tout statut',
  'Assign a visible worker to the selected organization through a scoped relationship.': 'Affecter un travailleur visible à l\'organisation sélectionnée via une relation définie.',
  'Attendance {attendance}% / Punctuality {punctuality}% / Completion {completion}%': 'Présence {attendance}% / Ponctualité {punctuality}% / Achèvement {completion}%',
  'Availability': 'Disponibilité',
  'Avatar': 'Avatar',
  'Background Check': 'Vérification des antécédents',
  'Bio': 'Biographie',
  'Break compliance {compliance}% / Review score {review}/5': 'Conformité des pauses {compliance}% / Note d\'évaluation {review}/5',
  'Certificate': 'Certificat',
  'Choose a worker': 'Choisir un travailleur',
  'Choose a worker before updating visibility.': 'Choisissez un travailleur avant de mettre à jour la visibilité.',
  'Choose a worker from the directory before creating a relationship.': 'Choisissez un travailleur dans l\'annuaire avant de créer une relation.',
  'Company Contractor': 'Contractant de l\'entreprise',
  'Company Employee': 'Employé de l\'entreprise',
  'Compliance documents': 'Documents de conformité',
  'Contact info': 'Coordonnées',
  'Control marketplace visibility for the selected worker profile.': 'Contrôler la visibilité sur la place de marché du profil du travailleur sélectionné.',
  'Copied': 'Copié',
  'Copy code': 'Copier le code',
  'Copy failed — select and copy manually.': 'Échec de la copie — sélectionnez et copiez manuellement.',
  'Copy instructions': 'Copier les instructions',
  'Create a worker invite code': 'Créer un code d\'invitation de travailleur',
  'Create an invite code to let workers join this organization during Android registration.': 'Créez un code d\'invitation pour permettre aux travailleurs de rejoindre cette organisation lors de l\'inscription sur Android.',
  'Create invite code': 'Créer un code d\'invitation',
  'Create relationship': 'Créer une relation',
  'Create worker invite code': 'Créer un code d\'invitation de travailleur',
  'Create worker relationship': 'Créer une relation de travailleur',
  'Created by': 'Créé par',
  'Creating…': 'Création en cours…',
  'Dashboard document risks: {expired} expired, {expiringSoon} expiring soon, {pending} pending or rejected.': 'Risques documentaires du tableau de bord : {expired} expirés, {expiringSoon} bientôt expirés, {pending} en attente ou rejetés.',
  'Dates': 'Dates',
  'Direct Deposit Form': 'Formulaire de dépôt direct',
  'Document opened in a protected blob URL. Allow popups if a new tab did not appear.': 'Document ouvert dans une URL blob protégée. Autorisez les fenêtres pop-up si un nouvel onglet n\'apparaît pas.',
  'Document verification/rejection is a review action; it does not transfer ownership. Sensitive documents remain private and require permission/consent to view.': 'La vérification/rejet d\'un document est une action de révision ; il ne transfère pas la propriété. Les documents sensibles restent privés et nécessitent une autorisation/un consentement pour être consultés.',
  'Done': 'Terminé',
  'Draft': 'Brouillon',
  'Driver License Id': 'Permis de conduire',
  'Edit profile': 'Modifier le profil',
  'Edit worker document': 'Modifier le document du travailleur',
  'Edit worker profile': 'Modifier le profil du travailleur',
  'Emergency contact': 'Contact d\'urgence',
  'Ended': 'Terminée',
  'Exclusive until': 'Exclusif jusqu\'au',
  'Exclusive Worker': 'Travailleur exclusif',
  'Expire': 'Expirer',
  'Expires {date} · {type} · {org}': 'Expire le {date} · {type} · {org}',
  'Expires after (days)': 'Expire après (jours)',
  'Expiring in 30 days': 'Expire dans 30 jours',
  'Expiry date': 'Date d\'expiration',
  'File': 'Fichier',
  'Free agent limited profile': 'Profil limité d\'agent libre',
  'Free-agent profile': 'Profil d\'agent libre',
  'Free-agent profiles expose limited public fields only until a relationship and any required document consent exists.': 'Les profils d\'agents libres n\'exposent que des champs publics limités jusqu\'à ce qu\'une relation et tout consentement documentaire requis existent.',
  'Fri': 'Ven',
  'Generate a short code that a worker enters during Android registration or from the link-org screen to join this organization automatically.': 'Générez un code court qu\'un travailleur saisit lors de l\'inscription sur Android ou depuis l\'écran de liaison d\'organisation pour rejoindre automatiquement cette organisation.',
  'Government Id': 'Pièce d\'identité gouvernementale',
  'Headline': 'Titre',
  'Immigration Document': 'Document d\'immigration',
  'Instructions copied!': 'Instructions copiées !',
  'Invite a worker to join {org}': 'Inviter un travailleur à rejoindre {org}',
  'Invite code copied!': 'Code d\'invitation copié !',
  'Invite code revoked.': 'Code d\'invitation révoqué.',
  'Invite worker': 'Inviter un travailleur',
  'Iqama': 'Iqama',
  'Issue date': 'Date d\'émission',
  'Issuer': 'Émetteur',
  'Languages': 'Langues',
  'Learned Through Mentorship': 'Acquise par mentorat',
  'License': 'Licence',
  'Limited marketplace profile': 'Profil limité sur la place de marché',
  'Loading free agents...': 'Chargement des agents libres...',
  'Loading worker relationships...': 'Chargement des relations de travailleurs...',
  'location matching consent enabled': 'consentement de correspondance de localisation activé',
  'Location matching consent enabled': 'Consentement de correspondance de localisation activé',
  'location matching consent off': 'consentement de correspondance de localisation désactivé',
  'Manage skills': 'Gérer les compétences',
  'Manage visibility': 'Gérer la visibilité',
  'Manage worker skills': 'Gérer les compétences du travailleur',
  'Marketplace visibility': 'Visibilité sur la place de marché',
  'Medical Clearance': 'Autorisation médicale',
  'Missing / pending': 'Manquant / en attente',
  'Missing worker management permission. If the logged-in user owns the worker profile, use worker-owned profile UI for direct free-agent changes.': 'Permission de gestion des travailleurs manquante. Si l\'utilisateur connecté possède le profil du travailleur, utilisez l\'interface de profil du travailleur pour les modifications directes d\'agent libre.',
  'Mon': 'Lun',
  'Name, email, phone, reference': 'Nom, e-mail, téléphone, référence',
  'National Id': 'Carte d\'identité nationale',
  'No blackout periods declared.': 'Aucune période d\'indisponibilité déclarée.',
  'No certification or document metadata provided yet.': 'Aucune métadonnée de certification ou de document fournie pour le moment.',
  'No completed-work experience recorded yet.': 'Aucune expérience de travail terminée enregistrée pour le moment.',
  'No contact info provided.': 'Aucune coordonnée fournie.',
  'No contact shown': 'Aucun contact affiché',
  'No invite codes yet': 'Aucun code d\'invitation pour le moment',
  'No private contact shown': 'Aucun contact privé affiché',
  'No recommendations yet.': 'Aucune recommandation pour le moment.',
  'No recurring availability declared.': 'Aucune disponibilité récurrente déclarée.',
  'No reliability data yet': 'Aucune donnée de fiabilité pour le moment',
  'No reliability data yet.': 'Aucune donnée de fiabilité pour le moment.',
  'No skills recorded yet.': 'Aucune compétence enregistrée pour le moment.',
  'No skills yet': 'Aucune compétence pour le moment',
  'No upcoming availability conflicts.': 'Aucun conflit de disponibilité à venir.',
  'No visible free-agent workers found.': 'Aucun agent libre visible trouvé.',
  'No worker relationships match this filter.': 'Aucune relation de travailleur ne correspond à ce filtre.',
  'No workers match these filters': 'Aucun travailleur ne correspond à ces filtres',
  'Not provided': 'Non fourni',
  'On break': 'En pause',
  'One-off': 'Ponctuel',
  'optional': 'optionnel',
  'Organization': 'Organisation',
  'Other': 'Autre',
  'Passport': 'Passeport',
  'Pending Verification': 'Vérification en attente',
  'Portfolio': 'Portfolio',
  'Postal code': 'Code postal',
  'Preferred locations': 'Emplacements préférés',
  'Private': 'Privé',
  'Profile': 'Profil',
  'Profile availability summary': 'Résumé de disponibilité du profil',
  'Profile media': 'Médias du profil',
  'Profile Photo': 'Photo de profil',
  'Profile summary unavailable': 'Résumé du profil indisponible',
  'Province/State/Region': 'Province/État/Région',
  'Public bio': 'Biographie publique',
  'Public headline': 'Titre public',
  'Public info': 'Informations publiques',
  'Punched in': 'Pointé entrant',
  'Punched out': 'Pointé sortant',
  'Rating': 'Évaluation',
  'Reason': 'Motif',
  'Recommended skill added.': 'Compétence recommandée ajoutée.',
  'Recommended Skills': 'Compétences recommandées',
  'Recurring windows': 'Plages récurrentes',
  'Register with the Punch app and enter invite code: {code}\nOrganization: {org}\nExpires: {date}': 'Inscrivez-vous sur l\'application Punch et saisissez le code d\'invitation : {code}\nOrganisation : {org}\nExpire le : {date}',
  'Reject document': 'Rejeter le document',
  'Reject worker document': 'Rejeter le document du travailleur',
  'Rejecting...': 'Rejet en cours...',
  'Rejection reason is required.': 'Le motif du rejet est requis.',
  'Relationship': 'Relation',
  'Relationship edits are scoped to {org}.': 'Les modifications de relation sont limitées à {org}.',
  'Relationship type': 'Type de relation',
  'Reliability': 'Fiabilité',
  'Reliability {score}/100': 'Fiabilité {score}/100',
  'Remove': 'Supprimer',
  'Resume': 'CV',
  'Review': 'Avis',
  'Reviewer notes': 'Notes du réviseur',
  'Safety Card': 'Carte de sécurité',
  'Safety Induction': 'Induction sécurité',
  'Sat': 'Sam',
  'Save document': 'Enregistrer le document',
  'Save profile': 'Enregistrer le profil',
  'Search and filters use profile, relationship, status, and skill metadata while keeping record IDs internal.': 'La recherche et les filtres utilisent les métadonnées de profil, de relation, de statut et de compétence tout en gardant les identifiants d\'enregistrement internes.',
  'Search canonical skills': 'Rechercher des compétences canoniques',
  'Search workers': 'Rechercher des travailleurs',
  'Select an organization to inspect workers.': 'Sélectionnez une organisation pour inspecter les travailleurs.',
  'Self Reported': 'Auto-déclarée',
  'Sensitive': 'Sensible',
  'Share this code with the worker. It will only be shown once.': 'Partagez ce code avec le travailleur. Il ne sera affiché qu\'une seule fois.',
  'Shared Worker': 'Travailleur partagé',
  'Site Access Card': 'Carte d\'accès au site',
  'Skill': 'Compétence',
  'Skill Experience': 'Expérience de compétence',
  'Skills': 'Compétences',
  'Submitted': 'Soumis',
  'Sun': 'Dim',
  'Tax Document': 'Document fiscal',
  'the selected organization': 'l\'organisation sélectionnée',
  'The worker uploads and owns these files. Your organization may review, verify, reject, expire, or revoke document records only within your permitted relationship and consent scope.': 'Le travailleur télécharge et possède ces fichiers. Votre organisation peut examiner, vérifier, rejeter, faire expirer ou révoquer les documents uniquement dans le cadre de votre relation et de votre consentement autorisés.',
  'Thu': 'Jeu',
  'to': 'à',
  'Trade Certificate': 'Certificat de métier',
  'Training Card': 'Carte de formation',
  'Try a different search, status, skill, document, or organization filter.': 'Essayez une autre recherche, un autre statut, une autre compétence, un autre document ou un autre filtre d\'organisation.',
  'Tue': 'Mar',
  'Unable to {action} worker.': 'Impossible de {action} le travailleur.',
  'Unable to add recommended skill.': 'Impossible d\'ajouter la compétence recommandée.',
  'Unable to create invite code.': 'Impossible de créer le code d\'invitation.',
  'Unable to create worker relationship.': 'Impossible de créer la relation du travailleur.',
  'Unable to end worker relationship.': 'Impossible de mettre fin à la relation du travailleur.',
  'Unable to expire worker document.': 'Impossible de faire expirer le document du travailleur.',
  'Unable to load worker availability.': 'Impossible de charger la disponibilité du travailleur.',
  'Unable to load worker profile.': 'Impossible de charger le profil du travailleur.',
  'Unable to load worker reliability.': 'Impossible de charger la fiabilité du travailleur.',
  'Unable to load worker visibility details.': 'Impossible de charger les détails de visibilité du travailleur.',
  'Unable to load workers.': 'Impossible de charger les travailleurs.',
  'Unable to open worker document file.': 'Impossible d\'ouvrir le fichier du document du travailleur.',
  'Unable to reject worker document.': 'Impossible de rejeter le document du travailleur.',
  'Unable to remove worker skill.': 'Impossible de supprimer la compétence du travailleur.',
  'Unable to revoke invite code.': 'Impossible de révoquer le code d\'invitation.',
  'Unable to revoke worker document.': 'Impossible de révoquer le document du travailleur.',
  'Unable to save worker document.': 'Impossible d\'enregistrer le document du travailleur.',
  'Unable to update worker profile visibility.': 'Impossible de mettre à jour la visibilité du profil du travailleur.',
  'Unable to update worker profile.': 'Impossible de mettre à jour le profil du travailleur.',
  'Unable to update worker relationship.': 'Impossible de mettre à jour la relation du travailleur.',
  'Unable to update worker skill.': 'Impossible de mettre à jour la compétence du travailleur.',
  'Unable to verify worker document.': 'Impossible de vérifier le document du travailleur.',
  'Unavailable / blackout': 'Indisponible / période de blocage',
  'Upcoming conflicts': 'Conflits à venir',
  'Update basic profile fields without expanding the worker list.': 'Mettre à jour les champs de base du profil sans développer la liste des travailleurs.',
  'Update visibility': 'Mettre à jour la visibilité',
  'Uses': 'Utilisations',
  'Verified': 'Vérifié',
  'Verify': 'Vérifier',
  'Verify worker document?': 'Vérifier le document du travailleur ?',
  'View details for {name}': 'Voir les détails de {name}',
  'Visibility': 'Visibilité',
  'Visibility: Free-agent visibility never exposes private documents or sensitive identity files.': 'Visibilité : la visibilité d\'agent libre n\'expose jamais de documents privés ou de fichiers d\'identité sensibles.',
  'Visibility/free-agent settings are managed in a modal and enforced again by backend permissions.': 'Les paramètres de visibilité/agent libre sont gérés dans une fenêtre modale et appliqués à nouveau par les permissions du backend.',
  'Visible relationship exists': 'Une relation visible existe',
  'Visible To All': 'Visible par tous',
  'Visible To Selected': 'Visible par les sélectionnés',
  'Wed': 'Mer',
  'Work area': 'Zone de travail',
  'Work eligibility': 'Admissibilité au travail',
  'Work Permit': 'Permis de travail',
  'Work radius km': 'Rayon de travail (km)',
  'Worker {action} complete.': '{action} du travailleur terminé.',
  'Worker contact not available': 'Contact du travailleur non disponible',
  'Worker directory': 'Répertoire des travailleurs',
  'Worker document added.': 'Document du travailleur ajouté.',
  'Worker document expired.': 'Le document du travailleur a expiré.',
  'Worker document file was not found.': 'Le fichier du document du travailleur n\'a pas été trouvé.',
  'Worker document rejected.': 'Document du travailleur rejeté.',
  'Worker document revoked.': 'Document du travailleur révoqué.',
  'Worker document updated.': 'Document du travailleur mis à jour.',
  'Worker document verified.': 'Document du travailleur vérifié.',
  'Worker email': 'E-mail du travailleur',
  'Worker invite codes': 'Codes d\'invitation des travailleurs',
  'Worker lists are scoped to the selected organization and current effective permissions.': 'Les listes de travailleurs sont limitées à l\'organisation sélectionnée et aux permissions effectives actuelles.',
  'Worker profile': 'Profil du travailleur',
  'Worker profile updated.': 'Profil du travailleur mis à jour.',
  'Worker profile visibility updated.': 'Visibilité du profil du travailleur mise à jour.',
  'Worker relationship created.': 'Relation du travailleur créée.',
  'Worker relationship creation requires worker management, worker sharing, or sharing management permission depending on relationship type.': 'La création d\'une relation de travailleur nécessite la permission de gestion des travailleurs, de partage des travailleurs ou de gestion du partage selon le type de relation.',
  'Worker relationship ended.': 'Relation du travailleur terminée.',
  'Worker relationship updated.': 'Relation du travailleur mise à jour.',
  'Worker relationships': 'Relations des travailleurs',
  'Worker roster access requires worker view or worker management permission.': 'L\'accès à la liste des travailleurs nécessite la permission de voir ou de gérer les travailleurs.',
  'Worker safely deleted.': 'Travailleur supprimé en toute sécurité.',
  'Worker skill added.': 'Compétence du travailleur ajoutée.',
  'Worker skill removed.': 'Compétence du travailleur supprimée.',
  'Worker visibility': 'Visibilité du travailleur',
  'Worker-owned documents': 'Documents appartenant au travailleur',
  'worker@example.com': 'worker@example.com',
  'Workers own their profile and uploaded documents. Organizations can manage relationships, assignments, compliance review, and eligibility.': 'Les travailleurs possèdent leur profil et les documents téléchargés. Les organisations peuvent gérer les relations, les affectations, l\'examen de conformité et l\'admissibilité.',
  'You are not authorized to open this worker document file.': 'Vous n\'êtes pas autorisé à ouvrir ce fichier de document du travailleur.',
})

Object.assign(ar, {
  'Active jobs': 'الوظائف النشطة',
  'Active rows': 'الصفوف النشطة',
  'Add shift': 'إضافة مناوبة',
  'Adjust filters or create a job/order to begin filling shifts.': 'عدّل عوامل التصفية أو أنشئ وظيفة/طلبًا لبدء تعبئة المناوبات.',
  'Agency not selected': 'لم يتم اختيار وكالة',
  'All rows': 'كل الصفوف',
  'All visibility': 'كل مستويات الظهور',
  'Application created from match.': 'تم إنشاء طلب من المطابقة.',
  'Application created.': 'تم إنشاء الطلب.',
  'Applications / assignments': 'الطلبات / التعيينات',
  'Apply': 'تطبيق',
  'Archive = hide this job/order from normal operations but keep history.': 'الأرشفة = إخفاء هذه الوظيفة/الطلب من العمليات العادية مع الاحتفاظ بالسجل.',
  'Assign worker': 'تعيين عامل',
  'Assignment created.': 'تم إنشاء التعيين.',
  'Assignment currency must be a 3-letter code such as CAD or USD.': 'يجب أن تكون عملة التعيين رمزًا من 3 أحرف مثل CAD أو USD.',
  'Assignment management permission is required.': 'صلاحية إدارة التعيينات مطلوبة.',
  'Assignment rate overrides must be valid numbers.': 'يجب أن تكون تعديلات أسعار التعيين أرقامًا صالحة.',
  'Bill rate': 'سعر الفوترة',
  'Bill rate must be a valid number.': 'يجب أن يكون سعر الفوترة رقمًا صالحًا.',
  'Bill {rate}': 'الفوترة {rate}',
  'Brands': 'العلامات',
  'Cancel = stop future use for this job/order.': 'الإلغاء = إيقاف الاستخدام المستقبلي لهذه الوظيفة/الطلب.',
  'Cancelled, completed, or expired shifts cannot receive new assignments.': 'لا يمكن للمناوبات الملغاة أو المكتملة أو المنتهية استقبال تعيينات جديدة.',
  'Client/company *': 'العميل/الشركة *',
  'Client/company is required before scheduling shifts.': 'العميل/الشركة مطلوبة قبل جدولة المناوبات.',
  'Company approval required': 'موافقة الشركة مطلوبة',
  'Company not selected': 'لم يتم اختيار شركة',
  'Company, agency, and location are locked to the granted relationship scope.': 'الشركة والوكالة والموقع مقفلة حسب نطاق العلاقة الممنوح.',
  'Completed, cancelled, expired, and unavailable jobs are hidden from this default view. Switch to History or adjust filters.': 'تُخفى الوظائف المكتملة والملغاة والمنتهية وغير المتاحة من هذا العرض الافتراضي. انتقل إلى السجل أو عدّل عوامل التصفية.',
  'Create job/order': 'إنشاء وظيفة/طلب',
  'Creating on behalf of': 'يتم الإنشاء نيابة عن',
  'Currency must be a 3-letter code such as CAD or USD.': 'يجب أن تكون العملة رمزًا من 3 أحرف مثل CAD أو USD.',
  'Delegated job': 'وظيفة مفوضة',
  'Delete job/order': 'حذف الوظيفة/الطلب',
  'Delete removes only records with no historical dependencies.': 'الحذف يزيل فقط السجلات التي لا تحتوي على تبعيات تاريخية.',
  'Demo job data purged.': 'تمت إزالة بيانات وظيفة العرض التجريبي.',
  'Edit job/order': 'تعديل الوظيفة/الطلب',
  'For': 'لصالح',
  'For company/client': 'للشركة/العميل',
  'Historical jobs cannot receive new assignments.': 'لا يمكن للوظائف التاريخية استقبال تعيينات جديدة.',
  'Historical jobs cannot receive new shifts.': 'لا يمكن للوظائف التاريخية استقبال مناوبات جديدة.',
  'History': 'السجل',
  'Import shifts': 'استيراد المناوبات',
  'Job archived.': 'تمت أرشفة الوظيفة.',
  'Job basics updated.': 'تم تحديث أساسيات الوظيفة.',
  'Job cancelled.': 'تم إلغاء الوظيفة.',
  'Job creation requires job create or manage permission.': 'يتطلب إنشاء الوظيفة صلاحية إنشاء أو إدارة الوظائف.',
  'Job deleted.': 'تم حذف الوظيفة.',
  'Job management permission is required.': 'صلاحية إدارة الوظائف مطلوبة.',
  'Job restored.': 'تمت استعادة الوظيفة.',
  'Job title *': 'عنوان الوظيفة *',
  'Job title is required.': 'عنوان الوظيفة مطلوب.',
  'Job type': 'نوع الوظيفة',
  'Job, company, location, skill': 'وظيفة، شركة، موقع، مهارة',
  'Job/order created.': 'تم إنشاء الوظيفة/الطلب.',
  'Jobs / Orders access requires job view or management permission.': 'يتطلب الوصول إلى الوظائف / الطلبات صلاحية عرض أو إدارة الوظائف.',
  'Jobs / Postings': 'الوظائف / المنشورات',
  'Jobs action failed.': 'فشل إجراء الوظائف.',
  'Jobs stay scoped to the selected tenant, organization, company, agency, and location context.': 'تبقى الوظائف ضمن نطاق المستأجر والمؤسسة والشركة والوكالة والموقع المحدد.',
  'Lifecycle view': 'عرض دورة الحياة',
  'Loading jobs...': 'جارٍ تحميل الوظائف...',
  'Location *': 'الموقع *',
  'Location is required before scheduling shifts.': 'الموقع مطلوب قبل جدولة المناوبات.',
  'Location selected, name unavailable': 'تم اختيار الموقع، والاسم غير متاح',
  'Minimum level': 'الحد الأدنى للمستوى',
  'No active jobs found': 'لم يتم العثور على وظائف نشطة',
  'No dependencies were found. Delete this empty record?': 'لم يتم العثور على تبعيات. هل تريد حذف هذا السجل الفارغ؟',
  'No description provided.': 'لم يتم تقديم وصف.',
  'No historical jobs found': 'لم يتم العثور على وظائف تاريخية',
  'No locations available for this company. Create a location/fence first.': 'لا توجد مواقع متاحة لهذه الشركة. أنشئ موقعًا/نطاقًا أولًا.',
  'No locations with job creation permission are granted for this relationship.': 'لا توجد مواقع ممنوحة لهذه العلاقة بصلاحية إنشاء الوظائف.',
  'No required skills listed': 'لا توجد مهارات مطلوبة مدرجة',
  'No required skills listed.': 'لا توجد مهارات مطلوبة مدرجة.',
  'Not set': 'غير محدد',
  'On behalf of: {company}': 'نيابة عن: {company}',
  'Open in Schedule': 'فتح في الجدول',
  'Own organization job': 'وظيفة المؤسسة نفسها',
  'Pay rate': 'سعر الأجر',
  'Pay rate must be a valid number.': 'يجب أن يكون سعر الأجر رقمًا صالحًا.',
  'Pay {rate}': 'الأجر {rate}',
  'Post jobs, add shifts, manage visibility, and move applications into assignments with names and labels first.': 'انشر الوظائف، أضف المناوبات، أدر الظهور، وانقل الطلبات إلى تعيينات مع إظهار الأسماء والتسميات أولًا.',
  'Posted by': 'نُشر بواسطة',
  'Posted by agency': 'نُشر بواسطة الوكالة',
  'Posting organization': 'المؤسسة الناشرة',
  'Posting organization is required.': 'المؤسسة الناشرة مطلوبة.',
  'Posting/managing agency': 'الوكالة الناشرة/المديرة',
  'Purge demo data = irreversible cleanup for demo/test job records only.': 'إزالة بيانات العرض التجريبي = تنظيف غير قابل للتراجع لسجلات وظائف العرض/الاختبار فقط.',
  'Purge demo data is irreversible and only for demo/test records.': 'إزالة بيانات العرض التجريبي غير قابلة للتراجع ومخصصة لسجلات العرض/الاختبار فقط.',
  'Radius km': 'النطاق كم',
  'Rates': 'الأسعار',
  'Required headcount must be greater than 0.': 'يجب أن يكون العدد المطلوب أكبر من 0.',
  'Requirement': 'المتطلب',
  'Restore this job/order to operational use.': 'استعادة هذه الوظيفة/الطلب للاستخدام التشغيلي.',
  'Save job': 'حفظ الوظيفة',
  'Select a shift before creating an assignment.': 'اختر مناوبة قبل إنشاء التعيين.',
  'Select a worker before creating an assignment.': 'اختر عاملًا قبل إنشاء التعيين.',
  'Select granted location': 'اختر موقعًا ممنوحًا',
  'Select location': 'اختر الموقع',
  'Selected location does not belong to the selected company or agency context.': 'الموقع المحدد لا ينتمي إلى سياق الشركة أو الوكالة المحددة.',
  'Selected location does not match the selected company or agency.': 'الموقع المحدد لا يطابق الشركة أو الوكالة المحددة.',
  'Selected shift location does not belong to this job company or agency context.': 'موقع المناوبة المحدد لا ينتمي إلى سياق شركة أو وكالة هذه الوظيفة.',
  'Shift added.': 'تمت إضافة المناوبة.',
  'Shift bill override must be a valid number.': 'يجب أن يكون تعديل فوترة المناوبة رقمًا صالحًا.',
  'Shift currency override must be a 3-letter code such as CAD or USD.': 'يجب أن تكون عملة المناوبة رمزًا من 3 أحرف مثل CAD أو USD.',
  'Shift end time is required.': 'وقت نهاية المناوبة مطلوب.',
  'Shift end time must be after the start time.': 'يجب أن يكون وقت نهاية المناوبة بعد وقت البداية.',
  'Shift pay override must be a valid number.': 'يجب أن يكون تعديل أجر المناوبة رقمًا صالحًا.',
  'Shift published.': 'تم نشر المناوبة.',
  'Shift rate overrides must be valid numbers.': 'يجب أن تكون تعديلات أسعار المناوبة أرقامًا صالحة.',
  'Shift start time is required.': 'وقت بداية المناوبة مطلوب.',
  'Shift updated.': 'تم تحديث المناوبة.',
  'Skill created.': 'تم إنشاء المهارة.',
  'This action may remove data if safe.': 'قد يزيل هذا الإجراء بيانات إذا كان ذلك آمنًا.',
  'Title': 'العنوان',
  'Type a reason for irreversible demo/test cleanup.': 'اكتب سببًا لتنظيف العرض/الاختبار غير القابل للتراجع.',
  'Unable to load jobs.': 'تعذر تحميل الوظائف.',
  'Unable to load selected job details.': 'تعذر تحميل تفاصيل الوظيفة المحددة.',
  'Visibility rule added.': 'تمت إضافة قاعدة الظهور.',
  'What skills are required?': 'ما المهارات المطلوبة؟',
  'Work mode': 'نمط العمل',
  'client company': 'الشركة العميلة',
  '{apps} pending apps / {assignments} pending assignments': '{apps} طلبات معلقة / {assignments} تعيينات معلقة',
  '{count} assignments': '{count} تعيينات',
  '{count} shifts': '{count} مناوبات',
  '{count} shifts added.': 'تمت إضافة {count} مناوبات.',
  '{count} total': '{count} الإجمالي',
  '{km} km radius': 'نطاق {km} كم',
  '{location} selected for the new job/order draft.': 'تم اختيار {location} لمسودة الوظيفة/الطلب الجديدة.',
  '{open} open / {assigned} assigned': '{open} مفتوحة / {assigned} معينة',
})

Object.assign(fr, {
  'Active jobs': 'Emplois actifs',
  'Active rows': 'Lignes actives',
  'Add shift': 'Ajouter une période',
  'Adjust filters or create a job/order to begin filling shifts.': 'Ajustez les filtres ou créez un emploi/une commande pour commencer à pourvoir les périodes.',
  'Agency not selected': 'Agence non sélectionnée',
  'All rows': 'Toutes les lignes',
  'All visibility': 'Toute visibilité',
  'Application created from match.': 'Candidature créée à partir de la correspondance.',
  'Application created.': 'Candidature créée.',
  'Applications / assignments': 'Candidatures / affectations',
  'Apply': 'Appliquer',
  'Archive = hide this job/order from normal operations but keep history.': 'Archiver = masquer cet emploi/cette commande des opérations normales tout en conservant l\'historique.',
  'Assign worker': 'Affecter un travailleur',
  'Assignment created.': 'Affectation créée.',
  'Assignment currency must be a 3-letter code such as CAD or USD.': 'La devise de l\'affectation doit être un code à 3 lettres comme CAD ou USD.',
  'Assignment management permission is required.': 'La permission de gestion des affectations est requise.',
  'Assignment rate overrides must be valid numbers.': 'Les remplacements de taux de l\'affectation doivent être des nombres valides.',
  'Bill rate': 'Taux facturé',
  'Bill rate must be a valid number.': 'Le taux facturé doit être un nombre valide.',
  'Bill {rate}': 'Facturation {rate}',
  'Brands': 'Marques',
  'Cancel = stop future use for this job/order.': 'Annuler = arrêter l\'utilisation future de cet emploi/cette commande.',
  'Cancelled, completed, or expired shifts cannot receive new assignments.': 'Les périodes annulées, terminées ou expirées ne peuvent pas recevoir de nouvelles affectations.',
  'Client/company *': 'Client/entreprise *',
  'Client/company is required before scheduling shifts.': 'Le client/l\'entreprise est requis avant de planifier des périodes.',
  'Company approval required': 'Approbation de l\'entreprise requise',
  'Company not selected': 'Entreprise non sélectionnée',
  'Company, agency, and location are locked to the granted relationship scope.': 'L\'entreprise, l\'agence et le site sont verrouillés selon le périmètre de relation accordé.',
  'Completed, cancelled, expired, and unavailable jobs are hidden from this default view. Switch to History or adjust filters.': 'Les emplois terminés, annulés, expirés et indisponibles sont masqués dans cette vue par défaut. Passez à Historique ou ajustez les filtres.',
  'Create job/order': 'Créer un emploi/une commande',
  'Creating on behalf of': 'Création pour le compte de',
  'Currency must be a 3-letter code such as CAD or USD.': 'La devise doit être un code à 3 lettres comme CAD ou USD.',
  'Delegated job': 'Emploi délégué',
  'Delete job/order': 'Supprimer l\'emploi/la commande',
  'Delete removes only records with no historical dependencies.': 'La suppression retire uniquement les enregistrements sans dépendances historiques.',
  'Demo job data purged.': 'Données d\'emploi de démonstration purgées.',
  'Edit job/order': 'Modifier l\'emploi/la commande',
  'For': 'Pour',
  'For company/client': 'Pour l\'entreprise/le client',
  'Historical jobs cannot receive new assignments.': 'Les emplois historiques ne peuvent pas recevoir de nouvelles affectations.',
  'Historical jobs cannot receive new shifts.': 'Les emplois historiques ne peuvent pas recevoir de nouvelles périodes.',
  'History': 'Historique',
  'Import shifts': 'Importer des périodes',
  'Job archived.': 'Emploi archivé.',
  'Job basics updated.': 'Informations de base de l\'emploi mises à jour.',
  'Job cancelled.': 'Emploi annulé.',
  'Job creation requires job create or manage permission.': 'La création d\'emploi nécessite la permission de créer ou gérer les emplois.',
  'Job deleted.': 'Emploi supprimé.',
  'Job management permission is required.': 'La permission de gestion des emplois est requise.',
  'Job restored.': 'Emploi restauré.',
  'Job title *': 'Titre de l\'emploi *',
  'Job title is required.': 'Le titre de l\'emploi est requis.',
  'Job type': 'Type d\'emploi',
  'Job, company, location, skill': 'Emploi, entreprise, site, compétence',
  'Job/order created.': 'Emploi/commande créé.',
  'Jobs / Orders access requires job view or management permission.': 'L\'accès aux emplois / commandes nécessite la permission de voir ou gérer les emplois.',
  'Jobs / Postings': 'Emplois / publications',
  'Jobs action failed.': 'L\'action sur les emplois a échoué.',
  'Jobs stay scoped to the selected tenant, organization, company, agency, and location context.': 'Les emplois restent limités au contexte de locataire, organisation, entreprise, agence et site sélectionné.',
  'Lifecycle view': 'Vue du cycle de vie',
  'Loading jobs...': 'Chargement des emplois...',
  'Location *': 'Site *',
  'Location is required before scheduling shifts.': 'Le site est requis avant de planifier des périodes.',
  'Location selected, name unavailable': 'Site sélectionné, nom indisponible',
  'Minimum level': 'Niveau minimal',
  'No active jobs found': 'Aucun emploi actif trouvé',
  'No dependencies were found. Delete this empty record?': 'Aucune dépendance trouvée. Supprimer cet enregistrement vide ?',
  'No description provided.': 'Aucune description fournie.',
  'No historical jobs found': 'Aucun emploi historique trouvé',
  'No locations available for this company. Create a location/fence first.': 'Aucun site disponible pour cette entreprise. Créez d\'abord un site/périmètre.',
  'No locations with job creation permission are granted for this relationship.': 'Aucun site avec permission de création d\'emploi n\'est accordé pour cette relation.',
  'No required skills listed': 'Aucune compétence requise indiquée',
  'No required skills listed.': 'Aucune compétence requise indiquée.',
  'Not set': 'Non défini',
  'On behalf of: {company}': 'Pour le compte de : {company}',
  'Open in Schedule': 'Ouvrir dans le planning',
  'Own organization job': 'Emploi de l\'organisation',
  'Pay rate': 'Taux payé',
  'Pay rate must be a valid number.': 'Le taux payé doit être un nombre valide.',
  'Pay {rate}': 'Paie {rate}',
  'Post jobs, add shifts, manage visibility, and move applications into assignments with names and labels first.': 'Publiez des emplois, ajoutez des périodes, gérez la visibilité et transformez les candidatures en affectations avec les noms et libellés en premier.',
  'Posted by': 'Publié par',
  'Posted by agency': 'Publié par l\'agence',
  'Posting organization': 'Organisation émettrice',
  'Posting organization is required.': 'L\'organisation émettrice est requise.',
  'Posting/managing agency': 'Agence émettrice/gestionnaire',
  'Purge demo data = irreversible cleanup for demo/test job records only.': 'Purger les données de démo = nettoyage irréversible réservé aux enregistrements d\'emplois de démo/test.',
  'Purge demo data is irreversible and only for demo/test records.': 'La purge des données de démo est irréversible et réservée aux enregistrements de démo/test.',
  'Radius km': 'Rayon km',
  'Rates': 'Taux',
  'Required headcount must be greater than 0.': 'L\'effectif requis doit être supérieur à 0.',
  'Requirement': 'Exigence',
  'Restore this job/order to operational use.': 'Restaurer cet emploi/cette commande pour l\'utilisation opérationnelle.',
  'Save job': 'Enregistrer l\'emploi',
  'Select a shift before creating an assignment.': 'Sélectionnez une période avant de créer une affectation.',
  'Select a worker before creating an assignment.': 'Sélectionnez un travailleur avant de créer une affectation.',
  'Select granted location': 'Sélectionner un site accordé',
  'Select location': 'Sélectionner un site',
  'Selected location does not belong to the selected company or agency context.': 'Le site sélectionné n\'appartient pas au contexte de l\'entreprise ou de l\'agence sélectionnée.',
  'Selected location does not match the selected company or agency.': 'Le site sélectionné ne correspond pas à l\'entreprise ou à l\'agence sélectionnée.',
  'Selected shift location does not belong to this job company or agency context.': 'Le site de période sélectionné n\'appartient pas au contexte d\'entreprise ou d\'agence de cet emploi.',
  'Shift added.': 'Période ajoutée.',
  'Shift bill override must be a valid number.': 'Le remplacement du taux facturé de la période doit être un nombre valide.',
  'Shift currency override must be a 3-letter code such as CAD or USD.': 'La devise de remplacement de la période doit être un code à 3 lettres comme CAD ou USD.',
  'Shift end time is required.': 'L\'heure de fin de période est requise.',
  'Shift end time must be after the start time.': 'L\'heure de fin doit être après l\'heure de début.',
  'Shift pay override must be a valid number.': 'Le remplacement du taux payé de la période doit être un nombre valide.',
  'Shift published.': 'Période publiée.',
  'Shift rate overrides must be valid numbers.': 'Les remplacements de taux de période doivent être des nombres valides.',
  'Shift start time is required.': 'L\'heure de début de période est requise.',
  'Shift updated.': 'Période mise à jour.',
  'Skill created.': 'Compétence créée.',
  'This action may remove data if safe.': 'Cette action peut supprimer des données si c\'est sûr.',
  'Title': 'Titre',
  'Type a reason for irreversible demo/test cleanup.': 'Saisissez une raison pour le nettoyage irréversible de démo/test.',
  'Unable to load jobs.': 'Impossible de charger les emplois.',
  'Unable to load selected job details.': 'Impossible de charger les détails de l\'emploi sélectionné.',
  'Visibility rule added.': 'Règle de visibilité ajoutée.',
  'What skills are required?': 'Quelles compétences sont requises ?',
  'Work mode': 'Mode de travail',
  'client company': 'entreprise cliente',
  '{apps} pending apps / {assignments} pending assignments': '{apps} candidatures en attente / {assignments} affectations en attente',
  '{count} assignments': '{count} affectations',
  '{count} shifts': '{count} périodes',
  '{count} shifts added.': '{count} périodes ajoutées.',
  '{count} total': '{count} au total',
  '{km} km radius': 'Rayon de {km} km',
  '{location} selected for the new job/order draft.': '{location} sélectionné pour le nouveau brouillon d\'emploi/commande.',
  '{open} open / {assigned} assigned': '{open} ouvertes / {assigned} affectées',
})

Object.assign(ar, {
  'Add visibility': 'إضافة ظهور',
  'Add visibility rule': 'إضافة قاعدة ظهور',
  'Applicant': 'مقدم الطلب',
  'Application approved.': 'تمت الموافقة على الطلب.',
  'Application rejected.': 'تم رفض الطلب.',
  'Applications / matches': 'الطلبات / المطابقات',
  'Applying org': 'المؤسسة المقدمة',
  'Approve applicant': 'الموافقة على مقدم الطلب',
  'Approve assignment': 'الموافقة على التعيين',
  'Approve this worker assignment for the company?': 'هل تريد الموافقة على تعيين هذا العامل للشركة؟',
  'Approve {worker} for this shift?': 'هل تريد الموافقة على {worker} لهذه المناوبة؟',
  'Archive = hide this job shift from normal operations but keep history.': 'الأرشفة = إخفاء مناوبة الوظيفة من العمليات العادية مع الاحتفاظ بالسجل.',
  'Assign {worker} to this shift?': 'هل تريد تعيين {worker} لهذه المناوبة؟',
  'Assignment approved.': 'تمت الموافقة على التعيين.',
  'Assignment cancelled.': 'تم إلغاء التعيين.',
  'Assignment deleted.': 'تم حذف التعيين.',
  'Assignment restored.': 'تمت استعادة التعيين.',
  'Assignment revoked.': 'تم سحب التعيين.',
  'Assignment updated.': 'تم تحديث التعيين.',
  'Attendance {score}%': 'الحضور {score}%',
  'Bill override': 'تعديل الفوترة',
  'Brand context': 'سياق العلامة',
  'Branding/context': 'العلامة/السياق',
  'Can apply': 'يمكنه التقديم',
  'Can assign': 'يمكنه التعيين',
  'Can view rates': 'يمكنه عرض الأسعار',
  'Cancel = stop future use for this assignment.': 'الإلغاء = إيقاف الاستخدام المستقبلي لهذا التعيين.',
  'Cancel = stop future use for this job shift.': 'الإلغاء = إيقاف الاستخدام المستقبلي لهذه المناوبة.',
  'Cancel this shift? Existing punch/timekeeping records are not changed by this UI action.': 'هل تريد إلغاء هذه المناوبة؟ لا يغيّر إجراء الواجهة هذا سجلات البصمة/الوقت الحالية.',
  'Category': 'الفئة',
  'Completed, cancelled, and unavailable assignments are hidden from this view.': 'تُخفى التعيينات المكتملة والملغاة وغير المتاحة من هذا العرض.',
  'Completed, cancelled, expired, and unavailable shifts are hidden from this view.': 'تُخفى المناوبات المكتملة والملغاة والمنتهية وغير المتاحة من هذا العرض.',
  'Confirm': 'تأكيد',
  'Confirm action': 'تأكيد الإجراء',
  'Conflicts {count}': 'تعارضات {count}',
  'Copy previous shift': 'نسخ المناوبة السابقة',
  'Cover note': 'ملاحظة التقديم',
  'Create application': 'إنشاء طلب',
  'Create assignment': 'إنشاء تعيين',
  'Create skill': 'إنشاء مهارة',
  'Currency override': 'تعديل العملة',
  'Delete assignment': 'حذف التعيين',
  'Delete job shift': 'حذف مناوبة الوظيفة',
  'Demo assignment data purged.': 'تمت إزالة بيانات تعيين العرض التجريبي.',
  'Demo shift data purged.': 'تمت إزالة بيانات مناوبة العرض التجريبي.',
  'Direct worker': 'عامل مباشر',
  'Duplicate this shift for the same job?': 'هل تريد نسخ هذه المناوبة لنفس الوظيفة؟',
  'Edit shift': 'تعديل المناوبة',
  'Eligible': 'مؤهل',
  'Ends *': 'النهاية *',
  'Existing CSV import workflow, launched from Jobs / Orders.': 'سير عمل استيراد CSV الحالي، يُفتح من الوظائف / الطلبات.',
  'Historical assignments cannot be approved.': 'لا يمكن الموافقة على التعيينات التاريخية.',
  'Historical assignments cannot be changed from this active workflow.': 'لا يمكن تغيير التعيينات التاريخية من سير العمل النشط هذا.',
  'Historical shifts cannot be duplicated from this active workflow.': 'لا يمكن نسخ المناوبات التاريخية من سير العمل النشط هذا.',
  'Historical shifts cannot be published.': 'لا يمكن نشر المناوبات التاريخية.',
  'Inherit job': 'وراثة الوظيفة',
  'Job company': 'شركة الوظيفة',
  'Job context': 'سياق الوظيفة',
  'Job location': 'موقع الوظيفة',
  'Job location - {location}': 'موقع الوظيفة - {location}',
  'Job/order': 'وظيفة/طلب',
  'Legacy skill': 'مهارة قديمة',
  'Location (granted sites) *': 'الموقع (المواقع الممنوحة) *',
  'Marketplace': 'السوق',
  'Match': 'المطابقة',
  'Missing required skills': 'المهارات المطلوبة مفقودة',
  'Missing required skills: {skills}': 'المهارات المطلوبة مفقودة: {skills}',
  'Missing: {skill}': 'مفقود: {skill}',
  'Mode': 'النمط',
  'Need': 'الحاجة',
  'No active assignments': 'لا توجد تعيينات نشطة',
  'No active shifts': 'لا توجد مناوبات نشطة',
  'No assignments match this lifecycle view.': 'لا توجد تعيينات تطابق عرض دورة الحياة هذا.',
  'No conflicts': 'لا توجد تعارضات',
  'No historical assignments': 'لا توجد تعيينات تاريخية',
  'No historical shifts': 'لا توجد مناوبات تاريخية',
  'No shifts match this lifecycle view.': 'لا توجد مناوبات تطابق عرض دورة الحياة هذا.',
  'No visibility rules yet.': 'لا توجد قواعد ظهور بعد.',
  'Not eligible': 'غير مؤهل',
  'On the way {time}': 'في الطريق {time}',
  'Only assignments pending company approval can be approved.': 'لا يمكن الموافقة إلا على التعيينات بانتظار موافقة الشركة.',
  'Pay override': 'تعديل الأجر',
  'Publish': 'نشر',
  'Publish shift': 'نشر المناوبة',
  'Publish this shift as an open shift workers can apply to?': 'هل تريد نشر هذه المناوبة كمناوبة مفتوحة يمكن للعمال التقديم عليها؟',
  'Punctuality {score}%': 'الالتزام بالمواعيد {score}%',
  'Purge demo data = irreversible cleanup for demo/test assignment records only.': 'إزالة بيانات العرض التجريبي = تنظيف غير قابل للتراجع لسجلات تعيينات العرض/الاختبار فقط.',
  'Purge demo data = irreversible cleanup for demo/test shift records only.': 'إزالة بيانات العرض التجريبي = تنظيف غير قابل للتراجع لسجلات مناوبات العرض/الاختبار فقط.',
  'Reject applicant': 'رفض مقدم الطلب',
  'Reject this open-shift application?': 'هل تريد رفض طلب المناوبة المفتوحة هذا؟',
  'Reliability pending': 'الموثوقية قيد الانتظار',
  'Remove this job visibility rule?': 'هل تريد إزالة قاعدة ظهور هذه الوظيفة؟',
  'Remove visibility': 'إزالة الظهور',
  'Repeat daily': 'تكرار يوميًا',
  'Resolved pay preview: {pay} / bill {bill}': 'معاينة الأجر المحسوب: {pay} / الفوترة {bill}',
  'Resolved pay {pay} / bill {bill}': 'الأجر المحسوب {pay} / الفوترة {bill}',
  'Restore this assignment to operational use.': 'استعادة هذا التعيين للاستخدام التشغيلي.',
  'Restore this job shift to operational use.': 'استعادة مناوبة الوظيفة هذه للاستخدام التشغيلي.',
  'Revoke this assignment while keeping its history.': 'سحب هذا التعيين مع الاحتفاظ بسجله.',
  'Save assignment': 'حفظ التعيين',
  'Save assignment status and rate overrides?': 'هل تريد حفظ حالة التعيين وتعديلات الأسعار؟',
  'Save shift': 'حفظ المناوبة',
  'Search workers, skills, status': 'ابحث عن العمال والمهارات والحالة',
  'Select shift': 'اختر مناوبة',
  'Shift already filled': 'المناوبة ممتلئة بالفعل',
  'Shift archived.': 'تمت أرشفة المناوبة.',
  'Shift changes stay inside the selected job/order.': 'تبقى تغييرات المناوبة داخل الوظيفة/الطلب المحدد.',
  'Shift deleted.': 'تم حذف المناوبة.',
  'Shift restored.': 'تمت استعادة المناوبة.',
  'Skill match': 'مطابقة المهارة',
  'Specific worker': 'عامل محدد',
  'Starts *': 'البداية *',
  'Supplying agency': 'الوكالة الموردة',
  'System cancel': 'إلغاء النظام',
  'This shift is already historical or unavailable.': 'هذه المناوبة تاريخية أو غير متاحة بالفعل.',
  'This shift is already published.': 'هذه المناوبة منشورة بالفعل.',
  'Unable to load matched workers.': 'تعذر تحميل العمال المطابقين.',
  'Update assignment': 'تحديث التعيين',
  'Visibility rule removed.': 'تمت إزالة قاعدة الظهور.',
  'Visible org': 'المؤسسة المرئية',
  'Whole job': 'الوظيفة كاملة',
  'Worker already assigned': 'العامل معين بالفعل',
  'Worker application': 'طلب عامل',
  'Worker unavailable': 'العامل غير متاح',
  'Workers needed *': 'العمال المطلوبون *',
  'Blocked': 'محظور',
  'Cancelled/completed/past shift blocked': 'المناوبة الملغاة/المكتملة/السابقة محظورة',
  'Missing geofence warning': 'تحذير نطاق جغرافي مفقود',
  'Outside declared availability': 'خارج التوفر المعلن',
  'Overfilled shift': 'مناوبة زائدة عن العدد',
  'Overlapping assignment': 'تعيين متداخل',
  'Schedule conflict': 'تعارض في الجدول',
  'source: {source}': 'المصدر: {source}',
  'this applicant': 'مقدم الطلب هذا',
  '{count} assigned': '{count} معين',
  '{km} km': '{km} كم',
  '{km} km away': 'يبعد {km} كم',
  '{km} km limit': 'حد {km} كم',
  '{score} reliability': 'موثوقية {score}',
})

Object.assign(fr, {
  'Add visibility': 'Ajouter une visibilité',
  'Add visibility rule': 'Ajouter une règle de visibilité',
  'Applicant': 'Candidat',
  'Application approved.': 'Candidature approuvée.',
  'Application rejected.': 'Candidature rejetée.',
  'Applications / matches': 'Candidatures / correspondances',
  'Applying org': 'Organisation candidate',
  'Approve applicant': 'Approuver le candidat',
  'Approve assignment': 'Approuver l\'affectation',
  'Approve this worker assignment for the company?': 'Approuver cette affectation de travailleur pour l\'entreprise ?',
  'Approve {worker} for this shift?': 'Approuver {worker} pour cette période ?',
  'Archive = hide this job shift from normal operations but keep history.': 'Archiver = masquer cette période des opérations normales tout en conservant l\'historique.',
  'Assign {worker} to this shift?': 'Affecter {worker} à cette période ?',
  'Assignment approved.': 'Affectation approuvée.',
  'Assignment cancelled.': 'Affectation annulée.',
  'Assignment deleted.': 'Affectation supprimée.',
  'Assignment restored.': 'Affectation restaurée.',
  'Assignment revoked.': 'Affectation révoquée.',
  'Assignment updated.': 'Affectation mise à jour.',
  'Attendance {score}%': 'Présence {score} %',
  'Bill override': 'Remplacement facturé',
  'Brand context': 'Contexte de marque',
  'Branding/context': 'Marque/contexte',
  'Can apply': 'Peut postuler',
  'Can assign': 'Peut affecter',
  'Can view rates': 'Peut voir les taux',
  'Cancel = stop future use for this assignment.': 'Annuler = arrêter l\'utilisation future de cette affectation.',
  'Cancel = stop future use for this job shift.': 'Annuler = arrêter l\'utilisation future de cette période.',
  'Cancel this shift? Existing punch/timekeeping records are not changed by this UI action.': 'Annuler cette période ? Les pointages et relevés existants ne sont pas modifiés par cette action.',
  'Category': 'Catégorie',
  'Completed, cancelled, and unavailable assignments are hidden from this view.': 'Les affectations terminées, annulées et indisponibles sont masquées dans cette vue.',
  'Completed, cancelled, expired, and unavailable shifts are hidden from this view.': 'Les périodes terminées, annulées, expirées et indisponibles sont masquées dans cette vue.',
  'Confirm': 'Confirmer',
  'Confirm action': 'Confirmer l\'action',
  'Conflicts {count}': 'Conflits {count}',
  'Copy previous shift': 'Copier la période précédente',
  'Cover note': 'Note de candidature',
  'Create application': 'Créer une candidature',
  'Create assignment': 'Créer une affectation',
  'Create skill': 'Créer une compétence',
  'Currency override': 'Remplacement de devise',
  'Delete assignment': 'Supprimer l\'affectation',
  'Delete job shift': 'Supprimer la période',
  'Demo assignment data purged.': 'Données d\'affectation de démonstration purgées.',
  'Demo shift data purged.': 'Données de période de démonstration purgées.',
  'Direct worker': 'Travailleur direct',
  'Duplicate this shift for the same job?': 'Dupliquer cette période pour le même emploi ?',
  'Edit shift': 'Modifier la période',
  'Eligible': 'Admissible',
  'Ends *': 'Fin *',
  'Existing CSV import workflow, launched from Jobs / Orders.': 'Flux d\'import CSV existant, lancé depuis Emplois / Commandes.',
  'Historical assignments cannot be approved.': 'Les affectations historiques ne peuvent pas être approuvées.',
  'Historical assignments cannot be changed from this active workflow.': 'Les affectations historiques ne peuvent pas être modifiées depuis ce flux actif.',
  'Historical shifts cannot be duplicated from this active workflow.': 'Les périodes historiques ne peuvent pas être dupliquées depuis ce flux actif.',
  'Historical shifts cannot be published.': 'Les périodes historiques ne peuvent pas être publiées.',
  'Inherit job': 'Hériter de l\'emploi',
  'Job company': 'Entreprise de l\'emploi',
  'Job context': 'Contexte de l\'emploi',
  'Job location': 'Site de l\'emploi',
  'Job location - {location}': 'Site de l\'emploi - {location}',
  'Job/order': 'Emploi/commande',
  'Legacy skill': 'Compétence héritée',
  'Location (granted sites) *': 'Site (sites accordés) *',
  'Marketplace': 'Marché',
  'Match': 'Correspondance',
  'Missing required skills': 'Compétences requises manquantes',
  'Missing required skills: {skills}': 'Compétences requises manquantes : {skills}',
  'Missing: {skill}': 'Manquant : {skill}',
  'Mode': 'Mode',
  'Need': 'Besoin',
  'No active assignments': 'Aucune affectation active',
  'No active shifts': 'Aucune période active',
  'No assignments match this lifecycle view.': 'Aucune affectation ne correspond à cette vue du cycle de vie.',
  'No conflicts': 'Aucun conflit',
  'No historical assignments': 'Aucune affectation historique',
  'No historical shifts': 'Aucune période historique',
  'No shifts match this lifecycle view.': 'Aucune période ne correspond à cette vue du cycle de vie.',
  'No visibility rules yet.': 'Aucune règle de visibilité pour le moment.',
  'Not eligible': 'Non admissible',
  'On the way {time}': 'En route {time}',
  'Only assignments pending company approval can be approved.': 'Seules les affectations en attente d\'approbation de l\'entreprise peuvent être approuvées.',
  'Pay override': 'Remplacement payé',
  'Publish': 'Publier',
  'Publish shift': 'Publier la période',
  'Publish this shift as an open shift workers can apply to?': 'Publier cette période comme période ouverte à laquelle les travailleurs peuvent postuler ?',
  'Punctuality {score}%': 'Ponctualité {score} %',
  'Purge demo data = irreversible cleanup for demo/test assignment records only.': 'Purger les données de démo = nettoyage irréversible réservé aux affectations de démo/test.',
  'Purge demo data = irreversible cleanup for demo/test shift records only.': 'Purger les données de démo = nettoyage irréversible réservé aux périodes de démo/test.',
  'Reject applicant': 'Rejeter le candidat',
  'Reject this open-shift application?': 'Rejeter cette candidature à une période ouverte ?',
  'Reliability pending': 'Fiabilité en attente',
  'Remove this job visibility rule?': 'Retirer cette règle de visibilité de l\'emploi ?',
  'Remove visibility': 'Retirer la visibilité',
  'Repeat daily': 'Répéter chaque jour',
  'Resolved pay preview: {pay} / bill {bill}': 'Aperçu payé résolu : {pay} / facturé {bill}',
  'Resolved pay {pay} / bill {bill}': 'Payé résolu {pay} / facturé {bill}',
  'Restore this assignment to operational use.': 'Restaurer cette affectation pour l\'utilisation opérationnelle.',
  'Restore this job shift to operational use.': 'Restaurer cette période pour l\'utilisation opérationnelle.',
  'Revoke this assignment while keeping its history.': 'Révoquer cette affectation tout en conservant son historique.',
  'Save assignment': 'Enregistrer l\'affectation',
  'Save assignment status and rate overrides?': 'Enregistrer le statut de l\'affectation et les remplacements de taux ?',
  'Save shift': 'Enregistrer la période',
  'Search workers, skills, status': 'Rechercher travailleurs, compétences, statut',
  'Select shift': 'Sélectionner une période',
  'Shift already filled': 'Période déjà pourvue',
  'Shift archived.': 'Période archivée.',
  'Shift changes stay inside the selected job/order.': 'Les changements de période restent dans l\'emploi/la commande sélectionné.',
  'Shift deleted.': 'Période supprimée.',
  'Shift restored.': 'Période restaurée.',
  'Skill match': 'Correspondance de compétence',
  'Specific worker': 'Travailleur précis',
  'Starts *': 'Début *',
  'Supplying agency': 'Agence fournisseuse',
  'System cancel': 'Annulation système',
  'This shift is already historical or unavailable.': 'Cette période est déjà historique ou indisponible.',
  'This shift is already published.': 'Cette période est déjà publiée.',
  'Unable to load matched workers.': 'Impossible de charger les travailleurs correspondants.',
  'Update assignment': 'Mettre à jour l\'affectation',
  'Visibility rule removed.': 'Règle de visibilité retirée.',
  'Visible org': 'Organisation visible',
  'Whole job': 'Emploi complet',
  'Worker already assigned': 'Travailleur déjà affecté',
  'Worker application': 'Candidature de travailleur',
  'Worker unavailable': 'Travailleur indisponible',
  'Workers needed *': 'Travailleurs requis *',
  'Blocked': 'Bloqué',
  'Cancelled/completed/past shift blocked': 'Période annulée/terminée/passée bloquée',
  'Missing geofence warning': 'Avertissement de périmètre manquant',
  'Outside declared availability': 'Hors disponibilité déclarée',
  'Overfilled shift': 'Période en sureffectif',
  'Overlapping assignment': 'Affectation chevauchante',
  'Schedule conflict': 'Conflit de planning',
  'source: {source}': 'source : {source}',
  'this applicant': 'ce candidat',
  '{count} assigned': '{count} affectés',
  '{km} km': '{km} km',
  '{km} km away': 'à {km} km',
  '{km} km limit': 'limite de {km} km',
  '{score} reliability': 'fiabilité {score}',
})

Object.assign(ar, {
  'Skill governance sections': 'أقسام حوكمة المهارات',
  'Requested Skills': 'المهارات المطلوبة للمراجعة',
  'Merge Skills': 'دمج المهارات',
  'Workforce Development': 'تطوير القوى العاملة',
  'Career Paths': 'مسارات المهنة',
  'Could not load requested skills.': 'تعذر تحميل طلبات المهارات.',
  'Could not load merge impact.': 'تعذر تحميل أثر الدمج.',
  'Could not load workforce development analytics.': 'تعذر تحميل تحليلات تطوير القوى العاملة.',
  'Could not load career paths.': 'تعذر تحميل مسارات المهنة.',
  'Choose an existing skill before mapping the request.': 'اختر مهارة موجودة قبل ربط الطلب.',
  'Skill request approved.': 'تمت الموافقة على طلب المهارة.',
  'Skill request mapped.': 'تم ربط طلب المهارة.',
  'Skill request rejected.': 'تم رفض طلب المهارة.',
  'Could not update the skill request.': 'تعذر تحديث طلب المهارة.',
  'Choose different source and target skills.': 'اختر مهارتين مختلفتين كمصدر وهدف.',
  'Skills merged and alias created.': 'تم دمج المهارات وإنشاء اسم بديل.',
  'Could not merge skills.': 'تعذر دمج المهارات.',
  'Skills unavailable': 'المهارات غير متاحة',
  'Your current role does not include skill catalog management access.': 'دورك الحالي لا يتضمن صلاحية إدارة كتالوج المهارات.',
  '{count} pending skill requests in this view.': '{count} طلبات مهارة معلقة في هذا العرض.',
  'Requested Skill': 'المهارة المطلوبة',
  'Requested By': 'طلبها',
  'Suggested Matches': 'مطابقات مقترحة',
  'Unknown': 'غير معروف',
  'General': 'عام',
  'No close matches': 'لا توجد مطابقات قريبة',
  'Map target': 'هدف الربط',
  'Search existing skills': 'ابحث في المهارات الموجودة',
  'Approve New': 'اعتماد كمهارة جديدة',
  'Map To Existing': 'ربط بمهارة موجودة',
  'No skill requests': 'لا توجد طلبات مهارات',
  'Requested skills for this status will appear here.': 'ستظهر هنا طلبات المهارات بهذه الحالة.',
  'Loading requested skills...': 'جارٍ تحميل طلبات المهارات...',
  'Source skill': 'المهارة المصدر',
  'Duplicate skill to deprecate': 'مهارة مكررة لإيقافها',
  'Target skill': 'المهارة الهدف',
  'Canonical skill to keep': 'المهارة الأساسية التي سيتم الاحتفاظ بها',
  'Loading merge impact...': 'جارٍ تحميل أثر الدمج...',
  'Workers affected': 'العمال المتأثرون',
  'Jobs affected': 'الوظائف المتأثرة',
  'Mentor claims affected': 'مطالبات المرشدين المتأثرة',
  'Learning goals affected': 'أهداف التعلم المتأثرة',
  'Mentor rates affected': 'أسعار المرشدين المتأثرة',
  'Aliases affected': 'الأسماء البديلة المتأثرة',
  'Relationships affected': 'العلاقات المتأثرة',
  '{source} will become an alias of {target}.': 'ستصبح {source} اسمًا بديلًا لـ {target}.',
  'Choose source and target skills to preview impact.': 'اختر مهارتي المصدر والهدف لمعاينة الأثر.',
  'Confirm Merge': 'تأكيد الدمج',
  'Skill graph analytics from jobs, actual work experience, mentorship goals, requests, and mentor coverage.': 'تحليلات شبكة المهارات من الوظائف وخبرة العمل الفعلية وأهداف الإرشاد والطلبات وتغطية المرشدين.',
  'Loading workforce development analytics...': 'جارٍ تحميل تحليلات تطوير القوى العاملة...',
  'Top Missing Skills': 'أهم المهارات الناقصة',
  'Most Requested Skills': 'المهارات الأكثر طلبًا',
  'Fastest Growing Skills': 'المهارات الأسرع نموًا',
  'Most Experienced Skills': 'المهارات ذات الخبرة الأعلى',
  'Emerging Skills': 'المهارات الناشئة',
  'Skills With No Available Mentors': 'مهارات بلا مرشدين متاحين',
  'Skill Shortages': 'نقص المهارات',
  'Workers Near Promotion': 'عمال قريبون من الترقية',
  'Potential Mentors': 'مرشدون محتملون',
  'No signals yet': 'لا توجد مؤشرات بعد',
  'Path': 'المسار',
  'Steps': 'الخطوات',
  'No description': 'لا يوجد وصف',
  'No steps': 'لا توجد خطوات',
  'No career paths': 'لا توجد مسارات مهنية',
  'Career path records will appear after they are seeded or added through admin tooling.': 'ستظهر سجلات المسارات المهنية بعد إضافتها عبر البذور أو أدوات الإدارة.',
  'Skill / role': 'المهارة / الدور',
  'All skills': 'كل المهارات',
  'No required skills': 'لا توجد مهارات مطلوبة',
  'Matched skill: {skill}': 'مهارة مطابقة: {skill}',
})

Object.assign(fr, {
  'Skill governance sections': 'Sections de gouvernance des compétences',
  'Requested Skills': 'Compétences demandées',
  'Merge Skills': 'Fusionner des compétences',
  'Workforce Development': 'Développement de la main-d’œuvre',
  'Career Paths': 'Parcours de carrière',
  'Could not load requested skills.': 'Impossible de charger les demandes de compétences.',
  'Could not load merge impact.': 'Impossible de charger l’impact de la fusion.',
  'Could not load workforce development analytics.': 'Impossible de charger les analyses de développement de la main-d’œuvre.',
  'Could not load career paths.': 'Impossible de charger les parcours de carrière.',
  'Choose an existing skill before mapping the request.': 'Choisissez une compétence existante avant de mapper la demande.',
  'Skill request approved.': 'Demande de compétence approuvée.',
  'Skill request mapped.': 'Demande de compétence mappée.',
  'Skill request rejected.': 'Demande de compétence rejetée.',
  'Could not update the skill request.': 'Impossible de mettre à jour la demande de compétence.',
  'Choose different source and target skills.': 'Choisissez une compétence source et une compétence cible différentes.',
  'Skills merged and alias created.': 'Compétences fusionnées et alias créé.',
  'Could not merge skills.': 'Impossible de fusionner les compétences.',
  'Skills unavailable': 'Compétences indisponibles',
  'Your current role does not include skill catalog management access.': 'Votre rôle actuel ne comprend pas l’accès à la gestion du catalogue de compétences.',
  '{count} pending skill requests in this view.': '{count} demandes de compétences en attente dans cette vue.',
  'Requested Skill': 'Compétence demandée',
  'Requested By': 'Demandée par',
  'Suggested Matches': 'Correspondances suggérées',
  'Unknown': 'Inconnu',
  'General': 'Général',
  'No close matches': 'Aucune correspondance proche',
  'Map target': 'Cible de mappage',
  'Search existing skills': 'Rechercher des compétences existantes',
  'Approve New': 'Approuver comme nouvelle',
  'Map To Existing': 'Mapper à une existante',
  'No skill requests': 'Aucune demande de compétence',
  'Requested skills for this status will appear here.': 'Les compétences demandées avec ce statut apparaîtront ici.',
  'Loading requested skills...': 'Chargement des demandes de compétences...',
  'Source skill': 'Compétence source',
  'Duplicate skill to deprecate': 'Compétence doublon à déprécier',
  'Target skill': 'Compétence cible',
  'Canonical skill to keep': 'Compétence canonique à conserver',
  'Loading merge impact...': 'Chargement de l’impact de fusion...',
  'Workers affected': 'Travailleurs touchés',
  'Jobs affected': 'Emplois touchés',
  'Mentor claims affected': 'Déclarations de mentor touchées',
  'Learning goals affected': 'Objectifs d’apprentissage touchés',
  'Mentor rates affected': 'Tarifs de mentor touchés',
  'Aliases affected': 'Alias touchés',
  'Relationships affected': 'Relations touchées',
  '{source} will become an alias of {target}.': '{source} deviendra un alias de {target}.',
  'Choose source and target skills to preview impact.': 'Choisissez les compétences source et cible pour prévisualiser l’impact.',
  'Confirm Merge': 'Confirmer la fusion',
  'Skill graph analytics from jobs, actual work experience, mentorship goals, requests, and mentor coverage.': 'Analyses du graphe de compétences à partir des emplois, de l’expérience réelle, des objectifs de mentorat, des demandes et de la couverture des mentors.',
  'Loading workforce development analytics...': 'Chargement des analyses de développement de la main-d’œuvre...',
  'Top Missing Skills': 'Principales compétences manquantes',
  'Most Requested Skills': 'Compétences les plus demandées',
  'Fastest Growing Skills': 'Compétences en plus forte croissance',
  'Most Experienced Skills': 'Compétences avec le plus d’expérience',
  'Emerging Skills': 'Compétences émergentes',
  'Skills With No Available Mentors': 'Compétences sans mentor disponible',
  'Skill Shortages': 'Pénuries de compétences',
  'Workers Near Promotion': 'Travailleurs proches d’une promotion',
  'Potential Mentors': 'Mentors potentiels',
  'No signals yet': 'Aucun signal pour le moment',
  'Path': 'Parcours',
  'Steps': 'Étapes',
  'No description': 'Aucune description',
  'No steps': 'Aucune étape',
  'No career paths': 'Aucun parcours de carrière',
  'Career path records will appear after they are seeded or added through admin tooling.': 'Les parcours de carrière apparaîtront après leur ajout par les données de départ ou les outils d’administration.',
  'Skill / role': 'Compétence / rôle',
  'All skills': 'Toutes les compétences',
  'No required skills': 'Aucune compétence requise',
  'Matched skill: {skill}': 'Compétence correspondante : {skill}',
})

Object.assign(ar, {
  'Mentorship / Apprenticeship': 'الإرشاد / التدرّب',
  'Loading mentorship and apprenticeship records...': 'جارٍ تحميل سجلات الإرشاد والتدرّب...',
  'Unable to load mentorship data.': 'تعذر تحميل بيانات الإرشاد.',
  'Manage approved mentors, apprentice goals, deterministic matching, sessions, booking requests, internal ledger records, and platform fees.': 'إدارة المرشدين المعتمدين وأهداف المتدربين والمطابقة المحددة والجلسات وطلبات الحجز وسجلات الحسابات الداخلية ورسوم المنصة.',
  'Mentorship sections': 'أقسام الإرشاد',
  'Mentor Approvals': 'اعتمادات المرشدين',
  'Learning Goals': 'أهداف التعلم',
  'Mentor Rates': 'أسعار المرشدين',
  'Payment Records': 'سجلات الدفع',
  'Platform Fees': 'رسوم المنصة',
  'Disputes': 'النزاعات',
  'Pending mentor approvals': 'اعتمادات المرشدين المعلقة',
  'Active mentors': 'المرشدون النشطون',
  'Active apprentices': 'المتدربون النشطون',
  'Active matches': 'المطابقات النشطة',
  'Upcoming sessions': 'الجلسات القادمة',
  'Completed sessions': 'الجلسات المكتملة',
  'Pending bookings': 'الحجوزات المعلقة',
  'Recorded gross amount': 'إجمالي المبالغ المسجلة',
  'Platform fees recorded': 'رسوم المنصة المسجلة',
  'Disputed payments': 'المدفوعات المتنازع عليها',
  'Mentorship participants': 'مشاركو الإرشاد',
  'Participant': 'المشارك',
  'Modes': 'الأوضاع',
  'Mentor skills': 'مهارات المرشد',
  'Mentor rating': 'تقييم المرشد',
  'Apprentice rating': 'تقييم المتدرب',
  'No mentorship participants yet.': 'لا يوجد مشاركون في الإرشاد بعد.',
  'Mentor': 'مرشد',
  'Apprentice': 'متدرب',
  'Activity only': 'نشاط فقط',
  '{approved} approved / {claimed} claimed': '{approved} معتمدة / {claimed} مطالب بها',
  '{count} completed': '{count} مكتملة',
  'No mentor reviews': 'لا توجد مراجعات للمرشد',
  'No apprentice reviews': 'لا توجد مراجعات للمتدرب',
  'Skills created here are shared with Worker Mentorship and can be selected for worker mentor claims or apprentice learning goals.': 'المهارات التي تُنشأ هنا تُشارك مع إرشاد العاملين ويمكن اختيارها لمطالبات المرشدين أو أهداف تعلم المتدربين.',
  'Skill name': 'اسم المهارة',
  'Category': 'الفئة',
  'Create skill': 'إنشاء مهارة',
  'No mentorship skills have been created.': 'لم يتم إنشاء مهارات إرشاد بعد.',
  'Uncategorized': 'غير مصنف',
  'Claimed skill': 'المهارة المطالب بها',
  'Evidence': 'الدليل',
  'No mentor skill claims are waiting for review.': 'لا توجد مطالبات مهارات مرشد بانتظار المراجعة.',
  'None': 'لا شيء',
  'Desired skill': 'المهارة المطلوبة',
  'Suggested mentors': 'المرشدون المقترحون',
  'No apprentice learning goals yet.': 'لا توجد أهداف تعلم للمتدربين بعد.',
  'No mentorship matches yet.': 'لا توجد مطابقات إرشاد بعد.',
  'Scheduled time': 'وقت الجدولة',
  'Delivery': 'طريقة التقديم',
  'No mentorship sessions yet.': 'لا توجد جلسات إرشاد بعد.',
  'Unassigned': 'غير معيّن',
  'No mentor rates yet.': 'لا توجد أسعار مرشدين بعد.',
  'Requested time': 'الوقت المطلوب',
  'Proposed rate': 'السعر المقترح',
  'No booking requests yet.': 'لا توجد طلبات حجز بعد.',
  'Booking': 'حجز',
  'Payer': 'الدافع',
  'Payee': 'المستفيد',
  'Gross': 'الإجمالي',
  'Platform fee': 'رسوم المنصة',
  'Mentor net': 'صافي المرشد',
  'No payment records yet.': 'لا توجد سجلات دفع بعد.',
  'Recorded': 'مسجل',
  'Paid Off Platform': 'مدفوع خارج المنصة',
  'Waived': 'متنازل عنه',
  'Default fee percent': 'نسبة الرسوم الافتراضية',
  'Default fixed fee': 'رسوم ثابتة افتراضية',
  'Create tenant default': 'إنشاء افتراضي للمستأجر',
  'Percent': 'النسبة',
  'Fixed amount': 'المبلغ الثابت',
  'No platform fee settings yet. Zero platform fee is used by default.': 'لا توجد إعدادات رسوم منصة بعد. تُستخدم رسوم صفرية افتراضياً.',
  'Tenant default': 'افتراضي المستأجر',
  'Record': 'السجل',
  'Resolution': 'الحل',
  'No disputed mentorship records.': 'لا توجد سجلات إرشاد متنازع عليها.',
  'Use the owning tab to update status.': 'استخدم التبويب المالك لتحديث الحالة.',
  'Accepted': 'مقبول',
  'Requested': 'مطلوب',
  'Scheduled': 'مجدول',
  'Proposed': 'مقترح',
  'Suspended': 'معلّق',
  'Disputed': 'متنازع عليه',
  'No Show': 'لم يحضر',
})

Object.assign(fr, {
  'Mentorship / Apprenticeship': 'Mentorat / Apprentissage',
  'Loading mentorship and apprenticeship records...': 'Chargement des dossiers de mentorat et d’apprentissage...',
  'Unable to load mentorship data.': 'Impossible de charger les données de mentorat.',
  'Manage approved mentors, apprentice goals, deterministic matching, sessions, booking requests, internal ledger records, and platform fees.': 'Gérez les mentors approuvés, les objectifs d’apprentissage, la mise en correspondance déterministe, les séances, les demandes de réservation, les écritures internes et les frais de plateforme.',
  'Mentorship sections': 'Sections de mentorat',
  'Mentor Approvals': 'Approbations des mentors',
  'Learning Goals': 'Objectifs d’apprentissage',
  'Mentor Rates': 'Tarifs des mentors',
  'Payment Records': 'Registres de paiement',
  'Platform Fees': 'Frais de plateforme',
  'Disputes': 'Litiges',
  'Pending mentor approvals': 'Approbations de mentors en attente',
  'Active mentors': 'Mentors actifs',
  'Active apprentices': 'Apprentis actifs',
  'Active matches': 'Correspondances actives',
  'Upcoming sessions': 'Séances à venir',
  'Completed sessions': 'Séances terminées',
  'Pending bookings': 'Réservations en attente',
  'Recorded gross amount': 'Montant brut enregistré',
  'Platform fees recorded': 'Frais de plateforme enregistrés',
  'Disputed payments': 'Paiements contestés',
  'Mentorship participants': 'Participants au mentorat',
  'Participant': 'Participant',
  'Modes': 'Modes',
  'Mentor skills': 'Compétences de mentor',
  'Mentor rating': 'Note du mentor',
  'Apprentice rating': 'Note de l’apprenti',
  'No mentorship participants yet.': 'Aucun participant au mentorat pour le moment.',
  'Mentor': 'Mentor',
  'Apprentice': 'Apprenti',
  'Activity only': 'Activité seulement',
  '{approved} approved / {claimed} claimed': '{approved} approuvées / {claimed} déclarées',
  '{count} completed': '{count} terminées',
  'No mentor reviews': 'Aucun avis de mentor',
  'No apprentice reviews': 'Aucun avis d’apprenti',
  'Skills created here are shared with Worker Mentorship and can be selected for worker mentor claims or apprentice learning goals.': 'Les compétences créées ici sont partagées avec le mentorat des employés et peuvent être sélectionnées pour les déclarations de mentor ou les objectifs d’apprentissage.',
  'Skill name': 'Nom de la compétence',
  'Category': 'Catégorie',
  'Create skill': 'Créer une compétence',
  'No mentorship skills have been created.': 'Aucune compétence de mentorat n’a été créée.',
  'Uncategorized': 'Non catégorisé',
  'Claimed skill': 'Compétence déclarée',
  'Evidence': 'Justificatif',
  'No mentor skill claims are waiting for review.': 'Aucune déclaration de compétence de mentor n’attend de révision.',
  'None': 'Aucun',
  'Desired skill': 'Compétence souhaitée',
  'Suggested mentors': 'Mentors suggérés',
  'No apprentice learning goals yet.': 'Aucun objectif d’apprentissage pour le moment.',
  'No mentorship matches yet.': 'Aucune correspondance de mentorat pour le moment.',
  'Scheduled time': 'Heure prévue',
  'Delivery': 'Mode de prestation',
  'No mentorship sessions yet.': 'Aucune séance de mentorat pour le moment.',
  'Unassigned': 'Non assigné',
  'No mentor rates yet.': 'Aucun tarif de mentor pour le moment.',
  'Requested time': 'Heure demandée',
  'Proposed rate': 'Tarif proposé',
  'No booking requests yet.': 'Aucune demande de réservation pour le moment.',
  'Booking': 'Réservation',
  'Payer': 'Payeur',
  'Payee': 'Bénéficiaire',
  'Gross': 'Brut',
  'Platform fee': 'Frais de plateforme',
  'Mentor net': 'Net du mentor',
  'No payment records yet.': 'Aucun registre de paiement pour le moment.',
  'Recorded': 'Enregistré',
  'Paid Off Platform': 'Payé hors plateforme',
  'Waived': 'Renoncé',
  'Default fee percent': 'Pourcentage de frais par défaut',
  'Default fixed fee': 'Frais fixes par défaut',
  'Create tenant default': 'Créer la valeur par défaut du locataire',
  'Percent': 'Pourcentage',
  'Fixed amount': 'Montant fixe',
  'No platform fee settings yet. Zero platform fee is used by default.': 'Aucun paramètre de frais de plateforme pour le moment. Des frais de zéro sont utilisés par défaut.',
  'Tenant default': 'Valeur par défaut du locataire',
  'Record': 'Enregistrement',
  'Resolution': 'Résolution',
  'No disputed mentorship records.': 'Aucun dossier de mentorat contesté.',
  'Use the owning tab to update status.': 'Utilisez l’onglet propriétaire pour mettre à jour le statut.',
  'Accepted': 'Accepté',
  'Requested': 'Demandé',
  'Scheduled': 'Prévu',
  'Proposed': 'Proposé',
  'Suspended': 'Suspendu',
  'Disputed': 'Contesté',
  'No Show': 'Absence',
})

// Marketing landing page (slug pages) — section chrome, CTAs, and sales-deck promo
Object.assign(ar, {
  'Live operational context': 'سياق تشغيلي مباشر',
  'Built for the workflow buyers search for': 'مصمّم لسير العمل الذي يبحث عنه المشترون',
  'Where this page fits': 'أين تندرج هذه الصفحة',
  'Differentiators': 'عوامل التميّز',
  'Why teams choose NexusWorkforce360': 'لماذا تختار الفرق NexusWorkforce360',
  'FAQ': 'الأسئلة الشائعة',
  'Common questions': 'أسئلة شائعة',
  'Related Pages': 'صفحات ذات صلة',
  'Explore connected workforce workflows': 'استكشف تدفقات عمل القوى العاملة المترابطة',
  'Explore Interactive Presentations': 'استكشف العروض التفاعلية',
  'NexusWorkforce360 Sales Decks': 'عروض NexusWorkforce360 التجارية',
  'FEATURED': 'مميّز',
  'Interactive Sales Deck': 'عرض تجاري تفاعلي',
  'Live browser-based presentation with interactive simulators and real-time demonstrations.': 'عرض مباشر في المتصفح مع محاكيات تفاعلية وتوضيحات فورية.',
  'Launch Presentation →': 'تشغيل العرض →',
  'Industry-Specific Decks': 'عروض مخصصة للقطاعات',
  'Printable, segment-specific decks for recruitment, construction, food & beverage, and security.': 'عروض قابلة للطباعة ومخصصة للتوظيف والمقاولات والأغذية والمشروبات والأمن.',
  'View All Decks →': 'عرض كل العروض →',
  'Ready to see NexusWorkforce360 in action?': 'هل أنت مستعد لرؤية NexusWorkforce360 أثناء العمل؟',
  'Sign in to an existing workspace or book a demo from the contact form.': 'سجّل الدخول إلى مساحة عمل قائمة أو احجز عرضًا توضيحيًا من نموذج التواصل.',
})

Object.assign(fr, {
  'Live operational context': 'Contexte opérationnel en direct',
  'Built for the workflow buyers search for': 'Conçu pour le flux de travail que recherchent les acheteurs',
  'Where this page fits': 'Où cette page s’inscrit',
  'Differentiators': 'Différenciateurs',
  'Why teams choose NexusWorkforce360': 'Pourquoi les équipes choisissent NexusWorkforce360',
  'FAQ': 'FAQ',
  'Common questions': 'Questions fréquentes',
  'Related Pages': 'Pages connexes',
  'Explore connected workforce workflows': 'Explorez les flux de travail connectés',
  'Explore Interactive Presentations': 'Explorer les présentations interactives',
  'NexusWorkforce360 Sales Decks': 'Présentations commerciales NexusWorkforce360',
  'FEATURED': 'À LA UNE',
  'Interactive Sales Deck': 'Présentation commerciale interactive',
  'Live browser-based presentation with interactive simulators and real-time demonstrations.': 'Présentation live dans le navigateur avec simulateurs interactifs et démonstrations en temps réel.',
  'Launch Presentation →': 'Lancer la présentation →',
  'Industry-Specific Decks': 'Présentations par secteur',
  'Printable, segment-specific decks for recruitment, construction, food & beverage, and security.': 'Présentations imprimables et segmentées pour le recrutement, la construction, la restauration et la sécurité.',
  'View All Decks →': 'Voir toutes les présentations →',
  'Ready to see NexusWorkforce360 in action?': 'Prêt à voir NexusWorkforce360 en action ?',
  'Sign in to an existing workspace or book a demo from the contact form.': 'Connectez-vous à un espace de travail existant ou réservez une démo depuis le formulaire de contact.',
})

// Admin contextual help (AdminHelp panel + adminHelpTopics)
Object.assign(ar, {
  'Hide help': 'إخفاء المساعدة',
  'Contextual admin help': 'مساعدة إدارية سياقية',
  'Network Dashboard help': 'مساعدة لوحة معلومات الشبكة',
  'Use the Network Dashboard to see workforce demand, supply, allocation, compliance, execution, and settlement signals in the current scope.': 'استخدم لوحة معلومات الشبكة لعرض إشارات الطلب والعرض والتخصيص والامتثال والتنفيذ والتسوية للقوى العاملة ضمن النطاق الحالي.',
  'Your visible metrics and shortcuts reflect your current tenant, organization, OU, and location scope.': 'تعكس المقاييس والاختصارات المرئية نطاقك الحالي من المستأجر والمؤسسة والوحدة التنظيمية والموقع.',
  'Review demand and supply health.': 'راجع صحة الطلب والعرض.',
  'Switch organization context when you manage more than one org.': 'بدّل سياق المؤسسة عندما تدير أكثر من مؤسسة.',
  'Open the module that owns the next operational step.': 'افتح الوحدة المسؤولة عن الخطوة التشغيلية التالية.',
  'If an action is missing, you may need additional permissions or a narrower organization/location context.': 'إذا كان هناك إجراء مفقود، فقد تحتاج إلى أذونات إضافية أو سياق مؤسسة/موقع أضيق.',
  'Organizations help': 'مساعدة المؤسسات',
  'Organizations model agencies and companies inside the tenant.': 'تمثّل المؤسسات الوكالات والشركات داخل المستأجر.',
  'Create and edit controls appear only when your role can manage organizations.': 'تظهر عناصر الإنشاء والتعديل فقط عندما يستطيع دورك إدارة المؤسسات.',
  'Create agency or company records.': 'أنشئ سجلات الوكالات أو الشركات.',
  'Add branding from Settings.': 'أضف العلامة التجارية من الإعدادات.',
  'Use relationships to connect companies and agencies.': 'استخدم العلاقات لربط الشركات والوكالات.',
  'Organization visibility is scoped; hidden orgs are not available through this workspace.': 'رؤية المؤسسات محدّدة النطاق؛ المؤسسات المخفية غير متاحة عبر مساحة العمل هذه.',
  'Network Relationships help': 'مساعدة علاقات الشبكة',
  'Network Relationships describe how agencies, companies, subcontractors, and partner organizations share workforce capacity and work demand.': 'تصف علاقات الشبكة كيفية مشاركة الوكالات والشركات والمقاولين من الباطن والمؤسسات الشريكة لقدرة القوى العاملة وطلب العمل.',
  'Manage options require organization relationship or organization management permission.': 'تتطلب خيارات الإدارة إذن علاقة المؤسسة أو إذن إدارة المؤسسة.',
  'Review active connections.': 'راجع الاتصالات النشطة.',
  'Add or end relationships when business ownership changes.': 'أضف العلاقات أو أنهِها عند تغيّر ملكية النشاط التجاري.',
  'Confirm the selected organization before saving.': 'أكّد المؤسسة المحددة قبل الحفظ.',
  'Relationship changes can affect worker sharing, job visibility, and assignment workflows.': 'قد تؤثّر تغييرات العلاقات على مشاركة العمال ووضوح الوظائف وسير عمل الإسناد.',
  'Org Structure help': 'مساعدة الهيكل التنظيمي',
  'Org Structure shows operational units, sites, projects, locations, and inherited access boundaries inside the selected organization.': 'يعرض الهيكل التنظيمي الوحدات التشغيلية والمواقع والمشاريع والأماكن وحدود الوصول الموروثة داخل المؤسسة المحددة.',
  'Users with view access can inspect the tree; manage access is needed to change structure.': 'يمكن للمستخدمين الذين لديهم صلاحية العرض فحص الشجرة؛ ويلزم وصول الإدارة لتغيير الهيكل.',
  'Select an organization.': 'اختر مؤسسة.',
  'Review location ownership.': 'راجع ملكية المواقع.',
  'Use OU context to understand inherited roles.': 'استخدم سياق الوحدة التنظيمية لفهم الأدوار الموروثة.',
  'Scoped users see only the branches and locations available to their access context.': 'يرى المستخدمون محدّدو النطاق فقط الفروع والمواقع المتاحة ضمن سياق وصولهم.',
  'Roles & Access help': 'مساعدة الأدوار والوصول',
  'Users & Access is for inviting admins, supervisors, and staff, then assigning scoped roles.': 'يُستخدم قسم المستخدمين والوصول لدعوة المسؤولين والمشرفين والموظفين ثم إسناد أدوار محدّدة النطاق.',
  'Invite, suspend, and role controls are shown only when your permissions allow them.': 'تظهر عناصر الدعوة والتعليق والأدوار فقط عندما تسمح بها أذوناتك.',
  'Invite users by normalized email or phone.': 'ادعُ المستخدمين عبر بريد إلكتروني أو هاتف موحّد التنسيق.',
  'Assign roles at the smallest useful scope.': 'أسند الأدوار في أصغر نطاق مفيد.',
  'Audit role assignments before changing tenant-wide access.': 'دقّق إسناد الأدوار قبل تغيير الوصول على مستوى المستأجر.',
  'Do not grant broad tenant roles when an organization, OU, or location role is enough.': 'لا تمنح أدوار المستأجر الواسعة عندما يكفي دور مؤسسة أو وحدة تنظيمية أو موقع.',
  'Workforce help': 'مساعدة القوى العاملة',
  'Workforce is the directory for global worker profiles, worker organization relationships, work areas, owned profile data, and documents.': 'القوى العاملة هي دليل الملفات الشخصية العالمية للعمال وعلاقاتهم بالمؤسسات ومناطق العمل وبيانات الملف المملوكة والمستندات.',
  'Profile edits, sharing, and document controls require worker management or sharing permissions.': 'تتطلب تعديلات الملف الشخصي والمشاركة وعناصر المستندات أذونات إدارة العمال أو المشاركة.',
  'Search by name, email, or phone.': 'ابحث بالاسم أو البريد الإلكتروني أو الهاتف.',
  'Open worker details intentionally.': 'افتح تفاصيل العامل بشكل مقصود.',
  'Attach workers to organizations through active relationships.': 'اربط العمال بالمؤسسات من خلال علاقات نشطة.',
  'Contact and document visibility follows tenant/org scope; hidden actions require additional permissions.': 'تتبع رؤية جهات الاتصال والمستندات نطاق المستأجر/المؤسسة؛ وتتطلب الإجراءات المخفية أذونات إضافية.',
  'Skills help': 'مساعدة المهارات',
  'Skills governs requested skills, canonical catalog quality, aliases, and duplicate merges.': 'تدير المهارات المهارات المطلوبة وجودة الكتالوج المعياري والأسماء البديلة ودمج التكرارات.',
  'Request moderation and merge actions require skill catalog management access.': 'تتطلب مراجعة الطلبات وإجراءات الدمج صلاحية إدارة كتالوج المهارات.',
  'Review pending requests.': 'راجع الطلبات المعلّقة.',
  'Map typos and variants to canonical skills.': 'اربط الأخطاء الإملائية والصيغ المختلفة بالمهارات المعيارية.',
  'Merge duplicates after checking impact.': 'ادمج التكرارات بعد التحقق من الأثر.',
  'Merges deprecate source skills and move canonical references without hard-deleting history.': 'يُلغي الدمج المهارات المصدر وينقل المراجع المعيارية دون حذف السجل نهائيًا.',
  'Scheduling help': 'مساعدة الجدولة',
  'Scheduling shows shift boards, applications, assignments, and shift-level allocation work.': 'تعرض الجدولة لوحات المناوبات والطلبات والإسنادات وعمل التخصيص على مستوى المناوبة.',
  'Create, assign, approve, duplicate, or cancel controls appear only with job/assignment permissions.': 'تظهر عناصر الإنشاء والإسناد والموافقة والنسخ والإلغاء فقط مع أذونات الوظيفة/الإسناد.',
  'Filter by date/location/job.': 'صفِّ حسب التاريخ/الموقع/الوظيفة.',
  'Open a shift for matched workers.': 'افتح مناوبة للعمال المطابقين.',
  'Jump to Jobs or Workers from shift details.': 'انتقل إلى الوظائف أو العمال من تفاصيل المناوبة.',
  'Schedule rows are limited to visible jobs, locations, and organizations.': 'تقتصر صفوف الجدول على الوظائف والمواقع والمؤسسات المرئية.',
  'Demand / Jobs help': 'مساعدة الطلب / الوظائف',
  'Demand / Jobs is for creating workforce demand, shifts, pay/bill context, and worker assignments.': 'يُستخدم قسم الطلب / الوظائف لإنشاء طلب القوى العاملة والمناوبات وسياق الأجر/الفوترة وإسنادات العمال.',
  'Posting, editing, assignment, and approval controls depend on job and assignment permissions.': 'تعتمد عناصر النشر والتعديل والإسناد والموافقة على أذونات الوظيفة والإسناد.',
  'Create or update a job.': 'أنشئ وظيفة أو حدّثها.',
  'Add shifts with location and rate context.': 'أضف مناوبات مع سياق الموقع والتعرفة.',
  'Review matches, applications, and assignments.': 'راجع التطابقات والطلبات والإسنادات.',
  'Bill-rate details are kept off worker-facing surfaces and scoped by admin permissions.': 'تُخفى تفاصيل تعرفة الفوترة عن الواجهات الموجهة للعمال وتُحدّد بأذونات المسؤول.',
  'Locations & Geofences help': 'مساعدة المواقع والأسوار الجغرافية',
  'Locations and geofences define where work happens and how punch validation is enforced.': 'تحدّد المواقع والأسوار الجغرافية مكان العمل وكيفية فرض التحقق من التسجيل.',
  'Location and geofence edits require location management permission.': 'تتطلب تعديلات المواقع والأسوار الجغرافية إذن إدارة المواقع.',
  'Confirm owner organization.': 'أكّد المؤسسة المالكة.',
  'Check geofence status.': 'تحقّق من حالة السور الجغرافي.',
  'Review live worker state when permitted.': 'راجع الحالة المباشرة للعمال عند السماح بذلك.',
  'Workers can only punch into assignment locations that pass tenant, assignment, and geofence checks.': 'لا يمكن للعمال التسجيل إلا في مواقع الإسناد التي تجتاز فحوصات المستأجر والإسناد والسور الجغرافي.',
  'Approvals / Timesheets help': 'مساعدة الموافقات / سجلات الدوام',
  'Approvals / Timesheets summarize shift evidence, punch events, breaks, offline review status, worker disputes, comments, and approval readiness.': 'تلخّص الموافقات / سجلات الدوام أدلة المناوبة وأحداث التسجيل والاستراحات وحالة المراجعة دون اتصال ونزاعات العمال والتعليقات وجاهزية الموافقة.',
  'Review actions require timesheet or punch review permissions in the current scope.': 'تتطلب إجراءات المراجعة أذونات مراجعة سجلات الدوام أو التسجيل ضمن النطاق الحالي.',
  'Filter by date, worker, location, job, agency, or company.': 'صفِّ حسب التاريخ أو العامل أو الموقع أو الوظيفة أو الوكالة أو الشركة.',
  'Review pending offline punches and disputed rows.': 'راجع تسجيلات عدم الاتصال المعلّقة والصفوف المتنازع عليها.',
  'Approve, reject, or comment before settlement.': 'وافق أو ارفض أو علّق قبل التسوية.',
  'Open shifts, disputed rows, and pending offline punches should be resolved before payroll export.': 'يجب حل المناوبات المفتوحة والصفوف المتنازع عليها وتسجيلات عدم الاتصال المعلّقة قبل تصدير كشف الرواتب.',
  'Settlement / Payroll Export help': 'مساعدة التسوية / تصدير كشف الرواتب',
  'Settlement / Payroll Export prepares CSV output from approved timesheet and assignment data.': 'يجهّز قسم التسوية / تصدير الرواتب مخرجات CSV من بيانات سجلات الدوام والإسنادات المعتمدة.',
  'Export buttons appear only with payroll/export or timekeeping review access.': 'تظهر أزرار التصدير فقط مع صلاحية الرواتب/التصدير أو مراجعة ضبط الوقت.',
  'Set the payroll date range.': 'حدّد نطاق تاريخ كشف الرواتب.',
  'Narrow by location or organization when needed.': 'ضيّق النطاق حسب الموقع أو المؤسسة عند الحاجة.',
  'Review CSV preview fields before download.': 'راجع حقول معاينة CSV قبل التنزيل.',
  'Exports follow visible scope and should be treated as sensitive payroll-adjacent data.': 'تتبع عمليات التصدير النطاق المرئي ويجب التعامل معها كبيانات حساسة مرتبطة بالرواتب.',
  'Messages help': 'مساعدة الرسائل',
  'Messages lets authorized admins communicate with locations, active workers, or individual workers.': 'تتيح الرسائل للمسؤولين المصرّح لهم التواصل مع المواقع أو العمال النشطين أو عمال محددين.',
  'Read and send controls follow message permissions and location/worker scope.': 'تتبع عناصر القراءة والإرسال أذونات الرسائل ونطاق الموقع/العامل.',
  'Select an accessible location.': 'اختر موقعًا يمكن الوصول إليه.',
  'Choose the audience.': 'اختر الجمهور.',
  'Refresh threads after sending.': 'حدّث المحادثات بعد الإرسال.',
  'Workers and locations outside your scope are not rendered as send targets.': 'لا تُعرض العمال والمواقع خارج نطاقك كوجهات للإرسال.',
  'Reports help': 'مساعدة التقارير',
  'Reports summarizes live scoped workforce, schedule fill, attendance exceptions, and approval readiness.': 'تلخّص التقارير القوى العاملة المحددة النطاق المباشرة وتعبئة الجدول واستثناءات الحضور وجاهزية الموافقة.',
  'Report availability depends on timesheet, export, punch review, or broad admin permissions.': 'يعتمد توفّر التقارير على أذونات سجلات الدوام أو التصدير أو مراجعة التسجيل أو أذونات المسؤول الواسعة.',
  'Set the date and operational filters.': 'حدّد التاريخ وعوامل التصفية التشغيلية.',
  'Review exceptions and approval rows.': 'راجع الاستثناءات وصفوف الموافقة.',
  'Open Payroll Export when approved rows are ready.': 'افتح تصدير الرواتب عندما تكون الصفوف المعتمدة جاهزة.',
  'Reports follow backend tenant, organization, and location scope and do not expose pay or bill rates.': 'تتبع التقارير نطاق المستأجر والمؤسسة والموقع في الخادم ولا تكشف عن معدلات الأجر أو الفوترة.',
  'Settings help': 'مساعدة الإعدادات',
  'Settings covers organization branding and notification-channel readiness.': 'تغطّي الإعدادات العلامة التجارية للمؤسسة وجاهزية قنوات الإشعارات.',
  'Branding updates require branding or organization management permission. WhatsApp setup requires tenant settings, message management, or admin access.': 'تتطلب تحديثات العلامة التجارية إذن العلامة التجارية أو إدارة المؤسسة. ويتطلب إعداد واتساب إعدادات المستأجر أو إدارة الرسائل أو وصول المسؤول.',
  'Pick the organization context.': 'اختر سياق المؤسسة.',
  'Update display name, logo, colors, and support contacts.': 'حدّث اسم العرض والشعار والألوان وجهات اتصال الدعم.',
  'Confirm WhatsApp provider readiness before enabling delivery.': 'أكّد جاهزية مزوّد واتساب قبل تفعيل الإرسال.',
  'Settings changes are tenant or organization scoped and do not grant permissions.': 'تكون تغييرات الإعدادات محددة بنطاق المستأجر أو المؤسسة ولا تمنح أذونات.',
})

Object.assign(fr, {
  'Hide help': 'Masquer l’aide',
  'Contextual admin help': 'Aide contextuelle de l’administration',
  'Network Dashboard help': 'Aide du tableau de bord réseau',
  'Use the Network Dashboard to see workforce demand, supply, allocation, compliance, execution, and settlement signals in the current scope.': 'Utilisez le tableau de bord réseau pour visualiser les signaux de demande, d’offre, d’allocation, de conformité, d’exécution et de règlement de la main-d’œuvre dans le périmètre actuel.',
  'Your visible metrics and shortcuts reflect your current tenant, organization, OU, and location scope.': 'Les indicateurs et raccourcis visibles reflètent votre périmètre actuel de locataire, d’organisation, d’unité organisationnelle et de site.',
  'Review demand and supply health.': 'Examinez la santé de la demande et de l’offre.',
  'Switch organization context when you manage more than one org.': 'Changez de contexte d’organisation lorsque vous en gérez plusieurs.',
  'Open the module that owns the next operational step.': 'Ouvrez le module responsable de la prochaine étape opérationnelle.',
  'If an action is missing, you may need additional permissions or a narrower organization/location context.': 'Si une action est absente, vous pourriez avoir besoin d’autorisations supplémentaires ou d’un contexte d’organisation/site plus restreint.',
  'Organizations help': 'Aide des organisations',
  'Organizations model agencies and companies inside the tenant.': 'Les organisations représentent les agences et les entreprises au sein du locataire.',
  'Create and edit controls appear only when your role can manage organizations.': 'Les commandes de création et de modification n’apparaissent que si votre rôle peut gérer les organisations.',
  'Create agency or company records.': 'Créez des fiches d’agence ou d’entreprise.',
  'Add branding from Settings.': 'Ajoutez l’image de marque depuis les Paramètres.',
  'Use relationships to connect companies and agencies.': 'Utilisez les relations pour connecter entreprises et agences.',
  'Organization visibility is scoped; hidden orgs are not available through this workspace.': 'La visibilité des organisations est délimitée ; les organisations masquées ne sont pas accessibles depuis cet espace.',
  'Network Relationships help': 'Aide des relations réseau',
  'Network Relationships describe how agencies, companies, subcontractors, and partner organizations share workforce capacity and work demand.': 'Les relations réseau décrivent comment les agences, entreprises, sous-traitants et organisations partenaires partagent la capacité de main-d’œuvre et la demande de travail.',
  'Manage options require organization relationship or organization management permission.': 'Les options de gestion nécessitent l’autorisation de relation d’organisation ou de gestion d’organisation.',
  'Review active connections.': 'Examinez les connexions actives.',
  'Add or end relationships when business ownership changes.': 'Ajoutez ou mettez fin aux relations lorsque la propriété commerciale change.',
  'Confirm the selected organization before saving.': 'Confirmez l’organisation sélectionnée avant d’enregistrer.',
  'Relationship changes can affect worker sharing, job visibility, and assignment workflows.': 'Les modifications de relations peuvent affecter le partage des employés, la visibilité des postes et les flux d’affectation.',
  'Org Structure help': 'Aide de la structure organisationnelle',
  'Org Structure shows operational units, sites, projects, locations, and inherited access boundaries inside the selected organization.': 'La structure organisationnelle affiche les unités opérationnelles, sites, projets, lieux et limites d’accès héritées au sein de l’organisation sélectionnée.',
  'Users with view access can inspect the tree; manage access is needed to change structure.': 'Les utilisateurs disposant d’un accès en lecture peuvent consulter l’arborescence ; un accès de gestion est requis pour modifier la structure.',
  'Select an organization.': 'Sélectionnez une organisation.',
  'Review location ownership.': 'Vérifiez la propriété des sites.',
  'Use OU context to understand inherited roles.': 'Utilisez le contexte d’unité organisationnelle pour comprendre les rôles hérités.',
  'Scoped users see only the branches and locations available to their access context.': 'Les utilisateurs à périmètre restreint ne voient que les branches et sites disponibles dans leur contexte d’accès.',
  'Roles & Access help': 'Aide des rôles et accès',
  'Users & Access is for inviting admins, supervisors, and staff, then assigning scoped roles.': 'Utilisateurs et accès sert à inviter administrateurs, superviseurs et personnel, puis à attribuer des rôles délimités.',
  'Invite, suspend, and role controls are shown only when your permissions allow them.': 'Les commandes d’invitation, de suspension et de rôle ne s’affichent que si vos autorisations le permettent.',
  'Invite users by normalized email or phone.': 'Invitez des utilisateurs par e-mail ou téléphone normalisé.',
  'Assign roles at the smallest useful scope.': 'Attribuez les rôles au plus petit périmètre utile.',
  'Audit role assignments before changing tenant-wide access.': 'Auditez les attributions de rôles avant de modifier l’accès à l’échelle du locataire.',
  'Do not grant broad tenant roles when an organization, OU, or location role is enough.': 'N’accordez pas de rôles étendus de locataire lorsqu’un rôle d’organisation, d’unité organisationnelle ou de site suffit.',
  'Workforce help': 'Aide de la main-d’œuvre',
  'Workforce is the directory for global worker profiles, worker organization relationships, work areas, owned profile data, and documents.': 'La main-d’œuvre est le répertoire des profils d’employés globaux, des relations d’organisation, des zones de travail, des données de profil détenues et des documents.',
  'Profile edits, sharing, and document controls require worker management or sharing permissions.': 'La modification des profils, le partage et les contrôles de documents nécessitent des autorisations de gestion ou de partage des employés.',
  'Search by name, email, or phone.': 'Recherchez par nom, e-mail ou téléphone.',
  'Open worker details intentionally.': 'Ouvrez les détails d’un employé de manière intentionnelle.',
  'Attach workers to organizations through active relationships.': 'Rattachez les employés aux organisations via des relations actives.',
  'Contact and document visibility follows tenant/org scope; hidden actions require additional permissions.': 'La visibilité des contacts et documents suit le périmètre du locataire/de l’organisation ; les actions masquées nécessitent des autorisations supplémentaires.',
  'Skills help': 'Aide des compétences',
  'Skills governs requested skills, canonical catalog quality, aliases, and duplicate merges.': 'Les compétences gèrent les compétences demandées, la qualité du catalogue canonique, les alias et les fusions de doublons.',
  'Request moderation and merge actions require skill catalog management access.': 'La modération des demandes et les actions de fusion nécessitent un accès de gestion du catalogue de compétences.',
  'Review pending requests.': 'Examinez les demandes en attente.',
  'Map typos and variants to canonical skills.': 'Associez les fautes de frappe et variantes aux compétences canoniques.',
  'Merge duplicates after checking impact.': 'Fusionnez les doublons après avoir vérifié l’impact.',
  'Merges deprecate source skills and move canonical references without hard-deleting history.': 'Les fusions déprécient les compétences sources et déplacent les références canoniques sans supprimer définitivement l’historique.',
  'Scheduling help': 'Aide de la planification',
  'Scheduling shows shift boards, applications, assignments, and shift-level allocation work.': 'La planification affiche les tableaux de quarts, candidatures, affectations et le travail d’allocation au niveau du quart.',
  'Create, assign, approve, duplicate, or cancel controls appear only with job/assignment permissions.': 'Les commandes de création, d’affectation, d’approbation, de duplication ou d’annulation n’apparaissent qu’avec les autorisations de poste/affectation.',
  'Filter by date/location/job.': 'Filtrez par date/site/poste.',
  'Open a shift for matched workers.': 'Ouvrez un quart pour les employés correspondants.',
  'Jump to Jobs or Workers from shift details.': 'Accédez aux Postes ou aux Employés depuis les détails du quart.',
  'Schedule rows are limited to visible jobs, locations, and organizations.': 'Les lignes de planning se limitent aux postes, sites et organisations visibles.',
  'Demand / Jobs help': 'Aide de la demande / des postes',
  'Demand / Jobs is for creating workforce demand, shifts, pay/bill context, and worker assignments.': 'Demande / Postes sert à créer la demande de main-d’œuvre, les quarts, le contexte de paie/facturation et les affectations d’employés.',
  'Posting, editing, assignment, and approval controls depend on job and assignment permissions.': 'Les commandes de publication, de modification, d’affectation et d’approbation dépendent des autorisations de poste et d’affectation.',
  'Create or update a job.': 'Créez ou mettez à jour un poste.',
  'Add shifts with location and rate context.': 'Ajoutez des quarts avec le contexte de site et de tarif.',
  'Review matches, applications, and assignments.': 'Examinez les correspondances, candidatures et affectations.',
  'Bill-rate details are kept off worker-facing surfaces and scoped by admin permissions.': 'Les détails du taux de facturation sont exclus des surfaces destinées aux employés et délimités par les autorisations d’administration.',
  'Locations & Geofences help': 'Aide des sites et géorepérages',
  'Locations and geofences define where work happens and how punch validation is enforced.': 'Les sites et géorepérages définissent où le travail a lieu et comment la validation des pointages est appliquée.',
  'Location and geofence edits require location management permission.': 'La modification des sites et géorepérages nécessite l’autorisation de gestion des sites.',
  'Confirm owner organization.': 'Confirmez l’organisation propriétaire.',
  'Check geofence status.': 'Vérifiez l’état du géorepérage.',
  'Review live worker state when permitted.': 'Examinez l’état en direct des employés lorsque cela est autorisé.',
  'Workers can only punch into assignment locations that pass tenant, assignment, and geofence checks.': 'Les employés ne peuvent pointer que sur les sites d’affectation qui réussissent les contrôles de locataire, d’affectation et de géorepérage.',
  'Approvals / Timesheets help': 'Aide des approbations / feuilles de temps',
  'Approvals / Timesheets summarize shift evidence, punch events, breaks, offline review status, worker disputes, comments, and approval readiness.': 'Approbations / Feuilles de temps résument les preuves de quart, les pointages, les pauses, l’état de revue hors ligne, les contestations des employés, les commentaires et l’état de préparation à l’approbation.',
  'Review actions require timesheet or punch review permissions in the current scope.': 'Les actions de revue nécessitent des autorisations de revue des feuilles de temps ou des pointages dans le périmètre actuel.',
  'Filter by date, worker, location, job, agency, or company.': 'Filtrez par date, employé, site, poste, agence ou entreprise.',
  'Review pending offline punches and disputed rows.': 'Examinez les pointages hors ligne en attente et les lignes contestées.',
  'Approve, reject, or comment before settlement.': 'Approuvez, rejetez ou commentez avant le règlement.',
  'Open shifts, disputed rows, and pending offline punches should be resolved before payroll export.': 'Les quarts ouverts, les lignes contestées et les pointages hors ligne en attente doivent être résolus avant l’export de la paie.',
  'Settlement / Payroll Export help': 'Aide du règlement / export de paie',
  'Settlement / Payroll Export prepares CSV output from approved timesheet and assignment data.': 'Règlement / Export de paie prépare une sortie CSV à partir des données approuvées de feuilles de temps et d’affectations.',
  'Export buttons appear only with payroll/export or timekeeping review access.': 'Les boutons d’export n’apparaissent qu’avec un accès de paie/export ou de revue du temps.',
  'Set the payroll date range.': 'Définissez la plage de dates de paie.',
  'Narrow by location or organization when needed.': 'Affinez par site ou organisation si nécessaire.',
  'Review CSV preview fields before download.': 'Vérifiez les champs d’aperçu CSV avant le téléchargement.',
  'Exports follow visible scope and should be treated as sensitive payroll-adjacent data.': 'Les exports suivent le périmètre visible et doivent être traités comme des données sensibles liées à la paie.',
  'Messages help': 'Aide des messages',
  'Messages lets authorized admins communicate with locations, active workers, or individual workers.': 'Les messages permettent aux administrateurs autorisés de communiquer avec des sites, des employés actifs ou des employés individuels.',
  'Read and send controls follow message permissions and location/worker scope.': 'Les commandes de lecture et d’envoi suivent les autorisations de message et le périmètre de site/employé.',
  'Select an accessible location.': 'Sélectionnez un site accessible.',
  'Choose the audience.': 'Choisissez l’audience.',
  'Refresh threads after sending.': 'Actualisez les fils après l’envoi.',
  'Workers and locations outside your scope are not rendered as send targets.': 'Les employés et sites hors de votre périmètre ne sont pas proposés comme destinataires.',
  'Reports help': 'Aide des rapports',
  'Reports summarizes live scoped workforce, schedule fill, attendance exceptions, and approval readiness.': 'Les rapports résument la main-d’œuvre délimitée en direct, le remplissage des plannings, les anomalies de présence et l’état de préparation à l’approbation.',
  'Report availability depends on timesheet, export, punch review, or broad admin permissions.': 'La disponibilité des rapports dépend des autorisations de feuilles de temps, d’export, de revue des pointages ou d’administration étendue.',
  'Set the date and operational filters.': 'Définissez la date et les filtres opérationnels.',
  'Review exceptions and approval rows.': 'Examinez les anomalies et les lignes d’approbation.',
  'Open Payroll Export when approved rows are ready.': 'Ouvrez l’export de paie lorsque les lignes approuvées sont prêtes.',
  'Reports follow backend tenant, organization, and location scope and do not expose pay or bill rates.': 'Les rapports suivent le périmètre back-end de locataire, d’organisation et de site et n’exposent pas les taux de paie ou de facturation.',
  'Settings help': 'Aide des paramètres',
  'Settings covers organization branding and notification-channel readiness.': 'Les paramètres couvrent l’image de marque de l’organisation et l’état des canaux de notification.',
  'Branding updates require branding or organization management permission. WhatsApp setup requires tenant settings, message management, or admin access.': 'Les mises à jour de l’image de marque nécessitent une autorisation de marque ou de gestion d’organisation. La configuration de WhatsApp nécessite les paramètres du locataire, la gestion des messages ou un accès administrateur.',
  'Pick the organization context.': 'Choisissez le contexte d’organisation.',
  'Update display name, logo, colors, and support contacts.': 'Mettez à jour le nom d’affichage, le logo, les couleurs et les contacts d’assistance.',
  'Confirm WhatsApp provider readiness before enabling delivery.': 'Confirmez la disponibilité du fournisseur WhatsApp avant d’activer l’envoi.',
  'Settings changes are tenant or organization scoped and do not grant permissions.': 'Les modifications de paramètres sont délimitées par locataire ou organisation et n’accordent pas d’autorisations.',
})

// Admin Organizations panel (OrganizationsPanel)
Object.assign(ar, {
  'Organization name is required.': 'اسم المؤسسة مطلوب.',
  'Organization updated.': 'تم تحديث المؤسسة.',
  'Organization created. Tenant role defaults and the root OU are created by the backend when needed.': 'تم إنشاء المؤسسة. يتولّى الخادم إنشاء أدوار المستأجر الافتراضية والوحدة التنظيمية الجذرية عند الحاجة.',
  'Unable to save organization.': 'تعذّر حفظ المؤسسة.',
  'Delete is blocked by business history. Archive is recommended.': 'الحذف محظور بسبب السجل التجاري. يُنصح بالأرشفة.',
  'Organization {action} complete.': 'تم إكمال الإجراء «{action}» للمؤسسة.',
  'Unable to {action} organization.': 'تعذّر تنفيذ الإجراء «{action}» على المؤسسة.',
  'Organization safely deleted.': 'تم حذف المؤسسة بأمان.',
  'Unable to delete organization.': 'تعذّر حذف المؤسسة.',
  'Manage companies, agencies, subcontractors, and platform-owner boundaries in the workforce network.': 'أدِر الشركات والوكالات والمقاولين من الباطن وحدود مالك المنصة في شبكة القوى العاملة.',
  'All': 'الكل',
  'Subcontractor organization type is not connected yet.': 'لم يتم ربط نوع مؤسسة المقاول من الباطن بعد.',
  'Subcontractors': 'المقاولون من الباطن',
  'Archived filtering is handled through status actions for now.': 'تتم تصفية المؤرشفة عبر إجراءات الحالة في الوقت الحالي.',
  'Platform owners': 'مالكو المنصة',
  'You can view organizations, but creating one requires organization management access.': 'يمكنك عرض المؤسسات، لكن إنشاء واحدة يتطلب صلاحية إدارة المؤسسات.',
  '+ Create Tenant Organization': '+ إنشاء مؤسسة للمستأجر',
  'Organization create/edit requires tenant RBAC management permission in the current backend routes.': 'يتطلب إنشاء/تعديل المؤسسة إذن إدارة صلاحيات المستأجر (RBAC) في مسارات الخادم الحالية.',
  'Edit tenant organization': 'تعديل مؤسسة المستأجر',
  'Use Network Relationships to onboard an Associated Client Company. This form creates only a tenant-local organization boundary.': 'استخدم علاقات الشبكة لإضافة شركة عميلة مرتبطة. ينشئ هذا النموذج حدود مؤسسة محلية للمستأجر فقط.',
  'Slug': 'المُعرّف (slug)',
  'Save tenant organization': 'حفظ مؤسسة المستأجر',
  'No organizations in this tenant yet': 'لا توجد مؤسسات في هذا المستأجر بعد',
  'Create a platform owner, agency, or company. The backend will ensure tenant defaults and a root OU for new organizations.': 'أنشئ مالك منصة أو وكالة أو شركة. سيضمن الخادم القيم الافتراضية للمستأجر ووحدة تنظيمية جذرية للمؤسسات الجديدة.',
  'Your access context does not include any organizations. Ask a platform admin to grant organization-scoped access.': 'لا يتضمّن سياق وصولك أي مؤسسات. اطلب من مسؤول المنصة منحك وصولاً محدّد النطاق بالمؤسسة.',
  'Root OU': 'الوحدة التنظيمية الجذرية',
  'Platform owner/admin boundary': 'حدود مالك/مسؤول المنصة',
  'Staffing agency': 'وكالة توظيف',
  'Root OU not visible': 'الوحدة التنظيمية الجذرية غير مرئية',
  'You can view this organization, but editing requires organization management access.': 'يمكنك عرض هذه المؤسسة، لكن تعديلها يتطلب صلاحية إدارة المؤسسات.',
  'Edit branding': 'تعديل العلامة التجارية',
  'No organizations found': 'لم يتم العثور على مؤسسات',
  'Change the filter or create an agency, company, or platform-owner organization.': 'غيّر عامل التصفية أو أنشئ مؤسسة من نوع وكالة أو شركة أو مالك منصة.',
  'Root OU is not visible in your current access context.': 'الوحدة التنظيمية الجذرية غير مرئية في سياق وصولك الحالي.',
  'Relationship count': 'عدد العلاقات',
  'Delete blocked': 'الحذف محظور',
  'Delete organization?': 'حذف المؤسسة؟',
  'Archive is recommended. Blocking dependencies: {dependencies}.': 'يُنصح بالأرشفة. التبعيات المانعة: {dependencies}.',
  'business history exists': 'يوجد سجل تجاري',
  'Delete {name} only if it has no business history.': 'احذف {name} فقط إذا لم يكن له سجل تجاري.',
  'this organization': 'هذه المؤسسة',
  'Archive instead': 'الأرشفة بدلاً من ذلك',
  'Platform owner': 'مالك المنصة',
  'pending': 'قيد الانتظار',
  'suspended': 'موقوف',
  'archived': 'مؤرشف',
  'pending_deletion': 'بانتظار الحذف',
  'deleted': 'محذوف',
  'suspend': 'تعليق',
  'archive': 'أرشفة',
  'restore': 'استعادة',
  'delete': 'حذف',
})

Object.assign(fr, {
  'Organization name is required.': 'Le nom de l’organisation est requis.',
  'Organization updated.': 'Organisation mise à jour.',
  'Organization created. Tenant role defaults and the root OU are created by the backend when needed.': 'Organisation créée. Les rôles par défaut du locataire et l’unité organisationnelle racine sont créés par le back-end si nécessaire.',
  'Unable to save organization.': 'Impossible d’enregistrer l’organisation.',
  'Delete is blocked by business history. Archive is recommended.': 'La suppression est bloquée par l’historique d’activité. L’archivage est recommandé.',
  'Organization {action} complete.': 'Action « {action} » terminée pour l’organisation.',
  'Unable to {action} organization.': 'Impossible d’effectuer l’action « {action} » sur l’organisation.',
  'Organization safely deleted.': 'Organisation supprimée en toute sécurité.',
  'Unable to delete organization.': 'Impossible de supprimer l’organisation.',
  'Manage companies, agencies, subcontractors, and platform-owner boundaries in the workforce network.': 'Gérez les entreprises, agences, sous-traitants et limites de propriétaire de plateforme dans le réseau de main-d’œuvre.',
  'Organizations': 'Organisations',
  'Companies': 'Entreprises',
  'Agencies': 'Agences',
  'Tenant': 'Locataire',
  'Relationships': 'Relations',
  'Branding': 'Image de marque',
  'Platform Owner': 'Propriétaire de plateforme',
  'All': 'Tous',
  'Subcontractor organization type is not connected yet.': 'Le type d’organisation sous-traitant n’est pas encore connecté.',
  'Subcontractors': 'Sous-traitants',
  'Archived filtering is handled through status actions for now.': 'Le filtrage des archivés est géré via les actions de statut pour le moment.',
  'Platform owners': 'Propriétaires de plateforme',
  'You can view organizations, but creating one requires organization management access.': 'Vous pouvez consulter les organisations, mais en créer une nécessite un accès de gestion des organisations.',
  '+ Create Tenant Organization': '+ Créer une organisation de locataire',
  'Organization create/edit requires tenant RBAC management permission in the current backend routes.': 'La création/modification d’organisation nécessite l’autorisation de gestion RBAC du locataire dans les routes back-end actuelles.',
  'Edit tenant organization': 'Modifier l’organisation du locataire',
  'Use Network Relationships to onboard an Associated Client Company. This form creates only a tenant-local organization boundary.': 'Utilisez les relations réseau pour intégrer une entreprise cliente associée. Ce formulaire crée uniquement une limite d’organisation locale au locataire.',
  'Slug': 'Identifiant (slug)',
  'Save tenant organization': 'Enregistrer l’organisation du locataire',
  'No organizations in this tenant yet': 'Aucune organisation dans ce locataire pour le moment',
  'Create a platform owner, agency, or company. The backend will ensure tenant defaults and a root OU for new organizations.': 'Créez un propriétaire de plateforme, une agence ou une entreprise. Le back-end garantira les valeurs par défaut du locataire et une unité organisationnelle racine pour les nouvelles organisations.',
  'Your access context does not include any organizations. Ask a platform admin to grant organization-scoped access.': 'Votre contexte d’accès n’inclut aucune organisation. Demandez à un administrateur de plateforme d’accorder un accès délimité par organisation.',
  'Root OU': 'Unité organisationnelle racine',
  'Platform owner/admin boundary': 'Limite propriétaire/administrateur de plateforme',
  'Staffing agency': 'Agence de placement',
  'Root OU not visible': 'Unité organisationnelle racine non visible',
  'You can view this organization, but editing requires organization management access.': 'Vous pouvez consulter cette organisation, mais sa modification nécessite un accès de gestion des organisations.',
  'Edit branding': 'Modifier l’image de marque',
  'No organizations found': 'Aucune organisation trouvée',
  'Change the filter or create an agency, company, or platform-owner organization.': 'Modifiez le filtre ou créez une organisation de type agence, entreprise ou propriétaire de plateforme.',
  'Root OU is not visible in your current access context.': 'L’unité organisationnelle racine n’est pas visible dans votre contexte d’accès actuel.',
  'Relationship count': 'Nombre de relations',
  'Delete blocked': 'Suppression bloquée',
  'Delete organization?': 'Supprimer l’organisation ?',
  'Archive is recommended. Blocking dependencies: {dependencies}.': 'L’archivage est recommandé. Dépendances bloquantes : {dependencies}.',
  'business history exists': 'un historique d’activité existe',
  'Delete {name} only if it has no business history.': 'Supprimez {name} uniquement s’il n’a aucun historique d’activité.',
  'this organization': 'cette organisation',
  'Archive instead': 'Archiver à la place',
  'Platform owner': 'Propriétaire de plateforme',
  'pending': 'en attente',
  'suspended': 'suspendu',
  'archived': 'archivé',
  'pending_deletion': 'suppression en attente',
  'deleted': 'supprimé',
  'suspend': 'suspendre',
  'archive': 'archiver',
  'restore': 'restaurer',
  'delete': 'supprimer',
})

// Admin Branding panel (BrandingPanel)
Object.assign(ar, {
  'Invalid value.': 'قيمة غير صالحة.',
  'Unable to update organization branding.': 'تعذّر تحديث العلامة التجارية للمؤسسة.',
  'Branding images must be PNG, JPEG, WebP, or SVG.': 'يجب أن تكون صور العلامة التجارية بصيغة PNG أو JPEG أو WebP أو SVG.',
  'Branding images must be 2 MB or smaller.': 'يجب ألا يتجاوز حجم صور العلامة التجارية 2 ميغابايت.',
  '{asset} uploaded.': 'تم رفع {asset}.',
  'Logo': 'الشعار',
  'Banner': 'اللافتة',
  '{label} must be a hex color like #2563eb.': 'يجب أن يكون {label} لونًا سداسيًا عشريًا مثل #2563eb.',
  'Branding updated.': 'تم تحديث العلامة التجارية.',
  '{label} preview': 'معاينة {label}',
  'Upload an image, or paste an image URL.': 'ارفع صورة أو الصق رابط صورة.',
  'Selected': 'المحدد',
  'URL is optional. Upload is friendlier; URL is there for advanced setups.': 'الرابط اختياري. الرفع أسهل؛ والرابط متاح للإعدادات المتقدمة.',
  'Clear {label}': 'مسح {label}',
  '{label} picker': 'منتقي {label}',
  'Use #RGB or #RRGGBB format.': 'استخدم صيغة #RGB أو #RRGGBB.',
  'Use the picker or enter a hex color.': 'استخدم المنتقي أو أدخل لونًا سداسيًا عشريًا.',
  '{label} presets': 'إعدادات {label} المسبقة',
  'Use {color}': 'استخدم {color}',
  'Branding settings': 'إعدادات العلامة التجارية',
  'Configure the selected agency or company brand without expanding settings into the workspace.': 'هيّئ علامة الوكالة أو الشركة المحددة دون توسيع الإعدادات إلى مساحة العمل.',
  'Branding edits require tenant branding, organization, or RBAC management permission.': 'يتطلب تعديل العلامة التجارية إذن إدارة العلامة التجارية للمستأجر أو المؤسسة أو صلاحيات RBAC.',
  'Display name': 'اسم العرض',
  'Short name': 'الاسم المختصر',
  'Legal name': 'الاسم القانوني',
  'Support email': 'بريد الدعم',
  'Support phone': 'هاتف الدعم',
  'Website URL': 'رابط الموقع الإلكتروني',
  'Save branding': 'حفظ العلامة التجارية',
  'Primary color': 'اللون الأساسي',
  'Secondary color': 'اللون الثانوي',
  'Accent color': 'لون التمييز',
  'Background color': 'لون الخلفية',
  'Surface color': 'لون السطح',
  'Text color': 'لون النص',
  'Button color': 'لون الزر',
  'Button text color': 'لون نص الزر',
})

Object.assign(fr, {
  'Invalid value.': 'Valeur invalide.',
  'Unable to update organization branding.': 'Impossible de mettre à jour l’image de marque de l’organisation.',
  'Branding images must be PNG, JPEG, WebP, or SVG.': 'Les images de marque doivent être au format PNG, JPEG, WebP ou SVG.',
  'Branding images must be 2 MB or smaller.': 'Les images de marque doivent faire 2 Mo ou moins.',
  '{asset} uploaded.': 'Téléversement de {asset} réussi.',
  'Logo': 'Logo',
  'Banner': 'Bannière',
  '{label} must be a hex color like #2563eb.': '{label} doit être une couleur hexadécimale comme #2563eb.',
  'Branding updated.': 'Image de marque mise à jour.',
  '{label} preview': 'Aperçu : {label}',
  'Upload an image, or paste an image URL.': 'Téléversez une image ou collez l’URL d’une image.',
  'Selected': 'Sélectionné',
  'URL is optional. Upload is friendlier; URL is there for advanced setups.': 'L’URL est facultative. Le téléversement est plus simple ; l’URL est destinée aux configurations avancées.',
  'Clear {label}': 'Effacer {label}',
  '{label} picker': 'Sélecteur {label}',
  'Use #RGB or #RRGGBB format.': 'Utilisez le format #RGB ou #RRGGBB.',
  'Use the picker or enter a hex color.': 'Utilisez le sélecteur ou saisissez une couleur hexadécimale.',
  '{label} presets': 'Préréglages {label}',
  'Use {color}': 'Utiliser {color}',
  'Branding settings': 'Paramètres de marque',
  'Configure the selected agency or company brand without expanding settings into the workspace.': 'Configurez la marque de l’agence ou de l’entreprise sélectionnée sans étendre les paramètres dans l’espace de travail.',
  'Branding edits require tenant branding, organization, or RBAC management permission.': 'La modification de l’image de marque nécessite l’autorisation de gestion de la marque du locataire, de l’organisation ou des RBAC.',
  'Display name': 'Nom d’affichage',
  'Short name': 'Nom court',
  'Legal name': 'Raison sociale',
  'Support email': 'E-mail d’assistance',
  'Support phone': 'Téléphone d’assistance',
  'Website URL': 'URL du site web',
  'Save branding': 'Enregistrer l’image de marque',
  'Primary color': 'Couleur principale',
  'Secondary color': 'Couleur secondaire',
  'Accent color': 'Couleur d’accent',
  'Background color': 'Couleur d’arrière-plan',
  'Surface color': 'Couleur de surface',
  'Text color': 'Couleur du texte',
  'Button color': 'Couleur du bouton',
  'Button text color': 'Couleur du texte du bouton',
})

Object.assign(ar, {
  'root': 'الجذر',
  'region': 'المنطقة',
  'company': 'الشركة',
  'location': 'الموقع',
  'department': 'القسم',
  'team': 'الفريق',
  'custom': 'مخصص',
  'Move': 'نقل',
  'Assign location': 'تعيين موقع',
  'Linked location': 'الموقع المرتبط',
  'Parent OU': 'الوحدة الأصل',
  'Role': 'الدور',
  'Create OU': 'إنشاء وحدة تنظيمية',
  'OU access inherits down through descendants in v1 when role assignments are marked to inherit. Location selectors are limited to the active access context.': 'تنتقل صلاحيات الوحدة التنظيمية إلى الوحدات الفرعية في الإصدار الأول عندما تُحدَّد تعيينات الأدوار للتوريث. تقتصر محدِّدات المواقع على سياق الوصول النشط.',
  'Organization tree': 'شجرة المؤسسة',
  'OU changes require tenant RBAC management permission.': 'تتطلب تغييرات الوحدة التنظيمية صلاحية إدارة التحكم في الوصول (RBAC) للمستأجر.',
  'Loading organization units...': 'جارٍ تحميل الوحدات التنظيمية...',
  'No organization units visible': 'لا توجد وحدات تنظيمية مرئية',
  'Create an OU to start organizing company, department, team, or location scopes.': 'أنشئ وحدة تنظيمية لبدء تنظيم نطاقات الشركة أو القسم أو الفريق أو الموقع.',
  'Assign scoped role': 'تعيين دور محدد النطاق',
  'OU details': 'تفاصيل الوحدة التنظيمية',
  '{type} scope': 'نطاق {type}',
  'Archive reason': 'سبب الأرشفة',
  'Move OU': 'نقل الوحدة التنظيمية',
  'Edit OU': 'تعديل الوحدة التنظيمية',
  'Tenant-scoped changes for {org}.': 'تغييرات على مستوى المستأجر لـ {org}.',
  'Save OU': 'حفظ الوحدة التنظيمية',
  'No accessible locations': 'لا توجد مواقع متاحة',
  'OU type': 'نوع الوحدة التنظيمية',
  'Assign scoped OU role': 'تعيين دور وحدة تنظيمية محدد النطاق',
  'Role assignment will target {unit} and may inherit down.': 'سيستهدف تعيين الدور {unit} وقد يُورَّث إلى الأسفل.',
  'Assigning...': 'جارٍ التعيين...',
  'Assign role': 'تعيين الدور',
  'User UUID': 'معرّف المستخدم (UUID)',
  '{label} ({count} permissions)': '{label} ({count} صلاحيات)',
  'Restore OU?': 'استعادة الوحدة التنظيمية؟',
  'Archive OU?': 'أرشفة الوحدة التنظيمية؟',
  '{name} will be restored. Parent OUs must already be active.': 'ستتم استعادة {name}. يجب أن تكون الوحدات الأصل نشطة بالفعل.',
  '{name} will be soft archived. OUs with active children or assigned active locations are rejected by the backend. Hard delete remains unsupported.': 'ستتم أرشفة {name} أرشفة مرنة. ترفض الخدمة الخلفية الوحدات التنظيمية التي لها وحدات فرعية نشطة أو مواقع نشطة مُعيَّنة. لا يزال الحذف النهائي غير مدعوم.',
  'Unable to load organization units.': 'تعذر تحميل الوحدات التنظيمية.',
  'Organization unit name is required.': 'اسم الوحدة التنظيمية مطلوب.',
  'Organization unit created.': 'تم إنشاء الوحدة التنظيمية.',
  'Organization unit moved.': 'تم نقل الوحدة التنظيمية.',
  'Location assigned.': 'تم تعيين الموقع.',
  'Organization unit updated.': 'تم تحديث الوحدة التنظيمية.',
  'Unable to save organization unit.': 'تعذر حفظ الوحدة التنظيمية.',
  'Choose a user and role before assigning OU access.': 'اختر مستخدمًا ودورًا قبل تعيين وصول الوحدة التنظيمية.',
  'Scoped OU role assigned.': 'تم تعيين دور الوحدة التنظيمية المحدد النطاق.',
  'Unable to assign scoped OU role.': 'تعذر تعيين دور الوحدة التنظيمية المحدد النطاق.',
  'Organization unit restored.': 'تمت استعادة الوحدة التنظيمية.',
  'Organization unit archived.': 'تمت أرشفة الوحدة التنظيمية.',
  'Unable to update organization unit archive status.': 'تعذر تحديث حالة أرشفة الوحدة التنظيمية.',
})

Object.assign(fr, {
  'root': 'Racine',
  'region': 'Région',
  'company': 'Entreprise',
  'location': 'Emplacement',
  'department': 'Département',
  'team': 'Équipe',
  'custom': 'Personnalisé',
  'Move': 'Déplacer',
  'Assign location': 'Attribuer un emplacement',
  'Linked location': 'Emplacement lié',
  'Parent OU': 'Unité parente',
  'Role': 'Rôle',
  'Create OU': 'Créer une UO',
  'OU access inherits down through descendants in v1 when role assignments are marked to inherit. Location selectors are limited to the active access context.': 'Les accès de l’UO sont hérités par les descendants en v1 lorsque les attributions de rôles sont marquées comme héritables. Les sélecteurs d’emplacement sont limités au contexte d’accès actif.',
  'Organization tree': 'Arborescence de l’organisation',
  'OU changes require tenant RBAC management permission.': 'Les modifications d’UO nécessitent l’autorisation de gestion RBAC du locataire.',
  'Loading organization units...': 'Chargement des unités organisationnelles...',
  'No organization units visible': 'Aucune unité organisationnelle visible',
  'Create an OU to start organizing company, department, team, or location scopes.': 'Créez une UO pour commencer à organiser les périmètres d’entreprise, de département, d’équipe ou d’emplacement.',
  'Assign scoped role': 'Attribuer un rôle délimité',
  'OU details': 'Détails de l’UO',
  '{type} scope': 'Périmètre {type}',
  'Archive reason': 'Motif d’archivage',
  'Move OU': 'Déplacer l’UO',
  'Edit OU': 'Modifier l’UO',
  'Tenant-scoped changes for {org}.': 'Modifications à l’échelle du locataire pour {org}.',
  'Save OU': 'Enregistrer l’UO',
  'No accessible locations': 'Aucun emplacement accessible',
  'OU type': 'Type d’UO',
  'Assign scoped OU role': 'Attribuer un rôle d’UO délimité',
  'Role assignment will target {unit} and may inherit down.': 'L’attribution du rôle ciblera {unit} et pourra être héritée vers le bas.',
  'Assigning...': 'Attribution...',
  'Assign role': 'Attribuer le rôle',
  'User UUID': 'UUID de l’utilisateur',
  '{label} ({count} permissions)': '{label} ({count} autorisations)',
  'Restore OU?': 'Restaurer l’UO ?',
  'Archive OU?': 'Archiver l’UO ?',
  '{name} will be restored. Parent OUs must already be active.': '{name} sera restaurée. Les UO parentes doivent déjà être actives.',
  '{name} will be soft archived. OUs with active children or assigned active locations are rejected by the backend. Hard delete remains unsupported.': '{name} sera archivée de manière réversible. Le backend rejette les UO comportant des enfants actifs ou des emplacements actifs attribués. La suppression définitive reste non prise en charge.',
  'Unable to load organization units.': 'Impossible de charger les unités organisationnelles.',
  'Organization unit name is required.': 'Le nom de l’unité organisationnelle est requis.',
  'Organization unit created.': 'Unité organisationnelle créée.',
  'Organization unit moved.': 'Unité organisationnelle déplacée.',
  'Location assigned.': 'Emplacement attribué.',
  'Organization unit updated.': 'Unité organisationnelle mise à jour.',
  'Unable to save organization unit.': 'Impossible d’enregistrer l’unité organisationnelle.',
  'Choose a user and role before assigning OU access.': 'Choisissez un utilisateur et un rôle avant d’attribuer l’accès à l’UO.',
  'Scoped OU role assigned.': 'Rôle d’UO délimité attribué.',
  'Unable to assign scoped OU role.': 'Impossible d’attribuer le rôle d’UO délimité.',
  'Organization unit restored.': 'Unité organisationnelle restaurée.',
  'Organization unit archived.': 'Unité organisationnelle archivée.',
  'Unable to update organization unit archive status.': 'Impossible de mettre à jour le statut d’archivage de l’unité organisationnelle.',
})

Object.assign(ar, {
  'Active {resources}': '{resources} النشطة',
  'Adjust filters or create a {job} to begin filling {shifts}.': 'عدّل عوامل التصفية أو أنشئ {job} لبدء تعبئة {shifts}.',
  'All {resources}': 'كل {resources}',
  'Agency locations': 'مواقع الوكالة',
  'Applications / {assignments}': 'الطلبات / {assignments}',
  'Assignment': 'تعيين',
  'Assign {worker}': 'تعيين {worker}',
  'Below Required Level': 'دون المستوى المطلوب',
  'Below required level: {skill}': 'دون المستوى المطلوب: {skill}',
  'Below required level: {skills}': 'دون المستوى المطلوب: {skills}',
  'Certificate Expired': 'الشهادة منتهية',
  'Certificate Pending': 'الشهادة قيد الانتظار',
  'Certificate expired: {skill}': 'الشهادة منتهية: {skill}',
  'Certificate expired: {skills}': 'الشهادات منتهية: {skills}',
  'Certificate pending: {skill}': 'الشهادة قيد الانتظار: {skill}',
  'Certificate pending: {skills}': 'الشهادات قيد الانتظار: {skills}',
  'Certification': 'شهادة',
  'Company Only': 'الشركة فقط',
  'Completed, cancelled, expired, and unavailable {resources} are hidden from this default view. Switch to History or adjust filters.': 'تُخفى {resources} المكتملة والملغاة والمنتهية وغير المتاحة من هذا العرض الافتراضي. انتقل إلى السجل أو عدّل عوامل التصفية.',
  'Create {resource}': 'إنشاء {resource}',
  'Declined': 'مرفوض من العامل',
  'Edit {resource}': 'تعديل {resource}',
  'Existing CSV import workflow, launched from {resources}.': 'سير عمل استيراد CSV الحالي، يُفتح من {resources}.',
  'Hybrid': 'هجين',
  'Marketplace Public': 'سوق عام',
  'Missing Location Geofence': 'سياج الموقع الجغرافي مفقود',
  'Missing Skill': 'مهارة مفقودة',
  'No active {resources} found': 'لم يتم العثور على {resources} نشطة',
  'No historical {resources} found': 'لم يتم العثور على {resources} تاريخية',
  'Offered': 'معروض',
  'Onsite': 'في الموقع',
  'Post {jobs}, add {shifts}, manage visibility, and move applications into {assignments} with names and labels first.': 'انشر {jobs}، أضف {shifts}، أدر الظهور، وانقل الطلبات إلى {assignments} مع إظهار الأسماء والتسميات أولًا.',
  'Outside Availability': 'خارج التوفر',
  'Overlapping Assignment': 'تعيين متداخل',
  'Own Workers': 'عمال المؤسسة',
  'Pending Company Approval': 'بانتظار موافقة الشركة',
  'Pending Worker Acceptance': 'بانتظار قبول العامل',
  'Preferred': 'مفضل',
  'Public Marketplace': 'السوق العام',
  'Remote': 'عن بُعد',
  'Select {resource}': 'اختر {resource}',
  'Shared Agencies': 'وكالات مشتركة',
  'Shift Expired': 'المناوبة منتهية',
  'Shift Not Actionable': 'المناوبة غير قابلة للإجراء',
  'Shift Overfilled': 'المناوبة زائدة عن العدد',
  'Specific Agencies': 'وكالات محددة',
  'Specific Worker': 'عامل محدد',
  'Specific Workers': 'عمال محددون',
  'Specific {worker}': '{worker} محدد',
  'Other accessible locations': 'المواقع الأخرى المتاحة',
  'Worker Pool': 'مجموعة العمال',
  'Worker Unavailable': 'العامل غير متاح',
  '{job}, company, {location}, skill': '{job}، شركة، {location}، مهارة',
  '{name} locations': 'مواقع {name}',
  '{resource} access requires job view or management permission.': 'يتطلب الوصول إلى {resource} صلاحية عرض أو إدارة الوظائف.',
  '{resources} stay scoped to the selected tenant, organization, company, agency, and {location} context.': 'تبقى {resources} ضمن نطاق المستأجر والمؤسسة والشركة والوكالة وسياق {location} المحدد.',
  '{workers} needed *': '{workers} مطلوبون *',
})

Object.assign(fr, {
  'Active {resources}': '{resources} actifs',
  'Adjust filters or create a {job} to begin filling {shifts}.': 'Ajustez les filtres ou créez un(e) {job} pour commencer à pourvoir les {shifts}.',
  'All agencies': 'Toutes les agences',
  'All companies': 'Toutes les entreprises',
  'All jobs': 'Tous les emplois',
  'All {resources}': 'Tous les {resources}',
  'All statuses': 'Tous les statuts',
  'Agency locations': 'Sites de l’agence',
  'Application': 'Candidature',
  'Applications / {assignments}': 'Candidatures / {assignments}',
  'Assign': 'Affecter',
  'Assigned': 'Affecté',
  'Assignments': 'Affectations',
  'Assign {worker}': 'Affecter {worker}',
  'Below Required Level': 'Sous le niveau requis',
  'Below required level: {skill}': 'Sous le niveau requis : {skill}',
  'Below required level: {skills}': 'Sous le niveau requis : {skills}',
  'Cancel shift': 'Annuler la période',
  'Certificate Expired': 'Certificat expiré',
  'Certificate Pending': 'Certificat en attente',
  'Certificate expired: {skill}': 'Certificat expiré : {skill}',
  'Certificate expired: {skills}': 'Certificats expirés : {skills}',
  'Certificate pending: {skill}': 'Certificat en attente : {skill}',
  'Certificate pending: {skills}': 'Certificats en attente : {skills}',
  'Certification': 'Certification',
  'Company Only': 'Entreprise seulement',
  'Completed, cancelled, expired, and unavailable {resources} are hidden from this default view. Switch to History or adjust filters.': 'Les {resources} terminés, annulés, expirés et indisponibles sont masqués dans cette vue par défaut. Passez à Historique ou ajustez les filtres.',
  'Create job': 'Créer un emploi',
  'Create shift': 'Créer une période',
  'Create {resource}': 'Créer {resource}',
  'Declined': 'Refusé',
  'Duplicate': 'Dupliquer',
  'Duplicate shift': 'Dupliquer la période',
  'Edit {resource}': 'Modifier {resource}',
  'Existing CSV import workflow, launched from {resources}.': 'Flux d’import CSV existant, lancé depuis {resources}.',
  'Filled': 'Pourvu',
  'Hybrid': 'Hybride',
  'In Progress': 'En cours',
  'Marketplace Public': 'Place de marché publique',
  'Missing Location Geofence': 'Géorepère du site manquant',
  'Missing Skill': 'Compétence manquante',
  'No location': 'Aucun site',
  'No active {resources} found': 'Aucun {resources} actif trouvé',
  'No historical {resources} found': 'Aucun {resources} historique trouvé',
  'No matched workers yet.': 'Aucun travailleur correspondant pour l’instant.',
  'Offered': 'Proposé',
  'Onsite': 'Sur site',
  'Outside Availability': 'Hors disponibilité',
  'Overlapping Assignment': 'Affectation chevauchante',
  'Own Workers': 'Travailleurs internes',
  'Partially Filled': 'Partiellement pourvu',
  'Pending Company Approval': 'En attente d’approbation de l’entreprise',
  'Pending Worker Acceptance': 'En attente d’acceptation du travailleur',
  'Posted': 'Publié',
  'Preferred': 'Préféré',
  'Public Marketplace': 'Place de marché publique',
  'Remote': 'À distance',
  'Not applied': 'Non candidat',
  'Not assigned': 'Non affecté',
  'Overview': 'Aperçu',
  'Other accessible locations': 'Autres sites accessibles',
  'Post {jobs}, add {shifts}, manage visibility, and move applications into {assignments} with names and labels first.': 'Publiez {jobs}, ajoutez {shifts}, gérez la visibilité et transformez les candidatures en {assignments} avec les noms et libellés en premier.',
  'Select worker': 'Sélectionner un travailleur',
  'Select {resource}': 'Sélectionner {resource}',
  'Shared Agencies': 'Agences partagées',
  'Shift cancelled.': 'Période annulée.',
  'Shift duplicated.': 'Période dupliquée.',
  'Shift Expired': 'Période expirée',
  'Shift Not Actionable': 'Période non actionnable',
  'Shift Overfilled': 'Période en sureffectif',
  'Specific Agencies': 'Agences spécifiques',
  'Specific Worker': 'Travailleur spécifique',
  'Specific Workers': 'Travailleurs spécifiques',
  'Specific {worker}': '{worker} spécifique',
  'Worker Pool': 'Bassin de travailleurs',
  'Worker Unavailable': 'Travailleur indisponible',
  'Worker assignment created.': 'Affectation du travailleur créée.',
  '{job}, company, {location}, skill': '{job}, entreprise, {location}, compétence',
  '{name} locations': 'Sites de {name}',
  '{resource} access requires job view or management permission.': 'L’accès à {resource} nécessite la permission de voir ou gérer les emplois.',
  '{resources} stay scoped to the selected tenant, organization, company, agency, and {location} context.': '{resources} restent limités au contexte de locataire, organisation, entreprise, agence et {location} sélectionné.',
  '{workers} needed *': '{workers} requis *',
})

Object.assign(ar, {
  'All availability states': 'كل حالات التوفر',
  'All conflict states': 'كل حالات التعارض',
  'All fill states': 'كل حالات الإشغال',
  'All visible organizations': 'كل المؤسسات المرئية',
  'Calendar access requires scheduling or jobs view permission.': 'يتطلب الوصول إلى التقويم صلاحية عرض الجدولة أو الوظائف.',
  'Calendar access requires scheduling or {resources} view permission.': 'يتطلب الوصول إلى التقويم صلاحية عرض الجدولة أو {resources}.',
  'Company / client': 'الشركة / العميل',
  'Conflicts': 'التعارضات',
  'Date-based view of job shifts, staffing fill, conflicts, warnings, and applicant load.': 'عرض حسب التاريخ لمناوبات الوظائف، والإشغال، والتعارضات، والتحذيرات، وحجم المتقدمين.',
  'Date-based view of {job} {shifts}, staffing fill, conflicts, warnings, and applicant load.': 'عرض حسب التاريخ لـ {shifts} {job}، والإشغال، والتعارضات، والتحذيرات، وحجم المتقدمين.',
  'Fill': 'الإشغال',
  'From {resource}': 'من {resource}',
  'Loading calendar...': 'جارٍ تحميل التقويم...',
  'Managing agency': 'الوكالة المديرة',
  'New {resource}': '{resource} جديد',
  'No job shifts match the selected dates and filters. Existing Jobs / Orders scheduling remains available in Operations/List View.': 'لا توجد مناوبات وظائف تطابق التواريخ والفلاتر المحددة. تبقى جدولة الوظائف / الطلبات الحالية متاحة في العمليات / عرض القائمة.',
  'No {job} {shifts} match the selected dates and filters. Existing {jobs} scheduling remains available in Operations/List View.': 'لا توجد {shifts} {job} تطابق التواريخ والفلاتر المحددة. تبقى جدولة {jobs} الحالية متاحة في العمليات / عرض القائمة.',
  'No {resources} in this calendar range': 'لا توجد {resources} ضمن نطاق التقويم هذا',
  'Required skills, credentials, visibility, and lightweight job creation follow the selected job/order. Use Jobs / Orders for deeper setup.': 'تتبع المهارات المطلوبة والاعتمادات والظهور وإنشاء الوظيفة الخفيف الوظيفة/الطلب المحدد. استخدم الوظائف / الطلبات للإعداد الأعمق.',
  'Required skills, credentials, visibility, and lightweight job creation follow the selected {job}. Use {jobs} for deeper setup.': 'تتبع المهارات المطلوبة والاعتمادات والظهور وإنشاء العمل الخفيف {job} المحدد. استخدم {jobs} للإعداد الأعمق.',
  'Scheduling calendar': 'تقويم الجدولة',
  'Unable to load schedule calendar.': 'تعذر تحميل تقويم الجدولة.',
  '{assigned}/{required} assigned · {pending} pending': '{assigned}/{required} معين · {pending} قيد الانتظار',
  '{assignment} failed.': 'فشل {assignment}.',
  '{count} conflicts': '{count} تعارض',
  '{count} warnings': '{count} تحذير',
  '{resources} calendar': 'تقويم {resources}',
  '{resource} created.': 'تم إنشاء {resource}.',
  '{resource} updated.': 'تم تحديث {resource}.',
  '{worker} assigned with warnings: {warnings}': 'تم تعيين {worker} مع تحذيرات: {warnings}',
  '{worker} assigned.': 'تم تعيين {worker}.',
  '{worker} has blocking availability conflicts for this {shift}.': 'لدى {worker} تعارضات توفر مانعة لهذه {shift}.',
  'Choose job/order': 'اختر وظيفة/طلبًا',
  'Choose {resource}': 'اختر {resource}',
  'End date/time': 'تاريخ/وقت الانتهاء',
  'From selected {resource}': 'من {resource} المحدد',
  'No jobs/orders are available. Create the job/order in Jobs / Orders first.': 'لا توجد وظائف/طلبات متاحة. أنشئ الوظيفة/الطلب في الوظائف / الطلبات أولًا.',
  'No {resources} are available. Create the {resource} in {source} first.': 'لا توجد {resources} متاحة. أنشئ {resource} في {source} أولًا.',
  'No {resource}': 'لا يوجد {resource}',
  'Publish/open now': 'انشر/افتح الآن',
  'Required headcount': 'العدد المطلوب',
  'Start date/time': 'تاريخ/وقت البدء',
  'Title / role': 'العنوان / الدور',
  'Today': 'اليوم',
  'Use {job} {location}': 'استخدم {location} {job}',
  '{resource} status': 'حالة {resource}',
  '{resource} detail': 'تفاصيل {resource}',
  'Add {worker}': 'إضافة {worker}',
  'Assign {workers}': 'تعيين {workers}',
  'Assignment {status}': 'تعيين {status}',
  'Attendance {value}': 'الحضور {value}',
  'Backend may return warnings during assignment.': 'قد يعيد الخادم تحذيرات أثناء التعيين.',
  'Blocking conflict: {message}': 'تعارض مانع: {message}',
  'Cancel edit': 'إلغاء التعديل',
  'Cancel {resource}': 'إلغاء {resource}',
  'Confirm assignment': 'تأكيد التعيين',
  'Duplicate {resource}': 'تكرار {resource}',
  'Editing is limited: {reasons}': 'التعديل محدود: {reasons}',
  'Inherited from {resource}': 'موروث من {resource}',
  'No assigned {workers} yet.': 'لا يوجد {workers} معينون بعد.',
  'No candidate availability data is visible for this {shift} and filter set.': 'لا تظهر بيانات توفر مرشحين لهذه {shift} ومجموعة الفلاتر.',
  'No contact': 'لا توجد معلومات تواصل',
  'No eligible {workers} are visible in this tenant scope.': 'لا يظهر {workers} مؤهلون في نطاق هذا المستأجر.',
  'No open slots remain. Overfill is disabled from Schedule.': 'لم تعد هناك خانات مفتوحة. تم تعطيل تجاوز العدد من الجدولة.',
  'Open full {resource}': 'فتح {resource} كاملًا',
  'Publish/open {resource}': 'نشر/فتح {resource}',
  'Punctuality {value}': 'الالتزام بالوقت {value}',
  'Reliability {value}': 'الموثوقية {value}',
  'Remove/cancel': 'إزالة/إلغاء',
  'Review applicants': 'مراجعة المتقدمين',
  'Save {resource}': 'حفظ {resource}',
  'Select eligible {worker}': 'اختر {worker} مؤهلًا',
  'Warning: {message}': 'تحذير: {message}',
  '{assigned} / {required} assigned': '{assigned} / {required} معين',
  '{assigned} assigned / {required} required': '{assigned} معين / {required} مطلوب',
  '{conflicts} conflicts / {warnings} warnings': '{conflicts} تعارضات / {warnings} تحذيرات',
  '{resource} cancelled.': 'تم إلغاء {resource}.',
  '{resource} duplicated.': 'تم تكرار {resource}.',
  '{resource} published/opened.': 'تم نشر/فتح {resource}.',
  '{score}% match': 'تطابق {score}%',
  '{worker} availability overlay': 'طبقة توفر {worker}',
})

Object.assign(fr, {
  'All availability states': 'Tous les états de disponibilité',
  'All conflict states': 'Tous les états de conflit',
  'All fill states': 'Tous les états de remplissage',
  'All visible organizations': 'Toutes les organisations visibles',
  'Calendar access requires scheduling or jobs view permission.': 'L’accès au calendrier nécessite la permission de voir la planification ou les emplois.',
  'Calendar access requires scheduling or {resources} view permission.': 'L’accès au calendrier nécessite la permission de voir la planification ou {resources}.',
  'Company / client': 'Entreprise / client',
  'Conflicts': 'Conflits',
  'Date-based view of job shifts, staffing fill, conflicts, warnings, and applicant load.': 'Vue par date des périodes de travail, du remplissage, des conflits, des avertissements et du volume de candidatures.',
  'Date-based view of {job} {shifts}, staffing fill, conflicts, warnings, and applicant load.': 'Vue par date des {shifts} de {job}, du remplissage, des conflits, des avertissements et du volume de candidatures.',
  'Fill': 'Remplissage',
  'From {resource}': 'Depuis {resource}',
  'Jobs / Orders': 'Emplois / Commandes',
  'Job / order': 'Emploi / commande',
  'Loading calendar...': 'Chargement du calendrier...',
  'Managing agency': 'Agence gestionnaire',
  'New {resource}': 'Nouveau {resource}',
  'No job shifts match the selected dates and filters. Existing Jobs / Orders scheduling remains available in Operations/List View.': 'Aucune période de travail ne correspond aux dates et filtres sélectionnés. La planification des Emplois / Commandes existants reste disponible dans Opérations / Vue liste.',
  'No {job} {shifts} match the selected dates and filters. Existing {jobs} scheduling remains available in Operations/List View.': 'Aucune {shifts} de {job} ne correspond aux dates et filtres sélectionnés. La planification des {jobs} existants reste disponible dans Opérations / Vue liste.',
  'No {resources} in this calendar range': 'Aucun {resources} dans cette plage de calendrier',
  'Required skills, credentials, visibility, and lightweight job creation follow the selected job/order. Use Jobs / Orders for deeper setup.': 'Les compétences requises, les justificatifs, la visibilité et la création légère suivent l’emploi/commande sélectionné. Utilisez Emplois / Commandes pour une configuration plus complète.',
  'Required skills, credentials, visibility, and lightweight job creation follow the selected {job}. Use {jobs} for deeper setup.': 'Les compétences requises, les justificatifs, la visibilité et la création légère suivent le {job} sélectionné. Utilisez {jobs} pour une configuration plus complète.',
  'Schedule action failed.': 'L’action de planification a échoué.',
  'Scheduling calendar': 'Calendrier de planification',
  'Unable to load schedule calendar.': 'Impossible de charger le calendrier de planification.',
  '{assigned}/{required} assigned · {pending} pending': '{assigned}/{required} affectés · {pending} en attente',
  '{assignment} failed.': 'Échec de {assignment}.',
  '{count} conflicts': '{count} conflits',
  '{count} warnings': '{count} avertissements',
  '{resources} calendar': 'Calendrier des {resources}',
  '{resource} created.': '{resource} créé.',
  '{resource} updated.': '{resource} mis à jour.',
  '{worker} assigned with warnings: {warnings}': '{worker} affecté avec avertissements : {warnings}',
  '{worker} assigned.': '{worker} affecté.',
  '{worker} has blocking availability conflicts for this {shift}.': '{worker} a des conflits de disponibilité bloquants pour ce {shift}.',
  'All locations': 'Tous les sites',
  'Choose job/order': 'Choisir un emploi/commande',
  'Choose {resource}': 'Choisir {resource}',
  'End date/time': 'Date/heure de fin',
  'From selected {resource}': 'Depuis {resource} sélectionné',
  'No jobs/orders are available. Create the job/order in Jobs / Orders first.': 'Aucun emploi/commande n’est disponible. Créez d’abord l’emploi/commande dans Emplois / Commandes.',
  'No {resources} are available. Create the {resource} in {source} first.': 'Aucun {resources} n’est disponible. Créez d’abord {resource} dans {source}.',
  'No {resource}': 'Aucun {resource}',
  'Publish/open now': 'Publier/ouvrir maintenant',
  'Required headcount': 'Effectif requis',
  'Start date/time': 'Date/heure de début',
  'Title / role': 'Titre / rôle',
  'Today': 'Aujourd’hui',
  'Use job location': 'Utiliser le site de l’emploi',
  'Use {job} {location}': 'Utiliser le {location} de {job}',
  '{resource} status': 'Statut de {resource}',
  '{resource} detail': 'Détail de {resource}',
  'Add {worker}': 'Ajouter {worker}',
  'Applicants': 'Candidats',
  'Assign {workers}': 'Affecter {workers}',
  'Assignment {status}': 'Affectation {status}',
  'Attendance {value}': 'Présence {value}',
  'Backend may return warnings during assignment.': 'Le backend peut retourner des avertissements pendant l’affectation.',
  'Blocking conflict: {message}': 'Conflit bloquant : {message}',
  'Cancel edit': 'Annuler la modification',
  'Cancel {resource}': 'Annuler {resource}',
  'Confirm assignment': 'Confirmer l’affectation',
  'Duplicate {resource}': 'Dupliquer {resource}',
  'Editing is limited: {reasons}': 'La modification est limitée : {reasons}',
  'Inherited from {resource}': 'Hérité de {resource}',
  'No assigned {workers} yet.': 'Aucun {workers} affecté pour l’instant.',
  'No candidate availability data is visible for this {shift} and filter set.': 'Aucune donnée de disponibilité candidate n’est visible pour ce {shift} et cet ensemble de filtres.',
  'No contact': 'Aucun contact',
  'No eligible {workers} are visible in this tenant scope.': 'Aucun {workers} admissible n’est visible dans cette portée de locataire.',
  'No open slots remain. Overfill is disabled from Schedule.': 'Aucun poste ouvert ne reste. Le sureffectif est désactivé depuis Planning.',
  'Open full {resource}': 'Ouvrir {resource} complet',
  'Open profile': 'Ouvrir le profil',
  'Publish/open {resource}': 'Publier/ouvrir {resource}',
  'Punctuality {value}': 'Ponctualité {value}',
  'Reliability {value}': 'Fiabilité {value}',
  'Remove/cancel': 'Retirer/annuler',
  'Review applicants': 'Examiner les candidats',
  'Save {resource}': 'Enregistrer {resource}',
  'Select eligible {worker}': 'Sélectionner {worker} admissible',
  'Warning: {message}': 'Avertissement : {message}',
  '{assigned} / {required} assigned': '{assigned} / {required} affectés',
  '{assigned} assigned / {required} required': '{assigned} affectés / {required} requis',
  '{conflicts} conflicts / {warnings} warnings': '{conflicts} conflits / {warnings} avertissements',
  '{resource} cancelled.': '{resource} annulé.',
  '{resource} duplicated.': '{resource} dupliqué.',
  '{resource} published/opened.': '{resource} publié/ouvert.',
  '{score}% match': '{score}% de correspondance',
  '{worker} availability overlay': 'Superposition de disponibilité {worker}',
})

Object.assign(ar, {
  'All assigned {workers}': 'كل {workers} المعينين',
  'Assigned {worker}': '{worker} معين',
  'Below required level': 'دون المستوى المطلوب',
  'Cancel {shift} {title} on {date}?': 'إلغاء {shift} {title} في {date}؟',
  'Certificate expired': 'الشهادة منتهية',
  'Certificate pending': 'الشهادة قيد الانتظار',
  'Create, edit, fill, and manage {resources} directly from the calendar.': 'أنشئ وعدّل واملأ وأدر {resources} مباشرة من التقويم.',
  'Matched {workers}': '{workers} مطابقون',
  'No assigned {worker} yet.': 'لا يوجد {worker} معين بعد.',
  'No matched {workers} yet.': 'لا يوجد {workers} مطابقون بعد.',
  'No {resources} scheduled for this range.': 'لا توجد {resources} مجدولة لهذا النطاق.',
  'Open {resources}': '{resources} المفتوحة',
  'Open {resource}': 'فتح {resource}',
  'Open {resource} detail': 'فتح تفاصيل {resource}',
  'Schedule workspace sections': 'أقسام مساحة عمل الجدولة',
  'Shift IDs stay internal; cards show {job}, organization, {location}, {worker}, skill, and status labels.': 'تبقى معرفات المناوبات داخلية؛ تعرض البطاقات {job} والمؤسسة و{location} و{worker} والمهارة وتسميات الحالة.',
  'Untitled {resource}': '{resource} بلا عنوان',
  'Workers needed': 'العمال المطلوبون',
  'this {resource}': 'هذا {resource}',
  '{job}, {worker}, {location}, skill': '{job}، {worker}، {location}، مهارة',
  '{resources} Schedule': 'جدول {resources}',
  '{resources} board': 'لوحة {resources}',
  '{workers} needed': '{workers} مطلوبون',
  '{worker} {assignment} created.': 'تم إنشاء {assignment} لـ {worker}.',
})

Object.assign(fr, {
  'All assigned workers': 'Tous les travailleurs affectés',
  'All assigned {workers}': 'Tous les {workers} affectés',
  'Applications': 'Candidatures',
  'Assigned worker': 'Travailleur affecté',
  'Assigned {worker}': '{worker} affecté',
  'Below required level': 'Sous le niveau requis',
  'Calendar': 'Calendrier',
  'Cancel reason': 'Motif d’annulation',
  'Cancel {shift} {title} on {date}?': 'Annuler {shift} {title} le {date} ?',
  'Certificate expired': 'Certificat expiré',
  'Certificate pending': 'Certificat en attente',
  'Choose a job': 'Choisir un emploi',
  'Coverage': 'Couverture',
  'Create, edit, fill, and manage shifts directly from the calendar.': 'Créez, modifiez, pourvoyez et gérez les périodes directement depuis le calendrier.',
  'Create, edit, fill, and manage {resources} directly from the calendar.': 'Créez, modifiez, pourvoyez et gérez {resources} directement depuis le calendrier.',
  'Day': 'Jour',
  'Ends': 'Fin',
  'Job, worker, location, skill': 'Emploi, travailleur, site, compétence',
  'Loading shifts...': 'Chargement des périodes...',
  'Matched workers': 'Travailleurs correspondants',
  'Matched {workers}': '{workers} correspondants',
  'No agency': 'Aucune agence',
  'No applicants yet.': 'Aucun candidat pour l’instant.',
  'No assigned worker yet.': 'Aucun travailleur affecté pour l’instant.',
  'No assigned {worker} yet.': 'Aucun {worker} affecté pour l’instant.',
  'No assignments yet.': 'Aucune affectation pour l’instant.',
  'No company': 'Aucune entreprise',
  'No matched {workers} yet.': 'Aucun {workers} correspondant pour l’instant.',
  'No shifts scheduled for this range.': 'Aucune période planifiée pour cette plage.',
  'No {resources} scheduled for this range.': 'Aucun {resources} planifié pour cette plage.',
  'Open Shifts': 'Périodes ouvertes',
  'Open job': 'Ouvrir l’emploi',
  'Open job detail': 'Ouvrir le détail de l’emploi',
  'Open shift status': 'Statut ouvert',
  'Open {resources}': '{resources} ouverts',
  'Open {resource}': 'Ouvrir {resource}',
  'Open {resource} detail': 'Ouvrir le détail de {resource}',
  'Refresh details': 'Actualiser les détails',
  'Schedule access requires job, shift, application, or assignment view permission.': 'L’accès au planning nécessite la permission de voir les emplois, périodes, candidatures ou affectations.',
  'Schedule workspace sections': 'Sections de l’espace Planning',
  'Scheduling board': 'Tableau de planification',
  'Shift IDs stay internal; cards show job, organization, location, worker, skill, and status labels.': 'Les identifiants de période restent internes ; les cartes affichent l’emploi, l’organisation, le site, le travailleur, la compétence et les libellés de statut.',
  'Shift IDs stay internal; cards show {job}, organization, {location}, {worker}, skill, and status labels.': 'Les identifiants de période restent internes ; les cartes affichent {job}, l’organisation, {location}, {worker}, la compétence et les libellés de statut.',
  'Starts': 'Début',
  'Templates': 'Modèles',
  'Unable to load schedule.': 'Impossible de charger le planning.',
  'Unable to load shift details.': 'Impossible de charger les détails de la période.',
  'Untitled job': 'Emploi sans titre',
  'Untitled {resource}': '{resource} sans titre',
  'Week': 'Semaine',
  'Workers needed': 'Travailleurs requis',
  'applications': 'candidatures',
  'assigned': 'affectés',
  'open': 'ouverts',
  'this {resource}': 'ce {resource}',
  '{job}, {worker}, {location}, skill': '{job}, {worker}, {location}, compétence',
  '{resources} Schedule': 'Planning des {resources}',
  '{resources} board': 'Tableau des {resources}',
  '{workers} needed': '{workers} requis',
  '{worker} {assignment} created.': '{assignment} de {worker} créée.',
})

Object.assign(ar, {
  'Adjusted / worker review': 'تعديل / مراجعة العامل',
  'Adjusted / {worker} review': 'تعديل / مراجعة {worker}',
  'Adjustment requires adjusted start, adjusted end, break minutes, total hours, and reason.': 'يتطلب التعديل بداية معدلة ونهاية معدلة ودقائق الاستراحة وإجمالي الساعات والسبب.',
  'All workers': 'كل العمال',
  'Approval': 'الموافقة',
  'Approvals / Timesheets': 'الموافقات / سجلات الوقت',
  'Approvals / {timesheet}': 'الموافقات / {timesheet}',
  'Approvals defaults to the broad review queue, including submitted rows needing supervisor or company review, worker challenges/disputes, comments, adjustments, and approval history close at hand.': 'تعرض الموافقات افتراضيًا طابور المراجعة الواسع، بما في ذلك الصفوف المرسلة التي تحتاج مراجعة المشرف أو الشركة، واعتراضات/نزاعات العاملين، والتعليقات، والتعديلات، وسجل الموافقات.',
  'Approved company and payroll-locked shifts feed settlement exports. Review details before previewing, exporting, or marking rows exported.': 'تغذي المناوبات المعتمدة من الشركة والمقفلة للرواتب صادرات التسوية. راجع التفاصيل قبل المعاينة أو التصدير أو وسم الصفوف كمصدّرة.',
  'Approved/export-ready {timesheets}': '{timesheets} معتمدة/جاهزة للتصدير',
  'Break duration': 'مدة الاستراحة',
  'Choose filters, then preview rows to see what the CSV will include before downloading.': 'اختر الفلاتر ثم عاين الصفوف لمعرفة ما سيحتويه ملف CSV قبل التنزيل.',
  'Disputed / challenged': 'متنازع عليه / معترض عليه',
  'Downloads include worker, location, job, assignment, agency, company, gross duration, break duration, net payable duration, offline review, and status columns. PDF adds a printable summary and paginated detail rows.': 'تشمل التنزيلات العامل والموقع والوظيفة والتعيين والوكالة والشركة والمدة الإجمالية ومدة الاستراحة والمدة الصافية المستحقة ومراجعة العمل دون اتصال وأعمدة الحالة. يضيف PDF ملخصًا قابلًا للطباعة وصفوف تفاصيل مرقمة.',
  'Downloads include {worker}, {location}, {job}, {assignment}, agency, company, gross duration, break duration, net payable duration, offline review, and status columns. PDF adds a printable summary and paginated detail rows.': 'تشمل التنزيلات {worker} و{location} و{job} و{assignment} والوكالة والشركة والمدة الإجمالية ومدة الاستراحة والمدة الصافية المستحقة ومراجعة العمل دون اتصال وأعمدة الحالة. يضيف PDF ملخصًا قابلًا للطباعة وصفوف تفاصيل مرقمة.',
  'Export CSV': 'تصدير CSV',
  'Export JSON': 'تصدير JSON',
  'Export PDF': 'تصدير PDF',
  'Export XLSX': 'تصدير XLSX',
  'Export ready': 'جاهز للتصدير',
  'Export row preview': 'معاينة صفوف التصدير',
  'Export-ready filters': 'فلاتر الجاهز للتصدير',
  'Exported': 'مصدّر',
  'Exports are managed in Settlement / Payroll Export. This view is for shift and punch review, worker challenges/disputes, supervisor approval, company approval, comments, and adjustment history.': 'تُدار الصادرات في التسوية / تصدير الرواتب. هذا العرض لمراجعة المناوبات والبصمات، واعتراضات/نزاعات العاملين، وموافقة المشرف، وموافقة الشركة، والتعليقات، وسجل التعديلات.',
  'Exports are managed in {payroll} Export / Settlement. This view is for shift and punch review, {worker} challenges/disputes, supervisor approval, company approval, comments, and adjustment history.': 'تُدار الصادرات في تصدير/تسوية {payroll}. هذا العرض لمراجعة المناوبات والبصمات، واعتراضات/نزاعات {worker}، وموافقة المشرف، وموافقة الشركة، والتعليقات، وسجل التعديلات.',
  'Gross duration': 'المدة الإجمالية',
  'Hidden by role': 'مخفي حسب الدور',
  'Job / assignment': 'الوظيفة / التعيين',
  'Loading matching timesheet rows...': 'جارٍ تحميل صفوف سجلات الوقت المطابقة...',
  'Loading matching {timesheet} rows...': 'جارٍ تحميل صفوف {timesheet} المطابقة...',
  'Manual dispute resolution requires final start and end times.': 'يتطلب حل النزاع اليدوي أوقات بداية ونهاية نهائية.',
  'No filter options are loaded yet. You can still export the selected date range if the backend has matching timesheet rows.': 'لم تُحمّل خيارات الفلترة بعد. لا يزال بإمكانك تصدير نطاق التاريخ المحدد إذا كانت لدى الخادم صفوف سجلات وقت مطابقة.',
  'No filter options are loaded yet. You can still export the selected date range if the backend has matching {timesheet} rows.': 'لم تُحمّل خيارات الفلترة بعد. لا يزال بإمكانك تصدير نطاق التاريخ المحدد إذا كانت لدى الخادم صفوف {timesheet} مطابقة.',
  'No shifts matched the selected date range and filters. Try widening the dates or clearing worker, location, job, agency, company, or assignment filters.': 'لم تطابق أي مناوبات نطاق التاريخ والفلاتر المحددة. جرّب توسيع التواريخ أو مسح فلاتر العامل أو الموقع أو الوظيفة أو الوكالة أو الشركة أو التعيين.',
  'No timesheet rows matched': 'لم تطابق أي صفوف سجلات وقت',
  'No {shifts} matched the selected date range and filters. Try widening the dates or clearing {worker}, {location}, {job}, agency, company, or {assignment} filters.': 'لم تطابق أي {shifts} نطاق التاريخ والفلاتر المحددة. جرّب توسيع التواريخ أو مسح فلاتر {worker} أو {location} أو {job} أو الوكالة أو الشركة أو {assignment}.',
  'No {timesheet} rows matched': 'لم تطابق أي صفوف {timesheet}',
  'Offline punch approved. Approved offline punches are eligible for timesheet/payroll export.': 'تم اعتماد البصمة دون اتصال. البصمات المعتمدة دون اتصال مؤهلة لتصدير سجلات الوقت/الرواتب.',
  'Offline punch rejected. Rejected offline punches remain available for audit and are excluded from payable time.': 'تم رفض البصمة دون اتصال. تبقى البصمات المرفوضة متاحة للتدقيق وتُستبعد من الوقت المستحق.',
  'PDF report': 'تقرير PDF',
  'Payroll export uses backend payroll-ready filtering by default. Unresolved disputes, rejected rows, and admin-adjusted rows awaiting worker acceptance are excluded and cannot be marked exported from this UI.': 'يستخدم تصدير الرواتب افتراضيًا فلترة الخادم للصفوف الجاهزة للرواتب. تُستبعد النزاعات غير المحلولة والصفوف المرفوضة والصفوف المعدلة إداريًا بانتظار قبول العامل ولا يمكن وسمها كمصدّرة من هذه الواجهة.',
  'Pending company approval': 'بانتظار موافقة الشركة',
  'Pending supervisor approval': 'بانتظار موافقة المشرف',
  'Preview not loaded': 'لم تُحمّل المعاينة',
  'Preview uses the same CSV export endpoint and current filters. Download is separate so you can check rows first.': 'تستخدم المعاينة نقطة تصدير CSV نفسها والفلاتر الحالية. التنزيل منفصل حتى تتمكن من فحص الصفوف أولًا.',
  'Rate': 'المعدل',
  'Refresh preview': 'تحديث المعاينة',
  'Refresh review queue': 'تحديث طابور المراجعة',
  'Resolve': 'حل',
  'Resolve dispute requires a resolution note.': 'يتطلب حل النزاع ملاحظة حل.',
  'Review completed shift and punch evidence for pending supervisor approval, pending company approval, rejected/disputed rows, worker challenge context, comments, and payable-hour decisions.': 'راجع أدلة المناوبات والبصمات المكتملة لموافقات المشرف أو الشركة المعلقة والصفوف المرفوضة/المتنازع عليها وسياق اعتراض العامل والتعليقات وقرارات الساعات المستحقة.',
  'Review queue': 'طابور المراجعة',
  'Review queue filters': 'فلاتر طابور المراجعة',
  'Review shift and punch evidence, resolve worker challenges, and approve payable hours before settlement.': 'راجع أدلة المناوبات والبصمات، وحل اعتراضات العاملين، واعتمد الساعات المستحقة قبل التسوية.',
  'Reviewer note added to the timesheet audit trail.': 'تمت إضافة ملاحظة المراجع إلى سجل تدقيق سجل الوقت.',
  'Selected export summary': 'ملخص التصدير المحدد',
  'Settlement / Payroll Export': 'التسوية / تصدير الرواتب',
  'Settlement defaults to approved, not-exported rows while keeping the same payroll export filters and formats available.': 'تعتمد التسوية افتراضيًا على الصفوف المعتمدة وغير المصدّرة مع إبقاء فلاتر وتنسيقات تصدير الرواتب نفسها متاحة.',
  'Showing the first 25 of {count} matching rows. The CSV download includes all matching rows.': 'يتم عرض أول 25 من {count} صفًا مطابقًا. يتضمن تنزيل CSV كل الصفوف المطابقة.',
  'Start date must be on or before end date.': 'يجب أن يكون تاريخ البداية في تاريخ النهاية أو قبله.',
  'Timesheet adjusted. It remains out of payroll export until the worker accepts it or a dispute is resolved.': 'تم تعديل سجل الوقت. يبقى خارج تصدير الرواتب حتى يقبله العامل أو يتم حل النزاع.',
  'Timesheet and payroll CSV export requires payroll/export permission. The backend remains the source of truth and currently limits this broad export to tenant admin access.': 'يتطلب تصدير CSV لسجل الوقت والرواتب صلاحية الرواتب/التصدير. يبقى الخادم مصدر الحقيقة ويقصر هذا التصدير الواسع حاليًا على وصول مسؤول المستأجر.',
  'Timesheet approved. Approved payable hours are available to export with the approved status filter.': 'تم اعتماد سجل الوقت. الساعات المستحقة المعتمدة متاحة للتصدير باستخدام فلتر الحالة المعتمدة.',
  'Timesheet dispute resolved. Resolved, payroll-locked final values are eligible for settlement export.': 'تم حل نزاع سجل الوقت. القيم النهائية المحلولة والمقفلة للرواتب مؤهلة لتصدير التسوية.',
  'Timesheet rejected/disputed. It will stay out of approved payable exports.': 'تم رفض/منازعة سجل الوقت. سيبقى خارج صادرات الوقت المستحق المعتمدة.',
  'Turn approved hours into payroll, billing, and integration-ready export files.': 'حوّل الساعات المعتمدة إلى ملفات تصدير جاهزة للرواتب والفوترة والتكامل.',
  'Unable to add reviewer note.': 'تعذرت إضافة ملاحظة المراجع.',
  'Unable to adjust timesheet.': 'تعذر تعديل سجل الوقت.',
  'Unable to approve offline punch.': 'تعذر اعتماد البصمة دون اتصال.',
  'Unable to approve timesheet.': 'تعذر اعتماد سجل الوقت.',
  'Unable to download the {format} export.': 'تعذر تنزيل تصدير {format}.',
  'Unable to load export filter options.': 'تعذر تحميل خيارات فلاتر التصدير.',
  'Unable to load the export preview.': 'تعذر تحميل معاينة التصدير.',
  'Unable to load timesheet approvals.': 'تعذر تحميل موافقات سجلات الوقت.',
  'Unable to reject offline punch.': 'تعذر رفض البصمة دون اتصال.',
  'Unable to reject timesheet.': 'تعذر رفض سجل الوقت.',
  'Unable to resolve timesheet dispute.': 'تعذر حل نزاع سجل الوقت.',
  'Use this queue for submitted shifts needing review, pending supervisor approval, pending company approval, and rejected or disputed/challenged shifts.': 'استخدم هذا الطابور للمناوبات المرسلة التي تحتاج مراجعة، أو موافقة المشرف المعلقة، أو موافقة الشركة المعلقة، أو المناوبات المرفوضة أو المتنازع/المعترض عليها.',
  'Work {location}': '{location} العمل',
  'Worker disputes, admin adjustments, rejection reasons, final values, and resolution notes are shown on each row and in the detail panel when the backend returns them.': 'تظهر نزاعات العاملين وتعديلات المسؤول وأسباب الرفض والقيم النهائية وملاحظات الحل في كل صف وفي لوحة التفاصيل عندما يعيدها الخادم.',
  '{format} download started: {filename}': 'بدأ تنزيل {format}: {filename}',
  '{job} / {assignment}': '{job} / {assignment}',
  '{location} / {job}': '{location} / {job}',
  '{payroll} Export / Settlement': 'تصدير/تسوية {payroll}',
  '{payroll} export uses backend payroll-ready filtering by default. Unresolved disputes, rejected rows, and admin-adjusted rows awaiting {worker} acceptance are excluded and cannot be marked exported from this UI.': 'يستخدم تصدير {payroll} افتراضيًا فلترة الخادم للصفوف الجاهزة للرواتب. تُستبعد النزاعات غير المحلولة والصفوف المرفوضة والصفوف المعدلة إداريًا بانتظار قبول {worker} ولا يمكن وسمها كمصدّرة من هذه الواجهة.',
  '{timesheet} and {payroll} CSV export requires payroll/export permission. The backend remains the source of truth and currently limits this broad export to tenant admin access.': 'يتطلب تصدير CSV لـ {timesheet} و{payroll} صلاحية الرواتب/التصدير. يبقى الخادم مصدر الحقيقة ويقصر هذا التصدير الواسع حاليًا على وصول مسؤول المستأجر.',
  '{timesheet} approval queue': 'طابور موافقة {timesheet}',
  '{timesheet} end date': 'تاريخ نهاية {timesheet}',
  '{timesheet} start date': 'تاريخ بداية {timesheet}',
  '{worker} disputes, admin adjustments, rejection reasons, final values, and resolution notes are shown on each row and in the detail panel when the backend returns them.': 'تظهر نزاعات {worker} وتعديلات المسؤول وأسباب الرفض والقيم النهائية وملاحظات الحل في كل صف وفي لوحة التفاصيل عندما يعيدها الخادم.',
  '{worker} profile': 'ملف {worker}',
  '{worker} {assignment}': '{worker} {assignment}',
})

Object.assign(fr, {
  'Actual time': 'Temps réel',
  'Adjusted / worker review': 'Ajusté / revue travailleur',
  'Adjusted / {worker} review': 'Ajusté / revue {worker}',
  'Adjustment requires adjusted start, adjusted end, break minutes, total hours, and reason.': 'L’ajustement nécessite un début ajusté, une fin ajustée, les minutes de pause, les heures totales et une raison.',
  'All assignments': 'Toutes les affectations',
  'All export states': 'Tous les états d’export',
  'All jobs / orders': 'Tous les emplois / commandes',
  'All workers': 'Tous les travailleurs',
  'Approval status': 'Statut d’approbation',
  'Approvals / Timesheets': 'Approbations / Feuilles de temps',
  'Approvals / {timesheet}': 'Approbations / {timesheet}',
  'Approvals defaults to the broad review queue, including submitted rows needing supervisor or company review, worker challenges/disputes, comments, adjustments, and approval history close at hand.': 'Les approbations affichent par défaut la file de revue large, incluant les lignes soumises nécessitant une revue superviseur ou entreprise, les contestations/litiges travailleurs, les commentaires, les ajustements et l’historique d’approbation.',
  'Approved company and payroll-locked shifts feed settlement exports. Review details before previewing, exporting, or marking rows exported.': 'Les périodes approuvées par l’entreprise et verrouillées pour la paie alimentent les exports de règlement. Vérifiez les détails avant de prévisualiser, exporter ou marquer les lignes exportées.',
  'Approved/export-ready {timesheets}': '{timesheets} approuvées/prêtes à exporter',
  'Break duration': 'Durée de pause',
  'Breaks': 'Pauses',
  'Choose filters, then preview rows to see what the CSV will include before downloading.': 'Choisissez les filtres, puis prévisualisez les lignes pour voir ce que le CSV inclura avant le téléchargement.',
  'Clear filters': 'Effacer les filtres',
  'Company / agency': 'Entreprise / agence',
  'Disputed / challenged': 'Contesté / en litige',
  'Downloading...': 'Téléchargement...',
  'Downloads include worker, location, job, assignment, agency, company, gross duration, break duration, net payable duration, offline review, and status columns. PDF adds a printable summary and paginated detail rows.': 'Les téléchargements incluent le travailleur, le site, l’emploi, l’affectation, l’agence, l’entreprise, la durée brute, la durée de pause, la durée nette payable, la revue hors ligne et les colonnes de statut. Le PDF ajoute un résumé imprimable et des lignes de détail paginées.',
  'Downloads include {worker}, {location}, {job}, {assignment}, agency, company, gross duration, break duration, net payable duration, offline review, and status columns. PDF adds a printable summary and paginated detail rows.': 'Les téléchargements incluent {worker}, {location}, {job}, {assignment}, l’agence, l’entreprise, la durée brute, la durée de pause, la durée nette payable, la revue hors ligne et les colonnes de statut. Le PDF ajoute un résumé imprimable et des lignes de détail paginées.',
  'Export CSV': 'Exporter CSV',
  'Export JSON': 'Exporter JSON',
  'Export PDF': 'Exporter PDF',
  'Export XLSX': 'Exporter XLSX',
  'Export ready': 'Prêt à exporter',
  'Export row preview': 'Aperçu des lignes d’export',
  'Export state': 'État d’export',
  'Export-ready filters': 'Filtres prêts à exporter',
  'Exported': 'Exporté',
  'Exports are managed in Settlement / Payroll Export. This view is for shift and punch review, worker challenges/disputes, supervisor approval, company approval, comments, and adjustment history.': 'Les exports sont gérés dans Règlement / Export paie. Cette vue sert à revoir les périodes et pointages, les contestations/litiges travailleurs, les approbations superviseur et entreprise, les commentaires et l’historique des ajustements.',
  'Exports are managed in {payroll} Export / Settlement. This view is for shift and punch review, {worker} challenges/disputes, supervisor approval, company approval, comments, and adjustment history.': 'Les exports sont gérés dans Export / Règlement {payroll}. Cette vue sert à revoir les périodes et pointages, les contestations/litiges {worker}, les approbations superviseur et entreprise, les commentaires et l’historique des ajustements.',
  'Format': 'Format',
  'Gross duration': 'Durée brute',
  'Hidden by role': 'Masqué par le rôle',
  'JSON integration payload': 'Charge utile d’intégration JSON',
  'Job / assignment': 'Emploi / affectation',
  'Loading completed shifts...': 'Chargement des périodes terminées...',
  'Loading export filters...': 'Chargement des filtres d’export...',
  'Loading matching timesheet rows...': 'Chargement des lignes de feuille de temps correspondantes...',
  'Loading matching {timesheet} rows...': 'Chargement des lignes {timesheet} correspondantes...',
  'Loading preview...': 'Chargement de l’aperçu...',
  'Location / job': 'Site / emploi',
  'Manual dispute resolution requires final start and end times.': 'La résolution manuelle du litige nécessite les heures finales de début et de fin.',
  'Mark approved rows exported after download': 'Marquer les lignes approuvées comme exportées après téléchargement',
  'Net payable': 'Net payable',
  'No filter options are loaded yet. You can still export the selected date range if the backend has matching timesheet rows.': 'Aucune option de filtre n’est encore chargée. Vous pouvez tout de même exporter la plage de dates sélectionnée si le backend contient des lignes de feuille de temps correspondantes.',
  'No filter options are loaded yet. You can still export the selected date range if the backend has matching {timesheet} rows.': 'Aucune option de filtre n’est encore chargée. Vous pouvez tout de même exporter la plage de dates sélectionnée si le backend contient des lignes {timesheet} correspondantes.',
  'No shifts matched the selected date range and filters. Try widening the dates or clearing worker, location, job, agency, company, or assignment filters.': 'Aucune période ne correspond à la plage de dates et aux filtres sélectionnés. Essayez d’élargir les dates ou d’effacer les filtres travailleur, site, emploi, agence, entreprise ou affectation.',
  'No timesheet rows matched': 'Aucune ligne de feuille de temps ne correspond',
  'No {shifts} matched the selected date range and filters. Try widening the dates or clearing {worker}, {location}, {job}, agency, company, or {assignment} filters.': 'Aucun {shifts} ne correspond à la plage de dates et aux filtres sélectionnés. Essayez d’élargir les dates ou d’effacer les filtres {worker}, {location}, {job}, agence, entreprise ou {assignment}.',
  'No {timesheet} rows matched': 'Aucune ligne {timesheet} ne correspond',
  'Not exported': 'Non exporté',
  'Offline punch approved. Approved offline punches are eligible for timesheet/payroll export.': 'Pointage hors ligne approuvé. Les pointages hors ligne approuvés sont admissibles à l’export feuilles de temps/paie.',
  'Offline punch rejected. Rejected offline punches remain available for audit and are excluded from payable time.': 'Pointage hors ligne rejeté. Les pointages hors ligne rejetés restent disponibles pour audit et sont exclus du temps payable.',
  'PDF report': 'Rapport PDF',
  'Payroll export uses backend payroll-ready filtering by default. Unresolved disputes, rejected rows, and admin-adjusted rows awaiting worker acceptance are excluded and cannot be marked exported from this UI.': 'L’export paie utilise par défaut le filtrage backend prêt pour la paie. Les litiges non résolus, les lignes rejetées et les lignes ajustées par l’admin en attente d’acceptation travailleur sont exclues et ne peuvent pas être marquées exportées depuis cette interface.',
  'Pending company approval': 'En attente d’approbation entreprise',
  'Pending supervisor approval': 'En attente d’approbation superviseur',
  'Preview not loaded': 'Aperçu non chargé',
  'Preview rows': 'Prévisualiser les lignes',
  'Preview uses the same CSV export endpoint and current filters. Download is separate so you can check rows first.': 'L’aperçu utilise le même endpoint d’export CSV et les filtres actuels. Le téléchargement est séparé pour permettre de vérifier les lignes d’abord.',
  'Punch standard payroll': 'Paie standard Punch',
  'Rate': 'Taux',
  'Refresh preview': 'Actualiser l’aperçu',
  'Refresh review queue': 'Actualiser la file de revue',
  'Resolve': 'Résoudre',
  'Resolve dispute requires a resolution note.': 'La résolution du litige nécessite une note de résolution.',
  'Review completed shift and punch evidence for pending supervisor approval, pending company approval, rejected/disputed rows, worker challenge context, comments, and payable-hour decisions.': 'Revoyez les preuves de périodes et pointages terminés pour les approbations superviseur et entreprise en attente, les lignes rejetées/en litige, le contexte de contestation travailleur, les commentaires et les décisions d’heures payables.',
  'Review queue': 'File de revue',
  'Review queue filters': 'Filtres de file de revue',
  'Review shift and punch evidence, resolve worker challenges, and approve payable hours before settlement.': 'Revoyez les preuves de périodes et de pointages, résolvez les contestations travailleurs et approuvez les heures payables avant règlement.',
  'Reviewer note added to the timesheet audit trail.': 'Note de réviseur ajoutée à la piste d’audit de la feuille de temps.',
  'Selected export summary': 'Résumé de l’export sélectionné',
  'Settlement / Payroll Export': 'Règlement / Export paie',
  'Settlement defaults to approved, not-exported rows while keeping the same payroll export filters and formats available.': 'Le règlement cible par défaut les lignes approuvées non exportées tout en conservant les mêmes filtres et formats d’export paie.',
  'Showing the first 25 of {count} matching rows. The CSV download includes all matching rows.': 'Affichage des 25 premières lignes sur {count} lignes correspondantes. Le téléchargement CSV inclut toutes les lignes correspondantes.',
  'Start date must be on or before end date.': 'La date de début doit être antérieure ou égale à la date de fin.',
  'Template label': 'Libellé du modèle',
  'Timesheet adjusted. It remains out of payroll export until the worker accepts it or a dispute is resolved.': 'Feuille de temps ajustée. Elle reste exclue de l’export paie jusqu’à acceptation du travailleur ou résolution du litige.',
  'Timesheet and payroll CSV export requires payroll/export permission. The backend remains the source of truth and currently limits this broad export to tenant admin access.': 'L’export CSV feuilles de temps et paie nécessite la permission paie/export. Le backend reste la source de vérité et limite actuellement cet export large à l’accès admin locataire.',
  'Timesheet approved. Approved payable hours are available to export with the approved status filter.': 'Feuille de temps approuvée. Les heures payables approuvées sont exportables avec le filtre de statut approuvé.',
  'Timesheet dispute resolved. Resolved, payroll-locked final values are eligible for settlement export.': 'Litige de feuille de temps résolu. Les valeurs finales résolues et verrouillées pour la paie sont admissibles à l’export de règlement.',
  'Timesheet end date': 'Date de fin de feuille de temps',
  'Timesheet rejected/disputed. It will stay out of approved payable exports.': 'Feuille de temps rejetée/en litige. Elle restera exclue des exports payables approuvés.',
  'Timesheet start date': 'Date de début de feuille de temps',
  'Turn approved hours into payroll, billing, and integration-ready export files.': 'Transformez les heures approuvées en fichiers prêts pour la paie, la facturation et l’intégration.',
  'Unable to add reviewer note.': 'Impossible d’ajouter la note du réviseur.',
  'Unable to adjust timesheet.': 'Impossible d’ajuster la feuille de temps.',
  'Unable to approve offline punch.': 'Impossible d’approuver le pointage hors ligne.',
  'Unable to approve timesheet.': 'Impossible d’approuver la feuille de temps.',
  'Unable to download the {format} export.': 'Impossible de télécharger l’export {format}.',
  'Unable to load export filter options.': 'Impossible de charger les options de filtre d’export.',
  'Unable to load the export preview.': 'Impossible de charger l’aperçu d’export.',
  'Unable to load timesheet approvals.': 'Impossible de charger les approbations de feuilles de temps.',
  'Unable to reject offline punch.': 'Impossible de rejeter le pointage hors ligne.',
  'Unable to reject timesheet.': 'Impossible de rejeter la feuille de temps.',
  'Unable to resolve timesheet dispute.': 'Impossible de résoudre le litige de feuille de temps.',
  'Use this queue for submitted shifts needing review, pending supervisor approval, pending company approval, and rejected or disputed/challenged shifts.': 'Utilisez cette file pour les périodes soumises nécessitant une revue, les approbations superviseur ou entreprise en attente, et les périodes rejetées ou contestées/en litige.',
  'Work location': 'Site de travail',
  'Work {location}': '{location} de travail',
  'Worker assignment': 'Affectation travailleur',
  'Worker disputes, admin adjustments, rejection reasons, final values, and resolution notes are shown on each row and in the detail panel when the backend returns them.': 'Les litiges travailleurs, ajustements admin, motifs de rejet, valeurs finales et notes de résolution apparaissent sur chaque ligne et dans le panneau de détail lorsque le backend les renvoie.',
  'XLSX workbook': 'Classeur XLSX',
  '{format} download started: {filename}': 'Téléchargement {format} commencé : {filename}',
  '{job} / {assignment}': '{job} / {assignment}',
  '{location} / {job}': '{location} / {job}',
  '{payroll} Export / Settlement': 'Export / Règlement {payroll}',
  '{payroll} export uses backend payroll-ready filtering by default. Unresolved disputes, rejected rows, and admin-adjusted rows awaiting {worker} acceptance are excluded and cannot be marked exported from this UI.': 'L’export {payroll} utilise par défaut le filtrage backend prêt pour la paie. Les litiges non résolus, les lignes rejetées et les lignes ajustées par l’admin en attente d’acceptation {worker} sont exclues et ne peuvent pas être marquées exportées depuis cette interface.',
  '{timesheet} and {payroll} CSV export requires payroll/export permission. The backend remains the source of truth and currently limits this broad export to tenant admin access.': 'L’export CSV {timesheet} et {payroll} nécessite la permission paie/export. Le backend reste la source de vérité et limite actuellement cet export large à l’accès admin locataire.',
  '{timesheet} approval queue': 'File d’approbation {timesheet}',
  '{timesheet} end date': 'Date de fin {timesheet}',
  '{timesheet} start date': 'Date de début {timesheet}',
  '{worker} disputes, admin adjustments, rejection reasons, final values, and resolution notes are shown on each row and in the detail panel when the backend returns them.': 'Les litiges {worker}, ajustements admin, motifs de rejet, valeurs finales et notes de résolution apparaissent sur chaque ligne et dans le panneau de détail lorsque le backend les renvoie.',
  '{worker} profile': 'Profil {worker}',
  '{worker} {assignment}': '{assignment} {worker}',
})

Object.assign(ar, {
  'Accept worker version': 'قبول نسخة العامل',
  'Actual': 'الفعلي',
  'Add approval, rejection, or audit context': 'أضف سياق الموافقة أو الرفض أو التدقيق',
  'Add note': 'إضافة ملاحظة',
  'Adjust': 'تعديل',
  'Adjust timesheet': 'تعديل سجل الوقت',
  'Adjusted break / total': 'الاستراحة المعدلة / الإجمالي',
  'Adjusted break minutes': 'دقائق الاستراحة المعدلة',
  'Adjusted end time': 'وقت النهاية المعدل',
  'Adjusted start time': 'وقت البداية المعدل',
  'Adjusted time': 'الوقت المعدل',
  'Adjustment reason': 'سبب التعديل',
  'Admin adjustment': 'تعديل المسؤول',
  'Admin adjustments are not export-ready until the worker accepts the adjustment or a dispute is resolved into final payroll values.': 'لا تكون تعديلات المسؤول جاهزة للتصدير حتى يقبل العامل التعديل أو يتم حل النزاع إلى قيم رواتب نهائية.',
  'Admin decision': 'قرار المسؤول',
  'Approve offline punch': 'اعتماد بصمة دون اتصال',
  'Approved offline punches are applied to the timesheet and become eligible for payroll export.': 'تُطبّق البصمات المعتمدة دون اتصال على سجل الوقت وتصبح مؤهلة لتصدير الرواتب.',
  'Audit trail': 'سجل التدقيق',
  'CSV, JSON, XLSX, and PDF export generations will appear here after they are downloaded or previewed.': 'ستظهر عمليات إنشاء صادرات CSV وJSON وXLSX وPDF هنا بعد تنزيلها أو معاينتها.',
  'Captured': 'تم الالتقاط',
  'Device captured': 'التقاط الجهاز',
  'Dispute resolution': 'حل النزاع',
  'Export history': 'سجل التصدير',
  'Export status': 'حالة التصدير',
  'Fence matched': 'السياج مطابق',
  'Filters': 'الفلاتر',
  'Final break / payable': 'الاستراحة النهائية / المستحق',
  'Final break minutes': 'دقائق الاستراحة النهائية',
  'Final end time': 'وقت النهاية النهائي',
  'Final payable hours': 'الساعات المستحقة النهائية',
  'Final start time': 'وقت البداية النهائي',
  'Final time': 'الوقت النهائي',
  'GPS accuracy': 'دقة GPS',
  'Generated': 'تم الإنشاء',
  'Generated by': 'أنشأه',
  'Generated export runs record timestamp, user, filters, template label, format, and row counts for audit review.': 'تسجل عمليات التصدير المنشأة الطابع الزمني والمستخدم والفلاتر وتسمية القالب والتنسيق وعدد الصفوف للمراجعة التدقيقية.',
  'Geofence / offsite diagnostics': 'تشخيص السياج الجغرافي / خارج الموقع',
  'Geofence review': 'مراجعة السياج الجغرافي',
  'Keep admin version': 'الاحتفاظ بنسخة المسؤول',
  'Loading export history...': 'جارٍ تحميل سجل التصدير...',
  'Loading offline punches...': 'جارٍ تحميل البصمات دون اتصال...',
  'Manual final values': 'قيم نهائية يدوية',
  'Marked: {count}': 'موسوم: {count}',
  'Matched': 'مطابق',
  'Needs supervisor review': 'يحتاج مراجعة المشرف',
  'No approval history recorded yet.': 'لم يتم تسجيل سجل موافقات بعد.',
  'No exports recorded yet': 'لم يتم تسجيل صادرات بعد',
  'No filters': 'لا توجد فلاتر',
  'No offline punches pending review': 'لا توجد بصمات دون اتصال بانتظار المراجعة',
  'Not {payroll} locked': '{payroll} غير مقفل',
  'Notes / exceptions': 'ملاحظات / استثناءات',
  'Offline punch details': 'تفاصيل البصمة دون اتصال',
  'Offline punches pending review': 'بصمات دون اتصال بانتظار المراجعة',
  'Offline reason': 'سبب عدم الاتصال',
  'Offline synced': 'تمت المزامنة دون اتصال',
  'Payroll lock': 'قفل الرواتب',
  'Punch source / offline review': 'مصدر البصمة / مراجعة دون اتصال',
  'Queued offline punches will appear here after Android syncs them.': 'ستظهر البصمات دون اتصال في الطابور هنا بعد أن يزامنها Android.',
  'Received': 'تم الاستلام',
  'Reject offline punch': 'رفض بصمة دون اتصال',
  'Reject timesheet': 'رفض سجل الوقت',
  'Rejected timesheets remain auditable and are excluded from approved payable exports.': 'تبقى سجلات الوقت المرفوضة قابلة للتدقيق وتُستبعد من صادرات الساعات المستحقة المعتمدة.',
  'Resolution note': 'ملاحظة الحل',
  'Resolution outcome': 'نتيجة الحل',
  'Resolve dispute': 'حل النزاع',
  'Resolve timesheet dispute': 'حل نزاع سجل الوقت',
  'Resolved disputes write final values, payroll-lock the row, and keep the approval history intact.': 'تكتب النزاعات المحلولة القيم النهائية وتقفل صف الرواتب وتحافظ على سجل الموافقات كما هو.',
  'Reviewer note': 'ملاحظة المراجع',
  'Rows': 'الصفوف',
  'Save adjustment': 'حفظ التعديل',
  'See offline punch queue and sync diagnostics when present': 'راجع طابور البصمات دون اتصال وتشخيصات المزامنة عند توفرها',
  'See punch/offline review and location audit when present': 'راجع مراجعة البصمة/دون اتصال وتدقيق الموقع عند توفرها',
  'Server received': 'استلام الخادم',
  'Sync': 'المزامنة',
  'Synced offline punches stay pending until a supervisor/admin approves or rejects them.': 'تبقى البصمات المتزامنة دون اتصال معلقة حتى يعتمدها أو يرفضها مشرف/مسؤول.',
  'Template': 'القالب',
  'Worker dispute': 'نزاع العامل',
  'Worker disputed at': 'اعترض العامل في',
  'by': 'بواسطة',
  'system': 'النظام',
  '{payroll} lock': 'قفل {payroll}',
  '{payroll} locked': '{payroll} مقفل',
  '{timesheet} review': 'مراجعة {timesheet}',
  '{worker} dispute': 'نزاع {worker}',
  '{worker} disputed at': 'اعتراض {worker} في',
})

Object.assign(fr, {
  'Accept worker version': 'Accepter la version du travailleur',
  'Action': 'Action',
  'Actual': 'Réel',
  'Add approval, rejection, or audit context': 'Ajouter le contexte d’approbation, de rejet ou d’audit',
  'Add note': 'Ajouter une note',
  'Adjust': 'Ajuster',
  'Adjust timesheet': 'Ajuster la feuille de temps',
  'Adjusted break / total': 'Pause ajustée / total',
  'Adjusted break minutes': 'Minutes de pause ajustées',
  'Adjusted end time': 'Heure de fin ajustée',
  'Adjusted start time': 'Heure de début ajustée',
  'Adjusted time': 'Temps ajusté',
  'Adjustment reason': 'Motif d’ajustement',
  'Admin adjustment': 'Ajustement admin',
  'Admin adjustments are not export-ready until the worker accepts the adjustment or a dispute is resolved into final payroll values.': 'Les ajustements admin ne sont pas prêts à exporter tant que le travailleur ne les accepte pas ou qu’un litige n’est pas résolu en valeurs finales de paie.',
  'Admin decision': 'Décision admin',
  'Approve offline punch': 'Approuver le pointage hors ligne',
  'Approved offline punches are applied to the timesheet and become eligible for payroll export.': 'Les pointages hors ligne approuvés sont appliqués à la feuille de temps et deviennent admissibles à l’export paie.',
  'Audit trail': 'Piste d’audit',
  'CSV, JSON, XLSX, and PDF export generations will appear here after they are downloaded or previewed.': 'Les générations d’exports CSV, JSON, XLSX et PDF apparaîtront ici après téléchargement ou prévisualisation.',
  'Captured': 'Capturé',
  'Device captured': 'Capturé par l’appareil',
  'Dispute resolution': 'Résolution du litige',
  'Export history': 'Historique des exports',
  'Export status': 'Statut d’export',
  'Fence matched': 'Géorepère correspondant',
  'Filters': 'Filtres',
  'Final break / payable': 'Pause finale / payable',
  'Final break minutes': 'Minutes de pause finales',
  'Final end time': 'Heure de fin finale',
  'Final payable hours': 'Heures payables finales',
  'Final start time': 'Heure de début finale',
  'Final time': 'Temps final',
  'GPS accuracy': 'Précision GPS',
  'Generated': 'Généré',
  'Generated by': 'Généré par',
  'Generated export runs record timestamp, user, filters, template label, format, and row counts for audit review.': 'Les exports générés enregistrent l’horodatage, l’utilisateur, les filtres, le libellé du modèle, le format et le nombre de lignes pour revue d’audit.',
  'Geofence / offsite diagnostics': 'Diagnostics géorepère / hors site',
  'Geofence review': 'Revue géorepère',
  'Keep admin version': 'Conserver la version admin',
  'Loading export history...': 'Chargement de l’historique des exports...',
  'Loading offline punches...': 'Chargement des pointages hors ligne...',
  'Manual final values': 'Valeurs finales manuelles',
  'Marked: {count}': 'Marquées : {count}',
  'Matched': 'Correspondant',
  'Needs supervisor review': 'Nécessite une revue superviseur',
  'No approval history recorded yet.': 'Aucun historique d’approbation enregistré pour l’instant.',
  'No exports recorded yet': 'Aucun export enregistré pour l’instant',
  'No filters': 'Aucun filtre',
  'No offline punches pending review': 'Aucun pointage hors ligne en attente de revue',
  'Not {payroll} locked': '{payroll} non verrouillé',
  'Notes / exceptions': 'Notes / exceptions',
  'Offline punch details': 'Détails du pointage hors ligne',
  'Offline punches pending review': 'Pointages hors ligne en attente de revue',
  'Offline reason': 'Motif hors ligne',
  'Offline synced': 'Synchronisé hors ligne',
  'Payroll lock': 'Verrou paie',
  'Punch source / offline review': 'Source du pointage / revue hors ligne',
  'Queued offline punches will appear here after Android syncs them.': 'Les pointages hors ligne en file apparaîtront ici après synchronisation Android.',
  'Received': 'Reçu',
  'Reject offline punch': 'Rejeter le pointage hors ligne',
  'Reject timesheet': 'Rejeter la feuille de temps',
  'Rejected timesheets remain auditable and are excluded from approved payable exports.': 'Les feuilles de temps rejetées restent auditables et sont exclues des exports payables approuvés.',
  'Resolution note': 'Note de résolution',
  'Resolution outcome': 'Résultat de résolution',
  'Resolve dispute': 'Résoudre le litige',
  'Resolve timesheet dispute': 'Résoudre le litige de feuille de temps',
  'Resolved disputes write final values, payroll-lock the row, and keep the approval history intact.': 'Les litiges résolus écrivent les valeurs finales, verrouillent la ligne pour la paie et conservent l’historique d’approbation.',
  'Reviewer note': 'Note du réviseur',
  'Rows': 'Lignes',
  'Save adjustment': 'Enregistrer l’ajustement',
  'See offline punch queue and sync diagnostics when present': 'Consultez la file des pointages hors ligne et les diagnostics de synchronisation lorsqu’ils sont présents',
  'See punch/offline review and location audit when present': 'Consultez la revue de pointage/hors ligne et l’audit du site lorsqu’ils sont présents',
  'Server received': 'Reçu par le serveur',
  'Sync': 'Synchro',
  'Synced offline punches stay pending until a supervisor/admin approves or rejects them.': 'Les pointages hors ligne synchronisés restent en attente jusqu’à approbation ou rejet par un superviseur/admin.',
  'Template': 'Modèle',
  'Worker dispute': 'Litige travailleur',
  'Worker disputed at': 'Travailleur en litige le',
  'by': 'par',
  'system': 'système',
  '{payroll} lock': 'Verrou {payroll}',
  '{payroll} locked': '{payroll} verrouillé',
  '{timesheet} review': 'Revue {timesheet}',
  '{worker} dispute': 'Litige {worker}',
  '{worker} disputed at': '{worker} en litige le',
})

Object.assign(ar, {
  'Approval gates': 'بوابات الموافقة',
  'Approval requirements': 'متطلبات الموافقة',
  'Assign workers: {value}': 'تعيين العمال: {value}',
  'Assignment approval: {value}': 'موافقة التعيين: {value}',
  'Assignments: {value}': 'التعيينات: {value}',
  'Back to dashboard': 'العودة إلى لوحة المعلومات',
  'Bill rates: {value}': 'أسعار الفوترة: {value}',
  'Configure permissions': 'تكوين الصلاحيات',
  'Configure which operational actions this agency can perform under this relationship.': 'كوّن الإجراءات التشغيلية التي يمكن لهذه الوكالة تنفيذها ضمن هذه العلاقة.',
  'Create a job first (Jobs tab), then add shifts from the job detail in the main Demand / Jobs workspace.': 'أنشئ وظيفة أولاً (تبويب الوظائف)، ثم أضف المناوبات من تفاصيل الوظيفة في مساحة الطلب / الوظائف الرئيسية.',
  'Create job for {company}': 'إنشاء وظيفة لـ {company}',
  'Create: {value}': 'الإنشاء: {value}',
  'Creating...': 'جارٍ الإنشاء...',
  'Cross-tenant federated relationship': 'علاقة اتحادية بين مستأجرين',
  'Granted Locations': 'المواقع الممنوحة',
  'Granted sites: {count}': 'المواقع الممنوحة: {count}',
  'Job approval: {value}': 'موافقة الوظيفة: {value}',
  'Job created successfully.': 'تم إنشاء الوظيفة بنجاح.',
  'Job creation is not permitted by the current policy.': 'إنشاء الوظائف غير مسموح به في السياسة الحالية.',
  'Job operations': 'عمليات الوظائف',
  'Jobs created by {agency} for {company} through this relationship.': 'وظائف أنشأتها {agency} لـ {company} من خلال هذه العلاقة.',
  'Jobs not visible': 'الوظائف غير مرئية',
  'Jobs posted by {agency} under this workforce relationship.': 'وظائف نشرتها {agency} ضمن علاقة القوى العاملة هذه.',
  'Jobs: {value}': 'الوظائف: {value}',
  'Loading relationship jobs...': 'جارٍ تحميل وظائف العلاقة...',
  'Location (granted sites only) *': 'الموقع (المواقع الممنوحة فقط) *',
  'Locations granted to this agency for operational use under this relationship.': 'مواقع ممنوحة لهذه الوكالة للاستخدام التشغيلي ضمن هذه العلاقة.',
  'Locations your agency has been granted access to by the client company.': 'مواقع منحت الشركة العميلة وكالتك صلاحية الوصول إليها.',
  'Manage grants': 'إدارة المنح',
  'No granted locations': 'لا توجد مواقع ممنوحة',
  'No jobs found': 'لم يتم العثور على وظائف',
  'No jobs have been created for this client yet.': 'لم يتم إنشاء وظائف لهذا العميل بعد.',
  'No jobs have been created for {company} yet. Use Create job to post the first one.': 'لم يتم إنشاء وظائف لـ {company} بعد. استخدم إنشاء وظيفة لنشر أول وظيفة.',
  'No locations are currently granted to this agency. Use Configure to add site grants.': 'لا توجد مواقع ممنوحة حالياً لهذه الوكالة. استخدم التكوين لإضافة منح مواقع.',
  'No locations have been granted to your agency yet. Ask the company to grant a site through the Configure wizard.': 'لم تُمنح وكالتك أي مواقع بعد. اطلب من الشركة منح موقع عبر معالج التكوين.',
  'No locations with job creation access are granted yet.': 'لم تُمنح بعد أي مواقع بصلاحية إنشاء الوظائف.',
  'Only {agency} workers who are assigned to jobs under this relationship appear here. Company internal workers are not exposed to the agency. Review assignment-linked workers from the main Workforce and Scheduling tabs.': 'يظهر هنا فقط عمال {agency} المعيّنون لوظائف ضمن هذه العلاقة. لا تظهر العمالة الداخلية للشركة للوكالة. راجع العمال المرتبطين بالتعيينات من تبويبي القوى العاملة والجدولة الرئيسيين.',
  'Operational scope': 'النطاق التشغيلي',
  'Pay rates: {value}': 'أسعار الأجر: {value}',
  'Permissions are set by the client company. You can view but not change them from the agency side.': 'تحدد الشركة العميلة الصلاحيات. يمكنك عرضها ولكن لا يمكنك تغييرها من جهة الوكالة.',
  'Posted by {agency} on behalf of {company} using this relationship granted scope. Relationship context is fixed; only granted locations are available.': 'نُشرت بواسطة {agency} نيابة عن {company} باستخدام النطاق الممنوح لهذه العلاقة. سياق العلاقة ثابت؛ المواقع الممنوحة فقط متاحة.',
  'Posting agency': 'الوكالة الناشرة',
  'Preset: {preset}': 'الإعداد المسبق: {preset}',
  'Receive jobs: {value}': 'استلام الوظائف: {value}',
  'Receive: {value}': 'الاستلام: {value}',
  'Relationship actions': 'إجراءات العلاقة',
  'Relationship status': 'حالة العلاقة',
  'Relationship workspace': 'مساحة عمل العلاقة',
  'Relationship workspace tabs': 'تبويبات مساحة عمل العلاقة',
  'Same-tenant relationship': 'علاقة ضمن المستأجر نفسه',
  'Select a granted location.': 'حدد موقعاً ممنوحاً.',
  'Shifts for relationship jobs are managed from the main Demand / Jobs and Scheduling tabs.': 'تُدار مناوبات وظائف العلاقة من تبويبات الطلب / الوظائف والجدولة الرئيسية.',
  'Shifts: {value}': 'المناوبات: {value}',
  'Suspend or revoke this relationship': 'تعليق هذه العلاقة أو إلغاؤها',
  'This company requires job approval. The job will be pending until approved by the company.': 'تتطلب هذه الشركة موافقة على الوظيفة. ستبقى الوظيفة معلقة حتى تعتمدها الشركة.',
  'This relationship is inactive. Shift creation and management are disabled.': 'هذه العلاقة غير نشطة. تم تعطيل إنشاء المناوبات وإدارتها.',
  'This relationship is revoked. Historical records remain but future operational actions are disabled.': 'تم إلغاء هذه العلاقة. تبقى السجلات التاريخية، لكن الإجراءات التشغيلية المستقبلية معطلة.',
  'This relationship is suspended. Access is paused until reactivated.': 'هذه العلاقة معلقة. تم إيقاف الوصول مؤقتاً حتى إعادة التفعيل.',
  'Timesheet approval: {value}': 'موافقة سجل الوقت: {value}',
  'Timesheet data for this relationship is visible in the Approvals / Timesheets tab. Filter by company and agency context to scope to this relationship.': 'تظهر بيانات سجلات الوقت لهذه العلاقة في تبويب الموافقات / سجلات الوقت. صفِّ حسب سياق الشركة والوكالة لحصر هذه العلاقة.',
  'Timesheets: {value}': 'سجلات الوقت: {value}',
  'Untitled': 'بدون عنوان',
  'View granted locations': 'عرض المواقع الممنوحة',
  'View only': 'عرض فقط',
  'Visibility preset': 'إعداد الرؤية المسبق',
  'Workforce operations': 'عمليات القوى العاملة',
  'You can view this connection, but only the company can change permissions.': 'يمكنك عرض هذا الاتصال، لكن الشركة فقط يمكنها تغيير الصلاحيات.',
  'You do not have permission to view jobs in this scope.': 'ليست لديك صلاحية عرض الوظائف في هذا النطاق.',
  'You do not have permission to view timesheets for this relationship.': 'ليست لديك صلاحية عرض سجلات الوقت لهذه العلاقة.',
  'Your agency worker pool can be assigned to {company} jobs and shifts. Go to the Workforce tab to manage your workers, then assign them from the Jobs tab once shifts are created.': 'يمكن تعيين مجموعة عمال وكالتك لوظائف ومناوبات {company}. انتقل إلى تبويب القوى العاملة لإدارة عمالك، ثم عيّنهم من تبويب الوظائف بعد إنشاء المناوبات.',
  '{value} job creation': 'إنشاء الوظائف: {value}',
  '{value} shifts': 'المناوبات: {value}',
  'All Documents': 'كل المستندات',
  'Consent policy details': 'تفاصيل سياسة الموافقة',
  'Credential Summary': 'ملخص الاعتمادات',
  'Current organization: {name} ({type}).': 'المؤسسة الحالية: {name} ({type}).',
  'Document consent controls whether a worker allows selected organizations to access specific document metadata or files. Consent is worker-controlled and does not change who owns the document.': 'تتحكم موافقة المستندات فيما إذا كان العامل يسمح لمؤسسات محددة بالوصول إلى بيانات مستندات معينة أو ملفاتها. الموافقة يتحكم بها العامل ولا تغيّر مالك المستند.',
  'Document consent request created.': 'تم إنشاء طلب موافقة المستند.',
  'Document consent updated.': 'تم تحديث موافقة المستند.',
  'Granted': 'ممنوح',
  'Loading consent records...': 'جارٍ تحميل سجلات الموافقة...',
  'Manager review can update consent request windows and expire requests. Worker document file verification and rejection are available from the selected worker profile document table when the backend grants document management scope.': 'يمكن لمراجعة المدير تحديث فترات طلب الموافقة وإنهاء الطلبات. يتوفر تحقق ملفات مستندات العامل ورفضها من جدول مستندات ملف العامل المحدد عندما يمنح backend نطاق إدارة المستندات.',
  'Managers can only set consent requests to pending or expired. Worker-owned grant/revoke remains backend enforced.': 'يمكن للمديرين تعيين طلبات الموافقة إلى معلقة أو منتهية فقط. يبقى منح/إلغاء العامل مفروضًا من backend.',
  'Managers can request consent or expire a request. Grant/revoke remains worker-owned where backend enforcement applies, and consent controls sharing between organizations, not compliance requirements.': 'يمكن للمديرين طلب الموافقة أو إنهاء الطلب. يبقى المنح/الإلغاء مملوكًا للعامل حيث يطبق backend ذلك، وتتحكم الموافقة بالمشاركة بين المؤسسات لا بمتطلبات الامتثال.',
  'No document consent records': 'لا توجد سجلات موافقة مستندات',
  'Notes: {notes}': 'ملاحظات: {notes}',
  'Request consent': 'طلب الموافقة',
  'Request document consent': 'طلب موافقة المستند',
  'Request document-sharing consent for a worker profile in the selected organization scope.': 'اطلب موافقة مشاركة المستندات لملف عامل ضمن نطاق المؤسسة المحددة.',
  'Review consent': 'مراجعة الموافقة',
  'Review document consent': 'مراجعة موافقة المستند',
  'Selected Documents': 'مستندات محددة',
  'Unable to create worker document consent.': 'تعذر إنشاء موافقة مستندات العامل.',
  'Unable to load worker document consents.': 'تعذر تحميل موافقات مستندات العامل.',
  'Unable to update worker document consent.': 'تعذر تحديث موافقة مستندات العامل.',
  'Update document consent?': 'تحديث موافقة المستند؟',
  '{title} will be marked {status}. Worker-owned grant/revoke transitions remain unavailable here.': 'سيتم تعليم {title} كـ {status}. تبقى انتقالات المنح/الإلغاء المملوكة للعامل غير متاحة هنا.',
  'Active': 'نشط',
  'Admin': 'مسؤول',
  'Admin Group': 'مجموعة المسؤولين',
  'Announcement only': 'إعلان فقط',
  'Announcement only: replies are disabled for your participant role.': 'إعلان فقط: تم تعطيل الردود لدورك كمشارك.',
  'Archive': 'أرشفة',
  'Archived': 'مؤرشف',
  'Broadcast': 'بث',
  'Closed': 'مغلق',
  'Conversation archived.': 'تمت أرشفة المحادثة.',
  'Conversations with workers in your tenant and assigned scope.': 'محادثات مع العمال في المستأجر والنطاق المعيّن لك.',
  'Conversations with {workers} in your tenant and assigned scope.': 'محادثات مع {workers} في المستأجر والنطاق المعيّن لك.',
  'Create thread': 'إنشاء سلسلة',
  'Direct': 'مباشر',
  'Direct Admin Worker': 'مسؤول مباشر - عامل',
  'Direct admin-worker': 'مسؤول مباشر - عامل',
  'Direct admin-{worker}': 'مسؤول مباشر - {worker}',
  'Direct/group': 'مباشر/مجموعة',
  'Fetching the latest worker conversations.': 'جارٍ جلب أحدث محادثات العمال.',
  'Fetching the latest {worker} conversations.': 'جارٍ جلب أحدث محادثات {worker}.',
  'Fetching thread messages.': 'جارٍ جلب رسائل السلسلة.',
  'First message': 'الرسالة الأولى',
  'Group': 'مجموعة',
  'Group title': 'عنوان المجموعة',
  'Groups': 'مجموعات',
  'Job ID': 'معرّف الوظيفة',
  'Job Thread': 'سلسلة الوظيفة',
  'Loading conversations...': 'جارٍ تحميل المحادثات...',
  'Loading eligible workers...': 'جارٍ تحميل العمال المؤهلين...',
  'Loading eligible {workers}...': 'جارٍ تحميل {workers} المؤهلين...',
  'Loading messages...': 'جارٍ تحميل الرسائل...',
  'Location Thread': 'سلسلة الموقع',
  'Message history is hidden because this admin context does not include message view permission.': 'سجل الرسائل مخفي لأن سياق الإدارة هذا لا يتضمن صلاحية عرض الرسائل.',
  'Name, email, phone': 'الاسم، البريد الإلكتروني، الهاتف',
  'New Message': 'رسالة جديدة',
  'New in-app message notifications are waiting in your inbox.': 'توجد إشعارات رسائل جديدة داخل التطبيق في صندوق الوارد.',
  'No conversation open': 'لا توجد محادثة مفتوحة',
  'No conversations yet': 'لا توجد محادثات بعد',
  'No eligible workers found. Workers cannot message each other; admins and supervisors can only start scoped conversations.': 'لم يتم العثور على عمال مؤهلين. لا يمكن للعمال مراسلة بعضهم؛ يمكن للمسؤولين والمشرفين فقط بدء محادثات محددة النطاق.',
  'No eligible {workers} found. {Workers} cannot message each other; admins and supervisors can only start scoped conversations.': 'لم يتم العثور على {workers} مؤهلين. لا يمكن لـ {Workers} مراسلة بعضهم؛ يمكن للمسؤولين والمشرفين فقط بدء محادثات محددة النطاق.',
  'No messages yet': 'لا توجد رسائل بعد',
  'No thread selected': 'لم يتم تحديد سلسلة',
  'Optional for direct messages': 'اختياري للرسائل المباشرة',
  'Participants can reply': 'يمكن للمشاركين الرد',
  'Recipient search': 'بحث المستلمين',
  'Replies enabled': 'الردود مفعّلة',
  'Reply policy': 'سياسة الرد',
  'Reply policy: {policy}': 'سياسة الرد: {policy}',
  'Search conversations': 'بحث في المحادثات',
  'Select a conversation': 'حدد محادثة',
  'Select a thread from the list or create a new message.': 'حدد سلسلة من القائمة أو أنشئ رسالة جديدة.',
  'Select location': 'حدد الموقع',
  'Select {location}': 'حدد {location}',
  'Send': 'إرسال',
  'Shift ID': 'معرّف المناوبة',
  'Shift Thread': 'سلسلة المناوبة',
  'Showing thread unread state because notification count is unavailable.': 'يتم عرض حالة السلسلة غير المقروءة لأن عدد الإشعارات غير متاح.',
  'Start a direct, group, job, shift, or location thread when you need to reach workers.': 'ابدأ سلسلة مباشرة أو جماعية أو خاصة بوظيفة أو مناوبة أو موقع عندما تحتاج إلى التواصل مع العمال.',
  'Start a direct, group, job, shift, or location thread when you need to reach {workers}.': 'ابدأ سلسلة مباشرة أو جماعية أو خاصة بوظيفة أو مناوبة أو موقع عندما تحتاج إلى التواصل مع {workers}.',
  'Supervisor': 'مشرف',
  'This thread is archived or closed.': 'هذه السلسلة مؤرشفة أو مغلقة.',
  'Thread': 'سلسلة',
  'Thread Info': 'معلومات السلسلة',
  'Unable to create message thread.': 'تعذر إنشاء سلسلة الرسائل.',
  'Unable to load conversation.': 'تعذر تحميل المحادثة.',
  'Unable to load eligible workers.': 'تعذر تحميل العمال المؤهلين.',
  'Unable to load eligible {workers}.': 'تعذر تحميل {workers} المؤهلين.',
  'Unable to load messages.': 'تعذر تحميل الرسائل.',
  'Unable to send message.': 'تعذر إرسال الرسالة.',
  'Unread': 'غير مقروء',
  'User': 'مستخدم',
  'You': 'أنت',
  'can reply': 'يمكنه الرد',
  '{count} unread': '{count} غير مقروء',
  '{count} unread message': '{count} رسالة غير مقروءة',
  '{count} unread messages': '{count} رسائل غير مقروءة',
  '{count} workers': '{count} عامل',
})

Object.assign(fr, {
  'Approval gates': 'Contrôles d’approbation',
  'Approval requirements': 'Exigences d’approbation',
  'Assign workers: {value}': 'Affecter des travailleurs : {value}',
  'Assignment approval: {value}': 'Approbation des affectations : {value}',
  'Assignments: {value}': 'Affectations : {value}',
  'Back to dashboard': 'Retour au tableau de bord',
  'Bill rates: {value}': 'Tarifs facturation : {value}',
  'Configure permissions': 'Configurer les permissions',
  'Configure which operational actions this agency can perform under this relationship.': 'Configurez les actions opérationnelles que cette agence peut effectuer dans cette relation.',
  'Create a job first (Jobs tab), then add shifts from the job detail in the main Demand / Jobs workspace.': 'Créez d’abord une offre (onglet Jobs), puis ajoutez des quarts depuis le détail de l’offre dans l’espace principal Demande / Jobs.',
  'Create job for {company}': 'Créer une offre pour {company}',
  'Create: {value}': 'Création : {value}',
  'Creating...': 'Création...',
  'Cross-tenant federated relationship': 'Relation fédérée inter-locataires',
  'Granted Locations': 'Lieux accordés',
  'Granted sites: {count}': 'Sites accordés : {count}',
  'Job approval: {value}': 'Approbation d’offre : {value}',
  'Job created successfully.': 'Offre créée avec succès.',
  'Job creation is not permitted by the current policy.': 'La création d’offres n’est pas autorisée par la politique actuelle.',
  'Job operations': 'Opérations d’offres',
  'Jobs created by {agency} for {company} through this relationship.': 'Offres créées par {agency} pour {company} via cette relation.',
  'Jobs not visible': 'Offres non visibles',
  'Jobs posted by {agency} under this workforce relationship.': 'Offres publiées par {agency} dans cette relation de main-d’œuvre.',
  'Jobs: {value}': 'Offres : {value}',
  'Loading relationship jobs...': 'Chargement des offres de la relation...',
  'Location (granted sites only) *': 'Lieu (sites accordés uniquement) *',
  'Locations granted to this agency for operational use under this relationship.': 'Lieux accordés à cette agence pour usage opérationnel dans cette relation.',
  'Locations your agency has been granted access to by the client company.': 'Lieux auxquels l’entreprise cliente a accordé l’accès à votre agence.',
  'Manage grants': 'Gérer les accès',
  'No granted locations': 'Aucun lieu accordé',
  'No jobs found': 'Aucune offre trouvée',
  'No jobs have been created for this client yet.': 'Aucune offre n’a encore été créée pour ce client.',
  'No jobs have been created for {company} yet. Use Create job to post the first one.': 'Aucune offre n’a encore été créée pour {company}. Utilisez Créer une offre pour publier la première.',
  'No locations are currently granted to this agency. Use Configure to add site grants.': 'Aucun lieu n’est actuellement accordé à cette agence. Utilisez Configurer pour ajouter des accès de site.',
  'No locations have been granted to your agency yet. Ask the company to grant a site through the Configure wizard.': 'Aucun lieu n’a encore été accordé à votre agence. Demandez à l’entreprise d’accorder un site via l’assistant Configurer.',
  'No locations with job creation access are granted yet.': 'Aucun lieu avec accès de création d’offres n’est encore accordé.',
  'Only {agency} workers who are assigned to jobs under this relationship appear here. Company internal workers are not exposed to the agency. Review assignment-linked workers from the main Workforce and Scheduling tabs.': 'Seuls les travailleurs de {agency} affectés à des offres dans cette relation apparaissent ici. Les travailleurs internes de l’entreprise ne sont pas exposés à l’agence. Consultez les travailleurs liés aux affectations dans les onglets Main-d’œuvre et Planification.',
  'Operational scope': 'Périmètre opérationnel',
  'Pay rates: {value}': 'Taux de paie : {value}',
  'Permissions are set by the client company. You can view but not change them from the agency side.': 'Les permissions sont définies par l’entreprise cliente. Vous pouvez les voir, mais pas les modifier côté agence.',
  'Posted by {agency} on behalf of {company} using this relationship granted scope. Relationship context is fixed; only granted locations are available.': 'Publié par {agency} pour le compte de {company} avec le périmètre accordé par cette relation. Le contexte de relation est fixe; seuls les lieux accordés sont disponibles.',
  'Posting agency': 'Agence émettrice',
  'Preset: {preset}': 'Préréglage : {preset}',
  'Receive jobs: {value}': 'Recevoir des offres : {value}',
  'Receive: {value}': 'Réception : {value}',
  'Relationship actions': 'Actions de relation',
  'Relationship status': 'Statut de la relation',
  'Relationship workspace': 'Espace de relation',
  'Relationship workspace tabs': 'Onglets de l’espace de relation',
  'Same-tenant relationship': 'Relation dans le même locataire',
  'Select a granted location.': 'Sélectionnez un lieu accordé.',
  'Shifts for relationship jobs are managed from the main Demand / Jobs and Scheduling tabs.': 'Les quarts des offres de relation sont gérés depuis les onglets principaux Demande / Jobs et Planification.',
  'Shifts: {value}': 'Quarts : {value}',
  'Suspend or revoke this relationship': 'Suspendre ou révoquer cette relation',
  'This company requires job approval. The job will be pending until approved by the company.': 'Cette entreprise exige l’approbation des offres. L’offre restera en attente jusqu’à approbation par l’entreprise.',
  'This relationship is inactive. Shift creation and management are disabled.': 'Cette relation est inactive. La création et la gestion des quarts sont désactivées.',
  'This relationship is revoked. Historical records remain but future operational actions are disabled.': 'Cette relation est révoquée. Les historiques restent disponibles, mais les actions opérationnelles futures sont désactivées.',
  'This relationship is suspended. Access is paused until reactivated.': 'Cette relation est suspendue. L’accès est en pause jusqu’à réactivation.',
  'Timesheet approval: {value}': 'Approbation feuille de temps : {value}',
  'Timesheet data for this relationship is visible in the Approvals / Timesheets tab. Filter by company and agency context to scope to this relationship.': 'Les données de feuille de temps de cette relation sont visibles dans l’onglet Approbations / Feuilles de temps. Filtrez par contexte entreprise et agence pour cibler cette relation.',
  'Timesheets: {value}': 'Feuilles de temps : {value}',
  'Untitled': 'Sans titre',
  'View granted locations': 'Voir les lieux accordés',
  'View only': 'Lecture seule',
  'Visibility preset': 'Préréglage de visibilité',
  'Workforce operations': 'Opérations main-d’œuvre',
  'You can view this connection, but only the company can change permissions.': 'Vous pouvez voir cette connexion, mais seule l’entreprise peut modifier les permissions.',
  'You do not have permission to view jobs in this scope.': 'Vous n’avez pas la permission de voir les offres dans ce périmètre.',
  'You do not have permission to view timesheets for this relationship.': 'Vous n’avez pas la permission de voir les feuilles de temps de cette relation.',
  'Your agency worker pool can be assigned to {company} jobs and shifts. Go to the Workforce tab to manage your workers, then assign them from the Jobs tab once shifts are created.': 'Le bassin de travailleurs de votre agence peut être affecté aux offres et quarts de {company}. Allez dans l’onglet Main-d’œuvre pour gérer vos travailleurs, puis affectez-les depuis l’onglet Jobs une fois les quarts créés.',
  '{value} job creation': 'Création d’offres : {value}',
  '{value} shifts': 'Quarts : {value}',
  'All Documents': 'Tous les documents',
  'Consent policy details': 'Détails de la politique de consentement',
  'Credential Summary': 'Résumé des justificatifs',
  'Current organization: {name} ({type}).': 'Organisation actuelle : {name} ({type}).',
  'Document consent controls whether a worker allows selected organizations to access specific document metadata or files. Consent is worker-controlled and does not change who owns the document.': 'Le consentement aux documents contrôle si un travailleur autorise certaines organisations à accéder à des métadonnées ou fichiers de documents. Le consentement est contrôlé par le travailleur et ne change pas le propriétaire du document.',
  'Document consent request created.': 'Demande de consentement aux documents créée.',
  'Document consent updated.': 'Consentement aux documents mis à jour.',
  'Granted': 'Accordé',
  'Loading consent records...': 'Chargement des enregistrements de consentement...',
  'Manager review can update consent request windows and expire requests. Worker document file verification and rejection are available from the selected worker profile document table when the backend grants document management scope.': 'La revue gestionnaire peut modifier les fenêtres de demande de consentement et expirer les demandes. La vérification et le rejet des fichiers de documents du travailleur sont disponibles dans le tableau des documents du profil travailleur sélectionné lorsque le backend accorde le périmètre de gestion des documents.',
  'Managers can only set consent requests to pending or expired. Worker-owned grant/revoke remains backend enforced.': 'Les gestionnaires peuvent seulement définir les demandes de consentement comme en attente ou expirées. L’octroi/la révocation par le travailleur reste appliqué côté backend.',
  'Managers can request consent or expire a request. Grant/revoke remains worker-owned where backend enforcement applies, and consent controls sharing between organizations, not compliance requirements.': 'Les gestionnaires peuvent demander un consentement ou expirer une demande. L’octroi/la révocation reste contrôlé par le travailleur lorsque le backend l’applique, et le consentement contrôle le partage entre organisations, pas les exigences de conformité.',
  'No document consent records': 'Aucun enregistrement de consentement aux documents',
  'Notes: {notes}': 'Notes : {notes}',
  'Request consent': 'Demander le consentement',
  'Request document consent': 'Demander le consentement au document',
  'Request document-sharing consent for a worker profile in the selected organization scope.': 'Demandez un consentement de partage de documents pour un profil travailleur dans le périmètre de l’organisation sélectionnée.',
  'Review consent': 'Revoir le consentement',
  'Review document consent': 'Revoir le consentement au document',
  'Selected Documents': 'Documents sélectionnés',
  'Unable to create worker document consent.': 'Impossible de créer le consentement aux documents du travailleur.',
  'Unable to load worker document consents.': 'Impossible de charger les consentements aux documents du travailleur.',
  'Unable to update worker document consent.': 'Impossible de mettre à jour le consentement aux documents du travailleur.',
  'Update document consent?': 'Mettre à jour le consentement au document ?',
  '{title} will be marked {status}. Worker-owned grant/revoke transitions remain unavailable here.': '{title} sera marqué {status}. Les transitions d’octroi/révocation contrôlées par le travailleur restent indisponibles ici.',
  'Active': 'Actif',
  'Admin': 'Admin',
  'Admin Group': 'Groupe admin',
  'Announcement only': 'Annonce uniquement',
  'Announcement only: replies are disabled for your participant role.': 'Annonce uniquement : les réponses sont désactivées pour votre rôle de participant.',
  'Archive': 'Archiver',
  'Archived': 'Archivé',
  'Broadcast': 'Diffusion',
  'Closed': 'Fermé',
  'Conversation archived.': 'Conversation archivée.',
  'Conversations with workers in your tenant and assigned scope.': 'Conversations avec les travailleurs dans votre locataire et votre périmètre assigné.',
  'Conversations with {workers} in your tenant and assigned scope.': 'Conversations avec les {workers} dans votre locataire et votre périmètre assigné.',
  'Create thread': 'Créer le fil',
  'Direct': 'Direct',
  'Direct Admin Worker': 'Admin-travailleur direct',
  'Direct admin-worker': 'Admin-travailleur direct',
  'Direct admin-{worker}': 'Admin-{worker} direct',
  'Direct/group': 'Direct/groupe',
  'Fetching the latest worker conversations.': 'Récupération des dernières conversations travailleurs.',
  'Fetching the latest {worker} conversations.': 'Récupération des dernières conversations {worker}.',
  'Fetching thread messages.': 'Récupération des messages du fil.',
  'First message': 'Premier message',
  'Group': 'Groupe',
  'Group title': 'Titre du groupe',
  'Groups': 'Groupes',
  'Job ID': 'ID du poste',
  'Job Thread': 'Fil du poste',
  'Loading conversations...': 'Chargement des conversations...',
  'Loading eligible workers...': 'Chargement des travailleurs admissibles...',
  'Loading eligible {workers}...': 'Chargement des {workers} admissibles...',
  'Loading messages...': 'Chargement des messages...',
  'Location Thread': 'Fil du lieu',
  'Message history is hidden because this admin context does not include message view permission.': 'L’historique des messages est masqué car ce contexte admin ne comprend pas la permission de voir les messages.',
  'Messages': 'Messages',
  'Name, email, phone': 'Nom, courriel, téléphone',
  'New Message': 'Nouveau message',
  'New in-app message notifications are waiting in your inbox.': 'De nouvelles notifications de messages dans l’application attendent dans votre boîte de réception.',
  'No conversation open': 'Aucune conversation ouverte',
  'No conversations yet': 'Aucune conversation pour l’instant',
  'No eligible workers found. Workers cannot message each other; admins and supervisors can only start scoped conversations.': 'Aucun travailleur admissible trouvé. Les travailleurs ne peuvent pas s’envoyer de messages; les admins et superviseurs peuvent seulement démarrer des conversations avec périmètre.',
  'No eligible {workers} found. {Workers} cannot message each other; admins and supervisors can only start scoped conversations.': 'Aucun {workers} admissible trouvé. Les {Workers} ne peuvent pas s’envoyer de messages; les admins et superviseurs peuvent seulement démarrer des conversations avec périmètre.',
  'No messages yet': 'Aucun message pour l’instant',
  'No thread selected': 'Aucun fil sélectionné',
  'Optional for direct messages': 'Facultatif pour les messages directs',
  'Participants can reply': 'Les participants peuvent répondre',
  'Recipient search': 'Recherche de destinataires',
  'Replies enabled': 'Réponses activées',
  'Reply policy': 'Politique de réponse',
  'Reply policy: {policy}': 'Politique de réponse : {policy}',
  'Search conversations': 'Rechercher des conversations',
  'Select a conversation': 'Sélectionnez une conversation',
  'Select a thread from the list or create a new message.': 'Sélectionnez un fil dans la liste ou créez un nouveau message.',
  'Select location': 'Sélectionner un lieu',
  'Select {location}': 'Sélectionner {location}',
  'Send': 'Envoyer',
  'Shift ID': 'ID du quart',
  'Shift Thread': 'Fil du quart',
  'Showing thread unread state because notification count is unavailable.': 'L’état non lu du fil est affiché car le nombre de notifications est indisponible.',
  'Start a direct, group, job, shift, or location thread when you need to reach workers.': 'Démarrez un fil direct, de groupe, de poste, de quart ou de lieu lorsque vous devez joindre des travailleurs.',
  'Start a direct, group, job, shift, or location thread when you need to reach {workers}.': 'Démarrez un fil direct, de groupe, de poste, de quart ou de lieu lorsque vous devez joindre les {workers}.',
  'Supervisor': 'Superviseur',
  'This thread is archived or closed.': 'Ce fil est archivé ou fermé.',
  'Thread': 'Fil',
  'Thread Info': 'Infos du fil',
  'Unable to create message thread.': 'Impossible de créer le fil de messages.',
  'Unable to load conversation.': 'Impossible de charger la conversation.',
  'Unable to load eligible workers.': 'Impossible de charger les travailleurs admissibles.',
  'Unable to load eligible {workers}.': 'Impossible de charger les {workers} admissibles.',
  'Unable to load messages.': 'Impossible de charger les messages.',
  'Unable to send message.': 'Impossible d’envoyer le message.',
  'Unread': 'Non lu',
  'User': 'Utilisateur',
  'You': 'Vous',
  'can reply': 'peut répondre',
  '{count} unread': '{count} non lu(s)',
  '{count} unread message': '{count} message non lu',
  '{count} unread messages': '{count} messages non lus',
  '{count} workers': '{count} travailleurs',
})

Object.assign(ar, {
  '123 Main St': '123 الشارع الرئيسي',
  '<kml>...</kml> or GeoJSON/JSON payload': '<kml>...</kml> أو حمولة GeoJSON/JSON',
  'Active punch state is current.': 'حالة التسجيل النشط محدثة.',
  'Active punch state updated.': 'تم تحديث حالة التسجيل النشط.',
  'Address / notes': 'العنوان / الملاحظات',
  'Address search is unavailable right now.': 'البحث عن العنوان غير متاح حالياً.',
  'Allow punches without schedule': 'السماح بالتسجيل دون جدول',
  'Archive {location}?': 'أرشفة {location}؟',
  'Auto': 'تلقائي',
  'Circle': 'دائرة',
  'Circle and polygon tools are active on the map.': 'أدوات الدائرة والمضلع نشطة على الخريطة.',
  'Circle captured from the map.': 'تم التقاط الدائرة من الخريطة.',
  'Circle latitude, longitude, and radius are required.': 'خط العرض وخط الطول ونصف قطر الدائرة مطلوبة.',
  'Circle: {lat}, {lon} - {radius} m': 'دائرة: {lat}، {lon} - {radius} م',
  'Click a row to highlight it on the map and edit its shape.': 'انقر على صف لتمييزه على الخريطة وتعديل شكله.',
  'Company / Client': 'الشركة / العميل',
  'Company / owner': 'الشركة / المالك',
  'Create a location/fence for the selected company.': 'أنشئ موقعاً/نطاقاً للشركة المحددة.',
  'Create new location': 'إنشاء موقع جديد',
  'Default company': 'الشركة الافتراضية',
  'Default radius': 'نصف القطر الافتراضي',
  'Draw a geofence shape or enter valid circle coordinates before saving.': 'ارسم شكل نطاق جغرافي أو أدخل إحداثيات دائرة صالحة قبل الحفظ.',
  'Draw a shape on the map or enter latitude, longitude, and radius.': 'ارسم شكلاً على الخريطة أو أدخل خط العرض وخط الطول ونصف القطر.',
  'Draw geofences': 'رسم النطاقات الجغرافية',
  'Draw one circle or polygon for {name}.': 'ارسم دائرة أو مضلعاً واحداً لـ {name}.',
  'Draw the boundary for {location}.': 'ارسم الحدود لـ {location}.',
  'Draw, import, edit, and monitor PostgreSQL-backed geofence boundaries from the map.': 'ارسم واستورد وعدّل وراقب حدود النطاقات الجغرافية المدعومة بـ PostgreSQL من الخريطة.',
  'Existing location': 'موقع موجود',
  'Export file': 'ملف التصدير',
  'Fence inventory': 'قائمة النطاقات',
  'Fence map loaded.': 'تم تحميل خريطة النطاقات.',
  'Fence mapper': 'مخطط النطاقات',
  'Fence saved to location. Live worker tracking is controlled from Location Edit.': 'تم حفظ النطاق في الموقع. يتم التحكم بتتبع العمال المباشر من تعديل الموقع.',
  'Fence selected. Use the map edit handles or draw a replacement shape.': 'تم تحديد النطاق. استخدم مقابض التعديل على الخريطة أو ارسم شكلاً بديلاً.',
  'Import failed. Use a KML, GeoJSON, JSON, or supported My Maps export.': 'فشل الاستيراد. استخدم KML أو GeoJSON أو JSON أو تصديراً مدعوماً من My Maps.',
  'Import from My Maps': 'الاستيراد من My Maps',
  'Import locations': 'استيراد المواقع',
  'Last update': 'آخر تحديث',
  'Live worker tracking is configured from Location Edit. This mapper only saves geofence boundaries.': 'يتم تكوين تتبع العمال المباشر من تعديل الموقع. يحفظ هذا المخطط حدود النطاق الجغرافي فقط.',
  'Loading access context...': 'جارٍ تحميل سياق الصلاحيات...',
  'Map controls': 'عناصر تحكم الخريطة',
  'Map moved to the selected address.': 'انتقلت الخريطة إلى العنوان المحدد.',
  'New fence': 'نطاق جديد',
  'No active punches are currently reported.': 'لا توجد تسجيلات نشطة مُبلغ عنها حالياً.',
  'No address results found.': 'لم يتم العثور على نتائج عناوين.',
  'No file selected.': 'لم يتم تحديد ملف.',
  'No locations configured.': 'لا توجد مواقع مكوّنة.',
  'No saved fences yet. Draw one on the map to start.': 'لا توجد نطاقات محفوظة بعد. ارسم واحداً على الخريطة للبدء.',
  'Owner organization is required.': 'المؤسسة المالكة مطلوبة.',
  'Polygon': 'مضلع',
  'Polygon captured from the map.': 'تم التقاط المضلع من الخريطة.',
  'Polygon points': 'نقاط المضلع',
  'Polygon: {count} points captured': 'مضلع: تم التقاط {count} نقطة',
  'Punch access': 'وصول التسجيل',
  'Punch monitor': 'مراقبة التسجيلات',
  'Punch monitor is ready.': 'مراقبة التسجيلات جاهزة.',
  'Refresh fences': 'تحديث النطاقات',
  'Refresh punches': 'تحديث التسجيلات',
  'Review this location geofence in read-only mode.': 'راجع النطاق الجغرافي لهذا الموقع بوضع القراءة فقط.',
  'Save fence to location': 'حفظ النطاق في الموقع',
  'Search address': 'بحث عن عنوان',
  'Select a company or owner, then draw one circle or polygon.': 'حدد شركة أو مالكاً، ثم ارسم دائرة أو مضلعاً واحداً.',
  'Select a saved location to edit its fence, or create a new one.': 'حدد موقعاً محفوظاً لتعديل نطاقه أو أنشئ موقعاً جديداً.',
  'Select an address result to move the map.': 'حدد نتيجة عنوان لتحريك الخريطة.',
  'Select company / owner': 'حدد الشركة / المالك',
  'Selected: {location} - {count} active punches': 'المحدد: {location} - التسجيلات النشطة: {count}',
  'Step 1: Select company / owner organization': 'الخطوة 1: حدد الشركة / المؤسسة المالكة',
  'Step 3: Location details': 'الخطوة 3: تفاصيل الموقع',
  'Step 4: Draw fence': 'الخطوة 4: رسم النطاق',
  'Unable to archive this location right now.': 'تعذرت أرشفة هذا الموقع حالياً.',
  'Unable to load mapper data right now.': 'تعذر تحميل بيانات المخطط حالياً.',
  'Unable to read that file.': 'تعذرت قراءة ذلك الملف.',
  'Unable to refresh active punches right now.': 'تعذر تحديث التسجيلات النشطة حالياً.',
  'Unable to save this fence. Check the shape and try again.': 'تعذر حفظ هذا النطاق. تحقق من الشكل وحاول مرة أخرى.',
  'Unknown worker': 'عامل غير معروف',
  'Untitled location': 'موقع بلا عنوان',
  'Upload KML, GeoJSON, or JSON. The text area is kept for advanced import payloads.': 'حمّل KML أو GeoJSON أو JSON. تم إبقاء مربع النص لحمولات الاستيراد المتقدمة.',
  'You can review fences, but editing is disabled.': 'يمكنك مراجعة النطاقات، لكن التعديل معطل.',
  'new location': 'موقع جديد',
  'shift required': 'المناوبة مطلوبة',
  'this location': 'هذا الموقع',
  'unscheduled allowed': 'غير المجدول مسموح',
  '{count} active fences loaded.': 'تم تحميل {count} نطاق نشط.',
  '{count} locations imported.': 'تم استيراد {count} موقع.',
  '{count} workers currently active.': '{count} عامل نشط حالياً.',
  '{file} loaded for import.': 'تم تحميل {file} للاستيراد.',
  '{imported} locations imported, {skipped} skipped.': 'تم استيراد {imported} موقع، وتخطي {skipped}.',
})

Object.assign(fr, {
  '123 Main St': '123 rue Principale',
  '<kml>...</kml> or GeoJSON/JSON payload': '<kml>...</kml> ou charge utile GeoJSON/JSON',
  'Active punch state is current.': 'L’état des pointages actifs est à jour.',
  'Active punch state updated.': 'État des pointages actifs mis à jour.',
  'Address / notes': 'Adresse / notes',
  'Address search is unavailable right now.': 'La recherche d’adresse est indisponible pour le moment.',
  'Allow punches without schedule': 'Autoriser les pointages sans horaire',
  'Archive {location}?': 'Archiver {location} ?',
  'Auto': 'Auto',
  'Circle': 'Cercle',
  'Circle and polygon tools are active on the map.': 'Les outils cercle et polygone sont actifs sur la carte.',
  'Circle captured from the map.': 'Cercle capturé depuis la carte.',
  'Circle latitude, longitude, and radius are required.': 'La latitude, la longitude et le rayon du cercle sont requis.',
  'Circle: {lat}, {lon} - {radius} m': 'Cercle : {lat}, {lon} - {radius} m',
  'Click a row to highlight it on the map and edit its shape.': 'Cliquez sur une ligne pour la mettre en évidence sur la carte et modifier sa forme.',
  'Company / Client': 'Entreprise / client',
  'Company / owner': 'Entreprise / propriétaire',
  'Create a location/fence for the selected company.': 'Créez un lieu/une clôture pour l’entreprise sélectionnée.',
  'Create new location': 'Créer un nouveau lieu',
  'Default company': 'Entreprise par défaut',
  'Default radius': 'Rayon par défaut',
  'Draw a geofence shape or enter valid circle coordinates before saving.': 'Dessinez une forme de géoclôture ou saisissez des coordonnées de cercle valides avant d’enregistrer.',
  'Draw a shape on the map or enter latitude, longitude, and radius.': 'Dessinez une forme sur la carte ou saisissez la latitude, la longitude et le rayon.',
  'Draw geofences': 'Dessiner des géoclôtures',
  'Draw one circle or polygon for {name}.': 'Dessinez un cercle ou un polygone pour {name}.',
  'Draw the boundary for {location}.': 'Dessinez la limite pour {location}.',
  'Draw, import, edit, and monitor PostgreSQL-backed geofence boundaries from the map.': 'Dessinez, importez, modifiez et surveillez depuis la carte les limites de géoclôture stockées dans PostgreSQL.',
  'Existing location': 'Lieu existant',
  'Export file': 'Fichier d’export',
  'Fence inventory': 'Inventaire des clôtures',
  'Fence map loaded.': 'Carte des clôtures chargée.',
  'Fence mapper': 'Cartographie des clôtures',
  'Fence saved to location. Live worker tracking is controlled from Location Edit.': 'Clôture enregistrée sur le lieu. Le suivi en direct des travailleurs se configure depuis Modifier le lieu.',
  'Fence selected. Use the map edit handles or draw a replacement shape.': 'Clôture sélectionnée. Utilisez les poignées d’édition de la carte ou dessinez une forme de remplacement.',
  'Import failed. Use a KML, GeoJSON, JSON, or supported My Maps export.': 'Import échoué. Utilisez un fichier KML, GeoJSON, JSON ou un export My Maps pris en charge.',
  'Import from My Maps': 'Importer depuis My Maps',
  'Import locations': 'Importer des lieux',
  'Last update': 'Dernière mise à jour',
  'Latitude': 'Latitude',
  'Live worker tracking is configured from Location Edit. This mapper only saves geofence boundaries.': 'Le suivi en direct des travailleurs se configure depuis Modifier le lieu. Cette carte enregistre uniquement les limites de géoclôture.',
  'Loading access context...': 'Chargement du contexte d’accès...',
  'Longitude': 'Longitude',
  'Map controls': 'Contrôles de carte',
  'Map moved to the selected address.': 'La carte a été déplacée vers l’adresse sélectionnée.',
  'New fence': 'Nouvelle clôture',
  'No active punches are currently reported.': 'Aucun pointage actif n’est actuellement signalé.',
  'No address results found.': 'Aucun résultat d’adresse trouvé.',
  'No file selected.': 'Aucun fichier sélectionné.',
  'No locations configured.': 'Aucun lieu configuré.',
  'No saved fences yet. Draw one on the map to start.': 'Aucune clôture enregistrée pour le moment. Dessinez-en une sur la carte pour commencer.',
  'Owner organization is required.': 'L’organisation propriétaire est requise.',
  'Polygon': 'Polygone',
  'Polygon captured from the map.': 'Polygone capturé depuis la carte.',
  'Polygon points': 'Points du polygone',
  'Polygon: {count} points captured': 'Polygone : {count} points capturés',
  'Punch access': 'Accès au pointage',
  'Punch monitor': 'Moniteur de pointage',
  'Punch monitor is ready.': 'Le moniteur de pointage est prêt.',
  'Radius': 'Rayon',
  'Refresh fences': 'Actualiser les clôtures',
  'Refresh punches': 'Actualiser les pointages',
  'Review this location geofence in read-only mode.': 'Consultez la géoclôture de ce lieu en lecture seule.',
  'Save fence to location': 'Enregistrer la clôture sur le lieu',
  'Search address': 'Rechercher une adresse',
  'Select a company or owner, then draw one circle or polygon.': 'Sélectionnez une entreprise ou un propriétaire, puis dessinez un cercle ou un polygone.',
  'Select a saved location to edit its fence, or create a new one.': 'Sélectionnez un lieu enregistré pour modifier sa clôture, ou créez-en un nouveau.',
  'Select an address result to move the map.': 'Sélectionnez un résultat d’adresse pour déplacer la carte.',
  'Select company / owner': 'Sélectionner entreprise / propriétaire',
  'Selected: {location} - {count} active punches': 'Sélection : {location} - {count} pointages actifs',
  'Step 1: Select company / owner organization': 'Étape 1 : sélectionner l’entreprise / organisation propriétaire',
  'Step 3: Location details': 'Étape 3 : détails du lieu',
  'Step 4: Draw fence': 'Étape 4 : dessiner la clôture',
  'Unable to archive this location right now.': 'Impossible d’archiver ce lieu pour le moment.',
  'Unable to load mapper data right now.': 'Impossible de charger les données de cartographie pour le moment.',
  'Unable to read that file.': 'Impossible de lire ce fichier.',
  'Unable to refresh active punches right now.': 'Impossible d’actualiser les pointages actifs pour le moment.',
  'Unable to save this fence. Check the shape and try again.': 'Impossible d’enregistrer cette clôture. Vérifiez la forme et réessayez.',
  'Unknown worker': 'Travailleur inconnu',
  'Untitled location': 'Lieu sans titre',
  'Upload KML, GeoJSON, or JSON. The text area is kept for advanced import payloads.': 'Téléversez un fichier KML, GeoJSON ou JSON. La zone de texte reste disponible pour les charges utiles d’import avancées.',
  'You can review fences, but editing is disabled.': 'Vous pouvez consulter les clôtures, mais la modification est désactivée.',
  'new location': 'nouveau lieu',
  'shift required': 'quart requis',
  'this location': 'ce lieu',
  'unscheduled allowed': 'sans horaire autorisé',
  '{count} active fences loaded.': '{count} clôtures actives chargées.',
  '{count} locations imported.': '{count} lieux importés.',
  '{count} workers currently active.': '{count} travailleurs actuellement actifs.',
  '{file} loaded for import.': '{file} chargé pour import.',
  '{imported} locations imported, {skipped} skipped.': '{imported} lieux importés, {skipped} ignorés.',
})

Object.assign(ar, {
  'Add or adjust shift': 'إضافة أو تعديل مناوبة',
  'CSV import': 'استيراد CSV',
  'Calendar editor': 'محرر التقويم',
  'Earliest': 'الأبكر',
  'Earliest allowed': 'الأبكر المسموح',
  'Imported {imported} of {total} rows.': 'تم استيراد {imported} من {total} صف.',
  'Location key': 'مفتاح الموقع',
  'No shifts found.': 'لم يتم العثور على مناوبات.',
  'Preview CSV': 'معاينة CSV',
  'Previewed {count} rows.': 'تمت معاينة {count} صف.',
  'Refresh calendar': 'تحديث التقويم',
  'Selected: {file}': 'المحدد: {file}',
  'Shift import workspace': 'مساحة استيراد المناوبات',
  'Shift saved.': 'تم حفظ المناوبة.',
  'Shift tool mode': 'وضع أداة المناوبات',
  'Start time': 'وقت البدء',
  'Unable to import shifts.': 'تعذر استيراد المناوبات.',
  'Unable to load shifts.': 'تعذر تحميل المناوبات.',
  'Unable to preview CSV.': 'تعذرت معاينة CSV.',
  'Unable to save shift.': 'تعذر حفظ المناوبة.',
  'Upload worker schedules, preview mapped rows, and adjust calendar records.': 'حمّل جداول العمال، وعاين الصفوف المطابقة، وعدّل سجلات التقويم.',
  'Upload worker shift CSV': 'تحميل ملف CSV لمناوبات العمال',
  'Worker ID or phone': 'معرّف العامل أو الهاتف',
  'Worker, location, and start time are required.': 'العامل والموقع ووقت البدء مطلوبة.',
})

Object.assign(fr, {
  'Add or adjust shift': 'Ajouter ou ajuster un quart',
  'CSV import': 'Import CSV',
  'Calendar editor': 'Éditeur de calendrier',
  'Earliest': 'Le plus tôt',
  'Earliest allowed': 'Plus tôt autorisé',
  'Imported {imported} of {total} rows.': '{imported} lignes importées sur {total}.',
  'Location key': 'Clé du lieu',
  'No shifts found.': 'Aucun quart trouvé.',
  'Preview CSV': 'Prévisualiser CSV',
  'Previewed {count} rows.': '{count} lignes prévisualisées.',
  'Refresh calendar': 'Actualiser le calendrier',
  'Selected: {file}': 'Sélectionné : {file}',
  'Shift import workspace': 'Espace d’import des quarts',
  'Shift saved.': 'Quart enregistré.',
  'Shift tool mode': 'Mode de l’outil de quarts',
  'Start time': 'Heure de début',
  'Unable to import shifts.': 'Impossible d’importer les quarts.',
  'Unable to load shifts.': 'Impossible de charger les quarts.',
  'Unable to preview CSV.': 'Impossible de prévisualiser le CSV.',
  'Unable to save shift.': 'Impossible d’enregistrer le quart.',
  'Upload worker schedules, preview mapped rows, and adjust calendar records.': 'Téléversez les horaires des travailleurs, prévisualisez les lignes mappées et ajustez les enregistrements du calendrier.',
  'Upload worker shift CSV': 'Téléverser le CSV des quarts travailleurs',
  'Worker ID or phone': 'ID travailleur ou téléphone',
  'Worker, location, and start time are required.': 'Le travailleur, le lieu et l’heure de début sont requis.',
})

// Authentication, account security, shared UI states, and worker navigation.
Object.assign(fr, {
  '30 seconds': '30 secondes',
  'Accept Invitation': 'Accepter l’invitation',
  'Accept invite': 'Accepter l’invitation',
  'Accepting...': 'Acceptation en cours...',
  'Account': 'Compte',
  'Active sessions': 'Sessions actives',
  'Active sessions could not be loaded.': 'Impossible de charger les sessions actives.',
  'Administrator Access': 'Accès administrateur',
  'Assigned shift': 'Quart attribué',
  'Awaiting approval': 'En attente d’approbation',
  'Can’t scan the QR code? Enter a setup key manually.': 'Impossible de numériser le code QR? Entrez une clé de configuration manuellement.',
  'Copy key': 'Copier la clé',
  'Could not send the skill request.': 'Impossible d’envoyer la demande de compétence.',
  'Create your admin account and first agency or company workspace.': 'Créez votre compte administrateur et votre premier espace de travail d’agence ou d’entreprise.',
  'Current session': 'Session actuelle',
  'Did you mean?': 'Vouliez-vous dire?',
  'Digits': 'Chiffres',
  'Each code can be used once. Store them securely; they will not be shown again.': 'Chaque code ne peut être utilisé qu’une fois. Conservez-les en lieu sûr; ils ne seront plus affichés.',
  'Enable MFA': 'Activer l’authentification multifacteur',
  'Enter an authenticator code or a one-time recovery code.': 'Entrez un code d’authentification ou un code de récupération à usage unique.',
  'First name optional': 'Prénom (facultatif)',
  'Hide': 'Masquer',
  'I saved these recovery codes': 'J’ai enregistré ces codes de récupération',
  'Invite accepted. You can sign in with your invited email.': 'Invitation acceptée. Vous pouvez vous connecter avec l’adresse courriel invitée.',
  'Invite not found or expired.': 'Invitation introuvable ou expirée.',
  'Join the organization scope selected by your administrator.': 'Rejoignez le périmètre organisationnel sélectionné par votre administrateur.',
  'Last name optional': 'Nom de famille (facultatif)',
  'Loading invite...': 'Chargement de l’invitation...',
  'Loading workspace…': 'Chargement de l’espace de travail…',
  'Loading your workspace…': 'Chargement de votre espace de travail…',
  'Location pending': 'Site à confirmer',
  'MFA enrollment failed': 'L’inscription à l’authentification multifacteur a échoué',
  'MFA verification failed': 'La vérification multifacteur a échoué',
  'Minimum 8 characters': 'Minimum de 8 caractères',
  'More': 'Plus',
  'New admins can create an agency or company workspace from the signup flow.': 'Les nouveaux administrateurs peuvent créer un espace de travail d’agence ou d’entreprise lors de l’inscription.',
  'No active sessions.': 'Aucune session active.',
  'No matching catalog skill.': 'Aucune compétence correspondante dans le catalogue.',
  'Organization invite': 'Invitation à une organisation',
  'Other sessions could not be revoked.': 'Impossible de révoquer les autres sessions.',
  'Sign out before setting up the invited account.': 'Déconnectez-vous avant de configurer le compte invité.',
  'Invitation accepted. Sign in again to load your updated access.': 'Invitation acceptée. Reconnectez-vous pour charger vos nouveaux accès.',
  'Sign in with the invited account before accepting this invitation.': 'Connectez-vous avec le compte invité avant d’accepter cette invitation.',
  'This account requires administrator review.': 'Ce compte nécessite une vérification par un administrateur.',
  'Global profiles belong to their account owners.': 'Les profils globaux appartiennent aux titulaires des comptes.',
  'Password changes require account-owner recovery or platform security administration.': 'La modification d’un mot de passe nécessite la récupération par le titulaire du compte ou l’administration de sécurité de la plateforme.',
  'Password optional': 'Mot de passe (facultatif)',
  'Period': 'Période',
  'Phone optional': 'Téléphone (facultatif)',
  'Privileged access requires multifactor authentication. Start enrollment to add this account to your authenticator app.': 'L’accès privilégié exige l’authentification multifacteur. Commencez l’inscription pour ajouter ce compte à votre application d’authentification.',
  'QR code for setting up time-based multifactor authentication.': 'Code QR pour configurer l’authentification multifacteur temporelle.',
  'Remove {name}': 'Retirer {name}',
  'Request "{name}"': 'Demander « {name} »',
  'Requesting...': 'Demande en cours...',
  'Reveal': 'Afficher',
  'Review devices signed in to your account. Network addresses and token details are never displayed.': 'Examinez les appareils connectés à votre compte. Les adresses réseau et les détails des jetons ne sont jamais affichés.',
  'Save your recovery codes': 'Enregistrez vos codes de récupération',
  'Scan this QR code with an authenticator app such as Google Authenticator, Microsoft Authenticator, 1Password, or Authy, then enter the six-digit code it shows.': 'Numérisez ce code QR avec une application comme Google Authenticator, Microsoft Authenticator, 1Password ou Authy, puis entrez le code à six chiffres affiché.',
  'Session could not be revoked.': 'Impossible de révoquer la session.',
  'Session revoked.': 'Session révoquée.',
  'Set up your authenticator': 'Configurer votre application d’authentification',
  'Setup key': 'Clé de configuration',
  'Setup key copied.': 'Clé de configuration copiée.',
  'Setup key hidden': 'Clé de configuration masquée',
  'Sign in with your administrator account to manage shifts and enterprise tools.': 'Connectez-vous avec votre compte administrateur pour gérer les quarts et les outils d’entreprise.',
  'Sign out other sessions': 'Fermer les autres sessions',
  'Sign out this session': 'Fermer cette session',
  'Six-digit verification code': 'Code de vérification à six chiffres',
  'Skill request sent for review.': 'Demande de compétence envoyée pour examen.',
  'Skill search is unavailable.': 'La recherche de compétences est indisponible.',
  'Something went wrong': 'Un problème est survenu',
  'Start MFA enrollment': 'Commencer l’inscription multifacteur',
  'Supplied by {name}': 'Fourni par {name}',
  'Technical details': 'Détails techniques',
  'The QR code could not be displayed. Use the manual setup key below.': 'Impossible d’afficher le code QR. Utilisez la clé de configuration manuelle ci-dessous.',
  'This section could not be displayed.': 'Impossible d’afficher cette section.',
  'Time based': 'Temporel',
  'Time pending': 'Horaire à confirmer',
  'Try again': 'Réessayer',
  'Unable to accept invite.': 'Impossible d’accepter l’invitation.',
  'Unknown device': 'Appareil inconnu',
  'Use at least 8 characters for the password.': 'Utilisez au moins 8 caractères pour le mot de passe.',
  'Verification code': 'Code de vérification',
  'Verify your sign-in': 'Vérifier votre connexion',
  'Verifying...': 'Vérification en cours...',
  'We couldn’t load this section.': 'Impossible de charger cette section.',
  'Worker mobile navigation': 'Navigation mobile du travailleur',
  'Worker workspace': 'Espace de travail du travailleur',
  'Your enrollment session expired. Start again to get a new setup key.': 'Votre session d’inscription a expiré. Recommencez pour obtenir une nouvelle clé de configuration.',
  '{count} other session(s) revoked.': '{count} autre(s) session(s) révoquée(s).',
  'Address optional': 'Adresse (facultatif)',
  'Ask an admin in the existing organization to send an invite from Roles & Access. This prevents duplicate agency/company records.': 'Demandez à un administrateur de l’organisation existante d’envoyer une invitation depuis Rôles et accès. Cela évite les doublons d’agences ou d’entreprises.',
  'Assign worker': 'Affecter un travailleur',
  'Bring in employees, contractors, or agency staff.': 'Ajoutez des employés, des sous-traitants ou du personnel d’agence.',
  'Business email optional': 'Courriel professionnel (facultatif)',
  'Business phone optional': 'Téléphone professionnel (facultatif)',
  'Choose a timezone.': 'Choisissez un fuseau horaire.',
  'Choose Agency or Company.': 'Choisissez Agence ou Entreprise.',
  'Confirm the mobile punch flow before the first live shift.': 'Validez le pointage mobile avant le premier quart réel.',
  'Continue to organization details': 'Continuer vers les détails de l’organisation',
  'Continue to organization type': 'Continuer vers le type d’organisation',
  'Create job/order': 'Créer un emploi ou une commande',
  'Create new organization': 'Créer une organisation',
  'Create the worksite or branch workers will punch into.': 'Créez le chantier ou la succursale où les travailleurs pointeront.',
  'Create workspace and show next steps': 'Créer l’espace de travail et afficher les prochaines étapes',
  'Creating admin workspace...': 'Création de l’espace administrateur...',
  'Details': 'Détails',
  'Enter a short URL-friendly slug.': 'Entrez un identifiant court compatible avec une URL.',
  'Enter a valid business email.': 'Entrez une adresse courriel professionnelle valide.',
  'Enter the organization name.': 'Entrez le nom de l’organisation.',
  'Enter your email.': 'Entrez votre adresse courriel.',
  'Enter your full name.': 'Entrez votre nom complet.',
  'Enter your organization email so an admin can invite you.': 'Entrez votre courriel professionnel afin qu’un administrateur puisse vous inviter.',
  'Existing organizations are joined by invitation so access stays tenant, organization, OU, and location scoped.': 'L’accès à une organisation existante se fait sur invitation afin de conserver un périmètre limité au locataire, à l’organisation, à l’unité organisationnelle et au site.',
  'Have an admin invite you to the current organization or OU.': 'Demandez à un administrateur de vous inviter dans l’organisation ou l’unité organisationnelle actuelle.',
  'Invite workers': 'Inviter des travailleurs',
  'Join existing organization': 'Rejoindre une organisation existante',
  'Join requests need an invitation from an existing organization admin. Ask them to use Roles & Access > Invite member.': 'Pour rejoindre une organisation, vous devez recevoir une invitation d’un administrateur existant. Demandez-lui d’utiliser Rôles et accès > Inviter un membre.',
  'Lowercase letters, numbers, and hyphens.': 'Lettres minuscules, chiffres et traits d’union.',
  'Next': 'Suivant',
  'Open admin workspace': 'Ouvrir l’espace administrateur',
  'Organization onboarding mode': 'Mode d’intégration de l’organisation',
  'Organization type': 'Type d’organisation',
  'Owns work locations or requests labor.': 'Possède des lieux de travail ou demande de la main-d’œuvre.',
  'Passwords must match.': 'Les mots de passe doivent correspondre.',
  'Re-enter password': 'Entrez de nouveau le mot de passe',
  'Referral applied.': 'Référence appliquée.',
  'Referral attribution was attached to this workspace.': 'L’attribution de la référence a été associée à cet espace de travail.',
  'Signup progress': 'Progression de l’inscription',
  'Slug': 'Identifiant URL',
  'Street, city, state, postal code': 'Rue, ville, province ou État, code postal',
  'Supplies/manages workers.': 'Fournit ou gère des travailleurs.',
  'Test punch': 'Tester le pointage',
  'This adjusts labels, dashboard cards, setup copy, and sales/marketing presets. You can change it later from Platform Admin controls.': 'Ce choix adapte les libellés, les cartes du tableau de bord, le contenu de configuration et les paramètres de vente et de marketing. Vous pourrez le modifier plus tard dans les contrôles d’administration de la plateforme.',
  'Timezone': 'Fuseau horaire',
  'Type': 'Type',
  'Unable to create the admin workspace.': 'Impossible de créer l’espace administrateur.',
  'Use at least 8 characters.': 'Utilisez au moins 8 caractères.',
  'Use lowercase letters, numbers, and hyphens only.': 'Utilisez uniquement des lettres minuscules, des chiffres et des traits d’union.',
  'Use the email you will use to administer this account.': 'Utilisez l’adresse courriel qui servira à administrer ce compte.',
  'Use this only when your agency or company is not already in this tenant.': 'Utilisez cette option seulement si votre agence ou entreprise n’existe pas déjà dans ce locataire.',
  'What best describes your organization?': 'Quelle description correspond le mieux à votre organisation?',
  'Workspace created': 'Espace de travail créé',
  'Your admin context is ready.': 'Votre contexte administrateur est prêt.',
  'Your organization was created. Access context is still warming up, so the workspace will use the registration response first.': 'Votre organisation a été créée. Le contexte d’accès est encore en cours d’initialisation; l’espace de travail utilisera d’abord la réponse d’inscription.',
  '{name} already exists. Choose Join existing organization or ask an admin there to invite you.': '{name} existe déjà. Choisissez Rejoindre une organisation existante ou demandez à un administrateur de vous inviter.',
  '{organization} setup creates your first organization context so the dashboard has useful next steps.': 'La configuration de {organization} crée votre premier contexte organisationnel afin que le tableau de bord propose des prochaines étapes utiles.',
  'Available': 'Disponible',
  'Docs': 'Docs',
  'Find': 'Trouver',
  'Find Work': 'Trouver du travail',
  'Learn': 'Apprendre',
  'Learning': 'Apprentissage',
  'Mentor': 'Mentor',
  'Mentorship': 'Mentorat',
  'Onsite': 'Sur place',
  'Project Work Log': 'Journal de travail de projet',
  'Schedule': 'Horaire',
  'Today': 'Aujourd’hui',
  'Work Log': 'Journal de travail',
  'Eligible to apply': 'Admissible à postuler',
  'Visible, not eligible to apply': 'Visible, mais non admissible à postuler',
  'Geography': 'Zone géographique',
  'matching location needed': 'zone de recherche requise',
  'worksite location unavailable': 'site de travail indisponible',
  'not suitable': 'non approprié',
  'not required': 'non requis',
  'eligible': 'admissible',
  'missing {skills}': 'compétences manquantes : {skills}',
  'conflict': 'conflit',
  'clear': 'aucun conflit',
  'Set your work search area in Profile. Your mailing address is not used for location matching.': 'Définissez votre zone de recherche d’emploi dans Profil. Votre adresse postale n’est pas utilisée pour la correspondance géographique.',
})

// Shared administration, learning, mentorship, reporting, and operations labels.
Object.assign(fr, {
  '1 visible location': '1 site visible',
  'AI Mentors': 'Mentors IA',
  'AI insight evidence': 'Preuves issues des analyses IA',
  'AI mentor': 'Mentor IA',
  'AI mentor details': 'Détails du mentor IA',
  'AI-generated session summary': 'Résumé de session généré par l’IA',
  'Access context': 'Contexte d’accès',
  'Access role summary': 'Résumé du rôle d’accès',
  'Account access': 'Accès au compte',
  'Account disabled': 'Compte désactivé',
  'Account status': 'Statut du compte',
  'Accuracy': 'Précision',
  'Acquired through mentorship': 'Acquise par le mentorat',
  'Action failed.': 'L’action a échoué.',
  'Activate': 'Activer',
  'Active bookings': 'Réservations actives',
  'Active filters': 'Filtres actifs',
  'Active goals': 'Objectifs actifs',
  'Active learning path': 'Parcours d’apprentissage actif',
  'Active learning paths': 'Parcours d’apprentissage actifs',
  'Active partners': 'Partenaires actifs',
  'Active workers': 'Travailleurs actifs',
  'Active workers on site': 'Travailleurs actifs sur le site',
  'Activities': 'Activités',
  'Activity': 'Activité',
  'Activity content': 'Contenu de l’activité',
  'Activity content (JSON, optional)': 'Contenu de l’activité (JSON, facultatif)',
  'Activity type': 'Type d’activité',
  'Add availability': 'Ajouter une disponibilité',
  'Add blackout': 'Ajouter une indisponibilité',
  'Add pair': 'Ajouter une paire',
  'Add question': 'Ajouter une question',
  'Admin Workspace': 'Espace administrateur',
  'Admin destination': 'Destination administrateur',
  'Admin destination heading': 'Titre de la destination administrateur',
  'Admin destination unavailable': 'Destination administrateur indisponible',
  'Admin workspace sections': 'Sections de l’espace administrateur',
  'Administrator': 'Administrateur',
  'Advanced JSON override': 'Remplacement JSON avancé',
  'Agenda': 'Agenda',
  'All AI mentors': 'Tous les mentors IA',
  'All approval states': 'Tous les états d’approbation',
  'All kinds': 'Tous les types',
  'All learners': 'Tous les apprenants',
  'All segments': 'Tous les segments',
  'All skill tracks': 'Tous les parcours de compétences',
  'All tenants': 'Tous les locataires',
  'All visible OUs': 'Toutes les unités organisationnelles visibles',
  'All visible agencies': 'Toutes les agences visibles',
  'All visible companies': 'Toutes les entreprises visibles',
  'All visible locations': 'Tous les sites visibles',
  'All visible organization data': 'Toutes les données organisationnelles visibles',
  'Already exported': 'Déjà exporté',
  'Already on your profile': 'Déjà dans votre profil',
  'Answer key (JSON, optional)': 'Corrigé (JSON, facultatif)',
  'Answer key (admin only)': 'Corrigé (administrateurs seulement)',
  'Any mentor': 'N’importe quel mentor',
  'Any requirement state': 'Tout état d’exigence',
  'Any role or skill': 'Tout rôle ou compétence',
  'App access active': 'Accès à l’application actif',
  'App access status': 'Statut d’accès à l’application',
  'Applications awaiting review': 'Candidatures en attente d’examen',
  'Applications need review': 'Candidatures à examiner',
  'Applied': 'Candidature envoyée',
  'Applied scope': 'Périmètre appliqué',
  'Apprentice Mode': 'Mode apprenti',
  'Apprentice Mode enabled': 'Mode apprenti activé',
  'Apprentice preferences': 'Préférences de l’apprenti',
  'Apprentice review': 'Évaluation de l’apprenti',
  'Apprentices / Learners': 'Apprentis / Apprenants',
  'Approval blocker': 'Blocage d’approbation',
  'Approval disabled: {reason}': 'Approbation désactivée : {reason}',
  'Approval queue': 'File d’approbation',
  'Approval state': 'État d’approbation',
  'Approve / reject': 'Approuver / rejeter',
  'Approve timesheet': 'Approuver la feuille de temps',
  'Approved / export-ready': 'Approuvé / prêt à exporter',
  'Approved and ready': 'Approuvé et prêt',
  'Approved skill': 'Compétence approuvée',
  'Approved skill rates': 'Tarifs des compétences approuvées',
  'Approved skills': 'Compétences approuvées',
  'Assessments': 'Évaluations',
  'Assigned AI mentor': 'Mentor IA attribué',
  'Assigned human mentor': 'Mentor humain attribué',
  'Assigned learners': 'Apprenants attribués',
  'Assigned mentors and learning paths': 'Mentors et parcours d’apprentissage attribués',
  'Assigned to': 'Attribué à',
  'Assignment context': 'Contexte de l’affectation',
  'Assignment goal': 'Objectif de l’affectation',
  'Assignment id': 'ID de l’affectation',
  'Assignment type': 'Type d’affectation',
  'Assignments need approval': 'Affectations à approuver',
  'Assistive OCR suggestions': 'Suggestions OCR assistées',
  'At risk': 'À risque',
  'At-risk coverage': 'Couverture à risque',
  'Attendance exceptions': 'Exceptions de présence',
  'Attention queue': 'File d’attention',
  'Attention required': 'Intervention requise',
  'Authorized locations': 'Sites autorisés',
  'Availability / activity': 'Disponibilité / activité',
  'Availability windows': 'Plages de disponibilité',
  'Available location': 'Site disponible',
  'Available to claim': 'Disponible à réclamer',
  'Average learner improvement': 'Progression moyenne des apprenants',
  'Awaiting review': 'En attente d’examen',
  'Badges': 'Badges',
  'Billing / Revenue': 'Facturation / Revenus',
  'Bio and preferences': 'Biographie et préférences',
  'Blackout dates': 'Dates d’indisponibilité',
  'Blocking exceptions': 'Exceptions bloquantes',
  'Booking Requests': 'Demandes de réservation',
  'Booking request': 'Demande de réservation',
  'Breadcrumb': 'Trajet GPS',
  'Break hours': 'Heures de pause',
  'Certificate level floor': 'Niveau minimal du certificat',
  'Certified': 'Certifié',
  'Checklist items (one per line)': 'Éléments de contrôle (un par ligne)',
  'Choose AI mentor': 'Choisir un mentor IA',
  'Choose coach': 'Choisir un coach',
  'Choose human mentor': 'Choisir un mentor humain',
  'Choose learner': 'Choisir un apprenant',
  'Claims': 'Déclarations',
  'Clear': 'Effacer',
  'Clear job': 'Effacer l’emploi',
  'Clear shift': 'Effacer le quart',
  'Clear tenant context': 'Effacer le contexte du locataire',
  'Client': 'Client',
  'Client / company': 'Client / entreprise',
  'Coach': 'Coach',
  'Coach impact': 'Impact du coach',
  'Coach worker profile id': 'ID du profil travailleur du coach',
  'Coaching minutes': 'Minutes de coaching',
  'Coaching minutes received': 'Minutes de coaching reçues',
  'Coaching minutes taught': 'Minutes de coaching données',
  'Competency': 'Compétence',
  'Complete': 'Terminer',
  'Completed activities': 'Activités terminées',
  'Completed exercises': 'Exercices terminés',
  'Completed match': 'Correspondance terminée',
  'Completed modules': 'Modules terminés',
  'Completed shifts': 'Quarts terminés',
  'Completed work': 'Travail terminé',
  'Completion rate': 'Taux d’achèvement',
  'Compliance summary': 'Résumé de conformité',
  'Configure first': 'Configurer d’abord',
  'Configured server-side': 'Configuré côté serveur',
  'Contact Sales': 'Contacter les ventes',
  'Contact Sales for PDF': 'Contacter les ventes pour le PDF',
  'Contact Sales for XLSX': 'Contacter les ventes pour XLSX',
  'Contact not provided': 'Coordonnées non fournies',
  'Content kind': 'Type de contenu',
  'Context': 'Contexte',
  'Conversation context': 'Contexte de la conversation',
  'Conversation inbox': 'Boîte de réception des conversations',
  'Conversation list': 'Liste des conversations',
  'Conversation summary': 'Résumé de la conversation',
  'Conversations': 'Conversations',
  'Correct option index': 'Indice de la bonne réponse',
  'Correct writing': 'Corriger le texte',
  'Corrected': 'Corrigé',
  'Country code': 'Code de pays',
  'Covered': 'Couvert',
  'Create draft': 'Créer un brouillon',
  'Create goal': 'Créer un objectif',
  'Create tenant': 'Créer un locataire',
  'Create the first tenant': 'Créer le premier locataire',
  'Creating tenant…': 'Création du locataire…',
  'Currencies': 'Devises',
  'Current': 'Actuel',
  'Current certification path': 'Parcours de certification actuel',
  'Current focus': 'Objectif actuel',
  'Current level': 'Niveau actuel',
  'Current mentorship setup': 'Configuration actuelle du mentorat',
  'Current recommendations': 'Recommandations actuelles',
  'Current streak': 'Série actuelle',
  'Currently working': 'Au travail actuellement',
  'Custom': 'Personnalisé',
  'Custom AI mentor': 'Mentor IA personnalisé',
  'Customer / owner': 'Client / propriétaire',
  'Customize your company/agency branding': 'Personnaliser l’image de votre entreprise ou agence',
  'Dashboard quick actions': 'Actions rapides du tableau de bord',
  'Dated execution workflow': 'Flux d’exécution daté',
  'Default floor': 'Seuil par défaut',
  'Default language': 'Langue par défaut',
  'Default/provider': 'Valeur par défaut / fournisseur',
  'Delete / archive': 'Supprimer / archiver',
  'Delete / archive saved template': 'Supprimer / archiver le modèle enregistré',
  'Description / coach notes': 'Description / notes du coach',
  'Difficulty': 'Difficulté',
  'Difficulty levels': 'Niveaux de difficulté',
  'Direct application': 'Candidature directe',
  'Document archived': 'Document archivé',
  'Documents & consent': 'Documents et consentement',
  'Due date (optional)': 'Date d’échéance (facultatif)',
  'Duplicate / customize': 'Dupliquer / personnaliser',
  'Duration minutes': 'Durée en minutes',
  'ETA': 'Heure d’arrivée prévue',
  'Edit AI mentor': 'Modifier le mentor IA',
  'Edit content': 'Modifier le contenu',
  'Edit linked skills': 'Modifier les compétences liées',
  'Edit manually from this output': 'Modifier manuellement à partir de ce résultat',
  'Effective permissions': 'Autorisations effectives',
  'Eligible open shifts': 'Quarts ouverts admissibles',
  'Email / phone': 'Courriel / téléphone',
  'Employment & Access': 'Emploi et accès',
  'End date': 'Date de fin',
  'Ended at': 'Terminé le',
  'Estimated cost': 'Coût estimé',
  'Estimated minutes': 'Minutes estimées',
  'Event': 'Événement',
  'Evidence history': 'Historique des preuves',
  'Evidence records': 'Dossiers de preuve',
  'Evidence summary': 'Résumé des preuves',
  'Evidence weight': 'Pondération de la preuve',
  'Exception': 'Exception',
  'Exception state': 'État de l’exception',
  'Expiring soon': 'Expire bientôt',
  'Expiry': 'Expiration',
  'Export readiness': 'Préparation à l’exportation',
  'Export workspace': 'Espace d’exportation',
  'External tenant': 'Locataire externe',
  'Fee settings': 'Paramètres des frais',
  'File missing from storage': 'Fichier absent du stockage',
  'File unavailable': 'Fichier indisponible',
  'Filter users by tenant': 'Filtrer les utilisateurs par locataire',
  'Find or generate': 'Trouver ou générer',
  'Fit trail': 'Ajuster le trajet',
  'Floor': 'Seuil',
  'Focus': 'Objectif',
  'Free-agent visibility': 'Visibilité des travailleurs autonomes',
  'French dictée': 'Dictée française',
  'French dictée practice': 'Exercice de dictée française',
  'French progress summary': 'Résumé des progrès en français',
  'French reading comprehension': 'Compréhension écrite en français',
  'French writing correction': 'Correction de texte en français',
  'From': 'De',
  'Fully staffed': 'Effectif complet',
  'GPS trail': 'Trajet GPS',
  'Generate dictée': 'Générer une dictée',
  'Generate learning path': 'Générer un parcours d’apprentissage',
  'Generate lesson': 'Générer une leçon',
  'Generate quiz': 'Générer un quiz',
  'Generate recommendations': 'Générer des recommandations',
  'Generate roadmap': 'Générer une feuille de route',
  'Generate summary': 'Générer un résumé',
  'Generated content review': 'Examen du contenu généré',
  'Generated passage request': 'Demande de passage généré',
  'Global Branding Defaults': 'Paramètres globaux de l’image de marque',
  'Global Skill Catalog': 'Catalogue mondial des compétences',
  'Global Users': 'Utilisateurs mondiaux',
  'Go to Platform Console': 'Accéder à la console de la plateforme',
  'Goal not set': 'Objectif non défini',
  'Granted location': 'Site accordé',
  'Gross hours': 'Heures brutes',
  'Guiding questions (one per line)': 'Questions directrices (une par ligne)',
  'Hard conflicts': 'Conflits bloquants',
  'Has exception': 'Comporte une exception',
  'Has trail': 'Comporte un trajet',
  'Help': 'Aide',
  'High': 'Élevé',
  'Historical GPS trail map': 'Carte des trajets GPS historiques',
  'Historical shifts': 'Quarts antérieurs',
  'Hour': 'Heure',
  'Human Mentors': 'Mentors humains',
  'Human mentor': 'Mentor humain',
  'Human mentor worker profile id': 'ID du profil travailleur du mentor humain',
  'Incoming as mentor': 'Reçues comme mentor',
  'Incoming booking requests': 'Demandes de réservation reçues',
  'Incorrect answer reports': 'Signalements de réponses incorrectes',
  'Industry': 'Secteur',
  'Inherit from job/order': 'Hériter de l’emploi ou de la commande',
  'Instructions': 'Instructions',
  'Internal': 'Interne',
  'Invite pending': 'Invitation en attente',
  'Isolated access': 'Accès isolé',
  'Job / order breakdown': 'Répartition par emploi / commande',
  'Jobs and orders summary': 'Résumé des emplois et commandes',
  'LLM Settings': 'Paramètres du modèle IA',
  'LLM configured': 'Modèle IA configuré',
  'LLM setup required': 'Configuration du modèle IA requise',
  'Language preference': 'Préférence linguistique',
  'Last 12 hours': '12 dernières heures',
  'Last 2 hours': '2 dernières heures',
  'Last 30 min': '30 dernières minutes',
  'Last GPS': 'Dernière position GPS',
  'Last punch': 'Dernier pointage',
  'Last sign-in': 'Dernière connexion',
  'Late arrivals': 'Arrivées tardives',
  'Latest trusted GPS': 'Dernière position GPS fiable',
  'Learner': 'Apprenant',
  'Learner activity': 'Activité de l’apprenant',
  'Learner activity is required.': 'L’activité de l’apprenant est requise.',
  'Learner attempts': 'Tentatives de l’apprenant',
  'Learner current level': 'Niveau actuel de l’apprenant',
  'Learner goal': 'Objectif de l’apprenant',
  'Learner level': 'Niveau de l’apprenant',
  'Learner message': 'Message de l’apprenant',
  'Learner profile': 'Profil de l’apprenant',
  'Learner profile id': 'ID du profil de l’apprenant',
  'Learner progress': 'Progrès de l’apprenant',
  'Learner score after': 'Note de l’apprenant après',
  'Learner score before': 'Note de l’apprenant avant',
  'Learner user account': 'Compte utilisateur de l’apprenant',
  'Learner worker profile id': 'ID du profil travailleur de l’apprenant',
  'Learner-aware rotation (optional)': 'Rotation adaptée à l’apprenant (facultatif)',
  'Learners': 'Apprenants',
  'Learners coached': 'Apprenants accompagnés',
  'Learning & Mentor Catalog': 'Catalogue d’apprentissage et de mentorat',
  'Learning Content Library': 'Bibliothèque de contenu d’apprentissage',
  'Learning Paths': 'Parcours d’apprentissage',
  'Learning activity': 'Activité d’apprentissage',
  'Learning goal': 'Objectif d’apprentissage',
  'Learning intelligence': 'Analyse de l’apprentissage',
  'Learning path': 'Parcours d’apprentissage',
  'Left side': 'Côté gauche',
  'Legacy / unscheduled': 'Hérité / non planifié',
  'Lesson generator': 'Générateur de leçons',
  'Level': 'Niveau',
  'Lifecycle': 'Cycle de vie',
  'Link certificate to skills': 'Lier le certificat aux compétences',
  'Linked': 'Lié',
  'Linked Staff profile': 'Profil de personnel lié',
  'Linked partner': 'Partenaire lié',
  'Linked skills': 'Compétences liées',
  'Linked skills updated.': 'Compétences liées mises à jour.',
  'Live': 'En direct',
  'Live / break': 'En direct / en pause',
  'Live / scheduled workers': 'Travailleurs actifs / planifiés',
  'Live GPS': 'GPS en direct',
  'Live Map / Breadcrumbs': 'Carte en direct / Trajets GPS',
  'Loading': 'Chargement',
  'Location breakdown': 'Répartition par site',
  'Location fallback': 'Site de remplacement',
  'Location filters': 'Filtres de site',
  'Location id': 'ID du site',
  'Locations & Geofences': 'Sites et géoclôtures',
  'Locations table': 'Tableau des sites',
  'Low': 'Faible',
  'Mailing address line 1': 'Adresse postale, ligne 1',
  'Mailing address line 2': 'Adresse postale, ligne 2',
  'Mailing/profile address': 'Adresse postale / du profil',
  'Malformed fallback': 'Valeur de remplacement incorrecte',
  'Manage tenant': 'Gérer le locataire',
})

Object.assign(fr, {
  'Accept share': 'Accepter le partage',
  'Coaching History': 'Historique du coaching',
  'Coaching session': 'Session de coaching',
  'Continue Learning': 'Poursuivre l’apprentissage',
  'Daily Practice': 'Exercice quotidien',
  'Estimated effort': 'Effort estimé',
  'Evidence Ledger': 'Registre des preuves',
  'Evidence will appear here as lessons, assessments, coaching, and reviews are recorded.': 'Les preuves apparaîtront ici à mesure que les leçons, évaluations, séances de coaching et révisions seront enregistrées.',
  'Family Progress Sharing': 'Partage des progrès avec la famille',
  'Guardian': 'Responsable',
  'Guardian email': 'Courriel du responsable',
  'Improved by {value}': 'Amélioration de {value}',
  'Keep building skills that belong to you — across every organization you ever join.': 'Continuez à développer des compétences qui vous appartiennent, dans toutes les organisations que vous rejoignez.',
  'Learners who share progress with you will appear here.': 'Les apprenants qui partagent leurs progrès avec vous apparaîtront ici.',
  'Learning Tracks': 'Parcours d’apprentissage',
  'Learning evidence': 'Preuve d’apprentissage',
  'Loading your learning home…': 'Chargement de votre espace d’apprentissage…',
  'Mastery': 'Maîtrise',
  'Missed questions': 'Questions manquées',
  'No completed coaching sessions yet.': 'Aucune session de coaching terminée pour le moment.',
  'No guardian shares yet.': 'Aucun partage avec un responsable pour le moment.',
  'No learning data available yet.': 'Aucune donnée d’apprentissage disponible pour le moment.',
  'No saved learning activity yet.': 'Aucune activité d’apprentissage enregistrée pour le moment.',
  'Parent, guardian, tutor': 'Parent, responsable, tuteur',
  'Practice session': 'Session d’exercice',
  'Practice signals': 'Indicateurs d’exercice',
  'Progress summary is available.': 'Le résumé des progrès est disponible.',
  'Recent Activity': 'Activité récente',
  'Recommendations': 'Recommandations',
  'Recommended Next Skill': 'Prochaine compétence recommandée',
  'Recommended skill': 'Compétence recommandée',
  'Recurring mistakes': 'Erreurs récurrentes',
  'Relationship': 'Relation',
  'Resend invite': 'Renvoyer l’invitation',
  'Revoke': 'Révoquer',
  'Score': 'Note',
  'Share progress': 'Partager les progrès',
  'Shared Learner Summaries': 'Résumés partagés des apprenants',
  'Shared with': 'Partagé avec',
  'Shared with {email}': 'Partagé avec {email}',
  'Spend a few minutes practicing today.': 'Consacrez quelques minutes à vous exercer aujourd’hui.',
  'Sponsored by {name}': 'Commandité par {name}',
  'Start a track below to begin your learning path.': 'Commencez un parcours ci-dessous pour démarrer votre apprentissage.',
  'Upcoming Coaching': 'Prochaines séances de coaching',
  'View summary': 'Voir le résumé',
  'We could not accept that shared learner invitation. Please retry.': 'Impossible d’accepter cette invitation de partage. Veuillez réessayer.',
  'We could not create that guardian share. Check the email and try again.': 'Impossible de créer ce partage avec le responsable. Vérifiez le courriel et réessayez.',
  'We could not load that shared progress summary. The share may have been revoked.': 'Impossible de charger ce résumé partagé. Le partage a peut-être été révoqué.',
  'We could not load your learning home. Please retry.': 'Impossible de charger votre espace d’apprentissage. Veuillez réessayer.',
  'We could not resend that guardian invite. Please retry.': 'Impossible de renvoyer l’invitation au responsable. Veuillez réessayer.',
  'We could not revoke that guardian share. Please retry.': 'Impossible de révoquer ce partage avec le responsable. Veuillez réessayer.',
  'Weight': 'Pondération',
  'Welcome back, {name}': 'Bon retour, {name}',
  'Your learning home': 'Votre espace d’apprentissage',
  'day streak': 'jours consécutifs',
  'mastery': 'de maîtrise',
  '{channel} attempt: {status}{date}': 'Tentative par {channel} : {status}{date}',
  '{count} activities': '{count} activités',
  '{count} evidence records': '{count} dossiers de preuve',
  '{count} minutes': '{count} minutes',
  'in-app': 'dans l’application',
  'email': 'courriel',
  'none': 'aucun canal',
})

Object.assign(fr, {
  'Action': 'Action',
  'Active tenant member': 'Membre actif du locataire',
  'Add a review comment': 'Ajouter un commentaire d’examen',
  'Add comment': 'Ajouter un commentaire',
  'Add time': 'Ajouter du temps',
  'Administrative / Non-project work': 'Travail administratif / hors projet',
  'Allowed': 'Autorisé',
  'Assignee': 'Personne affectée',
  'Change assignee': 'Changer la personne affectée',
  'Clear / change': 'Effacer / changer',
  'Comments': 'Commentaires',
  'Correct': 'Corriger',
  'Correct the work description': 'Corriger la description du travail',
  'Create project': 'Créer un projet',
  'Create task': 'Créer une tâche',
  'Current assignee': 'Personne actuellement affectée',
  'Delete this draft entry?': 'Supprimer cette entrée provisoire?',
  'Disabled': 'Désactivé',
  'Disabled by Platform Admin': 'Désactivé par l’administrateur de la plateforme',
  'Draft entry deleted.': 'Entrée provisoire supprimée.',
  'Draft refreshed from this week’s entries.': 'Brouillon actualisé à partir des entrées de cette semaine.',
  'Due': 'Échéance',
  'Entry descriptions': 'Descriptions des entrées',
  'Export XLSX': 'Exporter en XLSX',
  'Loading Project Work Log…': 'Chargement du journal de travail de projet…',
  'Missing-hours warning: {hours} hours below the configured weekly target.': 'Avertissement d’heures manquantes : {hours} heures sous l’objectif hebdomadaire configuré.',
  'My Work': 'Mon travail',
  'New project name': 'Nom du nouveau projet',
  'No assignable people match this search.': 'Aucune personne pouvant être affectée ne correspond à cette recherche.',
  'No submitted project timesheets are awaiting review.': 'Aucune feuille de temps de projet soumise n’attend d’examen.',
  'No tasks match this view.': 'Aucune tâche ne correspond à cette vue.',
  'Project': 'Projet',
  'Project / task': 'Projet / tâche',
  'Project Timesheet Approvals': 'Approbations des feuilles de temps de projet',
  'Project Work Log is disabled by the platform administrator.': 'Le journal de travail de projet est désactivé par l’administrateur de la plateforme.',
  'Project tasks stay separate from workforce jobs and punch-derived shifts.': 'Les tâches de projet restent distinctes des emplois et des quarts issus des pointages.',
  'Project time — separate from punches': 'Temps de projet — distinct des pointages',
  'Project timesheet review detail': 'Détails de l’examen de la feuille de temps de projet',
  'Projects and Tasks': 'Projets et tâches',
  'Record daily task time and submit the automatically assembled Monday–Sunday timesheet.': 'Enregistrez le temps quotidien par tâche et soumettez la feuille de temps du lundi au dimanche assemblée automatiquement.',
  'Rejected entry corrected. Review and resubmit the draft.': 'Entrée rejetée corrigée. Examinez et soumettez de nouveau le brouillon.',
  'Rejection reason (required)': 'Motif du rejet (requis)',
  'Resubmit Timesheet': 'Soumettre de nouveau la feuille de temps',
  'Review details': 'Examiner les détails',
  'Review submissions, descriptions, revisions, and export readiness.': 'Examinez les soumissions, descriptions, révisions et l’état de préparation à l’exportation.',
  'Save Draft': 'Enregistrer le brouillon',
  'Save assignee': 'Enregistrer la personne affectée',
  'Save time': 'Enregistrer le temps',
  'Search by name or email': 'Rechercher par nom ou courriel',
  'Searching assignable people…': 'Recherche de personnes pouvant être affectées…',
  'Selected assignee': 'Personne affectée sélectionnée',
  'Submission detail · revision {revision}': 'Détails de la soumission · révision {revision}',
  'Submit Timesheet': 'Soumettre la feuille de temps',
  'Task': 'Tâche',
  'Task filters': 'Filtres de tâches',
  'Task or activity': 'Tâche ou activité',
  'Task title': 'Titre de la tâche',
  'Team / unassigned': 'Équipe / non affecté',
  'This week': 'Cette semaine',
  'Time entry': 'Saisie de temps',
  'Time entry saved.': 'Saisie de temps enregistrée.',
  'Timesheet resubmitted.': 'Feuille de temps soumise de nouveau.',
  'Timesheet submitted.': 'Feuille de temps soumise.',
  'Weekly Timesheet': 'Feuille de temps hebdomadaire',
  'Weekly project timesheet grid': 'Grille hebdomadaire de la feuille de temps de projet',
  'Weekly project-timesheet submission is disabled.': 'La soumission de feuilles de temps de projet hebdomadaires est désactivée.',
  'Weekly total': 'Total hebdomadaire',
  'Work date': 'Date de travail',
  'XLSX export downloaded.': 'Exportation XLSX téléchargée.',
  'revision': 'révision',
  '{hours} hours': '{hours} heures',
})

Object.assign(fr, {
  'Adjust filters or check again later.': 'Modifiez les filtres ou revenez plus tard.',
  'All work modes': 'Tous les modes de travail',
  'Application submitted.': 'Candidature soumise.',
  'Application withdrawn.': 'Candidature retirée.',
  'Applications': 'Candidatures',
  'Apply': 'Postuler',
  'Assignment actions': 'Actions de l’affectation',
  'Available work': 'Travail disponible',
  'Break ended.': 'Pause terminée.',
  'Break started.': 'Pause commencée.',
  'Cannot Attend': 'Impossible d’être présent',
  'Directions': 'Itinéraire',
  'End Break': 'Terminer la pause',
  'Hybrid': 'Hybride',
  'Invite code': 'Code d’invitation',
  'Job, company, or location': 'Emploi, entreprise ou site',
  'Link an organization to unlock work operations.': 'Liez une organisation pour accéder aux opérations de travail.',
  'Link organization': 'Lier l’organisation',
  'Matched for you': 'Sélectionnés pour vous',
  'More worker destinations': 'Autres destinations du travailleur',
  'No assignment today': 'Aucune affectation aujourd’hui',
  'No visible shifts': 'Aucun quart visible',
  'Nothing else scheduled': 'Rien d’autre de planifié',
  'Notifications': 'Notifications',
  'On My Way': 'En route',
  'On-my-way status updated.': 'Statut En route mis à jour.',
  'Open shift': 'Quart ouvert',
  'Organization invite code': 'Code d’invitation de l’organisation',
  'Organization linked. Refreshing your worker context…': 'Organisation liée. Actualisation de votre contexte de travail…',
  'Punched in.': 'Arrivée pointée.',
  'Punched out.': 'Départ pointé.',
  'Recent history': 'Historique récent',
  'Remote': 'À distance',
  'Request Cancellation': 'Demander l’annulation',
  'Running Late': 'En retard',
  'Search shifts': 'Rechercher des quarts',
  'Send “{label}” to the organization?': 'Envoyer « {label} » à l’organisation?',
  'Shift application': 'Candidature au quart',
  'Start Break': 'Commencer la pause',
  'Upcoming assignments and available work remain accessible below.': 'Les affectations à venir et le travail disponible restent accessibles ci-dessous.',
  'Upcoming today': 'À venir aujourd’hui',
  'Visible shifts show backend eligibility and every reason an application may be blocked.': 'Les quarts visibles indiquent l’admissibilité déterminée par le serveur et chaque motif pouvant bloquer une candidature.',
  'Withdraw': 'Retirer',
  'Work mode': 'Mode de travail',
  'You can continue using Learning while your worker profile is tenantless.': 'Vous pouvez continuer à utiliser Apprentissage même si votre profil travailleur n’est lié à aucun locataire.',
  'You have no additional assignments today.': 'Vous n’avez aucune autre affectation aujourd’hui.',
  'Your workday, punch state, and next actions.': 'Votre journée de travail, votre état de pointage et les prochaines actions.',
  'hr': 'h',
  '{label} sent.': '{label} envoyé.',
  '{label}: add a note': '{label} : ajouter une note',
})

Object.assign(fr, {
  'Admins & roles': 'Administrateurs et rôles',
  'Approvals': 'Approbations',
  'Approx. locations': 'Nombre approximatif de sites',
  'Approx. supervisors': 'Nombre approximatif de superviseurs',
  'Approx. workers': 'Nombre approximatif de travailleurs',
  'Billing plan': 'Forfait de facturation',
  'Company type': 'Type d’entreprise',
  'Finish later': 'Terminer plus tard',
  'First job/order': 'Premier emploi / commande',
  'First shift': 'Premier quart',
  'Loading setup context...': 'Chargement du contexte de configuration...',
  'NexusWorkforce360 setup': 'Configuration de NexusWorkforce360',
  'Not set': 'Non défini',
  'Open admin page': 'Ouvrir la page d’administration',
  'Operating model': 'Modèle opérationnel',
  'Optional step': 'Étape facultative',
  'Organization profile': 'Profil de l’organisation',
  'Progress saves after each step. You can return here from Admin Settings later.': 'La progression est enregistrée après chaque étape. Vous pourrez revenir ici plus tard depuis les paramètres administrateur.',
  'Readiness checklist': 'Liste de contrôle de préparation',
  'Reporting/export': 'Rapports / exportation',
  'Required step': 'Étape requise',
  'Setup Wizard': 'Assistant de configuration',
  'Setup progress': 'Progression de la configuration',
  'Setup skipped.': 'Configuration ignorée.',
  'Skip': 'Ignorer',
  'Skipped': 'Ignoré',
  'Welcome / workspace': 'Bienvenue / espace de travail',
  '{completed} / {total} steps touched': '{completed} / {total} étapes commencées',
  'complete': 'terminé',
  'This page could not be displayed. Please reload to try again.': 'Impossible d’afficher cette page. Actualisez-la pour réessayer.',
})

Object.assign(fr, {
  'Add time off': 'Ajouter une absence',
  'All notifications marked read.': 'Toutes les notifications ont été marquées comme lues.',
  'Application': 'Candidature',
  'Assignment, shift, organization, and supervisor conversations.': 'Conversations liées aux affectations, quarts, organisations et superviseurs.',
  'Assignments, completed work, and pending applications share one timeline.': 'Les affectations, le travail terminé et les candidatures en attente partagent une même chronologie.',
  'Availability removed.': 'Disponibilité supprimée.',
  'Availability saved.': 'Disponibilité enregistrée.',
  'Calendar': 'Calendrier',
  'Choose a conversation': 'Choisir une conversation',
  'Continue your learning journey': 'Poursuivre votre parcours d’apprentissage',
  'Day': 'Jour',
  'Friday': 'Vendredi',
  'Learning stays separate from operational punch controls.': 'L’apprentissage reste distinct des contrôles opérationnels de pointage.',
  'Manage mentor or apprentice goals, bookings, sessions, confirmations, reviews, and the read-only payment ledger from More → Mentorship.': 'Gérez les objectifs de mentor ou d’apprenti, les réservations, sessions, confirmations, évaluations et le registre de paiement en lecture seule depuis Plus → Mentorat.',
  'Mark all read': 'Tout marquer comme lu',
  'Mark read': 'Marquer comme lu',
  'Message sent.': 'Message envoyé.',
  'Messages appear here without refreshing the full workspace.': 'Les messages apparaissent ici sans actualiser tout l’espace de travail.',
  'Monday': 'Lundi',
  'No messages yet': 'Aucun message pour le moment',
  'Notification marked read.': 'Notification marquée comme lue.',
  'One-time window': 'Plage ponctuelle',
  'Open Learning': 'Ouvrir Apprentissage',
  'Open assigned learning, recommended skills, daily practice, streaks, and coaching in the existing Learning workspace.': 'Ouvrez les apprentissages attribués, compétences recommandées, exercices quotidiens, séries et séances de coaching dans l’espace Apprentissage existant.',
  'Open related item': 'Ouvrir l’élément associé',
  'Participant': 'Participant',
  'Replies are disabled for this conversation.': 'Les réponses sont désactivées pour cette conversation.',
  'Saturday': 'Samedi',
  'Save window': 'Enregistrer la plage',
  'Set your usual week and time off. Assigned shifts still take priority.': 'Définissez votre semaine habituelle et vos absences. Les quarts attribués restent prioritaires.',
  'Sunday': 'Dimanche',
  'Thursday': 'Jeudi',
  'This Week': 'Cette semaine',
  'Time Off': 'Absences',
  'Time off removed.': 'Absence supprimée.',
  'Time off saved.': 'Absence enregistrée.',
  'Tuesday': 'Mardi',
  'Update': 'Mise à jour',
  'Updates link back to the related work item when the backend provides a destination.': 'Les mises à jour renvoient à l’élément de travail associé lorsque le serveur fournit une destination.',
  'Usual Week': 'Semaine habituelle',
  'Wednesday': 'Mercredi',
  '{count} unread': '{count} non lus',
})

// Canadian French translations for the authenticated workspace shell (workspace
// switcher/cards, platform console, admin navigation, access-context header,
// Network Dashboard, and industry-preset terminology). Kept in a dedicated
// module so the large audited surface stays reviewable and testable in one place.
Object.assign(fr, frWorkspaceLocalization)
Object.assign(fr,frSupervisorOperations,frEmployment)
// Union terms fill gaps only, so shared generic keys keep their audited wording.
for (const [key, value] of Object.entries(frUnionAllocation)) if (!(key in fr)) fr[key] = value
Object.assign(ar,arSupervisorOperations)
Object.assign(tr,trSupervisorOperations)
Object.assign(fr, interfaceDensityTranslations.fr)
Object.assign(ar, commandPaletteTranslations.ar)
Object.assign(tr, commandPaletteTranslations.tr)
Object.assign(ar, interfaceDensityTranslations.ar)
Object.assign(tr, interfaceDensityTranslations.tr)
Object.assign(ar, productivityNavigationTranslations.ar)
Object.assign(tr, productivityNavigationTranslations.tr)
for (const [key, value] of Object.entries(frWorkerCredentialSummary)) if (!(key in fr)) fr[key] = value
for (const [key, value] of Object.entries(arWorkerCredentialSummary)) if (!(key in ar)) ar[key] = value
Object.assign(fr, frDocumentIntelligence)
for (const [key, value] of Object.entries(frWorkerDocumentConsent)) if (!(key in fr)) fr[key] = value
for (const [key, value] of Object.entries(arWorkerDocumentConsent)) if (!(key in ar)) ar[key] = value
for (const [key, value] of Object.entries(frWorkerAssignmentReadiness)) if (!(key in fr)) fr[key] = value
for (const [key, value] of Object.entries(arWorkerAssignmentReadiness)) if (!(key in ar)) ar[key] = value
for (const [key, value] of Object.entries(frWorkerCareer)) if (!(key in fr)) fr[key] = value
for (const [key, value] of Object.entries(arWorkerCareer)) if (!(key in ar)) ar[key] = value
for (const [key, value] of Object.entries(frWorkerOnboardingPolicy)) if (!(key in fr)) fr[key] = value
for (const [key, value] of Object.entries(arWorkerOnboardingPolicy)) if (!(key in ar)) ar[key] = value
// Worker mobile web/PWA shell strings. Assigned after the workspace module so a
// key defined in both keeps the workspace wording it already shipped with.
for (const [key, value] of Object.entries(frWorkerMobileLocalization)) if (!(key in fr)) fr[key] = value

interface I18nContextValue {
  locale: Locale
  languageName: string
  isRtl: boolean
  setLocale: (locale: Locale) => void
  toggleLocale: () => void
  t: (text: string, vars?: Record<string, string | number>) => string
  formatDate: (value?: string | number | Date | null, options?: Intl.DateTimeFormatOptions) => string
  formatDateTime: (value?: string | number | Date | null, options?: Intl.DateTimeFormatOptions) => string
  formatTime: (value?: string | number | Date | null, options?: Intl.DateTimeFormatOptions) => string
  formatNumber: (value?: number | string | null, options?: Intl.NumberFormatOptions) => string
  formatCurrency: (value?: number | string | null, currency?: string | null) => string
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

// Locale persistence is optional; storage denial must not prevent shell initialization.
const readStoredLocale = () => {
  try { return window.localStorage.getItem(STORAGE_KEY) } catch { return null }
}
const writeStoredLocale = (locale: Locale) => {
  try { window.localStorage.setItem(STORAGE_KEY, locale) } catch { /* Keep the mounted locale. */ }
}

// Application (non-public) routes historically strip a locale prefix out of the
// URL so the hand-rolled app router matches unprefixed paths, and persist that
// choice. Public marketing routes must NOT do this — see detectInitialLocale.
const stripLocalePrefixForAppRoute = () => {
  if (typeof window === 'undefined') return null
  const { pathname, search, hash } = window.location
  const match = pathname.match(/^\/(ar|en|tr|fr)(\/|$)/i)
  if (!match) return null
  const locale = match[1].toLowerCase() as Locale
  const stripped = pathname.replace(/^\/(ar|en|tr|fr)(?=\/|$)/i, '') || '/'
  window.history.replaceState(window.history.state, '', `${stripped}${search}${hash}`)
  writeStoredLocale(locale)
  return locale
}

const documentLocale = (): Locale | null => {
  if (typeof document === 'undefined') return null
  const lang = document.documentElement.lang?.toLowerCase() as Locale
  return supportedLocales.includes(lang) ? lang : null
}

// Locale precedence:
//   1. Explicit URL locale prefix (authoritative)
//   2. Server/document locale (<html lang> injected before hydration)
//   3. Saved localStorage preference
//   4. Default English
//
// On PUBLIC marketing routes the explicit URL locale wins over localStorage and
// the URL is never rewritten, so /ar/ renders Arabic even when a visitor has an
// English or French preference stored, and an unprefixed canonical English URL
// stays English regardless of a prior saved marketing locale. Application routes
// keep the historical strip-and-remember behavior.
const detectInitialLocale = (): Locale => {
  if (typeof window === 'undefined') return 'en'
  const { pathname } = window.location
  const publicInfo = classifyPublicPath(pathname)

  if (publicInfo.isPublic) {
    const stored = readStoredLocale() as Locale | null
    const storedLocale = stored && (['en', 'ar', 'fr'] as const).includes(stored as PublicMarketingLocale)
      ? (stored as PublicMarketingLocale)
      : null
    return resolvePublicPageLocale({
      pathname,
      documentLocale: documentLocale() as PublicMarketingLocale | null,
      storedLocale,
    }).locale
  }

  const prefixedLocale = stripLocalePrefixForAppRoute()
  if (prefixedLocale) return prefixedLocale
  const stored = readStoredLocale()
  if (supportedLocales.includes(stored as Locale)) return stored as Locale
  const language = window.navigator.language.toLowerCase()
  if (language.startsWith('ar')) return 'ar'
  if (language.startsWith('tr')) return 'tr'
  if (language.startsWith('fr')) return 'fr'
  return 'en'
}

export const I18nProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale)

  const setLocale = useCallback((nextLocale: Locale) => {
    setLocaleState(nextLocale)
    writeStoredLocale(nextLocale)
  }, [])

  const toggleLocale = useCallback(() => {
    const index = supportedLocales.indexOf(locale)
    setLocale(supportedLocales[(index + 1) % supportedLocales.length])
  }, [locale, setLocale])
  const t = useCallback((text: string, vars?: Record<string, string | number>) => {
    const template = dictionaries[locale][text] ?? text
    if (!vars) return template
    return template.replace(/\{(\w+)\}/g, (match, key) => (key in vars ? String(vars[key]) : match))
  }, [locale])
  const intlLocale = locale

  const normalizeDate = useCallback((value?: string | number | Date | null) => {
    if (!value) return null
    const date = value instanceof Date ? value : new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }, [])

  const formatDate = useCallback((value?: string | number | Date | null, options?: Intl.DateTimeFormatOptions) => {
    const date = normalizeDate(value)
    if (!date) return value == null || value === '' ? '-' : String(value)
    return new Intl.DateTimeFormat(intlLocale, options || { dateStyle: 'medium' }).format(date)
  }, [intlLocale, normalizeDate])

  const formatDateTime = useCallback((value?: string | number | Date | null, options?: Intl.DateTimeFormatOptions) => {
    const date = normalizeDate(value)
    if (!date) return value == null || value === '' ? '-' : String(value)
    return new Intl.DateTimeFormat(intlLocale, options || { dateStyle: 'short', timeStyle: 'short' }).format(date)
  }, [intlLocale, normalizeDate])

  const formatTime = useCallback((value?: string | number | Date | null, options?: Intl.DateTimeFormatOptions) => {
    const date = normalizeDate(value)
    if (!date) return value == null || value === '' ? '-' : String(value)
    return new Intl.DateTimeFormat(intlLocale, options || { hour: 'numeric', minute: '2-digit' }).format(date)
  }, [intlLocale, normalizeDate])

  const formatNumber = useCallback((value?: number | string | null, options?: Intl.NumberFormatOptions) => {
    const number = Number(value)
    if (!Number.isFinite(number)) return value == null || value === '' ? '-' : String(value)
    return new Intl.NumberFormat(intlLocale, options).format(number)
  }, [intlLocale])

  const formatCurrency = useCallback((value?: number | string | null, currency?: string | null) => {
    const number = Number(value)
    if (!Number.isFinite(number)) return t('Rate not shown')
    const resolvedCurrency = currency || 'CAD'
    return new Intl.NumberFormat(intlLocale, { style: 'currency', currency: resolvedCurrency }).format(number)
  }, [intlLocale, t])

  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'
    supportedLocales.forEach((supportedLocale) => {
      document.body.classList.toggle(`locale-${supportedLocale}`, locale === supportedLocale)
    })
  }, [locale])

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    languageName: localeNames[locale],
    isRtl: locale === 'ar',
    setLocale,
    toggleLocale,
    t,
    formatDate,
    formatDateTime,
    formatTime,
    formatNumber,
    formatCurrency,
  }), [locale, setLocale, toggleLocale, t, formatDate, formatDateTime, formatTime, formatNumber, formatCurrency])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => {
  const context = useContext(I18nContext)
  if (!context) throw new Error('useI18n must be used within I18nProvider')
  return context
}

Object.assign(fr, tableColumnTranslations.fr)
Object.assign(ar, tableColumnTranslations.ar)
Object.assign(tr, tableColumnTranslations.tr)

Object.assign(fr, smartTableTranslations.fr)
Object.assign(ar, smartTableTranslations.ar)
Object.assign(tr, smartTableTranslations.tr)

Object.assign(fr, personalTableViewTranslations.fr)
Object.assign(ar, personalTableViewTranslations.ar)
Object.assign(tr, personalTableViewTranslations.tr)

Object.assign(fr, enterpriseWorkforceFr)
